import { Inject } from '@angular/core';
import templateSource from './app.component.html';
              import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz');
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy } from '@angular/core';
import * as THREE from 'three';

type TouchKey = 'forward' | 'back' | 'left' | 'right';
type PlatformKind = 'ice' | 'woodDisk' | 'fragileIce' | 'shakyIce' | 'fireBridge' | 'slipperyIce' | 'bouncePad';
type ShopItemType = 'passive' | 'consumable' | 'costume' | 'vehicle';
type ShopTab = 'costume' | 'gear' | 'vehicle';

interface Platform {
    mesh: THREE.Group;
    x: number;
    y: number;
    z: number;
    w: number;
    h: number;
    d: number;
    startX: number;
    startY: number;
    startZ: number;
    axis?: 'x' | 'z';
    distance?: number;
    moveX?: number;
    moveZ?: number;
    speed?: number;
    phase?: number;
    kind?: PlatformKind;
    tower?: boolean;
    shaky?: boolean;
    shakePower?: number;
    shakeGroup?: THREE.Group;
    slippery?: boolean;
    fireBridge?: boolean;
    fireCycle?: number;
    fireOffDuration?: number;
    firePhase?: number;
    fireGroup?: THREE.Group;
    bouncePad?: boolean;
    bouncePower?: number;
    bounceGroup?: THREE.Group;
    fragile?: boolean;
    fragileTimer?: number;
    breakLimit?: number;
    broken?: boolean;
    respawnTimer?: number;
    warningGroup?: THREE.Group;
}

interface Gift {
    mesh: THREE.Group;
    x: number;
    y: number;
    z: number;
    baseX: number;
    baseY: number;
    baseZ: number;
    collected: boolean;
}

interface Coin {
    mesh: THREE.Group;
    x: number;
    y: number;
    z: number;
    baseX: number;
    baseY: number;
    baseZ: number;
    collected: boolean;
}

interface HeartPickup {
    mesh: THREE.Group;
    x: number;
    y: number;
    z: number;
    collected: boolean;
}

interface Checkpoint {
    mesh: THREE.Group;
    x: number;
    y: number;
    z: number;
    index: number;
    reached: boolean;
}

interface GiftBurstParticle {
    mesh: THREE.Mesh;
    velocity: THREE.Vector3;
    spin: THREE.Vector3;
}

interface GiftBurst {
    group: THREE.Group;
    particles: GiftBurstParticle[];
    age: number;
    duration: number;
}

interface Enemy {
    mesh: THREE.Group;
    x: number;
    z: number;
    baseX: number;
    baseZ: number;
    y: number;
    axis: 'x' | 'z';
    distance: number;
    speed: number;
    phase: number;
    chasing?: boolean;
    chaseSpeed?: number;
    chaseRange?: number;
}

interface MeteorHazard {
    group: THREE.Group;
    warning: THREE.Mesh;
    body: THREE.Group;
    x: number;
    z: number;
    top: number;
    radius: number;
    timer: number;
    fallSpeed: number;
    falling: boolean;
    hit: boolean;
}

interface PlatformSpec {
    x: number;
    y: number;
    z: number;
    w: number;
    d: number;
    h?: number;
    axis?: 'x' | 'z';
    distance?: number;
    moveX?: number;
    moveZ?: number;
    speed?: number;
    phase?: number;
    kind?: PlatformKind;
    tower?: boolean;
    shaky?: boolean;
    shakePower?: number;
    slippery?: boolean;
    fireBridge?: boolean;
    fireCycle?: number;
    fireOffDuration?: number;
    firePhase?: number;
    bouncePad?: boolean;
    bouncePower?: number;
    fragile?: boolean;
    breakLimit?: number;
}

interface EnemySpec {
    x: number;
    z: number;
    axis: 'x' | 'z';
    distance: number;
    speed: number;
    phase?: number;
    chasing?: boolean;
    chaseSpeed?: number;
    chaseRange?: number;
}

interface LevelLayout {
    title: string;
    time: number;
    spawn: [number, number];
    goal: [number, number];
    platforms: PlatformSpec[];
    gifts: Array<[number, number]>;
    checkpoints?: Array<[number, number]>;
    enemies: EnemySpec[];
}

interface StageNode {
    gx: number;
    gz: number;
    w: number;
    d: number;
    y?: number;
    kind?: PlatformKind;
    tower?: boolean;
    axis?: 'x' | 'z';
    distance?: number;
    moveX?: number;
    moveZ?: number;
    speed?: number;
    phase?: number;
    shakePower?: number;
    fireCycle?: number;
    fireOffDuration?: number;
    firePhase?: number;
    bouncePower?: number;
    breakLimit?: number;
    enemyAxis?: 'x' | 'z' | 'both';
    gifts?: number;
}

interface MinimapPoint {
    x: number;
    y: number;
    collected?: boolean;
}

interface MinimapPlatform {
    left: number;
    top: number;
    width: number;
    height: number;
    kind?: PlatformKind;
}

interface MinimapBounds {
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
}

interface PlayerProfile {
    id: string;
    email: string;
    name: string;
    role: 'user' | 'admin' | string;
    coins: number;
    current_level: number;
    best_stage: number;
    best_score: number;
    total_coins: number;
    total_gifts: number;
    inventory: Record<string, boolean>;
    items: Record<string, number>;
    equipped_costume: string;
    equipped_vehicle: string;
}

type AdminUser = PlayerProfile & {
    created?: string;
    updated?: string;
    last_login?: string | null;
};

interface ShopItem {
    id: string;
    name: string;
    type: ShopItemType;
    price: number;
    description: string;
}

interface RankingEntry {
    rank: number;
    id?: string;
    name: string;
    best_stage: number;
    best_score: number;
    total_coins: number;
}

interface SnowApiResult {
    ok: boolean;
    data: Record<string, unknown>;
    message?: string;
    offline?: boolean;
}

interface BgmNote {
    midi?: number;
    beats: number;
    velocity?: number;
}

interface DimensionPortal {
    group: THREE.Group;
    x: number;
    y: number;
    z: number;
    target: THREE.Vector3;
    cooldown: number;
}

const pixelMaterial = (parameters: THREE.MeshStandardMaterialParameters): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({
        roughness: 0.92,
        metalness: 0,
        flatShading: true,
        ...parameters
    });

@Component({
    selector: 'app-root',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/app.component.scss */
:host {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background: #071426;
  overflow: hidden;
  image-rendering: pixelated;
}

.game-shell {
  position: relative;
  width: 100vw;
  height: 100vh;
  min-height: 560px;
  overflow: hidden;
  background: #050a18;
  color: #17202a;
  font-family: "Courier New", "Monaco", ui-monospace, monospace;
  text-rendering: geometricPrecision;
}

.game-shell::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 8;
  pointer-events: none;
  opacity: 0;
  background: repeating-linear-gradient(90deg, rgba(255, 45, 45, 0.28) 0 12px, transparent 12px 24px), repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.12) 0 8px, transparent 8px 18px), rgba(122, 0, 0, 0.3);
}

.game-shell.damage-flash::after {
  animation: damageFlash 0.48s steps(4, end);
}

.game-viewport {
  position: absolute;
  inset: 0;
}

.game-shell.custom-mode .game-viewport,
.game-shell.shop-mode .game-viewport {
  z-index: 20;
  pointer-events: none;
}

.game-viewport canvas {
  display: block;
  width: 100%;
  height: 100%;
  image-rendering: pixelated;
}

.sky-effects {
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
}

.sky-star-layer {
  position: absolute;
  left: -3vw;
  top: -3vh;
  width: 3px;
  height: 3px;
  background: #f8fbff;
  opacity: 0.92;
  image-rendering: pixelated;
  animation: skyTwinkle 2.4s steps(2, end) infinite;
}

.layer-a {
  box-shadow: 9vw 13vh 0 #fff8ce, 17vw 61vh 0 #d7ecff, 27vw 24vh 0 #ffffff, 36vw 74vh 0 #bfe1ff, 45vw 18vh 0 #fff6d8, 53vw 48vh 0 #ffffff, 64vw 31vh 0 #cfe7ff, 72vw 67vh 0 #fff8ce, 83vw 22vh 0 #ffffff, 94vw 55vh 0 #d7ecff, 103vw 36vh 0 #fff6d8;
}

.layer-b {
  width: 2px;
  height: 2px;
  background: #cfe7ff;
  animation-duration: 3.1s;
  animation-delay: 0.65s;
  box-shadow: 6vw 35vh 0 #d7ecff, 14vw 78vh 0 #ffffff, 24vw 45vh 0 #fff7d8, 32vw 12vh 0 #ffffff, 43vw 67vh 0 #b8d8ff, 57vw 26vh 0 #ffffff, 68vw 82vh 0 #fff7d8, 78vw 43vh 0 #cfe7ff, 88vw 73vh 0 #ffffff, 96vw 18vh 0 #b8d8ff;
}

.layer-c {
  width: 5px;
  height: 5px;
  background: #fff2ae;
  opacity: 0.78;
  animation-duration: 1.8s;
  animation-delay: 1.15s;
  box-shadow: 11vw 84vh 0 #fff2ae, 31vw 55vh 0 #ffffff, 49vw 8vh 0 #fff2ae, 61vw 72vh 0 #dceeff, 76vw 15vh 0 #ffffff, 91vw 39vh 0 #fff2ae;
}

.sky-meteor {
  --from-x: 112vw;
  --from-y: -8vh;
  --to-x: -24vw;
  --to-y: 56vh;
  --duration: 7.2s;
  --delay: 0s;
  position: absolute;
  left: 0;
  top: 0;
  width: 124px;
  height: 4px;
  opacity: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0), #9fd8ff 38%, #fff6c9 72%, #ffffff 100%);
  box-shadow: 0 0 0 2px rgba(160, 216, 255, 0.18), 0 0 18px rgba(255, 249, 213, 0.7);
  transform-origin: right center;
  animation: meteorFall var(--duration) linear infinite;
  animation-delay: var(--delay);
}

.meteor-b {
  --from-x: 108vw;
  --from-y: 12vh;
  --to-x: -18vw;
  --to-y: 72vh;
  --duration: 9.6s;
  --delay: 2.2s;
  width: 94px;
}

.meteor-c {
  --from-x: 126vw;
  --from-y: 34vh;
  --to-x: 8vw;
  --to-y: 92vh;
  --duration: 12s;
  --delay: 5.4s;
  width: 78px;
}

@keyframes skyTwinkle {
  0%, 100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
}
@keyframes meteorFall {
  0%, 48% {
    opacity: 0;
    transform: translate3d(var(--from-x), var(--from-y), 0) rotate(-28deg);
  }
  54% {
    opacity: 1;
  }
  66% {
    opacity: 0.92;
  }
  82%, 100% {
    opacity: 0;
    transform: translate3d(var(--to-x), var(--to-y), 0) rotate(-28deg);
  }
}
@keyframes damageFlash {
  0% {
    opacity: 0;
  }
  18% {
    opacity: 0.92;
  }
  36% {
    opacity: 0.18;
  }
  58% {
    opacity: 0.74;
  }
  100% {
    opacity: 0;
  }
}
@keyframes damageBanner {
  0% {
    transform: translateX(-50%) scale(0.82);
    filter: brightness(1.6);
  }
  18% {
    transform: translateX(-50%) scale(1.08);
  }
  38% {
    transform: translateX(-50%) scale(1);
  }
  100% {
    transform: translateX(-50%) scale(1);
    filter: brightness(1);
  }
}
@keyframes gameOverEnter {
  0% {
    opacity: 0;
    transform: scale(1.08);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
@keyframes gameOverNoise {
  0%, 100% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(-8px, 6px, 0);
  }
}
.hud {
  position: absolute;
  top: 14px;
  left: 14px;
  right: 14px;
  z-index: 5;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: start;
  gap: 12px;
  pointer-events: none;
}

.game-shell.paused-mode .hud,
.game-shell.menu-mode .hud {
  z-index: 12;
}

.player-hud {
  justify-self: start;
  display: grid;
  gap: 10px;
}

.lives-hud,
.time-hud,
.gift-hud,
.coin-hud,
.account-hud,
.level-hud,
.minimap-hud,
.menu-btn,
.game-menu-action,
.banner,
.touch-controls button {
  border: 2px solid rgba(255, 242, 146, 0.72);
  background: rgba(3, 7, 16, 0.42);
  box-shadow: 5px 5px 0 rgba(0, 0, 0, 0.44);
  backdrop-filter: none;
}

.lives-hud,
.gift-hud,
.coin-hud,
.time-hud,
.level-hud {
  display: flex;
  align-items: center;
  color: #ffe66d;
  font-weight: 950;
  letter-spacing: 0;
  text-shadow: 2px 2px 0 #5b3713, 4px 4px 0 #05060b;
}

.right-hud {
  justify-self: end;
  display: grid;
  justify-items: end;
  grid-template-columns: auto auto auto;
  gap: 8px;
  padding-right: 86px;
}

.account-hud {
  display: grid;
  gap: 3px;
  min-width: 148px;
  min-height: 54px;
  padding: 8px 10px;
  color: #d8e9ff;
  font-size: 10px;
  line-height: 1;
  text-transform: uppercase;
}

.account-hud strong {
  max-width: 142px;
  overflow: hidden;
  color: #ffffff;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-shadow: 2px 2px 0 #05060b;
}

.account-hud span {
  max-width: 142px;
  overflow: hidden;
  color: #91f0ff;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.top-hud {
  justify-self: center;
  display: grid;
  justify-items: center;
  gap: 7px;
  pointer-events: auto;
}

.lives-hud {
  gap: 10px;
  min-height: 54px;
  padding: 8px 14px;
  font-size: 30px;
  line-height: 1;
}

.lives-hud small {
  align-self: end;
  color: #d8e9ff;
  font-size: 10px;
  text-shadow: 2px 2px 0 #05060b;
}

.life-heart {
  position: relative;
  width: 30px;
  height: 26px;
  background: #f03f4d;
  box-shadow: -9px 0 0 #f03f4d, 9px 0 0 #f03f4d, 0 9px 0 #f03f4d, 0 18px 0 #f03f4d, 5px 23px 0 #8d1828, 5px 5px 0 #05060b;
}

.life-heart::before,
.life-heart::after {
  content: "";
  position: absolute;
  top: -9px;
  width: 15px;
  height: 15px;
  background: #ff6b75;
}

.life-heart::before {
  left: -9px;
}

.life-heart::after {
  right: -9px;
}

.minimap-hud {
  width: 222px;
  padding: 8px;
  color: #fff1a6;
}

.minimap-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  color: #ffe66d;
  font-size: 12px;
  font-weight: 950;
  line-height: 1;
  text-shadow: 2px 2px 0 #5b3713, 4px 4px 0 #05060b;
}

.minimap-head strong {
  color: #ffffff;
}

.minimap-canvas {
  position: relative;
  width: 100%;
  height: 138px;
  overflow: hidden;
  border: 2px solid rgba(145, 240, 255, 0.68);
  background: linear-gradient(90deg, rgba(145, 240, 255, 0.08) 1px, transparent 1px), linear-gradient(0deg, rgba(145, 240, 255, 0.08) 1px, transparent 1px), rgba(3, 9, 23, 0.72);
  background-size: 12px 12px;
  box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.35), 4px 4px 0 rgba(0, 0, 0, 0.38);
}

.minimap-canvas.admin-warp-enabled {
  cursor: crosshair;
  pointer-events: auto;
}

.minimap-platform,
.minimap-gift,
.minimap-heart,
.minimap-checkpoint,
.minimap-player,
.minimap-goal {
  position: absolute;
  display: block;
  transform: translate(-50%, -50%);
  image-rendering: pixelated;
}

.minimap-platform {
  transform: none;
  border: 1px solid rgba(188, 232, 255, 0.28);
  background: rgba(132, 207, 255, 0.18);
}

.minimap-platform.wood {
  background: rgba(240, 187, 98, 0.22);
  border-color: rgba(240, 187, 98, 0.38);
}

.minimap-platform.fragile {
  background: rgba(110, 231, 255, 0.28);
  border-color: rgba(255, 61, 76, 0.5);
}

.minimap-gift {
  width: 8px;
  height: 8px;
  background: #ffc342;
  box-shadow: 0 0 0 2px #7a3e12, 0 0 8px rgba(255, 238, 120, 0.65);
}

.minimap-gift.collected {
  opacity: 0.18;
  box-shadow: 0 0 0 1px #273343;
}

.minimap-heart {
  z-index: 2;
  width: 8px;
  height: 8px;
  background: #ff3f6d;
  transform: translate(-50%, -50%) rotate(45deg);
  box-shadow: 0 0 0 2px #6e1026, 0 0 8px rgba(255, 63, 109, 0.72);
}

.minimap-heart::before,
.minimap-heart::after {
  content: "";
  position: absolute;
  width: 8px;
  height: 8px;
  background: #ff3f6d;
}

.minimap-heart::before {
  left: -4px;
  top: 0;
}

.minimap-heart::after {
  left: 0;
  top: -4px;
}

.minimap-heart.collected {
  opacity: 0.18;
  box-shadow: 0 0 0 1px #273343;
}

.minimap-checkpoint {
  z-index: 2;
  width: 10px;
  height: 10px;
  border: 2px solid #76eeff;
  background: rgba(118, 238, 255, 0.2);
  box-shadow: 0 0 0 2px #07101d, 0 0 8px rgba(118, 238, 255, 0.72);
}

.minimap-checkpoint.reached {
  border-color: #fff173;
  background: rgba(255, 241, 115, 0.36);
  box-shadow: 0 0 0 2px #07101d, 0 0 10px rgba(255, 241, 115, 0.9);
}

.minimap-player {
  z-index: 3;
  width: 13px;
  height: 13px;
  background: #f03f4d;
  box-shadow: 0 0 0 3px #fff8ef, 0 0 0 5px #07101d, 0 0 10px rgba(255, 241, 166, 0.86);
}

.minimap-player::after {
  content: "";
  position: absolute;
  left: 4px;
  top: -8px;
  width: 5px;
  height: 8px;
  background: #fff8ef;
}

.minimap-goal {
  z-index: 2;
  width: 14px;
  height: 14px;
  border: 3px solid #91f0ff;
  background: rgba(116, 231, 255, 0.22);
  box-shadow: 0 0 10px rgba(116, 231, 255, 0.8);
}

.time-hud {
  justify-self: center;
  min-width: 140px;
  justify-content: center;
  padding: 4px 18px 6px;
  color: #ffe970;
  font-size: clamp(34px, 4.6vw, 54px);
  line-height: 1;
}

.gift-hud {
  gap: 10px;
  min-height: 54px;
  padding: 8px 12px 8px 16px;
  font-size: clamp(28px, 3.5vw, 42px);
  line-height: 1;
}

.coin-hud {
  gap: 8px;
  min-height: 54px;
  padding: 8px 12px;
  color: #fff1a6;
  font-size: clamp(24px, 3vw, 34px);
  line-height: 1;
}

.gift-mark {
  position: relative;
  width: 34px;
  height: 34px;
  background: #d3a12d;
  box-shadow: 4px 4px 0 #05060b;
}

.gift-mark::before,
.gift-mark::after {
  content: "";
  position: absolute;
  background: #e52b37;
}

.gift-mark::before {
  left: 13px;
  top: -2px;
  width: 8px;
  height: 38px;
}

.gift-mark::after {
  left: -2px;
  top: 13px;
  width: 38px;
  height: 8px;
}

.coin-mark {
  width: 28px;
  height: 28px;
  border: 4px solid #9f6f18;
  background: #ffdf53;
  box-shadow: 4px 4px 0 #05060b, inset 5px 0 0 rgba(255, 255, 255, 0.38), inset -5px 0 0 rgba(120, 73, 0, 0.28);
}

.menu-btn {
  position: absolute;
  top: 0;
  right: 0;
  min-height: 46px;
  padding: 0 16px;
  border-radius: 0;
  background: rgba(35, 76, 120, 0.76);
  color: #f5fbff;
  font-weight: 850;
  text-shadow: 2px 2px 0 #05060b;
  text-transform: uppercase;
  pointer-events: auto;
  cursor: pointer;
}

.level-hud {
  position: absolute;
  left: 18px;
  bottom: 18px;
  z-index: 5;
  padding: 8px 14px;
  color: #ffe66d;
  font-size: clamp(28px, 4.2vw, 46px);
  line-height: 1;
  pointer-events: none;
}

.banner {
  position: absolute;
  left: 50%;
  top: 96px;
  z-index: 6;
  transform: translateX(-50%);
  display: grid;
  justify-items: center;
  gap: 4px;
  min-width: min(420px, 100vw - 32px);
  padding: 14px 18px;
  border-radius: 0;
  text-align: center;
  color: #ffe66d;
}

.banner strong {
  font-size: 22px;
  line-height: 1.15;
  text-shadow: 2px 2px 0 #5b3713, 4px 4px 0 #05060b;
}

.banner span {
  min-height: 17px;
  font-size: 13px;
  color: #dbe9ff;
  text-shadow: 2px 2px 0 #05060b;
}

.banner-danger {
  top: 38%;
  min-width: min(560px, 100vw - 32px);
  padding: 18px 24px 20px;
  border: 4px solid #fff1a6;
  background: repeating-linear-gradient(90deg, rgba(255, 241, 166, 0.13) 0 10px, transparent 10px 20px), #5b1015;
  box-shadow: 0 0 0 4px #140306, 10px 10px 0 rgba(0, 0, 0, 0.62), 0 0 34px rgba(255, 45, 45, 0.52);
  color: #fff06f;
  animation: damageBanner 1.55s steps(2, end);
}

.banner-danger::before,
.banner-danger::after {
  content: "";
  position: absolute;
  top: 50%;
  width: 18px;
  height: 42px;
  background: #ff2e3a;
  box-shadow: 0 0 0 4px #140306, 0 -18px 0 #fff1a6, 0 18px 0 #fff1a6;
  transform: translateY(-50%);
}

.banner-danger::before {
  left: 18px;
}

.banner-danger::after {
  right: 18px;
}

.banner-danger strong {
  color: #fff06f;
  font-size: clamp(42px, 8vw, 88px);
  line-height: 0.88;
  text-shadow: 4px 4px 0 #c41d2c, 8px 8px 0 #140306;
}

.banner-danger span {
  min-height: 0;
  color: #ffffff;
  font-size: clamp(18px, 3vw, 30px);
  font-weight: 950;
  line-height: 1;
  text-shadow: 3px 3px 0 #c41d2c, 6px 6px 0 #140306;
}

.game-menu-screen {
  position: absolute;
  inset: 0;
  z-index: 9;
  display: grid;
  place-items: center;
  padding: 24px;
  background: repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.028) 0 8px, transparent 8px 16px), rgba(1, 5, 13, 0.48);
  pointer-events: auto;
}

.game-menu-panel {
  display: grid;
  justify-items: center;
  gap: 18px;
  min-width: min(340px, 100vw - 48px);
  padding: 28px 30px 30px;
  border: 4px solid #91f0ff;
  background: rgba(5, 12, 24, 0.88);
  box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.82), 10px 10px 0 rgba(0, 0, 0, 0.55), inset 0 0 0 4px rgba(255, 241, 166, 0.18);
}

.game-menu-panel strong {
  color: #ffe970;
  font-size: clamp(42px, 7vw, 72px);
  line-height: 0.9;
  text-shadow: 3px 3px 0 #5b3713, 6px 6px 0 #05060b;
}

.game-menu-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(118px, 1fr));
  gap: 12px;
  width: min(360px, 100vw - 82px);
}

.game-menu-action {
  min-height: 48px;
  padding: 0 16px;
  border-radius: 0;
  background: #4f8d57;
  color: #ffffff;
  font-size: 16px;
  font-weight: 950;
  text-shadow: 2px 2px 0 #253d24;
  text-transform: uppercase;
  cursor: pointer;
}

.game-over-screen {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: grid;
  place-items: center;
  overflow: hidden;
  padding: 24px;
  background: radial-gradient(circle at 50% 34%, rgba(126, 14, 23, 0.64), transparent 38%), repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.035) 0 8px, transparent 8px 16px), repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.03) 0 8px, transparent 8px 16px), rgba(2, 3, 8, 0.9);
  color: #fff1a6;
  pointer-events: auto;
  animation: gameOverEnter 0.32s steps(4, end);
}

.game-over-noise {
  position: absolute;
  inset: -20px;
  opacity: 0.34;
  background: repeating-linear-gradient(0deg, transparent 0 7px, rgba(255, 255, 255, 0.08) 7px 9px), repeating-linear-gradient(90deg, transparent 0 11px, rgba(255, 46, 58, 0.08) 11px 14px);
  animation: gameOverNoise 0.7s steps(2, end) infinite;
}

.game-over-panel {
  position: relative;
  width: min(780px, 100vw - 40px);
  display: grid;
  justify-items: center;
  gap: 14px;
  padding: 30px 28px 34px;
  border: 7px solid #fff1a6;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px), #26070d;
  background-size: 12px 12px;
  box-shadow: 0 0 0 7px #080107, 14px 14px 0 rgba(0, 0, 0, 0.72), inset 0 0 0 7px rgba(255, 46, 58, 0.28);
  text-align: center;
}

.dead-santa {
  position: relative;
  width: 82px;
  height: 78px;
  background: #d82431;
  box-shadow: 0 -18px 0 #fff8ef, 0 -34px 0 -8px #d82431, -22px 24px 0 -8px #fff8ef, 22px 24px 0 -8px #fff8ef, 0 42px 0 -8px #151923, 7px 7px 0 #060106;
}

.dead-santa::before,
.dead-santa::after {
  content: "";
  position: absolute;
  top: 22px;
  width: 18px;
  height: 6px;
  background: #07070a;
  box-shadow: 0 8px 0 #07070a;
}

.dead-santa::before {
  left: 18px;
  transform: rotate(45deg);
}

.dead-santa::after {
  right: 18px;
  transform: rotate(-45deg);
}

.broken-hearts {
  display: flex;
  gap: 18px;
  height: 26px;
  align-items: center;
}

.broken-hearts span {
  position: relative;
  width: 22px;
  height: 20px;
  background: #3a1019;
  box-shadow: -7px 0 0 #3a1019, 7px 0 0 #3a1019, 0 7px 0 #3a1019, 4px 14px 0 #100307, 0 0 0 4px #ff2e3a;
}

.broken-hearts span::after {
  content: "";
  position: absolute;
  left: 9px;
  top: -4px;
  width: 5px;
  height: 30px;
  background: #fff1a6;
  transform: rotate(18deg);
}

.game-over-reason,
.game-over-copy {
  margin: 0;
  color: #ffffff;
  font-weight: 950;
  text-shadow: 3px 3px 0 #a70f1f, 6px 6px 0 #070106;
}

.game-over-reason {
  padding: 8px 14px;
  border: 4px solid #ff2e3a;
  background: #080107;
  font-size: clamp(18px, 2.5vw, 30px);
}

.game-over-panel h2 {
  margin: 0;
  color: #fff06f;
  font-size: clamp(58px, 11vw, 132px);
  line-height: 0.82;
  letter-spacing: 0;
  text-shadow: 5px 5px 0 #ff2e3a, 10px 10px 0 #070106, 15px 15px 0 rgba(0, 0, 0, 0.45);
}

.game-over-copy {
  font-size: clamp(24px, 4vw, 44px);
  color: #dbe9ff;
}

.restart-button {
  min-width: min(360px, 100vw - 92px);
  padding: 16px 28px;
  border: 6px solid #fff1a6;
  border-radius: 0;
  background: #7fa75a;
  color: #ffffff;
  box-shadow: 7px 0 0 #111a28, 0 7px 0 #111a28, 12px 12px 0 rgba(0, 0, 0, 0.64);
  font: inherit;
  font-size: clamp(30px, 5vw, 58px);
  font-weight: 950;
  line-height: 1;
  text-shadow: 3px 3px 0 #2f4e2f, 6px 6px 0 #070106;
  cursor: pointer;
}

.restart-button:active {
  transform: translate(6px, 6px);
  box-shadow: 2px 0 0 #111a28, 0 2px 0 #111a28;
}

.game-over-home-button {
  min-width: min(240px, 100vw - 110px);
  padding: 12px 22px;
  border: 5px solid #91f0ff;
  border-radius: 0;
  background: #244a78;
  color: #ffffff;
  box-shadow: 5px 0 0 #111a28, 0 5px 0 #111a28, 9px 9px 0 rgba(0, 0, 0, 0.58);
  font: inherit;
  font-size: clamp(18px, 3vw, 34px);
  font-weight: 950;
  line-height: 1;
  text-shadow: 2px 2px 0 #123056, 5px 5px 0 #070106;
  cursor: pointer;
}

.game-over-home-button:active {
  transform: translate(4px, 4px);
  box-shadow: 1px 0 0 #111a28, 0 1px 0 #111a28;
}

.start-screen {
  position: absolute;
  inset: 0;
  z-index: 7;
  display: grid;
  align-content: start;
  align-items: start;
  justify-items: center;
  gap: 18px;
  box-sizing: border-box;
  padding: clamp(44px, 8vh, 92px) clamp(16px, 3vw, 32px) clamp(28px, 6vh, 64px);
  overflow-x: hidden;
  overflow-y: auto;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px), linear-gradient(180deg, rgba(12, 31, 77, 0.64), rgba(3, 8, 20, 0.98));
  background-size: 16px 16px, 16px 16px, 100% 100%;
  isolation: isolate;
  pointer-events: auto;
}

.start-screen::before,
.start-screen::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.start-screen::before {
  display: none;
}

.start-screen::after {
  background: radial-gradient(circle at 50% 48%, transparent 0 31%, rgba(1, 4, 13, 0.34) 66%, rgba(1, 4, 13, 0.68) 100%);
}

.next-stage-screen {
  align-content: center;
  align-items: center;
  justify-items: center;
  background: rgba(3, 8, 20, 0.36);
}

.pixel-sky {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.star {
  position: absolute;
  width: 4px;
  height: 4px;
  background: #fff5cf;
  box-shadow: 0 0 0 2px rgba(255, 245, 207, 0.18);
  animation: pixelTwinkle 1.7s steps(2, end) infinite;
}

.s1 {
  left: 16%;
  top: 22%;
}

.s2 {
  left: 72%;
  top: 18%;
  animation-delay: 0.45s;
}

.s3 {
  left: 82%;
  top: 62%;
  animation-delay: 0.9s;
}

.s4 {
  left: 24%;
  top: 70%;
  animation-delay: 1.2s;
}

.s5 {
  left: 42%;
  top: 16%;
  width: 6px;
  height: 6px;
  animation-delay: 0.25s;
}

.s6 {
  left: 58%;
  top: 78%;
  animation-delay: 0.7s;
}

.s7 {
  left: 89%;
  top: 34%;
  width: 3px;
  height: 3px;
  animation-delay: 1.05s;
}

.s8 {
  left: 8%;
  top: 52%;
  width: 5px;
  height: 5px;
  animation-delay: 1.35s;
}

.pixel-meteor {
  position: absolute;
  width: 96px;
  height: 6px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0), #8ed1ff 34%, #fff7cb 72%, #ffffff 100%);
  box-shadow: 0 0 0 2px rgba(255, 247, 203, 0.16);
  opacity: 0;
  animation: pixelMeteor 5.8s linear infinite;
}

.pm1 {
  left: 78%;
  top: 12%;
}

.pm2 {
  left: 96%;
  top: 42%;
  width: 70px;
  animation-duration: 8.2s;
  animation-delay: 2.4s;
}

.cloud {
  position: absolute;
  width: 96px;
  height: 18px;
  background: #9bb7cf;
  box-shadow: 18px -18px 0 #9bb7cf, 36px -30px 0 #9bb7cf, 54px -18px 0 #9bb7cf, 72px 0 0 #9bb7cf, 0 18px 0 rgba(35, 55, 82, 0.65), 18px 18px 0 rgba(35, 55, 82, 0.65), 36px 18px 0 rgba(35, 55, 82, 0.65), 54px 18px 0 rgba(35, 55, 82, 0.65);
  opacity: 0.34;
}

.c1 {
  left: 12%;
  top: 18%;
}

.c2 {
  right: 12%;
  top: 28%;
  transform: scale(0.74);
}

.pixel-panel {
  position: relative;
  z-index: 2;
  width: min(720px, 100vw - 48px);
  display: grid;
  justify-items: center;
  gap: 14px;
  padding: 28px 30px 32px;
  border: 6px solid #eef8ff;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(180deg, rgba(22, 48, 76, 0.96), rgba(8, 18, 35, 0.98));
  background-size: 14px 14px, 14px 14px, 100% 100%;
  box-shadow: 0 0 0 5px rgba(5, 10, 20, 0.98), 10px 0 0 #07101d, 0 10px 0 #07101d, 14px 14px 0 rgba(0, 0, 0, 0.74), inset 0 0 0 5px rgba(132, 196, 233, 0.5), inset 0 -18px 0 rgba(4, 10, 22, 0.26);
  color: #f7fbff;
  text-align: center;
}

.pixel-panel::before {
  content: "";
  position: absolute;
  inset: 14px;
  border: 2px solid rgba(255, 241, 166, 0.22);
  pointer-events: none;
}

.pixel-panel::after {
  content: "";
  position: absolute;
  left: 28px;
  right: 28px;
  top: -6px;
  height: 6px;
  background: repeating-linear-gradient(90deg, #e93443 0 18px, #fff4cf 18px 30px, #5fa6cf 30px 48px);
  pointer-events: none;
}

.pixel-panel > * {
  position: relative;
  z-index: 1;
}

.pixel-panel:not(.next-panel) {
  width: min(760px, 100vw - 48px);
  min-height: 344px;
  padding: 42px 30px 34px;
  border-color: #dff4ff;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(180deg, rgba(24, 51, 80, 0.98), rgba(8, 18, 35, 0.99));
  box-shadow: 0 0 0 5px rgba(8, 15, 28, 0.96), 10px 0 0 #07101d, 0 10px 0 #07101d, 14px 14px 0 rgba(0, 0, 0, 0.74), inset 0 0 0 5px rgba(132, 196, 233, 0.5), 0 0 44px rgba(145, 240, 255, 0.18);
}

.next-panel {
  width: min(650px, 100vw - 44px);
  gap: 18px;
  padding: 28px 26px 32px;
  border-color: #91f0ff;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), rgba(9, 31, 61, 0.94);
  box-shadow: 12px 0 0 #101329, 0 12px 0 #101329, 12px 12px 0 rgba(0, 0, 0, 0.72), inset 0 0 0 6px rgba(116, 231, 255, 0.46), 0 0 36px rgba(116, 231, 255, 0.24);
}

.start-topline {
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(640px, 100%);
}

.stage-chip {
  margin: 0;
  padding: 8px 14px;
  border: 3px solid #edf7ff;
  background: #4f8d57;
  box-shadow: 5px 5px 0 rgba(0, 0, 0, 0.52);
  color: #fff;
  font-weight: 900;
  text-transform: uppercase;
}

.clear-chip {
  border-color: #91f0ff;
  background: #244a78;
  color: #fff1a6;
}

.start-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
}

.start-meta span {
  min-width: 132px;
  padding: 8px 11px;
  border: 3px solid #edf7ff;
  background: #4f855f;
  color: #ffffff;
  box-shadow: 5px 5px 0 rgba(0, 0, 0, 0.52);
  font-size: 15px;
  font-weight: 900;
  line-height: 1;
  text-transform: uppercase;
  text-shadow: 2px 2px 0 #253d24;
}

.pixel-panel h1 {
  margin: 0;
  color: #ffd34e;
  max-width: 100%;
  font-family: "Lucida Console", "Courier New", ui-monospace, monospace;
  font-size: 72px;
  font-weight: 950;
  line-height: 0.92;
  letter-spacing: 0;
  -webkit-font-smoothing: none;
  font-smooth: never;
  text-rendering: optimizeSpeed;
  white-space: nowrap;
  text-transform: uppercase;
  -webkit-text-stroke: 2px #ff8b1f;
  text-shadow: 0 4px 0 #ff8b1f, 4px 0 0 #ff8b1f, 4px 4px 0 #ff6f1f, 0 8px 0 #9c3d17, 8px 0 0 #9c3d17, 8px 8px 0 #1e2a78, 12px 12px 0 #07101d, 16px 16px 0 rgba(0, 0, 0, 0.52);
}

.pixel-panel .next-title {
  color: #91f0ff;
  font-size: 68px;
  text-shadow: 4px 0 0 #2d65ff, 0 4px 0 #2d65ff, 4px 4px 0 #15329b, 8px 8px 0 #061227, 12px 12px 0 rgba(0, 0, 0, 0.62);
}

.next-stage-card {
  display: grid;
  gap: 7px;
  min-width: min(440px, 100vw - 88px);
  padding: 14px 18px;
  border: 4px solid #fff1a6;
  background: #080f24;
  box-shadow: 6px 6px 0 #02040b, inset 0 0 0 4px rgba(116, 231, 255, 0.2);
  text-transform: uppercase;
}

.next-stage-card span {
  color: #91f0ff;
  font-size: 30px;
  font-weight: 950;
  text-shadow: 3px 3px 0 #061227;
}

.next-stage-card strong {
  color: #ffffff;
  font-size: 20px;
  line-height: 1.15;
  text-shadow: 2px 2px 0 #061227;
}

.route-name {
  min-height: 24px;
  margin: 0;
  color: #dbe9f7;
  font-size: 25px;
  font-weight: 800;
  text-shadow: 2px 2px 0 #07101d;
}

.start-actions {
  display: grid;
  justify-items: center;
  gap: 10px;
}

.start-actions.title-actions {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 10px;
  width: min(520px, 100vw - 88px);
}

.start-actions.title-actions.with-admin {
  display: grid;
  grid-template-columns: repeat(2, minmax(150px, 1fr));
  width: min(340px, 100vw - 88px);
}

.start-actions.title-actions .pixel-start,
.start-actions.title-actions .pixel-shop,
.start-actions.title-actions .pixel-custom,
.start-actions.title-actions .pixel-admin {
  width: 100%;
  min-width: 0;
  min-height: 52px;
  padding: 10px 18px;
  font-size: 20px;
}

.pixel-start {
  min-width: 172px;
  padding: 12px 22px;
  border: 4px solid #edf7ff;
  background: #76a35b;
  color: #fff;
  box-shadow: 5px 0 0 #23324a, 0 5px 0 #23324a, 8px 8px 0 rgba(0, 0, 0, 0.5);
  font: inherit;
  font-size: 26px;
  font-weight: 900;
  text-transform: uppercase;
  cursor: pointer;
}

.pixel-shop,
.pixel-custom,
.pixel-admin {
  min-width: 150px;
  padding: 10px 18px;
  border: 4px solid #edf7ff;
  background: #244a78;
  color: #fff1a6;
  box-shadow: 5px 0 0 #23324a, 0 5px 0 #23324a, 8px 8px 0 rgba(0, 0, 0, 0.5);
  font: inherit;
  font-size: 20px;
  font-weight: 900;
  text-transform: uppercase;
  cursor: pointer;
}

.pixel-shop {
  background: #315f89;
}

.pixel-admin {
  background: #7a3e68;
  color: #fff1a6;
}

.pixel-start:hover,
.pixel-start:focus-visible,
.pixel-shop:hover,
.pixel-shop:focus-visible,
.pixel-custom:hover,
.pixel-custom:focus-visible,
.pixel-admin:hover,
.pixel-admin:focus-visible {
  background: #89b86c;
  outline: none;
  box-shadow: 5px 0 0 #23324a, 0 5px 0 #23324a, 8px 8px 0 rgba(0, 0, 0, 0.5), 0 0 0 4px rgba(255, 241, 166, 0.32);
}

.pixel-start:active,
.pixel-shop:active,
.pixel-custom:active,
.pixel-admin:active {
  transform: translate(4px, 4px);
  box-shadow: 2px 2px 0 #23324a;
}

.account-panel {
  position: relative;
  z-index: 2;
  width: min(520px, 100vw - 48px);
  margin-top: 18px;
}

.account-card {
  display: grid;
  gap: 10px;
  padding: 14px;
  border: 4px solid #91f0ff;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), rgba(6, 15, 32, 0.92);
  background-size: 12px 12px, 12px 12px, 100% 100%;
  box-shadow: 6px 6px 0 rgba(0, 0, 0, 0.7), inset 0 0 0 3px rgba(255, 241, 166, 0.18);
  color: #f7fbff;
}

.compact-account {
  gap: 12px;
}

.account-tabs,
.account-profile,
.account-stats,
.shop-list,
.ranking-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.account-tabs button,
.account-profile button,
.account-action,
.shop-item {
  border: 3px solid #edf7ff;
  background: #4f855f;
  color: #fff;
  box-shadow: 4px 4px 0 #07101d;
  font: inherit;
  font-weight: 900;
  text-transform: uppercase;
  cursor: pointer;
}

.account-tabs button {
  flex: 1;
  padding: 8px 10px;
}

.account-tabs button.active {
  background: #244a78;
  color: #fff1a6;
}

.pixel-input {
  width: 100%;
  min-height: 42px;
  border: 3px solid #dff4ff;
  border-radius: 0;
  background: #091324;
  color: #ffffff;
  padding: 0 12px;
  font: inherit;
  font-size: 15px;
  outline: none;
  box-shadow: inset 4px 4px 0 rgba(0, 0, 0, 0.36);
}

.pixel-input:focus {
  border-color: #fff1a6;
}

.account-action {
  min-height: 44px;
}

.account-action:disabled {
  opacity: 0.55;
  cursor: wait;
}

.account-message {
  margin: 0;
  color: #fff1a6;
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
}

.account-profile {
  justify-content: space-between;
}

.account-profile div {
  display: grid;
  gap: 3px;
}

.account-profile span,
.account-stats span {
  color: #91f0ff;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.account-profile strong {
  color: #fff1a6;
  font-size: 22px;
  line-height: 1;
  text-shadow: 3px 3px 0 #07101d;
}

.account-profile button {
  padding: 7px 10px;
  font-size: 12px;
}

.account-stats {
  flex-wrap: wrap;
}

.account-stats span {
  padding: 6px 8px;
  border: 2px solid rgba(255, 241, 166, 0.6);
  background: rgba(0, 0, 0, 0.32);
}

.ranking-card {
  display: grid;
  gap: 10px;
  margin-top: 12px;
  padding: 13px;
  border: 4px solid #fff1a6;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px), rgba(4, 11, 26, 0.92);
  background-size: 12px 12px, 12px 12px, 100% 100%;
  box-shadow: 6px 6px 0 rgba(0, 0, 0, 0.7), inset 0 0 0 3px rgba(145, 240, 255, 0.15);
  color: #f7fbff;
}

.ranking-title {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 10px;
}

.ranking-title div {
  display: grid;
  gap: 2px;
}

.ranking-title span,
.ranking-title small,
.ranking-empty {
  color: #91f0ff;
  font-size: 11px;
  font-weight: 900;
  line-height: 1;
  text-transform: uppercase;
}

.ranking-title strong {
  color: #fff1a6;
  font-size: 23px;
  line-height: 1;
  text-shadow: 3px 3px 0 #07101d;
}

.ranking-title small {
  color: #ffffff;
}

.shop-list {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
}

.shop-item {
  display: grid;
  gap: 4px;
  min-height: 58px;
  padding: 7px 6px;
  text-align: left;
  background: #244a78;
}

.shop-item strong,
.shop-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shop-item strong {
  font-size: 12px;
}

.shop-item span {
  color: #fff1a6;
  font-size: 11px;
}

.shop-item.owned {
  background: #4f855f;
}

.shop-item.equipped {
  outline: 3px solid #fff1a6;
}

.ranking-list {
  display: grid;
  gap: 5px;
  max-height: 224px;
  overflow-y: auto;
  padding-right: 4px;
}

.ranking-row {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 48px 86px 62px;
  justify-content: stretch;
  min-height: 24px;
  padding: 5px 7px;
  border: 2px solid rgba(145, 240, 255, 0.38);
  background: rgba(0, 0, 0, 0.24);
  color: #d8e9ff;
  font-size: 12px;
  font-weight: 900;
}

.ranking-row.me {
  border-color: rgba(255, 241, 166, 0.9);
  background: rgba(79, 133, 95, 0.42);
  box-shadow: inset 0 0 0 2px rgba(255, 241, 166, 0.18);
}

.ranking-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ranking-rank,
.ranking-stage {
  color: #91f0ff;
}

.ranking-row strong {
  color: #fff1a6;
  text-align: right;
}

.ranking-row small {
  overflow: hidden;
  color: #ffffff;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ranking-empty {
  margin: 0;
  padding: 8px;
  border: 2px solid rgba(145, 240, 255, 0.3);
  background: rgba(0, 0, 0, 0.22);
  color: #d8e9ff;
}

.admin-screen {
  position: absolute;
  inset: 0;
  z-index: 7;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  padding: 88px 28px 42px;
  overflow-y: auto;
  pointer-events: auto;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px), linear-gradient(180deg, rgba(3, 8, 20, 0.66), rgba(3, 8, 20, 0.92));
  background-size: 16px 16px, 16px 16px, 100% 100%;
}

.admin-layout {
  width: min(1080px, 100vw - 56px);
}

.admin-panel {
  display: grid;
  gap: 16px;
  padding: 18px;
  border: 4px solid #fff1a6;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), rgba(7, 13, 28, 0.96);
  background-size: 12px 12px, 12px 12px, 100% 100%;
  box-shadow: 7px 7px 0 rgba(0, 0, 0, 0.72), inset 0 0 0 3px rgba(145, 240, 255, 0.16);
  color: #f7fbff;
}

.admin-panel-head,
.admin-head-actions,
.admin-section-title,
.admin-user-row,
.admin-user-stats,
.admin-user-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.admin-panel-head {
  justify-content: space-between;
}

.admin-panel-head div:first-child,
.admin-section-title,
.admin-user-main {
  display: grid;
  gap: 4px;
}

.admin-panel-head span,
.admin-section-title span,
.admin-user-main span,
.admin-user-main small {
  color: #91f0ff;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.admin-panel-head strong {
  color: #fff1a6;
  font-size: 30px;
  line-height: 1;
  text-shadow: 3px 3px 0 #07101d;
  text-transform: uppercase;
}

.admin-back,
.admin-stage-button,
.admin-user-actions button {
  min-height: 36px;
  border: 3px solid #edf7ff;
  background: #244a78;
  color: #ffffff;
  box-shadow: 4px 4px 0 #07101d;
  font: inherit;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  cursor: pointer;
}

.admin-back {
  padding: 7px 12px;
  background: #4f855f;
}

.admin-back:disabled,
.admin-user-actions button:disabled {
  opacity: 0.52;
  cursor: wait;
}

.admin-content {
  display: grid;
  gap: 16px;
}

.admin-section {
  display: grid;
  gap: 10px;
  padding: 12px;
  border: 2px solid rgba(145, 240, 255, 0.42);
  background: rgba(0, 0, 0, 0.24);
}

.admin-section-title {
  justify-content: space-between;
}

.admin-section-title strong {
  color: #fff1a6;
  font-size: 18px;
  line-height: 1;
  text-shadow: 2px 2px 0 #07101d;
  text-transform: uppercase;
}

.admin-stage-grid {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 8px;
}

.admin-stage-button {
  min-height: 48px;
}

.admin-stage-button.active {
  border-color: #fff1a6;
  background: #7a3e68;
}

.admin-message {
  margin: 0;
  color: #fff1a6;
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
}

.admin-user-list {
  display: grid;
  gap: 8px;
  max-height: min(440px, 100vh - 390px);
  overflow-y: auto;
  padding-right: 4px;
}

.admin-user-row {
  justify-content: space-between;
  min-height: 70px;
  padding: 10px;
  border: 2px solid rgba(255, 241, 166, 0.5);
  background: rgba(3, 9, 23, 0.72);
}

.admin-user-main {
  min-width: 0;
  flex: 1;
}

.admin-user-main strong {
  overflow: hidden;
  color: #ffffff;
  font-size: 16px;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-user-main span {
  overflow: hidden;
  max-width: 280px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-user-stats {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.admin-user-stats span {
  min-width: 74px;
  padding: 6px 8px;
  border: 2px solid rgba(145, 240, 255, 0.38);
  background: rgba(0, 0, 0, 0.26);
  color: #fff1a6;
  font-size: 12px;
  font-weight: 900;
  text-align: center;
  text-transform: uppercase;
}

.admin-user-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.admin-user-actions button {
  min-width: 58px;
  padding: 6px 8px;
}

.admin-user-actions button.danger {
  background: #7a2430;
}

.shop-screen {
  position: absolute;
  inset: 0;
  z-index: 7;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  padding: 104px 28px 62px;
  pointer-events: auto;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px), linear-gradient(180deg, rgba(3, 8, 20, 0.38), rgba(3, 8, 20, 0.88));
  background-size: 16px 16px, 16px 16px, 100% 100%;
}

.shop-layout {
  width: min(1120px, 100vw - 56px);
  min-height: min(590px, 100vh - 156px);
  display: grid;
  grid-template-columns: minmax(320px, 430px) minmax(430px, 1fr);
  gap: 22px;
  align-items: stretch;
}

.shop-preview-panel,
.shop-panel {
  position: relative;
  display: grid;
  gap: 14px;
  padding: 18px;
  border: 4px solid #91f0ff;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px), rgba(6, 15, 32, 0.95);
  background-size: 12px 12px, 12px 12px, 100% 100%;
  box-shadow: 7px 7px 0 rgba(0, 0, 0, 0.7), inset 0 0 0 3px rgba(255, 241, 166, 0.18);
  color: #f7fbff;
}

.shop-preview-panel {
  min-height: 520px;
  overflow: hidden;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px), rgba(6, 15, 32, 0.06);
  background-size: 12px 12px, 12px 12px, 100% 100%;
}

.shop-preview-frame {
  inset: 58px 28px 144px;
}

.shop-preview-frame .preview-floor {
  bottom: 24px;
}

.shop-selected-card {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 18px;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: end;
  padding: 12px;
  border: 3px solid rgba(255, 241, 166, 0.72);
  background: rgba(3, 9, 23, 0.78);
  box-shadow: 5px 5px 0 rgba(0, 0, 0, 0.55);
}

.shop-selected-copy {
  min-width: 0;
  display: grid;
  gap: 5px;
}

.shop-selected-copy span {
  color: #91f0ff;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.shop-selected-copy strong {
  overflow: hidden;
  color: #fff1a6;
  font-size: 21px;
  line-height: 1;
  text-overflow: ellipsis;
  text-shadow: 3px 3px 0 #07101d;
  white-space: nowrap;
}

.shop-selected-copy p {
  margin: 0;
  color: #d8e9ff;
  font-size: 12px;
  line-height: 1.25;
}

.shop-buy-button {
  min-height: 46px;
  min-width: 124px;
  padding: 0 12px;
  border: 3px solid #edf7ff;
  background: #4f855f;
  color: #fff;
  box-shadow: 4px 4px 0 #07101d;
  font: inherit;
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
  cursor: pointer;
}

.shop-buy-button:hover,
.shop-buy-button:focus-visible {
  background: #89b86c;
  outline: none;
}

.shop-buy-button:disabled {
  opacity: 0.56;
  cursor: default;
}

.shop-panel-head,
.shop-account-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.shop-panel-head div,
.shop-account-strip div {
  display: grid;
  gap: 4px;
}

.shop-panel-head span,
.shop-account-strip span {
  color: #91f0ff;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.shop-panel-head strong {
  color: #fff1a6;
  font-size: 30px;
  line-height: 1;
  text-shadow: 3px 3px 0 #07101d;
  text-transform: uppercase;
}

.shop-account-strip {
  padding: 9px 10px;
  border: 2px solid rgba(255, 241, 166, 0.55);
  background: rgba(0, 0, 0, 0.26);
}

.shop-account-strip strong {
  color: #ffffff;
  font-size: 18px;
  line-height: 1;
}

.shop-back {
  min-height: 36px;
  padding: 7px 12px;
  border: 3px solid #edf7ff;
  background: #4f855f;
  color: #fff;
  box-shadow: 4px 4px 0 #07101d;
  font: inherit;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  cursor: pointer;
}

.shop-back:hover,
.shop-back:focus-visible {
  background: #89b86c;
  outline: none;
}

.shop-message {
  margin: 0;
  color: #fff1a6;
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
}

.shop-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.shop-tab {
  min-height: 34px;
  padding: 7px 8px;
  border: 2px solid rgba(237, 247, 255, 0.72);
  background: rgba(8, 18, 36, 0.78);
  color: #d8e9ff;
  box-shadow: 3px 3px 0 #07101d;
  font: inherit;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  cursor: pointer;
}

.shop-tab:hover,
.shop-tab:focus-visible,
.shop-tab.active {
  border-color: #fff1a6;
  background: #356b75;
  color: #fff1a6;
  outline: none;
}

.shop-page-list {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-content: start;
  max-height: min(444px, 100vh - 330px);
  overflow-y: auto;
  padding: 2px 4px 6px 2px;
}

.shop-page-item {
  grid-template-columns: 58px minmax(0, 1fr);
  grid-template-rows: auto auto;
  min-height: 112px;
  gap: 10px;
  align-items: center;
  padding: 11px;
  border-color: rgba(237, 247, 255, 0.82);
  background: #244a78;
}

.shop-page-item:hover,
.shop-page-item:focus-visible,
.shop-page-item.selected {
  border-color: #fff1a6;
  background: #315f89;
  outline: none;
}

.shop-page-item.selected {
  box-shadow: 4px 4px 0 #07101d, 0 0 0 3px rgba(255, 241, 166, 0.4);
}

.shop-page-item.equipped {
  background: #4f855f;
}

.shop-page-item.owned:not(.equipped) {
  background: #356b75;
}

.shop-item-art {
  position: relative;
  grid-row: 1/span 2;
  width: 50px;
  height: 50px;
  border: 3px solid #edf7ff;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px), #091324;
  background-size: 8px 8px, 8px 8px, 100% 100%;
  box-shadow: 4px 4px 0 #07101d;
  image-rendering: pixelated;
}

.shop-item-art::before,
.shop-item-art::after {
  content: "";
  position: absolute;
  image-rendering: pixelated;
}

.art-classic::before,
.art-blue-costume::before,
.art-gold-costume::before {
  left: 15px;
  top: 10px;
  width: 20px;
  height: 28px;
  background: #d21f2b;
  box-shadow: -8px 7px 0 #9b171e, 8px 7px 0 #9b171e, 0 24px 0 #edf7ff;
}

.art-classic::after,
.art-blue-costume::after,
.art-gold-costume::after {
  left: 12px;
  top: 23px;
  width: 26px;
  height: 5px;
  background: #edf7ff;
  box-shadow: 0 9px 0 #07101d;
}

.art-blue-costume::before {
  background: #2d69e8;
  box-shadow: -8px 7px 0 #173c99, 8px 7px 0 #173c99, 0 24px 0 #edf7ff;
}

.art-gold-costume::before {
  background: #f0b72d;
  box-shadow: -8px 7px 0 #9f6612, 8px 7px 0 #9f6612, 0 24px 0 #fff1a6;
}

.art-alex-costume::before,
.art-steve-costume::before {
  left: 18px;
  top: 7px;
  width: 15px;
  height: 16px;
  background: #ffd1ad;
  box-shadow: -3px 14px 0 #5aa574, 3px 14px 0 #5aa574, -9px 18px 0 #ffd1ad, 9px 18px 0 #ffd1ad, -4px 29px 0 #6f4c35, 4px 29px 0 #6f4c35, -4px 38px 0 #60789e, 4px 38px 0 #60789e;
}

.art-alex-costume::after {
  left: 15px;
  top: 4px;
  width: 21px;
  height: 7px;
  background: #c97925;
  box-shadow: -2px 7px 0 #c97925, 16px 7px 0 #c97925, 17px 18px 0 #c97925, 18px 27px 0 #8f501b, 4px 12px 0 #2f7d24, 12px 12px 0 #2f7d24, 8px 16px 0 #ffb66d;
}

.art-steve-costume::before {
  background: #b77b5d;
  box-shadow: -3px 14px 0 #12b4bc, 3px 14px 0 #12b4bc, -9px 18px 0 #b77b5d, 9px 18px 0 #b77b5d, -4px 29px 0 #4637a5, 4px 29px 0 #4637a5, -4px 38px 0 #111418, 4px 38px 0 #111418;
}

.art-steve-costume::after {
  left: 15px;
  top: 4px;
  width: 21px;
  height: 8px;
  background: #2d1b10;
  box-shadow: -2px 8px 0 #2d1b10, 17px 8px 0 #2d1b10, 4px 13px 0 #4355c7, 13px 13px 0 #4355c7, 6px 21px 0 #2d1b10, 11px 21px 0 #2d1b10;
}

.art-mario-costume::before {
  left: 18px;
  top: 9px;
  width: 15px;
  height: 15px;
  background: #ffb66d;
  box-shadow: -2px -6px 0 #f01515, 6px -8px 0 #f01515, 13px -6px 0 #f01515, 7px 12px 0 #8b4f1e, -5px 17px 0 #f01515, 5px 17px 0 #f01515, -5px 27px 0 #4355c7, 5px 27px 0 #4355c7, -8px 38px 0 #8b4f1e, 8px 38px 0 #8b4f1e;
}

.art-mario-costume::after {
  left: 14px;
  top: 14px;
  width: 24px;
  height: 5px;
  background: transparent;
  box-shadow: 5px 0 0 #111418, 15px 0 0 #111418, 7px 8px 0 #2d1b10, 12px 8px 0 #2d1b10, 9px 26px 0 #ffdf53, 17px 26px 0 #ffdf53;
}

.art-among-crewmate-costume::before {
  left: 15px;
  top: 8px;
  width: 22px;
  height: 33px;
  background: #d0101c;
  box-shadow: -5px 8px 0 #d0101c, -6px 18px 0 #6c0630, 0 30px 0 #6c0630, 12px 30px 0 #6c0630;
}

.art-among-crewmate-costume::after {
  left: 20px;
  top: 16px;
  width: 22px;
  height: 10px;
  background: #a8e7f3;
  box-shadow: 5px 2px 0 #edf7ff, -12px 12px 0 #6c0630;
}

.art-sonic-costume::before {
  left: 15px;
  top: 8px;
  width: 21px;
  height: 17px;
  background: #244cf3;
  box-shadow: -7px 3px 0 #244cf3, -10px 9px 0 #244cf3, -7px 15px 0 #244cf3, 4px 17px 0 #ffc99b, 2px 27px 0 #244cf3, -4px 37px 0 #ff1f22, 8px 37px 0 #ff1f22;
}

.art-sonic-costume::after {
  left: 20px;
  top: 12px;
  width: 9px;
  height: 12px;
  background: #ffffff;
  box-shadow: 10px 0 0 #ffffff, 5px 10px 0 #ffc99b, 3px 22px 0 #ffffff, 16px 22px 0 #ffffff, 3px 31px 0 #ffffff, 16px 31px 0 #ffffff;
}

.art-frog-costume::before {
  left: 13px;
  top: 9px;
  width: 25px;
  height: 18px;
  background: #65bd37;
  box-shadow: -2px -5px 0 #65bd37, 17px -5px 0 #65bd37, 0 19px 0 #67b8f0, -7px 24px 0 #65bd37, 7px 24px 0 #65bd37, -3px 34px 0 #a96b2c, 8px 34px 0 #a96b2c, -4px 41px 0 #2f7d24, 10px 41px 0 #2f7d24;
}

.art-frog-costume::after {
  left: 13px;
  top: 4px;
  width: 8px;
  height: 8px;
  background: #ffffff;
  box-shadow: 19px 0 0 #ffffff, 2px 2px 0 #111418, 21px 2px 0 #111418, 9px 17px 0 #8b4f1e, 15px 17px 0 #8b4f1e;
}

.art-speed-boots::before {
  left: 9px;
  top: 24px;
  width: 13px;
  height: 13px;
  background: #91f0ff;
  box-shadow: 20px 0 0 #91f0ff, 0 10px 0 #07101d, 20px 10px 0 #07101d;
}

.art-speed-boots::after {
  left: 4px;
  top: 15px;
  width: 12px;
  height: 5px;
  background: #fff1a6;
  box-shadow: 12px -5px 0 #fff1a6, 24px -10px 0 #fff1a6, 33px -15px 0 #fff1a6;
}

.art-magnet-charm::before {
  left: 13px;
  top: 10px;
  width: 20px;
  height: 24px;
  border: 7px solid #d21f2b;
  border-top: 0;
  background: transparent;
}

.art-magnet-charm::after {
  left: 10px;
  top: 8px;
  width: 11px;
  height: 9px;
  background: #91f0ff;
  box-shadow: 21px 0 0 #fff1a6, 4px 27px 0 #edf7ff, 25px 27px 0 #edf7ff;
}

.art-double-jump::before {
  left: 21px;
  top: 8px;
  width: 8px;
  height: 28px;
  background: #91f0ff;
  box-shadow: -8px 9px 0 #edf7ff, 8px 9px 0 #edf7ff, -14px 16px 0 #2d69e8, 14px 16px 0 #2d69e8;
}

.art-double-jump::after {
  left: 13px;
  top: 29px;
  width: 24px;
  height: 5px;
  background: #fff1a6;
  box-shadow: 0 8px 0 #fff1a6, 6px -18px 0 #fff1a6, 12px -23px 0 #fff1a6;
}

.art-banish-bell::before {
  left: 13px;
  top: 10px;
  width: 24px;
  height: 25px;
  background: #ffdf53;
  clip-path: polygon(28% 0, 72% 0, 90% 78%, 100% 78%, 100% 100%, 0 100%, 0 78%, 10% 78%);
  box-shadow: inset 0 -6px 0 #9f6f18;
}

.art-banish-bell::after {
  left: 20px;
  top: 5px;
  width: 10px;
  height: 7px;
  background: #edf7ff;
  box-shadow: 2px 29px 0 #9f6f18, 8px 29px 0 #9f6f18;
}

.art-rudolph-vehicle::before {
  left: 8px;
  top: 22px;
  width: 28px;
  height: 12px;
  background: #8a5530;
  box-shadow: 21px -8px 0 #8a5530, 27px -6px 0 #563018, -2px 14px 0 #563018, 19px 14px 0 #563018;
}

.art-rudolph-vehicle::after {
  left: 36px;
  top: 10px;
  width: 5px;
  height: 12px;
  background: #d3b06a;
  box-shadow: 8px 0 0 #d3b06a, 4px 13px 0 #ff2348, -28px 21px 0 #07101d, -4px 21px 0 #07101d;
}

.art-bmw-530-blue::before,
.art-bmw-320i-black-7907::before,
.art-mercedes-e-class-white::before,
.art-porsche-911-gt3-pink::before,
.art-cybertruck::before,
.art-bugatti-chiron-red::before {
  left: 5px;
  top: 23px;
  width: 40px;
  height: 13px;
  background: #1559c9;
  box-shadow: 6px -10px 0 #8fe9ff, 15px -12px 0 #8fe9ff, 4px 14px 0 #07101d, 28px 14px 0 #07101d;
}

.art-bmw-530-blue::after,
.art-bmw-320i-black-7907::after,
.art-mercedes-e-class-white::after,
.art-porsche-911-gt3-pink::after,
.art-cybertruck::after,
.art-bugatti-chiron-red::after {
  left: 8px;
  top: 32px;
  width: 7px;
  height: 7px;
  background: #d8e2ee;
  box-shadow: 28px 0 0 #d8e2ee, 1px -10px 0 #fff7c7, 31px -10px 0 #fff7c7;
}

.art-bmw-320i-black-7907::before {
  background: #080b12;
  box-shadow: 6px -10px 0 #8fe9ff, 15px -12px 0 #8fe9ff, 4px 14px 0 #07101d, 28px 14px 0 #07101d, 14px -1px 0 #d8e2ee;
}

.art-bmw-320i-black-7907::after {
  background: #d8e2ee;
  box-shadow: 28px 0 0 #d8e2ee, 1px -10px 0 #fff7c7, 31px -10px 0 #fff7c7, 12px -1px 0 #f7fbff, 17px -1px 0 #111418;
}

.art-mercedes-e-class-white::before {
  background: #f3f8ff;
  box-shadow: 6px -10px 0 #8fe9ff, 15px -12px 0 #8fe9ff, 4px 14px 0 #07101d, 28px 14px 0 #07101d, 20px -3px 0 #d8e2ee;
}

.art-porsche-911-gt3-pink::before {
  top: 24px;
  height: 11px;
  background: #ffc7da;
  box-shadow: 8px -8px 0 #8fe9ff, 18px -9px 0 #8fe9ff, 4px 13px 0 #07101d, 29px 13px 0 #07101d, 14px -1px 0 #071f4f, 26px -5px 0 #071f4f;
}

.art-porsche-911-gt3-pink::after {
  background: #d8e2ee;
  box-shadow: 28px 0 0 #d8e2ee, 2px -9px 0 #fff7c7, 30px -9px 0 #fff7c7, 21px -15px 0 #071f4f, 26px -15px 0 #071f4f;
}

.art-cybertruck::before {
  top: 21px;
  height: 16px;
  background: #a9b1ba;
  clip-path: polygon(0 42%, 42% 0, 100% 26%, 100% 100%, 0 100%);
  box-shadow: 4px 15px 0 #07101d, 29px 15px 0 #07101d;
}

.art-cybertruck::after {
  top: 24px;
  width: 30px;
  height: 4px;
  background: #8fe9ff;
  box-shadow: 1px 13px 0 #d8e2ee, 28px 13px 0 #d8e2ee, 0 -8px 0 #fff7c7;
}

.art-bugatti-chiron-red::before {
  top: 24px;
  height: 11px;
  background: #db1628;
  box-shadow: 8px -8px 0 #8fe9ff, 18px -9px 0 #8fe9ff, 4px 13px 0 #07101d, 29px 13px 0 #07101d, 18px 0 0 #07101d;
}

.art-bugatti-chiron-red::after {
  background: #d8e2ee;
  box-shadow: 28px 0 0 #d8e2ee, 2px -9px 0 #fff7c7, 30px -9px 0 #fff7c7, 15px -5px 0 #07101d;
}

.art-none::before {
  left: 10px;
  top: 23px;
  width: 30px;
  height: 8px;
  background: #edf7ff;
  box-shadow: 0 9px 0 #4d6077, 7px -9px 0 #91f0ff, 15px -15px 0 #fff1a6;
}

.art-none::after {
  left: 8px;
  top: 12px;
  width: 6px;
  height: 6px;
  background: #d21f2b;
  box-shadow: 28px 0 0 #d21f2b, 6px 25px 0 #07101d, 26px 25px 0 #07101d;
}

.shop-item-copy {
  min-width: 0;
  display: grid;
  gap: 5px;
}

.shop-item-copy strong {
  overflow: hidden;
  color: #ffffff;
  font-size: 14px;
  line-height: 1;
  text-overflow: ellipsis;
  text-shadow: 2px 2px 0 #07101d;
  white-space: nowrap;
}

.shop-item-copy small {
  overflow: hidden;
  display: -webkit-box;
  color: #d8e9ff;
  font-size: 11px;
  line-height: 1.25;
  text-transform: none;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.shop-item-meta {
  grid-column: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.shop-item-meta span,
.shop-item-meta strong {
  overflow: hidden;
  font-size: 11px;
  font-weight: 900;
  line-height: 1;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.shop-item-meta span {
  color: #91f0ff;
}

.shop-item-meta strong {
  color: #fff1a6;
}

.custom-screen {
  position: absolute;
  inset: 0;
  z-index: 7;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  padding: 104px 28px 62px;
  pointer-events: auto;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px), linear-gradient(180deg, rgba(3, 8, 20, 0.12), rgba(3, 8, 20, 0.34));
  background-size: 16px 16px, 16px 16px, 100% 100%;
}

.custom-layout {
  width: min(1020px, 100vw - 56px);
  min-height: min(560px, 100vh - 166px);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(310px, 400px);
  gap: 22px;
  align-items: stretch;
}

.custom-preview-panel,
.custom-closet-panel {
  position: relative;
  border: 4px solid #91f0ff;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px), rgba(6, 15, 32, 0.72);
  background-size: 12px 12px, 12px 12px, 100% 100%;
  box-shadow: 6px 6px 0 rgba(0, 0, 0, 0.68), inset 0 0 0 3px rgba(255, 241, 166, 0.15);
  color: #f7fbff;
}

.custom-preview-panel {
  min-height: 460px;
  overflow: hidden;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px), rgba(6, 15, 32, 0.05);
  background-size: 12px 12px, 12px 12px, 100% 100%;
}

.custom-label {
  position: absolute;
  left: 18px;
  top: 16px;
  z-index: 2;
  display: grid;
  gap: 4px;
}

.custom-label span,
.custom-panel-head span,
.costume-copy small {
  color: #91f0ff;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.custom-label strong,
.custom-panel-head strong {
  color: #fff1a6;
  font-size: 23px;
  line-height: 1;
  text-shadow: 3px 3px 0 #07101d;
}

.custom-preview-frame {
  position: absolute;
  inset: 58px 30px 28px;
  border: 3px solid rgba(255, 241, 166, 0.42);
  background: radial-gradient(circle at 38% 48%, rgba(145, 240, 255, 0.06), rgba(3, 8, 20, 0.02) 46%, rgba(3, 8, 20, 0.1) 100%);
}

.preview-corner {
  position: absolute;
  width: 24px;
  height: 24px;
  border-color: #fff1a6;
  border-style: solid;
}

.corner-a {
  left: -3px;
  top: -3px;
  border-width: 4px 0 0 4px;
}

.corner-b {
  right: -3px;
  top: -3px;
  border-width: 4px 4px 0 0;
}

.corner-c {
  right: -3px;
  bottom: -3px;
  border-width: 0 4px 4px 0;
}

.corner-d {
  left: -3px;
  bottom: -3px;
  border-width: 0 0 4px 4px;
}

.preview-floor {
  position: absolute;
  left: 18%;
  right: 18%;
  bottom: 42px;
  height: 10px;
  background: rgba(255, 241, 166, 0.32);
  box-shadow: 0 0 0 3px rgba(7, 16, 29, 0.7), 0 0 24px rgba(145, 240, 255, 0.24);
}

.custom-closet-panel {
  display: grid;
  grid-template-rows: auto auto auto auto minmax(0, 1fr);
  gap: 14px;
  padding: 18px;
}

.custom-panel-head {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 12px;
}

.custom-panel-head div {
  display: grid;
  gap: 5px;
}

.custom-loadout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.custom-loadout div {
  min-width: 0;
  border: 2px solid rgba(145, 240, 255, 0.42);
  background: rgba(7, 16, 29, 0.62);
  padding: 8px;
}

.custom-loadout span,
.custom-section-title span {
  display: block;
  color: #91f0ff;
  font-size: 11px;
  font-weight: 900;
  line-height: 1;
  text-transform: uppercase;
}

.custom-loadout strong {
  display: block;
  overflow: hidden;
  margin-top: 5px;
  color: #fff1a6;
  font-size: 13px;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.custom-back {
  min-width: 74px;
  min-height: 40px;
  border: 3px solid #edf7ff;
  background: #4f855f;
  color: #fff;
  box-shadow: 4px 4px 0 #07101d;
  font: inherit;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  cursor: pointer;
}

.custom-back:hover,
.custom-back:focus-visible {
  background: #76a35b;
  outline: none;
}

.custom-message {
  margin: 0;
  min-height: 22px;
  color: #fff1a6;
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
}

.custom-lists {
  overflow: auto;
  min-height: 0;
  display: grid;
  align-content: start;
  gap: 16px;
  padding-right: 4px;
}

.custom-empty {
  margin: 0;
  padding: 12px;
  border: 2px solid rgba(145, 240, 255, 0.38);
  background: rgba(0, 0, 0, 0.24);
  color: #d8e9ff;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.custom-section {
  display: grid;
  gap: 8px;
}

.costume-list {
  display: grid;
  align-content: start;
  gap: 10px;
}

.costume-item {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 62px;
  padding: 9px 10px;
  border: 3px solid #dff4ff;
  background: #244a78;
  color: #ffffff;
  box-shadow: 4px 4px 0 #07101d;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.costume-item.equipped {
  border-color: #fff1a6;
  background: #4f855f;
}

.custom-ride-item {
  grid-template-columns: 58px minmax(0, 1fr) auto;
}

.custom-item-art {
  grid-row: auto;
}

.costume-swatch {
  width: 32px;
  height: 32px;
  border: 3px solid #edf7ff;
  box-shadow: 4px 4px 0 #07101d, inset 0 -9px 0 rgba(0, 0, 0, 0.24);
}

.costume-copy {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.costume-copy strong,
.costume-copy small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.costume-copy strong {
  color: #ffffff;
  font-size: 15px;
  line-height: 1;
}

.costume-copy small {
  color: #fff1a6;
}

.costume-check {
  min-width: 34px;
  color: #fff1a6;
  font-size: 12px;
  font-weight: 900;
  text-align: right;
}

@media (min-width: 1180px) {
  .start-screen:not(.next-stage-screen) {
    grid-template-columns: minmax(580px, 760px) minmax(360px, 520px);
    justify-content: center;
    align-content: center;
    align-items: center;
    gap: 28px;
    padding: clamp(32px, 6vh, 74px) 32px;
  }
  .start-screen:not(.next-stage-screen) .pixel-panel:not(.next-panel) {
    width: 100%;
  }
  .start-screen:not(.next-stage-screen) .account-panel {
    width: 100%;
    margin-top: 0;
  }
  .start-screen:not(.next-stage-screen) .ranking-list {
    max-height: min(320px, 100vh - 430px);
  }
}
@media (min-width: 1180px) and (max-width: 1280px) {
  .start-screen:not(.next-stage-screen) .pixel-panel h1 {
    font-size: 56px;
  }
}
@media (min-width: 1180px) and (max-height: 920px) {
  .start-screen:not(.next-stage-screen) {
    grid-template-columns: minmax(540px, 680px) minmax(360px, 500px);
    gap: 22px;
    padding: 30px 32px 36px;
  }
  .start-screen:not(.next-stage-screen) .pixel-panel:not(.next-panel) {
    gap: 10px;
    min-height: auto;
    padding: 30px 24px 28px;
  }
  .start-screen:not(.next-stage-screen) .pixel-panel h1 {
    font-size: 60px;
  }
  .start-screen:not(.next-stage-screen) .route-name {
    font-size: 20px;
  }
  .start-screen:not(.next-stage-screen) .pixel-start {
    padding: 10px 18px;
    font-size: 22px;
  }
  .start-screen:not(.next-stage-screen) .pixel-shop,
  .start-screen:not(.next-stage-screen) .pixel-custom,
  .start-screen:not(.next-stage-screen) .pixel-admin {
    padding: 8px 14px;
    font-size: 17px;
  }
  .start-screen:not(.next-stage-screen) .account-card,
  .start-screen:not(.next-stage-screen) .ranking-card {
    padding: 11px;
  }
  .start-screen:not(.next-stage-screen) .ranking-list {
    max-height: min(240px, 100vh - 378px);
  }
}
@media (max-width: 760px) {
  .start-screen {
    padding: 96px 14px 176px;
  }
  .pixel-panel:not(.next-panel),
  .pixel-panel {
    width: min(600px, 100vw - 28px);
    min-height: auto;
    gap: 12px;
    padding: 22px 18px 26px;
    border-width: 5px;
  }
  .pixel-panel::before {
    inset: 11px;
  }
  .pixel-panel::after {
    left: 20px;
    right: 20px;
  }
  .start-topline {
    flex-direction: column;
    gap: 8px;
  }
  .pixel-panel h1 {
    font-size: 48px;
    -webkit-text-stroke-width: 1px;
    text-shadow: 0 3px 0 #ff8b1f, 3px 0 0 #ff8b1f, 3px 3px 0 #ff6f1f, 0 6px 0 #9c3d17, 6px 0 0 #9c3d17, 6px 6px 0 #1e2a78, 9px 9px 0 #07101d, 12px 12px 0 rgba(0, 0, 0, 0.52);
  }
  .pixel-panel .next-title {
    font-size: 48px;
  }
  .route-name {
    font-size: 18px;
  }
  .pixel-start {
    min-width: 150px;
    padding: 11px 18px;
    font-size: 22px;
  }
  .pixel-shop,
  .pixel-custom,
  .pixel-admin {
    min-width: 136px;
    padding: 9px 16px;
    font-size: 17px;
  }
  .right-hud {
    grid-template-columns: auto;
    gap: 6px;
    padding-right: 70px;
  }
  .account-hud {
    display: none;
  }
  .account-panel {
    width: min(600px, 100vw - 28px);
    margin-top: 12px;
  }
  .ranking-list {
    max-height: 180px;
  }
  .shop-screen {
    padding: 86px 14px 26px;
    overflow-y: auto;
    place-items: start center;
  }
  .admin-screen {
    padding: 86px 14px 26px;
    place-items: start center;
  }
  .admin-layout {
    width: min(600px, 100vw - 28px);
  }
  .admin-panel {
    padding: 14px;
  }
  .admin-panel-head,
  .admin-head-actions,
  .admin-user-row {
    align-items: stretch;
    flex-direction: column;
  }
  .admin-stage-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .admin-user-stats,
  .admin-user-actions {
    justify-content: stretch;
  }
  .admin-user-actions button {
    flex: 1;
  }
  .shop-layout {
    width: min(600px, 100vw - 28px);
    min-height: auto;
    grid-template-columns: 1fr;
    gap: 14px;
  }
  .shop-preview-panel {
    min-height: 390px;
  }
  .shop-panel {
    padding: 14px;
  }
  .shop-preview-frame {
    inset: 54px 18px 136px;
  }
  .shop-selected-card {
    left: 14px;
    right: 14px;
    bottom: 14px;
    grid-template-columns: 1fr;
    gap: 10px;
  }
  .shop-buy-button {
    width: 100%;
  }
  .shop-panel-head,
  .shop-account-strip {
    align-items: stretch;
    flex-direction: column;
  }
  .shop-back {
    width: 100%;
  }
  .shop-page-list {
    grid-template-columns: 1fr;
    max-height: none;
  }
  .custom-screen {
    padding: 86px 14px 26px;
    overflow-y: auto;
    place-items: start center;
  }
  .custom-layout {
    width: min(600px, 100vw - 28px);
    min-height: auto;
    grid-template-columns: 1fr;
    gap: 14px;
  }
  .custom-preview-panel {
    min-height: 330px;
  }
  .custom-preview-frame {
    inset: 54px 18px 22px;
  }
  .preview-floor {
    bottom: 30px;
  }
  .custom-closet-panel {
    padding: 14px;
  }
}
@media (max-width: 420px) {
  .start-screen {
    padding-top: 84px;
  }
  .start-meta span {
    min-width: 118px;
    padding: 7px 8px;
    font-size: 13px;
  }
  .pixel-panel h1 {
    font-size: 34px;
    text-shadow: 0 2px 0 #ff8b1f, 2px 0 0 #ff8b1f, 2px 2px 0 #ff6f1f, 0 4px 0 #9c3d17, 4px 0 0 #9c3d17, 4px 4px 0 #1e2a78, 6px 6px 0 #07101d, 8px 8px 0 rgba(0, 0, 0, 0.52);
  }
  .start-actions.title-actions {
    width: min(360px, 100vw - 28px);
    gap: 6px;
  }
  .start-actions.title-actions.with-admin {
    width: min(300px, 100vw - 28px);
  }
  .start-actions.title-actions .pixel-start,
  .start-actions.title-actions .pixel-shop,
  .start-actions.title-actions .pixel-custom,
  .start-actions.title-actions .pixel-admin {
    min-height: 42px;
    padding: 8px 6px;
    font-size: 14px;
  }
  .custom-panel-head {
    align-items: stretch;
    flex-direction: column;
  }
  .custom-back {
    width: 100%;
  }
  .shop-page-list {
    grid-template-columns: 1fr;
  }
  .admin-stage-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .costume-item {
    grid-template-columns: 36px minmax(0, 1fr) auto;
    gap: 8px;
  }
  .ranking-title {
    align-items: start;
    flex-direction: column;
  }
  .ranking-row {
    grid-template-columns: 34px minmax(0, 1fr) 38px 70px;
  }
  .ranking-row small {
    display: none;
  }
}
@keyframes pixelTwinkle {
  0%, 100% {
    opacity: 0.35;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.8);
  }
}
@keyframes pixelMeteor {
  0%, 42% {
    opacity: 0;
    transform: translate3d(0, 0, 0) rotate(-28deg);
  }
  50% {
    opacity: 1;
  }
  68%, 100% {
    opacity: 0;
    transform: translate3d(-95vw, 52vh, 0) rotate(-28deg);
  }
}
.touch-controls {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 18px;
  z-index: 6;
  display: none;
  align-items: end;
  justify-content: space-between;
  pointer-events: none;
}

.joystick-pad {
  position: relative;
  width: 152px;
  height: 152px;
  border: 5px solid rgba(222, 241, 255, 0.72);
  border-radius: 50%;
  background: radial-gradient(circle at center, rgba(255, 255, 255, 0.16) 0 20px, rgba(0, 0, 0, 0.42) 21px 34px, rgba(255, 255, 255, 0.1) 35px 39px, rgba(5, 13, 27, 0.76) 40px 100%), rgba(8, 15, 27, 0.64);
  box-shadow: inset 0 0 0 4px rgba(0, 0, 0, 0.52), inset 0 0 0 12px rgba(118, 238, 255, 0.1), 5px 5px 0 rgba(0, 0, 0, 0.42), 0 0 18px rgba(118, 238, 255, 0.18);
  pointer-events: auto;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.joystick-pad.active {
  border-color: rgba(255, 241, 115, 0.84);
  box-shadow: inset 0 0 0 4px rgba(0, 0, 0, 0.52), inset 0 0 0 12px rgba(255, 241, 115, 0.12), 5px 5px 0 rgba(0, 0, 0, 0.42), 0 0 22px rgba(255, 241, 115, 0.32);
}

.joystick-ring {
  position: absolute;
  inset: 24px;
  border: 4px solid rgba(255, 255, 255, 0.62);
  border-radius: 50%;
  box-shadow: inset 0 0 0 3px rgba(0, 0, 0, 0.46), 0 0 0 3px rgba(0, 0, 0, 0.34);
  pointer-events: none;
}

.joystick-arrow {
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
  filter: drop-shadow(2px 2px 0 rgba(0, 0, 0, 0.58));
}

.joystick-arrow.arrow-up {
  left: 50%;
  top: 12px;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: 12px solid rgba(255, 255, 255, 0.74);
  transform: translateX(-50%);
}

.joystick-arrow.arrow-right {
  right: 12px;
  top: 50%;
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-left: 12px solid rgba(255, 255, 255, 0.74);
  transform: translateY(-50%);
}

.joystick-arrow.arrow-down {
  left: 50%;
  bottom: 12px;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 12px solid rgba(255, 255, 255, 0.74);
  transform: translateX(-50%);
}

.joystick-arrow.arrow-left {
  left: 12px;
  top: 50%;
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-right: 12px solid rgba(255, 255, 255, 0.74);
  transform: translateY(-50%);
}

.joystick-knob-shell {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 54px;
  height: 54px;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.joystick-knob {
  display: block;
  width: 54px;
  height: 54px;
  border: 5px solid rgba(255, 255, 255, 0.86);
  border-radius: 50%;
  background: radial-gradient(circle at 42% 38%, #ffffff 0 15px, #d9e0e6 16px 21px, #0b111c 22px 100%);
  box-shadow: inset 0 0 0 4px rgba(0, 0, 0, 0.4), 3px 3px 0 rgba(0, 0, 0, 0.45);
  will-change: transform;
}

.touch-controls button {
  color: #f5fbff;
  font-weight: 850;
  text-shadow: 2px 2px 0 #05060b;
  pointer-events: auto;
  touch-action: none;
}

.jump-btn {
  width: 70px !important;
  height: 70px !important;
  border-radius: 0 !important;
  font-size: 24px !important;
}

@media (hover: none), (max-width: 760px) {
  .game-shell {
    min-height: 100svh;
  }
  .touch-controls {
    display: flex;
  }
  .hud {
    top: 10px;
    left: 10px;
    right: 10px;
    align-items: start;
  }
  .player-hud {
    gap: 7px;
  }
  .lives-hud {
    min-height: 42px;
    padding: 6px 9px;
    font-size: 22px;
  }
  .life-heart {
    width: 24px;
    height: 20px;
    box-shadow: -7px 0 0 #f03f4d, 7px 0 0 #f03f4d, 0 7px 0 #f03f4d, 0 14px 0 #f03f4d, 4px 18px 0 #8d1828, 4px 4px 0 #05060b;
  }
  .life-heart::before,
  .life-heart::after {
    top: -7px;
    width: 12px;
    height: 12px;
  }
  .life-heart::before {
    left: -7px;
  }
  .life-heart::after {
    right: -7px;
  }
  .minimap-hud {
    width: 156px;
    padding: 6px;
  }
  .minimap-head {
    margin-bottom: 5px;
    font-size: 10px;
  }
  .minimap-canvas {
    height: 96px;
    background-size: 10px 10px;
  }
  .minimap-gift {
    width: 6px;
    height: 6px;
    box-shadow: 0 0 0 1px #7a3e12, 0 0 6px rgba(255, 238, 120, 0.58);
  }
  .minimap-heart,
  .minimap-heart::before,
  .minimap-heart::after {
    width: 6px;
    height: 6px;
  }
  .minimap-heart::before {
    left: -3px;
  }
  .minimap-heart::after {
    top: -3px;
  }
  .minimap-checkpoint {
    width: 8px;
    height: 8px;
    border-width: 1px;
  }
  .minimap-player {
    width: 10px;
    height: 10px;
    box-shadow: 0 0 0 2px #fff8ef, 0 0 0 4px #07101d, 0 0 8px rgba(255, 241, 166, 0.8);
  }
  .minimap-goal {
    width: 11px;
    height: 11px;
    border-width: 2px;
  }
  .time-hud {
    min-width: 102px;
    padding: 5px 11px;
    font-size: 28px;
  }
  .top-hud {
    gap: 5px;
  }
  .gift-hud {
    min-height: 42px;
    margin-right: 68px;
    padding: 6px 9px;
    font-size: 22px;
  }
  .gift-mark {
    width: 25px;
    height: 25px;
  }
  .gift-mark::before {
    left: 10px;
    width: 6px;
    height: 29px;
  }
  .gift-mark::after {
    top: 10px;
    width: 29px;
    height: 6px;
  }
  .menu-btn {
    min-height: 36px;
    padding: 0 10px;
    font-size: 12px;
  }
  .banner {
    top: 86px;
    padding: 12px 14px;
  }
  .banner strong {
    font-size: 18px;
  }
  .game-over-screen {
    padding: 16px;
  }
  .game-menu-screen {
    padding: 16px;
  }
  .game-menu-panel {
    gap: 14px;
    padding: 22px 18px 24px;
    border-width: 4px;
  }
  .game-menu-actions {
    grid-template-columns: 1fr;
    width: min(260px, 100vw - 64px);
    gap: 10px;
  }
  .game-menu-action {
    min-height: 42px;
    padding: 0 18px;
    font-size: 13px;
  }
  .game-over-panel {
    gap: 10px;
    padding: 22px 18px 26px;
    border-width: 5px;
  }
  .dead-santa {
    width: 62px;
    height: 58px;
    transform: scale(0.9);
  }
  .broken-hearts {
    gap: 12px;
  }
  .restart-button {
    padding: 13px 20px;
    border-width: 5px;
  }
  .level-hud {
    bottom: 176px;
    font-size: 24px;
  }
}
@media (max-width: 560px) {
  .hud {
    grid-template-columns: auto auto auto;
    gap: 8px;
  }
  .minimap-hud {
    width: 132px;
  }
  .minimap-canvas {
    height: 82px;
  }
  .time-hud {
    min-width: 86px;
    font-size: 23px;
  }
  .gift-hud {
    margin-right: 0;
  }
  .right-hud {
    padding-right: 70px;
  }
}`],
})
export class AppComponent implements AfterViewInit, OnDestroy {
    public giftsCollected = 0;
    public giftTotal = 0;
    public coinsCollected = 0;
    public coinTotal = 0;
    public walletCoins = 0;
    public lives = 3;
    public timeText = '03:00';
    public stage = 1;
    public stageTotal = 17;
    public started = false;
    public paused = false;
    public gameMenuOpen = false;
    public banner = 'Snow Gift Run';
    public bannerDetail = 'Collect the gifts and enter the portal.';
    public bannerTone: 'normal' | 'danger' = 'normal';
    public damageFlashActive = false;
    public gameOver = false;
    public gameOverReason = 'ALL LIVES LOST';
    public introMode: 'title' | 'next' = 'title';
    public lastClearedStage = 0;
    public minimapPlatforms: MinimapPlatform[] = [];
    public minimapGifts: MinimapPoint[] = [];
    public minimapHearts: MinimapPoint[] = [];
    public minimapCheckpoints: MinimapPoint[] = [];
    public minimapPlayer: MinimapPoint = { x: 50, y: 50 };
    public minimapGoal: MinimapPoint = { x: 50, y: 50 };
    public account: PlayerProfile | null = null;
    public authMode: 'login' | 'signup' = 'login';
    public authEmail = '';
    public authPassword = '';
    public authName = '';
    public authMessage = '';
    public authLoading = false;
    public saveMessage = 'Guest progress is temporary.';
    public isCustomPage = false;
    public isShopPage = false;
    public isAdminPage = false;
    public customMessage = '';
    public customTab: ShopTab = 'costume';
    public selectedShopItemId = 'classic';
    public shopTab: ShopTab = 'costume';
    public joystickActive = false;
    public joystickKnobX = 0;
    public joystickKnobY = 0;
    public adminUsers: AdminUser[] = [];
    public adminLoading = false;
    public adminMessage = '';
    public readonly adminStageOptions = Array.from({ length: 17 }, (_, index) => index + 1);
    public readonly shopTabs: Array<{ id: ShopTab; label: string }> = [
        { id: 'costume', label: 'Coats' },
        { id: 'gear', label: 'Gear' },
        { id: 'vehicle', label: 'Rides' }
    ];
    public readonly customTabs: Array<{ id: ShopTab; label: string }> = [
        { id: 'costume', label: 'Coats' },
        { id: 'gear', label: 'Gear' },
        { id: 'vehicle', label: 'Rides' }
    ];
    public readonly classicCostume: ShopItem = {
        id: 'classic',
        name: 'Classic Coat',
        type: 'costume',
        price: 0,
        description: 'Original Santa suit.'
    };
    public shopItems: ShopItem[] = [
        { id: 'banish_bell', name: 'Banish Bell', type: 'consumable', price: 90, description: 'Block one monster hit later.' },
        { id: 'blue_costume', name: 'Blue Coat', type: 'costume', price: 220, description: 'A cold-night Santa coat.' },
        { id: 'gold_costume', name: 'Gold Coat', type: 'costume', price: 320, description: 'A shiny ranking flex.' },
        { id: 'alex_costume', name: 'Block Explorer', type: 'costume', price: 420, description: 'Orange hair and green voxel adventurer look.' },
        { id: 'steve_costume', name: 'Block Miner', type: 'costume', price: 430, description: 'Teal shirt, blue pants, square-world style.' },
        { id: 'mario_costume', name: 'Pixel Plumber', type: 'costume', price: 520, description: 'Red cap, blue overalls, retro pixel hero fit.' },
        { id: 'among_crewmate_costume', name: 'Red Crewmate', type: 'costume', price: 460, description: 'Rounded red suit with a glassy visor.' },
        { id: 'sonic_costume', name: 'Blue Speedster', type: 'costume', price: 560, description: 'Blue quills, white gloves, red shoes.' },
        { id: 'frog_costume', name: 'Frog Friend', type: 'costume', price: 500, description: 'Green frog head with a blue shirt.' },
        { id: 'rudolph_vehicle', name: 'Rudolph', type: 'vehicle', price: 480, description: 'A glowing-nose winter ride.' },
        { id: 'bmw_530_blue', name: 'BMW 530', type: 'vehicle', price: 720, description: 'Blue executive snow cruiser.' },
        { id: 'bmw_320i_black_7907', name: 'BMW 320i 7907', type: 'vehicle', price: 790, description: 'Black 320i with plate 7907.' },
        { id: 'mercedes_e_class_white', name: 'Mercedes E-Class', type: 'vehicle', price: 760, description: 'White luxury winter sedan.' },
        { id: 'porsche_911_gt3_pink', name: 'Porsche 911 GT3', type: 'vehicle', price: 1180, description: 'Pink GT3 snow track car.' },
        { id: 'cybertruck', name: 'Cybertruck', type: 'vehicle', price: 880, description: 'Angular electric ice truck.' },
        { id: 'bugatti_chiron_red', name: 'Bugatti Chiron', type: 'vehicle', price: 1400, description: 'Red hypercar for fast flexing.' }
    ];
    public ranking: RankingEntry[] = [];

    private viewportElement!: HTMLDivElement;
    private scene!: THREE.Scene;
    private world!: THREE.Group;
    private scenery!: THREE.Group;
    private camera!: THREE.PerspectiveCamera;
    private renderer!: THREE.WebGLRenderer;
    private player!: THREE.Group;
    private snow!: THREE.Points;
    private stars!: THREE.Points;
    private starBaseColors = new Float32Array(0);
    private starPhases = new Float32Array(0);
    private snowFallSpeeds = new Float32Array(0);
    private snowDriftPhases = new Float32Array(0);
    private snowColumns = 1;
    private goal!: THREE.Group;
    private goalLight!: THREE.PointLight;
    private adminOutfit?: THREE.Group;
    private adminMount?: THREE.Group;
    private vehicleMount?: THREE.Group;
    private santaRider?: THREE.Group;
    private adminHalo?: THREE.Group;
    private adminAura?: THREE.Group;
    private baseAvatar?: THREE.Group;
    private costumeOutfits?: THREE.Group;

    private readonly spawn = new THREE.Vector3(0, 0, 0);
    private playerPosition = new THREE.Vector3(0, 0, 0);
    private playerVelocity = new THREE.Vector3(0, 0, 0);
    private cameraPosition = new THREE.Vector3();
    private cameraTarget = new THREE.Vector3();
    private mouseLook = new THREE.Vector2(0, 0);
    private mouseLookTarget = new THREE.Vector2(0, 0);
    private slideDrift = new THREE.Vector2(0, 0);
    private previewPointer = new THREE.Vector2(0, 0);
    private previewRaycaster = new THREE.Raycaster();
    private previewTarget = new THREE.Vector3();
    private joystickVector = new THREE.Vector2(0, 0);
    private joystickCenter = new THREE.Vector2(0, 0);
    private joystickPointerId = -1;
    private jumpPointerId = -1;
    private joystickBaseYaw = 0;
    private mobileLookYaw = 0;
    private mobileLookYawTarget = 0;
    private mobileDesiredYaw = 0;
    private mobileLookMode = false;
    private projectedPlayer = new THREE.Vector3();
    private playerWorldPosition = new THREE.Vector3();
    private cameraRight = new THREE.Vector3();
    private goalPosition = new THREE.Vector3();
    private viewportAspect = 1;
    private readonly sceneBackground = new THREE.Color(0x030817);
    private readonly sceneFog = new THREE.Fog(0x030817, 38, 105);
    private readonly mysticSceneBackground = new THREE.Color(0x07051d);
    private readonly mysticSceneFog = new THREE.Fog(0x07051d, 24, 86);

    private platforms: Platform[] = [];
    private gifts: Gift[] = [];
    private coins: Coin[] = [];
    private hearts: HeartPickup[] = [];
    private checkpoints: Checkpoint[] = [];
    private giftBursts: GiftBurst[] = [];
    private enemies: Enemy[] = [];
    private meteors: MeteorHazard[] = [];
    private dimensionPortals: DimensionPortal[] = [];
    private dimensionPortalCooldown = 0;
    private meteorTimer = 0;
    private keys = new Set<string>();
    private touch: Record<TouchKey, boolean> = {
        forward: false,
        back: false,
        left: false,
        right: false
    };

    private animationId = 0;
    private lastFrame = 0;
    private elapsed = 0;
    private timeLeft = 180;
    private currentLevelIndex = 0;
    private activeLevel?: LevelLayout;
    private jumpQueued = false;
    private jumpBufferTimer = 0;
    private coyoteTimer = 0;
    private airJumpsUsed = 0;
    private finished = false;
    private activeCheckpointIndex = -1;
    private readonly respawnPoint = new THREE.Vector3();
    private hudAccumulator = 0;
    private damageBannerTimer = 0;
    private damageFlashTimer = 0;
    private adminTestMode = false;
    private bankedCoinsThisRun = 0;
    private coinDepositPending = 0;
    private coinDepositTimer = 0;
    private coinDepositPromise: Promise<number> | null = null;
    private audioContext?: AudioContext;
    private bgmMaster?: GainNode;
    private bgmFilter?: BiquadFilterNode;
    private bgmSchedulerId = 0;
    private bgmNextLoopTime = 0;
    private minimapBounds: MinimapBounds = { minX: -20, maxX: 20, minZ: -40, maxZ: 8 };
    private readonly progressStorageKey = 'snow-gift-run:level-index';
    private readonly localUsersKey = 'snow-gift-run:local-users';
    private readonly localSessionKey = 'snow-gift-run:local-session';
    private readonly noVehicleId = 'none';
    private readonly gameplayKeyCodes = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space', 'KeyW', 'KeyA', 'KeyS', 'KeyD', 'ShiftLeft', 'ShiftRight']);
    private readonly maxLives = 3;
    private readonly joystickMaxDistance = 54;
    private readonly joystickMoveDeadZone = 0.16;
    private readonly mobileLookDeadZone = 0.28;
    private readonly mobileLookTurnSpeedMin = 0.42;
    private readonly mobileLookTurnSpeedMax = 1.35;
    private readonly mobileLookSmoothing = 2.6;
    private readonly playerMoveSpeed = 6.1;
    private readonly playerDashSpeed = 10.2;
    private readonly playerTurnBlend = 0.22;
    private readonly playerDashTurnBlend = 0.3;
    private readonly playerJumpVelocity = 10.4;
    private readonly enemyPatrolSpeedScale = 0.84;
    private readonly enemyChaseSpeedScale = 0.82;
    private readonly meteorFallSpeedScale = 0.78;
    private readonly scoreStageMultiplier = 20000;
    private readonly scoreGiftMultiplier = 220;
    private readonly scoreCoinMultiplier = 120;
    private readonly scoreClearBonus = 10000;
    private readonly scoreClearTimeMultiplier = 75;
    private readonly scoreAttemptTimeMultiplier = 8;
    private readonly publicStageTotal = 17;
    private readonly removedShopRefunds: Record<string, number> = {
        speed_boots: 120,
        magnet_charm: 160,
        double_jump: 260
    };
    private readonly vehicleRideHeights: Record<string, number> = {
        rudolph_vehicle: 0.46,
        bmw_530_blue: 0.35,
        bmw_320i_black_7907: 0.35,
        mercedes_e_class_white: 0.36,
        porsche_911_gt3_pink: 0.31,
        cybertruck: 0.43,
        bugatti_chiron_red: 0.32
    };
    private readonly bgmBeatSeconds = 60 / 132;
    private readonly bgmScheduleAheadSeconds = 0.7;
    // Public-domain carol melody with an original synthesized arrangement. No sampled recording is shipped.
    private readonly bgmMelody: BgmNote[] = [
        { midi: 67, beats: 1, velocity: 0.84 },
        { midi: 72, beats: 0.75, velocity: 1 }, { midi: 72, beats: 0.25, velocity: 0.72 }, { midi: 74, beats: 0.5 }, { midi: 72, beats: 0.5 }, { midi: 71, beats: 0.5 }, { midi: 69, beats: 0.5 },
        { midi: 69, beats: 1.5, velocity: 0.86 }, { midi: 69, beats: 1.5, velocity: 0.72 },
        { midi: 74, beats: 0.75, velocity: 1 }, { midi: 74, beats: 0.25, velocity: 0.7 }, { midi: 76, beats: 0.5 }, { midi: 74, beats: 0.5 }, { midi: 72, beats: 0.5 }, { midi: 71, beats: 0.5 },
        { midi: 67, beats: 1.5, velocity: 0.86 }, { midi: 67, beats: 1.5, velocity: 0.72 },
        { midi: 76, beats: 0.75, velocity: 1 }, { midi: 76, beats: 0.25, velocity: 0.7 }, { midi: 77, beats: 0.5 }, { midi: 76, beats: 0.5 }, { midi: 74, beats: 0.5 }, { midi: 72, beats: 0.5 },
        { midi: 69, beats: 1.5, velocity: 0.88 }, { midi: 67, beats: 0.75, velocity: 0.74 }, { midi: 67, beats: 0.75, velocity: 0.74 },
        { midi: 69, beats: 1 }, { midi: 74, beats: 1 }, { midi: 71, beats: 1 }, { midi: 72, beats: 2, velocity: 1 }, { beats: 1 },
        { midi: 67, beats: 0.75, velocity: 0.86 }, { midi: 72, beats: 0.25 }, { midi: 72, beats: 0.5 }, { midi: 72, beats: 0.5 }, { midi: 71, beats: 0.5 }, { midi: 71, beats: 0.5 },
        { midi: 72, beats: 0.5 }, { midi: 71, beats: 0.5 }, { midi: 69, beats: 0.5 }, { midi: 67, beats: 1 }, { beats: 0.5 },
        { midi: 74, beats: 0.75, velocity: 0.96 }, { midi: 76, beats: 0.25 }, { midi: 74, beats: 0.5 }, { midi: 72, beats: 0.5 }, { midi: 67, beats: 0.75 }, { midi: 67, beats: 0.25 },
        { midi: 67, beats: 0.5 }, { midi: 69, beats: 0.5 }, { midi: 74, beats: 0.5 }, { midi: 71, beats: 0.5 }, { midi: 72, beats: 1.5, velocity: 1 }, { beats: 0.5 }
    ];
    private readonly bgmHarmony: number[][] = [
        [48, 55, 60, 64],
        [41, 48, 53, 57],
        [45, 52, 57, 60],
        [43, 50, 55, 59],
        [48, 55, 60, 64],
        [45, 52, 57, 60],
        [41, 48, 53, 57],
        [43, 50, 55, 59],
        [48, 55, 60, 64],
        [41, 48, 53, 57],
        [43, 50, 55, 59],
        [48, 55, 60, 64]
    ];
    private readonly bgmLoopBeats = this.bgmMelody.reduce((total, note) => total + note.beats, 0);

    private readonly snowMaterial = pixelMaterial({ color: 0xf7fcff, roughness: 0.98 });
    private readonly sideMaterial = pixelMaterial({ color: 0x687584, roughness: 0.94 });
    private readonly iceMaterial = pixelMaterial({ color: 0x84cfff, roughness: 0.62 });
    private readonly stoneMaterials = [
        pixelMaterial({ color: 0x9eaec4, roughness: 0.96 }),
        pixelMaterial({ color: 0x7f8fa8, roughness: 0.96 }),
        pixelMaterial({ color: 0xc5d9ee, roughness: 0.94 }),
        pixelMaterial({ color: 0x60789e, roughness: 0.98 }),
        pixelMaterial({ color: 0x9b92c6, roughness: 0.96 })
    ];
    private readonly stoneLineMaterial = pixelMaterial({ color: 0x172234, roughness: 0.98 });
    private readonly woodMaterial = pixelMaterial({ color: 0x6c4528, roughness: 0.9 });
    private readonly woodTopMaterial = pixelMaterial({ color: 0xb9823d, roughness: 0.86 });
    private readonly woodDarkMaterial = pixelMaterial({ color: 0x3d2618, roughness: 0.94 });
    private readonly woodRingMaterial = pixelMaterial({ color: 0xf0bb62, roughness: 0.78 });
    private readonly redMaterial = pixelMaterial({ color: 0xd21f2b, roughness: 0.82 });
    private readonly darkRedMaterial = pixelMaterial({ color: 0x9b171e, roughness: 0.88 });
    private readonly whiteMaterial = pixelMaterial({ color: 0xffffff, roughness: 0.94 });
    private readonly blackMaterial = pixelMaterial({ color: 0x111418, roughness: 0.9 });
    private readonly goldMaterial = pixelMaterial({ color: 0xffc342, roughness: 0.72 });
    private readonly adminVoidMaterial = pixelMaterial({ color: 0x070915, roughness: 0.7, emissive: 0x29105c, emissiveIntensity: 0.5 });
    private readonly adminCapeMaterial = pixelMaterial({ color: 0x2a1352, roughness: 0.68, emissive: 0x45118a, emissiveIntensity: 0.42 });
    private readonly adminGoldMaterial = pixelMaterial({ color: 0xf7e6a6, roughness: 0.48, metalness: 0.12, emissive: 0xffb83d, emissiveIntensity: 0.4 });
    private readonly adminCyanMaterial = pixelMaterial({ color: 0x80fbff, roughness: 0.42, emissive: 0x19dfff, emissiveIntensity: 0.9 });
    private readonly adminMagentaMaterial = pixelMaterial({ color: 0xff60f2, roughness: 0.46, emissive: 0xff2bd4, emissiveIntensity: 0.72 });
    private readonly adminGlowMaterial = new THREE.MeshBasicMaterial({ color: 0x9effff, transparent: true, opacity: 0.6, depthWrite: false });
    private readonly adminAuraMaterial = new THREE.MeshBasicMaterial({ color: 0xfff1a8, transparent: true, opacity: 0.72, depthWrite: false });
    private readonly rudolphBodyMaterial = pixelMaterial({ color: 0x8a5530, roughness: 0.86 });
    private readonly rudolphDarkMaterial = pixelMaterial({ color: 0x563018, roughness: 0.9 });
    private readonly rudolphAntlerMaterial = pixelMaterial({ color: 0xd3b06a, roughness: 0.74 });
    private readonly rudolphNoseMaterial = pixelMaterial({ color: 0xff2348, roughness: 0.38, emissive: 0xff1138, emissiveIntensity: 1.3 });
    private readonly carBlueMaterial = pixelMaterial({ color: 0x1559c9, roughness: 0.44, metalness: 0.18, emissive: 0x061b48, emissiveIntensity: 0.12 });
    private readonly carBlackMaterial = pixelMaterial({ color: 0x080b12, roughness: 0.42, metalness: 0.22, emissive: 0x020713, emissiveIntensity: 0.08 });
    private readonly carWhiteMaterial = pixelMaterial({ color: 0xf3f8ff, roughness: 0.5, metalness: 0.12 });
    private readonly carSilverMaterial = pixelMaterial({ color: 0xa9b1ba, roughness: 0.5, metalness: 0.18 });
    private readonly carRedMaterial = pixelMaterial({ color: 0xdb1628, roughness: 0.38, metalness: 0.2, emissive: 0x3c050b, emissiveIntensity: 0.12 });
    private readonly carPinkMaterial = pixelMaterial({ color: 0xffc7da, roughness: 0.38, metalness: 0.12, emissive: 0x5a2033, emissiveIntensity: 0.08 });
    private readonly carDarkBlueMaterial = pixelMaterial({ color: 0x071f4f, roughness: 0.62, metalness: 0.08 });
    private readonly carGlassMaterial = pixelMaterial({ color: 0x8fe9ff, roughness: 0.28, metalness: 0.04, emissive: 0x0a5d7a, emissiveIntensity: 0.32 });
    private readonly carLightMaterial = pixelMaterial({ color: 0xfff7c7, roughness: 0.34, emissive: 0xffd763, emissiveIntensity: 0.75 });
    private readonly carTailLightMaterial = pixelMaterial({ color: 0xff2438, roughness: 0.4, emissive: 0xff1028, emissiveIntensity: 0.55 });
    private readonly carTireMaterial = pixelMaterial({ color: 0x090b10, roughness: 0.86 });
    private readonly carRimMaterial = pixelMaterial({ color: 0xd8e2ee, roughness: 0.42, metalness: 0.2 });
    private readonly coinMaterial = pixelMaterial({ color: 0xffdf53, roughness: 0.58, emissive: 0x9d6b00, emissiveIntensity: 0.25 });
    private readonly coinDarkMaterial = pixelMaterial({ color: 0x9f6f18, roughness: 0.82 });
    private readonly checkpointBaseMaterial = pixelMaterial({ color: 0x273343, roughness: 0.86, emissive: 0x0b2433, emissiveIntensity: 0.2 });
    private readonly checkpointRingMaterial = pixelMaterial({ color: 0x76eeff, roughness: 0.42, emissive: 0x1fcfff, emissiveIntensity: 0.82 });
    private readonly checkpointReachedMaterial = pixelMaterial({ color: 0xfff173, roughness: 0.44, emissive: 0xffc342, emissiveIntensity: 0.95 });
    private readonly checkpointCoreMaterial = new THREE.MeshBasicMaterial({ color: 0x9effff, transparent: true, opacity: 0.35, side: THREE.DoubleSide, depthWrite: false });
    private readonly heartMaterial = pixelMaterial({ color: 0xff3f6d, roughness: 0.56, emissive: 0xff123f, emissiveIntensity: 0.65 });
    private readonly heartDarkMaterial = pixelMaterial({ color: 0xb91438, roughness: 0.72, emissive: 0x7a0d22, emissiveIntensity: 0.35 });
    private readonly slipperyIceMaterial = pixelMaterial({ color: 0xd8fbff, roughness: 0.12, metalness: 0.08, emissive: 0x52dfff, emissiveIntensity: 0.36 });
    private readonly iceVeinMaterial = new THREE.MeshBasicMaterial({ color: 0x78dfff, transparent: true, opacity: 0.34, depthWrite: false });
    private readonly iceGlareMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.46, depthWrite: false });
    private readonly shakyWarningMaterial = new THREE.MeshBasicMaterial({ color: 0xfff173, transparent: true, opacity: 0.52, depthWrite: false });
    private readonly fireMaterial = new THREE.MeshBasicMaterial({ color: 0xff6a1f, transparent: true, opacity: 0.86, depthWrite: false });
    private readonly fireCoreMaterial = new THREE.MeshBasicMaterial({ color: 0xfff173, transparent: true, opacity: 0.92, depthWrite: false });
    private readonly emberMaterial = new THREE.MeshBasicMaterial({ color: 0xff2d24, transparent: true, opacity: 0.72, depthWrite: false });
    private readonly bouncePadMaterial = pixelMaterial({ color: 0x55f7a0, roughness: 0.34, emissive: 0x14d978, emissiveIntensity: 0.72 });
    private readonly bouncePadCoreMaterial = new THREE.MeshBasicMaterial({ color: 0xfff173, transparent: true, opacity: 0.72, depthWrite: false });
    private readonly ribbonMaterial = pixelMaterial({ color: 0xe63030, roughness: 0.8 });
    private readonly faceMaterial = pixelMaterial({ color: 0xffd1ad, roughness: 0.88 });
    private readonly skinTanMaterial = pixelMaterial({ color: 0xb77b5d, roughness: 0.88 });
    private readonly skinPeachMaterial = pixelMaterial({ color: 0xffb66d, roughness: 0.88 });
    private readonly alexHairMaterial = pixelMaterial({ color: 0xc97925, roughness: 0.84 });
    private readonly alexHairDarkMaterial = pixelMaterial({ color: 0x8f501b, roughness: 0.9 });
    private readonly alexShirtMaterial = pixelMaterial({ color: 0x5aa574, roughness: 0.86 });
    private readonly alexPantsMaterial = pixelMaterial({ color: 0x6f4c35, roughness: 0.88 });
    private readonly steveHairMaterial = pixelMaterial({ color: 0x2d1b10, roughness: 0.88 });
    private readonly steveShirtMaterial = pixelMaterial({ color: 0x12b4bc, roughness: 0.82 });
    private readonly stevePantsMaterial = pixelMaterial({ color: 0x4637a5, roughness: 0.86 });
    private readonly marioRedMaterial = pixelMaterial({ color: 0xf01515, roughness: 0.76 });
    private readonly marioBlueMaterial = pixelMaterial({ color: 0x4355c7, roughness: 0.82 });
    private readonly marioBrownMaterial = pixelMaterial({ color: 0x8b4f1e, roughness: 0.88 });
    private readonly amongRedMaterial = pixelMaterial({ color: 0xd0101c, roughness: 0.74 });
    private readonly amongDarkMaterial = pixelMaterial({ color: 0x6c0630, roughness: 0.82 });
    private readonly amongVisorMaterial = pixelMaterial({ color: 0xa8e7f3, roughness: 0.28, metalness: 0.06, emissive: 0x225e7a, emissiveIntensity: 0.36 });
    private readonly sonicBlueMaterial = pixelMaterial({ color: 0x244cf3, roughness: 0.74 });
    private readonly sonicTanMaterial = pixelMaterial({ color: 0xffc99b, roughness: 0.86 });
    private readonly sonicShoeMaterial = pixelMaterial({ color: 0xff1f22, roughness: 0.72 });
    private readonly frogGreenMaterial = pixelMaterial({ color: 0x65bd37, roughness: 0.84 });
    private readonly frogDarkGreenMaterial = pixelMaterial({ color: 0x2f7d24, roughness: 0.88 });
    private readonly frogShirtMaterial = pixelMaterial({ color: 0x67b8f0, roughness: 0.82 });
    private readonly frogPantsMaterial = pixelMaterial({ color: 0xa96b2c, roughness: 0.88 });
    private readonly sackMaterial = pixelMaterial({ color: 0x9c6a3b, roughness: 0.94 });
    private readonly pineMaterial = pixelMaterial({ color: 0x0d3527, roughness: 0.96 });
    private readonly roofMaterial = pixelMaterial({ color: 0x273d86, roughness: 0.86 });
    private readonly windowMaterial = pixelMaterial({ color: 0xffe58f, roughness: 0.7, emissive: 0xffc34a, emissiveIntensity: 0.9 });
    private readonly castleRoofMaterial = pixelMaterial({ color: 0x9e4d2d, roughness: 0.84 });
    private readonly castleBannerMaterial = pixelMaterial({ color: 0x324ed8, roughness: 0.58, emissive: 0x0d2a88, emissiveIntensity: 0.18 });
    private readonly torchPoleMaterial = pixelMaterial({ color: 0x241915, roughness: 0.86 });
    private readonly mountainMaterial = pixelMaterial({ color: 0x050913, roughness: 1 });
    private readonly portalRingMaterial = pixelMaterial({ color: 0x74e7ff, roughness: 0.45, emissive: 0x1c9dff, emissiveIntensity: 0.8 });
    private readonly portalInnerMaterial = pixelMaterial({ color: 0xb483ff, roughness: 0.48, emissive: 0x7e3cff, emissiveIntensity: 0.55 });
    private readonly portalCoreMaterial = new THREE.MeshBasicMaterial({ color: 0x5ee7ff, transparent: true, opacity: 0.22, side: THREE.DoubleSide, depthWrite: false });
    private readonly portalClosedMaterial = pixelMaterial({ color: 0x37414d, roughness: 0.9, emissive: 0x081120, emissiveIntensity: 0.18 });
    private readonly meteorMaterial = pixelMaterial({ color: 0x4a2d21, roughness: 0.88, emissive: 0xff3b21, emissiveIntensity: 0.35 });
    private readonly meteorCoreMaterial = pixelMaterial({ color: 0xff6c2e, roughness: 0.62, emissive: 0xff3b21, emissiveIntensity: 1.1 });
    private readonly giftSparkGeometry = new THREE.BoxGeometry(0.16, 0.16, 0.16);
    private readonly giftSparkMaterials = [
        new THREE.MeshBasicMaterial({ color: 0xfff173, transparent: true, opacity: 1 }),
        new THREE.MeshBasicMaterial({ color: 0xff3b3b, transparent: true, opacity: 1 }),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1 }),
        new THREE.MeshBasicMaterial({ color: 0x74e7ff, transparent: true, opacity: 1 })
    ];
    private readonly levels: LevelLayout[] = [
        {
            title: 'Wide frozen yard',
            time: 165,
            spawn: [0, 2],
            goal: [10, -31],
            platforms: [
                { x: 0, y: 0, z: 0, w: 15, h: 0.85, d: 9 },
                { x: 0, y: 0.55, z: -8.5, w: 5.2, h: 0.85, d: 8.5 },
                { x: -8, y: 0.9, z: -13, w: 9, h: 0.85, d: 7 },
                { x: 8, y: 0.9, z: -13, w: 9, h: 0.85, d: 7 },
                { x: 0, y: 1.25, z: -19, w: 14, h: 0.85, d: 8 },
                { x: -9.5, y: 1.55, z: -24, w: 8, h: 0.85, d: 5.2 },
                { x: 4.5, y: 1.75, z: -25, w: 5.2, h: 0.85, d: 9.5 },
                { x: 10, y: 2.05, z: -31, w: 11, h: 0.85, d: 8 }
            ],
            gifts: [[-4.8, 1.2], [4.8, 1.2], [0, -8.5], [-9.6, -13], [8.8, -13], [-3.8, -19], [4.6, -19], [-9.5, -24], [6.8, -25], [10, -31]],
            enemies: [{ x: 0, z: -19, axis: 'x', distance: 3.1, speed: 1.25 }, { x: 8, z: -13, axis: 'z', distance: 2.0, speed: 1.15 }]
        },
        {
            title: 'Twin roof fork',
            time: 175,
            spawn: [0, 1],
            goal: [-12, -33],
            platforms: [
                { x: 0, y: 0, z: 0, w: 13, h: 0.85, d: 8 },
                { x: -7, y: 0.65, z: -8, w: 5, h: 0.85, d: 9 },
                { x: 7, y: 0.65, z: -8, w: 5, h: 0.85, d: 9 },
                { x: -12, y: 1.0, z: -15, w: 9, h: 0.85, d: 7 },
                { x: 12, y: 1.0, z: -15, w: 9, h: 0.85, d: 7 },
                { x: 0, y: 1.35, z: -20, w: 18, h: 0.85, d: 7.5 },
                { x: 8.5, y: 1.65, z: -27, w: 5, h: 0.85, d: 9 },
                { x: -7, y: 1.75, z: -29, w: 10, h: 0.85, d: 7 },
                { x: -12, y: 2.05, z: -33, w: 8.5, h: 0.85, d: 7 }
            ],
            gifts: [[-3.8, 1], [3.8, 1], [-7, -8], [7, -8], [-12, -15], [12, -15], [-4, -20], [4, -20], [8.5, -27], [-7, -29], [-12, -33]],
            enemies: [{ x: -12, z: -15, axis: 'x', distance: 2.8, speed: 1.25 }, { x: 0, z: -20, axis: 'x', distance: 4.5, speed: 1.1 }]
        },
        {
            title: 'Snow square circuit',
            time: 185,
            spawn: [0, 2],
            goal: [14, -30],
            platforms: [
                { x: 0, y: 0, z: 0, w: 15, h: 0.85, d: 9 },
                { x: -8, y: 0.55, z: -8, w: 6, h: 0.85, d: 8 },
                { x: 8, y: 0.55, z: -8, w: 6, h: 0.85, d: 8 },
                { x: -14, y: 0.95, z: -16, w: 8, h: 0.85, d: 8 },
                { x: 0, y: 1.2, z: -16, w: 14, h: 0.85, d: 7 },
                { x: 14, y: 1.45, z: -16, w: 8, h: 0.85, d: 8 },
                { x: 14, y: 1.75, z: -24, w: 6, h: 0.85, d: 9 },
                { x: 2, y: 1.9, z: -26, w: 15, h: 0.85, d: 6 },
                { x: -10, y: 2.05, z: -24, w: 7, h: 0.85, d: 7 },
                { x: 14, y: 2.2, z: -30, w: 9, h: 0.85, d: 7 }
            ],
            gifts: [[-4.5, 0], [4.5, 0], [-8, -8], [8, -8], [-14, -16], [0, -16], [14, -16], [14, -24], [-10, -24], [2, -26], [14, -30]],
            enemies: [{ x: 0, z: -16, axis: 'x', distance: 4.2, speed: 1.28 }, { x: 14, z: -24, axis: 'z', distance: 2.4, speed: 1.2 }]
        },
        {
            title: 'Crossed ice bridges',
            time: 190,
            spawn: [-5, 1],
            goal: [0, -36],
            platforms: [
                { x: -5, y: 0, z: 0, w: 13, h: 0.85, d: 8 },
                { x: 5, y: 0.55, z: -7, w: 9, h: 0.85, d: 5 },
                { x: -8, y: 0.75, z: -12, w: 8, h: 0.85, d: 7 },
                { x: 8, y: 0.95, z: -15, w: 8, h: 0.85, d: 7 },
                { x: 0, y: 1.25, z: -20, w: 20, h: 0.85, d: 7 },
                { x: -13, y: 1.55, z: -26, w: 7, h: 0.85, d: 8 },
                { x: 13, y: 1.55, z: -26, w: 7, h: 0.85, d: 8 },
                { x: 0, y: 1.85, z: -30, w: 10, h: 0.85, d: 6 },
                { x: 0, y: 2.1, z: -36, w: 12, h: 0.85, d: 7 }
            ],
            gifts: [[-8, 0], [-1, 0], [5, -7], [-8, -12], [8, -15], [-6, -20], [6, -20], [-13, -26], [13, -26], [0, -30], [0, -36]],
            enemies: [{ x: 0, z: -20, axis: 'x', distance: 5.4, speed: 1.15 }, { x: -13, z: -26, axis: 'z', distance: 2.0, speed: 1.3 }, { x: 13, z: -26, axis: 'z', distance: 2.0, speed: 1.3 }]
        },
        {
            title: 'Outer rim loop',
            time: 200,
            spawn: [0, 2],
            goal: [-16, -31],
            platforms: [
                { x: 0, y: 0, z: 0, w: 16, h: 0.85, d: 9 },
                { x: 10, y: 0.55, z: -7, w: 7, h: 0.85, d: 8 },
                { x: 16, y: 0.8, z: -14, w: 7, h: 0.85, d: 8 },
                { x: 8, y: 1.05, z: -20, w: 14, h: 0.85, d: 7 },
                { x: -6, y: 1.3, z: -20, w: 14, h: 0.85, d: 7 },
                { x: -16, y: 1.55, z: -14, w: 7, h: 0.85, d: 8 },
                { x: -16, y: 1.75, z: -24, w: 7, h: 0.85, d: 9 },
                { x: -8, y: 1.95, z: -31, w: 10, h: 0.85, d: 7 },
                { x: 4, y: 2.1, z: -30, w: 9, h: 0.85, d: 6 },
                { x: -16, y: 2.2, z: -31, w: 7.5, h: 0.85, d: 7 }
            ],
            gifts: [[-4, 0], [4, 0], [10, -7], [16, -14], [8, -20], [-6, -20], [-16, -14], [-16, -24], [4, -30], [-8, -31], [-16, -31]],
            enemies: [{ x: 8, z: -20, axis: 'x', distance: 3.8, speed: 1.22 }, { x: -6, z: -20, axis: 'x', distance: 3.8, speed: 1.18 }]
        },
        {
            title: 'Moving maze field',
            time: 205,
            spawn: [0, 2],
            goal: [15, -35],
            platforms: [
                { x: 0, y: 0, z: 0, w: 15, h: 0.85, d: 9 },
                { x: -8, y: 0.55, z: -8, w: 7, h: 0.85, d: 7, axis: 'x', distance: 1.8, speed: 0.85 },
                { x: 8, y: 0.55, z: -8, w: 7, h: 0.85, d: 7, axis: 'x', distance: 1.8, speed: 0.85, phase: 1.4 },
                { x: 0, y: 0.9, z: -15, w: 15, h: 0.85, d: 7 },
                { x: -12, y: 1.2, z: -22, w: 8, h: 0.85, d: 8 },
                { x: 12, y: 1.2, z: -22, w: 8, h: 0.85, d: 8 },
                { x: 0, y: 1.55, z: -26, w: 10, h: 0.85, d: 7, axis: 'z', distance: 1.8, speed: 1.0 },
                { x: 15, y: 1.9, z: -35, w: 10, h: 0.85, d: 8 },
                { x: -8, y: 1.9, z: -35, w: 8, h: 0.85, d: 7 }
            ],
            gifts: [[-4, 0], [4, 0], [-8, -8], [8, -8], [0, -15], [-12, -22], [12, -22], [0, -26], [-8, -35], [15, -35]],
            enemies: [{ x: 0, z: -15, axis: 'x', distance: 4.8, speed: 1.22 }, { x: 12, z: -22, axis: 'z', distance: 2.2, speed: 1.35 }]
        },
        {
            title: 'High roof plaza',
            time: 215,
            spawn: [-6, 2],
            goal: [16, -38],
            platforms: [
                { x: -6, y: 0, z: 0, w: 14, h: 0.85, d: 9 },
                { x: 6, y: 0.65, z: -7, w: 8, h: 0.85, d: 8 },
                { x: 16, y: 0.95, z: -12, w: 10, h: 0.85, d: 7 },
                { x: 4, y: 1.25, z: -18, w: 18, h: 0.85, d: 8 },
                { x: -12, y: 1.45, z: -18, w: 8, h: 0.85, d: 8 },
                { x: -12, y: 1.75, z: -28, w: 8, h: 0.85, d: 9 },
                { x: 2, y: 1.95, z: -29, w: 16, h: 0.85, d: 7 },
                { x: 16, y: 2.25, z: -28, w: 8, h: 0.85, d: 9 },
                { x: 16, y: 2.45, z: -38, w: 10, h: 0.85, d: 8 }
            ],
            gifts: [[-10, 1], [-2, 1], [6, -7], [16, -12], [-12, -18], [4, -18], [-12, -28], [2, -29], [16, -28], [16, -38]],
            enemies: [{ x: 4, z: -18, axis: 'x', distance: 5.6, speed: 1.2 }, { x: -12, z: -28, axis: 'z', distance: 2.6, speed: 1.2 }, { x: 16, z: -28, axis: 'z', distance: 2.6, speed: 1.3 }]
        },
        {
            title: 'Final sky maze',
            time: 230,
            spawn: [0, 2],
            goal: [0, -44],
            platforms: [
                { x: 0, y: 0, z: 0, w: 17, h: 0.85, d: 10 },
                { x: -12, y: 0.55, z: -9, w: 8, h: 0.85, d: 8 },
                { x: 12, y: 0.55, z: -9, w: 8, h: 0.85, d: 8 },
                { x: 0, y: 0.85, z: -15, w: 16, h: 0.85, d: 7 },
                { x: -16, y: 1.15, z: -22, w: 8, h: 0.85, d: 9 },
                { x: 16, y: 1.15, z: -22, w: 8, h: 0.85, d: 9 },
                { x: 0, y: 1.45, z: -27, w: 18, h: 0.85, d: 8 },
                { x: -10, y: 1.75, z: -35, w: 9, h: 0.85, d: 8 },
                { x: 10, y: 1.75, z: -35, w: 9, h: 0.85, d: 8 },
                { x: 0, y: 2.05, z: -39, w: 12, h: 0.85, d: 7 },
                { x: 0, y: 2.25, z: -44, w: 10, h: 0.85, d: 7 }
            ],
            gifts: [[-5, 1], [5, 1], [-12, -9], [12, -9], [0, -15], [-16, -22], [16, -22], [-6, -27], [6, -27], [-10, -35], [10, -35], [0, -39], [0, -44]],
            enemies: [{ x: 0, z: -15, axis: 'x', distance: 5.2, speed: 1.25 }, { x: 0, z: -27, axis: 'x', distance: 6.0, speed: 1.35 }, { x: -10, z: -35, axis: 'z', distance: 2.4, speed: 1.25 }, { x: 10, z: -35, axis: 'z', distance: 2.4, speed: 1.2 }]
        },
        {
            title: 'Quake ridge',
            time: 245,
            spawn: [0, 0],
            goal: [0, -1],
            platforms: [],
            gifts: [],
            enemies: []
        },
        {
            title: 'Dimension fire bridge',
            time: 260,
            spawn: [0, 0],
            goal: [0, -1],
            platforms: [],
            gifts: [],
            enemies: []
        },
        {
            title: 'Knife-edge ice causeway',
            time: 275,
            spawn: [0, 0],
            goal: [0, -1],
            platforms: [],
            gifts: [],
            enemies: []
        },
        {
            title: 'Frozen fire field',
            time: 290,
            spawn: [0, 0],
            goal: [0, -1],
            platforms: [],
            gifts: [],
            enemies: []
        },
        {
            title: 'Bounce snowman climb',
            time: 310,
            spawn: [0, 0],
            goal: [0, -1],
            platforms: [],
            gifts: [],
            enemies: []
        },
        {
            title: 'Tower bridge gauntlet',
            time: 330,
            spawn: [0, 0],
            goal: [0, -1],
            platforms: [],
            gifts: [],
            enemies: []
        },
        {
            title: 'Secret castle court',
            time: 360,
            spawn: [0, 0],
            goal: [0, -1],
            platforms: [],
            gifts: [],
            enemies: []
        },
        {
            title: 'Castle leap trial',
            time: 380,
            spawn: [0, 0],
            goal: [0, -1],
            platforms: [],
            gifts: [],
            enemies: []
        },
        {
            title: 'Mystic aurora sanctum',
            time: 405,
            spawn: [0, 0],
            goal: [0, -1],
            platforms: [],
            gifts: [],
            enemies: []
        }
    ];

    constructor(@Inject( ElementRef<HTMLElement>)         private host: ElementRef<HTMLElement>,@Inject( NgZone)         private zone: NgZone,@Inject( ChangeDetectorRef    )         private changeDetector: ChangeDetectorRef    ) { }

    public ngAfterViewInit(): void {
        const viewport = this.host.nativeElement.querySelector('.game-viewport');
        if (!(viewport instanceof HTMLDivElement)) return;
        this.viewportElement = viewport;

        this.zone.runOutsideAngular(() => {
            this.currentLevelIndex = this.loadSavedLevelIndex();
            this.initScene();
            this.resetGame();
            this.syncPageFromLocation();
            void this.loadAccount();
            this.lastFrame = performance.now();
            this.animationId = requestAnimationFrame(this.tick);
        });
    }

    public ngOnDestroy(): void {
        this.stopBackgroundMusic(0.05);
        cancelAnimationFrame(this.animationId);
        if (this.coinDepositTimer) window.clearTimeout(this.coinDepositTimer);
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('blur', this.clearControls);
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.clearTouchControls);
        window.removeEventListener('pointercancel', this.clearTouchControls);
        window.removeEventListener('popstate', this.onPopState);
        this.viewportElement?.removeEventListener('pointerleave', this.onPointerLeave);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.renderer?.dispose();
        if (this.audioContext && this.audioContext.state !== 'closed') void this.audioContext.close();
    }

    public setTouch(key: TouchKey, active: boolean): void {
        if (this.isMenuPage || this.paused || this.gameMenuOpen) {
            this.touch[key] = false;
            return;
        }
        this.touch[key] = active;
    }

    public startJoystick(event: PointerEvent): void {
        if (this.isMenuPage || this.paused || this.gameMenuOpen) return;
        if (this.joystickPointerId !== -1 && this.joystickPointerId !== event.pointerId) return;
        const target = event.currentTarget;
        if (!(target instanceof HTMLElement)) return;

        const starting = this.joystickPointerId === -1;
        event.preventDefault();
        event.stopPropagation();
        this.joystickPointerId = event.pointerId;
        this.joystickActive = true;
        const rect = target.getBoundingClientRect();
        this.joystickCenter.set(rect.left + rect.width / 2, rect.top + rect.height / 2);
        try {
            target.setPointerCapture(event.pointerId);
        } catch {
            // Some mobile browsers can reject pointer capture during edge gestures.
        }
        this.mobileLookMode = true;
        if (starting) {
            this.joystickBaseYaw = this.mobileLookYaw;
            this.mobileLookYawTarget = this.mobileLookYaw;
            this.mobileDesiredYaw = this.mobileLookYaw;
        }
        this.updateJoystickFromPointer(event);
    }

    public moveJoystick(event: PointerEvent): void {
        if (event.pointerId !== this.joystickPointerId) return;
        event.preventDefault();
        event.stopPropagation();
        this.updateJoystickFromPointer(event);
    }

    public endJoystick(event?: PointerEvent): void {
        if (event && event.pointerId !== this.joystickPointerId) return;
        if (event) {
            event.preventDefault();
            event.stopPropagation();
            const target = event.currentTarget;
            if (target instanceof HTMLElement) {
                try {
                    target.releasePointerCapture(event.pointerId);
                } catch {
                    // Pointer capture may already be released by the browser.
                }
            }
        }
        this.resetJoystick();
    }

    public pressJump(event: PointerEvent): void {
        if (this.isMenuPage || this.paused || this.gameMenuOpen) return;
        event.preventDefault();
        event.stopPropagation();
        this.jumpPointerId = event.pointerId;
        const target = event.currentTarget;
        if (target instanceof HTMLElement) {
            try {
                target.setPointerCapture(event.pointerId);
            } catch {
                // Jump still works without capture; capture just improves multitouch release.
            }
        }
        this.queueJump();
    }

    public releaseJump(event?: PointerEvent): void {
        if (event && event.pointerId !== this.jumpPointerId) return;
        if (event) {
            event.preventDefault();
            event.stopPropagation();
            const target = event.currentTarget;
            if (target instanceof HTMLElement) {
                try {
                    target.releasePointerCapture(event.pointerId);
                } catch {
                    // Capture may already be gone.
                }
            }
        }
        this.jumpPointerId = -1;
    }

    public queueJump(): void {
        if (this.paused || this.gameMenuOpen || this.isMenuPage || this.gameOver || !this.started) return;
        this.jumpQueued = true;
        this.jumpBufferTimer = 0.16;
    }

    public togglePause(): void {
        this.setPaused(!this.paused);
    }

    public resumeGame(): void {
        this.setPaused(false);
    }

    public toggleGameMenu(): void {
        if (this.gameMenuOpen) {
            this.continueGame();
            return;
        }
        this.openGameMenu();
    }

    public openGameMenu(): void {
        if (!this.gameMenuButtonVisible) return;
        this.gameMenuOpen = true;
        this.clearControls();
        if (this.pauseButtonVisible && !this.paused) this.stopBackgroundMusic(0.18);
        this.changeDetector.detectChanges();
    }

    public continueGame(): void {
        const shouldRestartMusic = this.gameMenuOpen && this.pauseButtonVisible && !this.paused;
        this.gameMenuOpen = false;
        if (this.paused) {
            this.setPaused(false);
            return;
        }
        if (shouldRestartMusic) this.startBackgroundMusic();
        this.changeDetector.detectChanges();
    }

    public restartFromGameMenu(): void {
        this.gameMenuOpen = false;
        this.resetGame();
    }

    public homeFromGameMenu(): void {
        this.gameMenuOpen = false;
        this.goHome();
    }

    public stopOrResumeFromGameMenu(): void {
        if (this.paused) {
            this.continueGame();
            return;
        }
        if (!this.pauseButtonVisible) return;
        this.gameMenuOpen = false;
        this.setPaused(true);
    }

    public readInput(event: Event): string {
        return event.target instanceof HTMLInputElement ? event.target.value : '';
    }

    public teleportFromMinimap(event: MouseEvent): void {
        if (!this.isAdmin || !this.started || this.paused || this.gameMenuOpen || this.gameOver || this.isMenuPage) return;
        const target = event.currentTarget;
        if (!(target instanceof HTMLElement)) return;

        const rect = target.getBoundingClientRect();
        const xRatio = THREE.MathUtils.clamp((event.clientX - rect.left) / Math.max(rect.width, 1), 0, 1);
        const zRatio = THREE.MathUtils.clamp((event.clientY - rect.top) / Math.max(rect.height, 1), 0, 1);
        const worldX = this.minimapBounds.minX + xRatio * (this.minimapBounds.maxX - this.minimapBounds.minX);
        const worldZ = this.minimapBounds.minZ + zRatio * (this.minimapBounds.maxZ - this.minimapBounds.minZ);

        if (!this.teleportPlayerToMapPoint(worldX, worldZ)) return;
        event.preventDefault();
        event.stopPropagation();
    }

    public get isMenuPage(): boolean {
        return this.isCustomPage || this.isShopPage || this.isAdminPage;
    }

    public get isAdmin(): boolean {
        return this.account?.role === 'admin';
    }

    private visibleStageCount(role = this.account?.role): number {
        return role === 'admin' ? this.levels.length : this.publicStageTotal;
    }

    private clampLevelIndexForRole(index: number, role = this.account?.role): number {
        return THREE.MathUtils.clamp(index, 0, this.visibleStageCount(role) - 1);
    }

    private enforceVisibleStageAccess(rebuild = true): void {
        this.stageTotal = this.visibleStageCount();
        const clampedIndex = this.clampLevelIndexForRole(this.currentLevelIndex);
        if (clampedIndex === this.currentLevelIndex) return;

        this.currentLevelIndex = clampedIndex;
        if (rebuild && this.world) {
            this.createLevel();
            this.resetGame();
        }
    }

    public get pauseButtonVisible(): boolean {
        return this.started && !this.finished && !this.gameOver && !this.isMenuPage;
    }

    public get gameMenuButtonVisible(): boolean {
        return !this.isMenuPage && (this.started || this.gameOver || this.paused || this.gameMenuOpen);
    }

    public get gameMenuOverlayVisible(): boolean {
        return !this.isMenuPage && (this.gameMenuOpen || this.paused);
    }

    public get gameMenuStopLabel(): string {
        return this.paused ? 'Resume' : 'Stop';
    }

    public get livesLabel(): string {
        return this.isAdmin ? '∞' : String(this.lives);
    }

    public get coinBalanceLabel(): string {
        return this.isAdmin ? '∞' : String(this.walletCoins + this.unbankedRunCoins);
    }

    public get joystickKnobTransform(): string {
        return `translate3d(${this.joystickKnobX}px, ${this.joystickKnobY}px, 0)`;
    }

    public get shopCatalogItems(): ShopItem[] {
        return [this.classicCostume, ...this.shopItems.filter((item) => item.id !== this.classicCostume.id)];
    }

    public get shopTabCatalogItems(): ShopItem[] {
        return this.shopCatalogItems.filter((item) => this.itemMatchesShopTab(item, this.shopTab));
    }

    public get visibleRanking(): RankingEntry[] {
        return this.ranking.slice(0, 50).map((entry) => ({
            ...entry,
            best_stage: Math.min(this.publicStageTotal, entry.best_stage)
        }));
    }

    private get unbankedRunCoins(): number {
        return Math.max(0, this.coinsCollected - this.bankedCoinsThisRun);
    }

    public get selectedShopItem(): ShopItem {
        return this.shopCatalogItems.find((item) => item.id === this.selectedShopItemId) ?? this.classicCostume;
    }

    public get selectedShopStatus(): string {
        return this.shopItemStatus(this.selectedShopItem);
    }

    public get selectedShopActionLabel(): string {
        const item = this.selectedShopItem;
        if (!this.account) return 'Login Required';
        if (item.type === 'costume' || item.type === 'vehicle') {
            if (this.isEquippedShopItem(item)) return 'Equipped';
            if (this.owns(item)) return 'Equip';
            return this.isAdmin ? 'Unlock' : `Buy ${item.price}`;
        }
        if (item.type !== 'consumable' && this.owns(item)) return 'Owned';
        return this.isAdmin ? 'Get Item' : `Buy ${item.price}`;
    }

    public get selectedShopActionDisabled(): boolean {
        const item = this.selectedShopItem;
        if (!this.account) return true;
        if (item.type === 'costume' || item.type === 'vehicle') return this.isEquippedShopItem(item);
        return item.type !== 'consumable' && this.owns(item);
    }

    public get ownedCostumeItems(): ShopItem[] {
        const ownedShopCostumes = this.shopItems.filter((item) => item.type === 'costume' && this.owns(item));
        return [this.classicCostume, ...ownedShopCostumes];
    }

    public get ownedVehicleItems(): ShopItem[] {
        const noVehicleItem: ShopItem = {
            id: this.noVehicleId,
            name: 'On Foot',
            type: 'vehicle',
            price: 0,
            description: 'No ride equipped.'
        };
        const ownedShopVehicles = this.shopItems.filter((item) => item.type === 'vehicle' && this.owns(item));
        if (!this.account) return [noVehicleItem];
        if (this.isAdmin) return ownedShopVehicles.length > 0 ? ownedShopVehicles : [noVehicleItem];
        return [noVehicleItem, ...ownedShopVehicles];
    }

    public get ownedGearItems(): ShopItem[] {
        return this.shopItems.filter((item) => this.itemMatchesShopTab(item, 'gear') && this.hasCustomGearItem(item));
    }

    public get customTabItems(): ShopItem[] {
        if (this.customTab === 'vehicle') return this.ownedVehicleItems;
        if (this.customTab === 'gear') return this.ownedGearItems;
        return this.ownedCostumeItems;
    }

    public get customEmptyMessage(): string {
        if (this.customTab === 'gear') return 'No gear owned';
        if (this.customTab === 'vehicle') return 'No rides owned';
        return 'No coats owned';
    }

    public get equippedCostumeName(): string {
        if (this.isAdmin) return 'Royal Admin Coat';
        const equippedId = this.account?.equipped_costume ?? 'classic';
        const equipped = [this.classicCostume, ...this.shopItems].find((item) => item.id === equippedId);
        return equipped?.name ?? this.classicCostume.name;
    }

    public get equippedVehicleName(): string {
        const equippedId = this.account?.equipped_vehicle ?? this.noVehicleId;
        if (this.isAdmin && equippedId === this.noVehicleId) return 'Rudolph';
        if (equippedId === this.noVehicleId) return 'On Foot';
        const equipped = this.shopItems.find((item) => item.id === equippedId && item.type === 'vehicle');
        return equipped?.name ?? 'On Foot';
    }

    public openCustomPage(): void {
        this.setCustomPage(true, true);
    }

    public closeCustomPage(): void {
        this.setCustomPage(false, true);
    }

    public async openShopPage(): Promise<void> {
        await this.flushCoinDeposits();
        this.setShopPage(true, true);
    }

    public closeShopPage(): void {
        this.setShopPage(false, true);
    }

    public openAdminPage(): void {
        this.setAdminPage(true, true);
    }

    public closeAdminPage(): void {
        this.setAdminPage(false, true);
    }

    public isEquippedCostume(item: ShopItem): boolean {
        return (this.account?.equipped_costume ?? 'classic') === item.id;
    }

    public isEquippedVehicle(item: ShopItem): boolean {
        const equippedId = this.account?.equipped_vehicle ?? this.noVehicleId;
        if (this.isAdmin && equippedId === this.noVehicleId) return item.id === 'rudolph_vehicle';
        return equippedId === item.id;
    }

    public isEquippedShopItem(item: ShopItem): boolean {
        if (item.type === 'costume') return this.isEquippedCostume(item);
        if (item.type === 'vehicle') return this.isEquippedVehicle(item);
        return false;
    }

    public selectCustomTab(tab: ShopTab): void {
        this.customTab = tab;
        this.customMessage = '';
        this.changeDetector.detectChanges();
    }

    public selectShopTab(tab: ShopTab): void {
        this.shopTab = tab;
        if (!this.itemMatchesShopTab(this.selectedShopItem, tab)) {
            const currentVehicleId = this.account?.equipped_vehicle ?? this.noVehicleId;
            const preferred = tab === 'costume'
                ? this.shopCatalogItems.find((item) => item.id === (this.account?.equipped_costume ?? 'classic'))
                : tab === 'vehicle'
                    ? this.shopCatalogItems.find((item) => item.id === currentVehicleId && item.type === 'vehicle')
                    : undefined;
            this.selectedShopItemId = (preferred ?? this.shopTabCatalogItems[0] ?? this.classicCostume).id;
        }
        this.applyShopPreview();
        this.changeDetector.detectChanges();
    }

    public costumeSwatch(item: ShopItem): string {
        const palette: Record<string, string> = {
            classic: '#d21f2b',
            blue_costume: '#2d69e8',
            gold_costume: '#f0b72d',
            alex_costume: '#5aa574',
            steve_costume: '#12b4bc',
            mario_costume: '#f01515',
            among_crewmate_costume: '#d0101c',
            sonic_costume: '#244cf3',
            frog_costume: '#65bd37'
        };
        return palette[item.id] ?? '#d21f2b';
    }

    public async selectCustomItem(item: ShopItem): Promise<void> {
        if (item.type === 'costume') {
            await this.selectCustomCostume(item);
            return;
        }
        if (item.type === 'vehicle') {
            await this.selectCustomVehicle(item);
            return;
        }
        this.selectCustomGear(item);
    }

    public async selectCustomCostume(item: ShopItem): Promise<void> {
        if (!this.owns(item)) return;
        if (!this.account) {
            this.applyCostume(item.id);
            this.customMessage = `${item.name} equipped`;
            this.changeDetector.detectChanges();
            return;
        }

        await this.equipCostume(item);
        this.customMessage = this.isEquippedCostume(item) ? `${item.name} equipped` : this.customMessage;
        this.changeDetector.detectChanges();
    }

    public async selectCustomVehicle(item: ShopItem): Promise<void> {
        if (item.id !== this.noVehicleId && !this.owns(item)) return;
        if (!this.account) {
            this.customMessage = 'Login to equip rides';
            this.changeDetector.detectChanges();
            return;
        }

        await this.equipVehicle(item);
        this.customMessage = this.isEquippedVehicle(item) ? `${item.name} equipped` : this.customMessage;
        this.changeDetector.detectChanges();
    }

    public selectCustomGear(item: ShopItem): void {
        const count = this.itemCount(item.id);
        this.customMessage = item.type === 'consumable'
            ? `${item.name} x${count}`
            : `${item.name} ready`;
        this.changeDetector.detectChanges();
    }

    public async submitAuth(): Promise<void> {
        if (this.authLoading) return;
        this.authLoading = true;
        this.authMessage = '';
        this.changeDetector.detectChanges();

        const path = this.authMode === 'signup' ? 'signup' : 'login';
        const result = await this.snowApi(path, {
            email: this.authEmail,
            password: this.authPassword,
            name: this.authName
        });
        this.authLoading = false;

        if (!result.ok) {
            this.authMessage = result.message ?? '계정 처리에 실패했습니다.';
            this.changeDetector.detectChanges();
            return;
        }

        this.applyAccountPayload(result.data);
        this.authPassword = '';
        this.authMessage = result.offline ? 'Local save mode' : 'Saved with account';
        this.changeDetector.detectChanges();
    }

    public async logout(): Promise<void> {
        await this.flushCoinDeposits();
        await this.snowApi('logout');
        this.account = null;
        this.walletCoins = 0;
        this.saveMessage = 'Guest progress is temporary.';
        this.authMessage = 'Logged out';
        this.applyCostume('classic');
        this.applyVehicle(this.noVehicleId);
        this.enforceVisibleStageAccess();
        this.changeDetector.detectChanges();
    }

    public async purchase(item: ShopItem): Promise<boolean> {
        if (!this.account) {
            this.authMessage = '로그인하면 코인으로 구매할 수 있어요.';
            this.changeDetector.detectChanges();
            return false;
        }
        if (this.isAdmin) {
            this.grantAdminPurchase(item);
            this.authMessage = item.type === 'consumable' ? `${item.name} added` : `${item.name} unlocked`;
            this.changeDetector.detectChanges();
            return true;
        }

        const pendingCoins = await this.takePendingCoinsForPurchase(item);
        const result = await this.snowApi('purchase', { item_id: item.id, pending_coins: pendingCoins });
        if (!result.ok) {
            this.restorePendingCoinsAfterPurchase(pendingCoins);
            this.authMessage = result.message ?? '구매에 실패했습니다.';
            this.changeDetector.detectChanges();
            return false;
        }
        if (pendingCoins > 0) this.bankedCoinsThisRun += pendingCoins;
        this.applyAccountPayload(result.data);
        this.authMessage = `${item.name} purchased`;
        this.changeDetector.detectChanges();
        return true;
    }

    public async activateShopItem(item: ShopItem): Promise<void> {
        this.selectShopItem(item);
        await this.confirmSelectedShopItem();
    }

    public selectShopItem(item: ShopItem): void {
        this.selectedShopItemId = item.id;
        this.applyShopPreview();
        this.changeDetector.detectChanges();
    }

    public async confirmSelectedShopItem(): Promise<void> {
        const item = this.selectedShopItem;
        if (!this.account) {
            this.authMessage = '로그인하면 코인으로 구매할 수 있어요.';
            this.changeDetector.detectChanges();
            return;
        }
        if (item.type === 'costume' && this.owns(item)) {
            await this.equipCostume(item);
            if (this.isEquippedCostume(item)) this.authMessage = `${item.name} equipped`;
            this.applyShopPreview();
            this.changeDetector.detectChanges();
            return;
        }
        if (item.type === 'vehicle' && this.owns(item)) {
            await this.equipVehicle(item);
            if (this.isEquippedVehicle(item)) this.authMessage = `${item.name} equipped`;
            this.applyShopPreview();
            this.changeDetector.detectChanges();
            return;
        }

        if (item.type !== 'consumable' && this.owns(item)) {
            this.authMessage = `${item.name} owned`;
            this.changeDetector.detectChanges();
            return;
        }

        const purchased = await this.purchase(item);
        if (item.type === 'costume' && purchased) {
            await this.equipCostume(item);
            if (this.isEquippedCostume(item)) this.authMessage = `${item.name} equipped`;
            this.applyShopPreview();
            this.changeDetector.detectChanges();
        }
        if (item.type === 'vehicle' && purchased) {
            await this.equipVehicle(item);
            if (this.isEquippedVehicle(item)) this.authMessage = `${item.name} equipped`;
            this.applyShopPreview();
            this.changeDetector.detectChanges();
        }
    }

    public async equipCostume(item: ShopItem | string): Promise<void> {
        const costumeId = typeof item === 'string' ? item : item.id;
        if (!this.account) return;
        const result = await this.snowApi('equip', { costume_id: costumeId });
        if (!result.ok) {
            this.authMessage = result.message ?? '코스튬 변경에 실패했습니다.';
            this.changeDetector.detectChanges();
            return;
        }
        this.applyAccountPayload(result.data);
        this.changeDetector.detectChanges();
    }

    public async equipVehicle(item: ShopItem | string): Promise<void> {
        const vehicleId = typeof item === 'string' ? item : item.id;
        if (!this.account) return;
        const result = await this.snowApi('equip', { item_id: vehicleId, item_type: 'vehicle' });
        if (!result.ok) {
            this.authMessage = result.message ?? '탈것 변경에 실패했습니다.';
            this.changeDetector.detectChanges();
            return;
        }
        this.applyAccountPayload(result.data);
        this.changeDetector.detectChanges();
    }

    public async loadAdminUsers(): Promise<void> {
        if (!this.isAdmin) {
            this.adminUsers = [];
            this.adminMessage = this.account ? '관리자 권한이 필요합니다.' : '로그인이 필요합니다.';
            this.changeDetector.detectChanges();
            return;
        }

        this.adminLoading = true;
        this.adminMessage = '';
        this.changeDetector.detectChanges();

        const result = await this.snowApi('admin/users', { limit: 200 });
        this.adminLoading = false;
        if (!result.ok) {
            this.adminMessage = result.message ?? '사용자 목록을 불러오지 못했습니다.';
            this.changeDetector.detectChanges();
            return;
        }

        this.applyAdminPayload(result.data);
        this.adminMessage = result.offline ? 'Local admin mode' : 'Users loaded';
        this.changeDetector.detectChanges();
    }

    public async adminChangeUserLevel(user: AdminUser, delta: number): Promise<void> {
        const nextLevel = THREE.MathUtils.clamp(Number(user.current_level ?? 1) + delta, 1, this.publicStageTotal);
        await this.adminUpdateUser(user, { current_level: nextLevel });
    }

    public async adminAdjustUserCoins(user: AdminUser, delta: number): Promise<void> {
        const nextCoins = Math.max(0, Number(user.coins ?? 0) + delta);
        await this.adminUpdateUser(user, { coins: nextCoins });
    }

    public async adminResetUser(user: AdminUser): Promise<void> {
        if (!this.isAdmin) return;
        this.adminLoading = true;
        this.adminMessage = '';
        this.changeDetector.detectChanges();

        const result = await this.snowApi('admin/reset-user', { user_id: user.id, limit: 200 });
        this.adminLoading = false;
        if (!result.ok) {
            this.adminMessage = result.message ?? '사용자 초기화에 실패했습니다.';
            this.changeDetector.detectChanges();
            return;
        }

        this.applyAdminPayload(result.data);
        this.adminMessage = `${user.name} reset`;
        this.changeDetector.detectChanges();
    }

    public startAdminStage(stageNumber: number): void {
        if (!this.isAdmin) {
            this.adminMessage = '관리자 권한이 필요합니다.';
            this.changeDetector.detectChanges();
            return;
        }

        const levelIndex = THREE.MathUtils.clamp(stageNumber - 1, 0, this.levels.length - 1);
        this.setAdminPage(false, true);
        this.currentLevelIndex = levelIndex;
        this.createLevel();
        this.resetGame();
        this.startGame();
        this.adminTestMode = true;
        this.banner = `Admin Test ${this.stage}`;
        this.bannerDetail = this.activeLevel?.title ?? '';
        this.saveMessage = 'Admin test mode';
        this.changeDetector.detectChanges();
    }

    public owns(item: ShopItem | string): boolean {
        const id = typeof item === 'string' ? item : item.id;
        if (id === 'classic') return true;
        const type = typeof item === 'string' ? this.shopItems.find((candidate) => candidate.id === id)?.type : item.type;
        if (this.isAdmin && (type === 'costume' || type === 'vehicle')) return true;
        return !!this.account?.inventory?.[id];
    }

    public itemCount(itemId: string): number {
        return this.account?.items?.[itemId] ?? 0;
    }

    public shopItemLabel(item: ShopItem): string {
        if (item.type === 'consumable') {
            const count = this.itemCount(item.id);
            if (this.isAdmin) return count > 0 ? `${count} owned / ∞ coin` : '∞ coin';
            return count > 0 ? `${count} owned / ${item.price} coin` : `${item.price} coin`;
        }
        if (this.owns(item)) {
            if (item.type === 'costume') return this.isEquippedCostume(item) ? 'Equipped' : 'Equip';
            if (item.type === 'vehicle') return this.isEquippedVehicle(item) ? 'Equipped' : 'Equip';
            return 'Owned';
        }
        if (this.isAdmin) return '∞ coin';
        return `${item.price} coin`;
    }

    public shopItemStatus(item: ShopItem): string {
        if (this.isEquippedShopItem(item)) return 'Equipped';
        if (item.type === 'consumable') {
            const count = this.itemCount(item.id);
            if (count > 0) return `${count} owned`;
        } else if (this.owns(item)) {
            return 'Owned';
        }
        if (!this.account) return 'Login';
        if (this.isAdmin) return '∞ coin';
        return item.price === 0 ? 'Free' : `${item.price} coin`;
    }

    public shopItemTypeLabel(item: ShopItem): string {
        if (item.type === 'costume') return 'Coat';
        if (item.type === 'vehicle') return 'Ride';
        if (item.type === 'passive') return 'Gear';
        return 'Use';
    }

    public shopItemArtClass(item: ShopItem): string {
        return `art-${item.id.replace(/_/g, '-')}`;
    }

    public customItemStatus(item: ShopItem): string {
        if (item.type === 'costume') return this.isEquippedCostume(item) ? 'EQUIPPED' : 'OWNED';
        if (item.type === 'vehicle') {
            if (this.isEquippedVehicle(item)) return 'EQUIPPED';
            return item.id === this.noVehicleId ? 'AVAILABLE' : 'OWNED';
        }
        if (item.type === 'consumable') return `${this.itemCount(item.id)} OWNED`;
        return 'ACTIVE';
    }

    public isEquippedCustomItem(item: ShopItem): boolean {
        if (item.type === 'costume') return this.isEquippedCostume(item);
        if (item.type === 'vehicle') return this.isEquippedVehicle(item);
        return false;
    }

    private itemMatchesShopTab(item: ShopItem, tab: ShopTab): boolean {
        if (tab === 'costume') return item.type === 'costume';
        if (tab === 'vehicle') return item.type === 'vehicle';
        return item.type === 'passive' || item.type === 'consumable';
    }

    private hasCustomGearItem(item: ShopItem): boolean {
        if (this.isAdmin) return true;
        if (item.type === 'consumable') return this.itemCount(item.id) > 0;
        return this.owns(item);
    }

    public startGame(): void {
        if (this.isCustomPage) this.setCustomPage(false, true);
        if (this.isShopPage) this.setShopPage(false, true);
        if (this.isAdminPage) this.setAdminPage(false, true);
        this.adminTestMode = false;
        this.enforceVisibleStageAccess();
        this.ensureAudioContext();
        this.paused = false;
        this.gameMenuOpen = false;
        this.startBackgroundMusic();
        this.started = true;
        this.banner = '';
        this.bannerDetail = '';
        this.bannerTone = 'normal';
        this.damageBannerTimer = 0;
        this.damageFlashTimer = 0;
        this.damageFlashActive = false;
        this.gameOver = false;
        this.clearControls();
        this.changeDetector.detectChanges();
    }

    public resetGame(): void {
        const level = this.activeLevel ?? this.buildExpandedLevel(this.levels[this.currentLevelIndex], this.currentLevelIndex);
        this.stopBackgroundMusic();
        this.started = false;
        this.paused = false;
        this.gameMenuOpen = false;
        this.timeLeft = level.time;
        this.lives = this.maxLives;
        this.finished = false;
        this.activeCheckpointIndex = -1;
        this.respawnPoint.copy(this.spawn);
        this.giftsCollected = 0;
        this.banner = `Stage ${this.stage}`;
        this.bannerDetail = level.title;
        this.bannerTone = 'normal';
        this.damageBannerTimer = 0;
        this.damageFlashTimer = 0;
        this.damageFlashActive = false;
        this.gameOver = false;
        this.gameOverReason = 'ALL LIVES LOST';
        this.introMode = 'title';
        this.lastClearedStage = 0;
        this.mouseLook.set(0, 0);
        this.mouseLookTarget.set(0, 0);
        this.mobileLookYaw = 0;
        this.mobileLookYawTarget = 0;
        this.mobileDesiredYaw = 0;
        this.joystickBaseYaw = 0;
        this.mobileLookMode = false;
        this.slideDrift.set(0, 0);
        this.airJumpsUsed = 0;
        this.dimensionPortalCooldown = 0;
        this.meteors.forEach((meteor) => this.world.remove(meteor.group));
        this.meteors = [];
        this.meteorTimer = 0.8;
        this.platforms.forEach((platform) => {
            platform.x = platform.startX;
            platform.y = platform.startY;
            platform.z = platform.startZ;
            platform.mesh.position.set(platform.x, platform.y, platform.z);
            platform.broken = false;
            platform.fragileTimer = 0;
            platform.respawnTimer = 0;
            platform.mesh.visible = true;
            if (platform.fireGroup) platform.fireGroup.visible = false;
            if (platform.warningGroup) {
                platform.warningGroup.visible = false;
                platform.warningGroup.children.forEach((child) => {
                    const material = (child as THREE.Mesh).material;
                    if (material instanceof THREE.MeshBasicMaterial) material.opacity = 0.14;
                });
            }
        });
        this.gifts.forEach((gift) => {
            gift.collected = false;
            gift.x = gift.baseX;
            gift.y = gift.baseY;
            gift.z = gift.baseZ;
            gift.mesh.position.set(gift.x, gift.y, gift.z);
            gift.mesh.visible = true;
        });
        if (this.unbankedRunCoins > 0) void this.flushCoinDeposits();
        this.coinsCollected = 0;
        this.bankedCoinsThisRun = 0;
        this.coins.forEach((coin) => {
            coin.collected = false;
            coin.x = coin.baseX;
            coin.y = coin.baseY;
            coin.z = coin.baseZ;
            coin.mesh.position.set(coin.x, coin.y, coin.z);
            coin.mesh.visible = true;
        });
        this.hearts.forEach((heart) => {
            heart.collected = false;
            heart.mesh.visible = true;
        });
        this.checkpoints.forEach((checkpoint) => this.resetCheckpoint(checkpoint));
        this.clearControls();
        this.setPlayerSpawn();
        this.applyEquippedLook();
        this.updateHud();
        this.changeDetector.detectChanges();
    }

    public goHome(): void {
        this.gameMenuOpen = false;
        if (this.isCustomPage) this.setCustomPage(false, false);
        if (this.isShopPage) this.setShopPage(false, false);
        if (this.isAdminPage) this.setAdminPage(false, false);
        this.adminTestMode = false;
        this.resetGame();
        this.introMode = 'title';
        this.pushViewPath('/');
        this.changeDetector.detectChanges();
    }

    private syncPageFromLocation(): void {
        if (typeof window === 'undefined') return;
        const path = window.location.pathname.replace(/\/+$/, '') || '/';
        if (path === '/custom') {
            this.setCustomPage(true, false);
            return;
        }
        if (path === '/shop') {
            this.setShopPage(true, false);
            return;
        }
        if (path === '/admin') {
            this.setAdminPage(true, false);
            return;
        }
        this.setCustomPage(false, false);
        this.setShopPage(false, false);
        this.setAdminPage(false, false);
    }

    private setCustomPage(active: boolean, updateHistory: boolean): void {
        this.isCustomPage = active;
        if (active) {
            this.isShopPage = false;
            this.isAdminPage = false;
            this.started = false;
            this.paused = false;
            this.gameMenuOpen = false;
            this.gameOver = false;
            this.introMode = 'title';
            this.banner = '';
            this.bannerDetail = '';
            this.bannerTone = 'normal';
            this.clearControls();
            this.customMessage = this.account ? '' : 'Guest closet';
            this.applyCostume(this.account?.equipped_costume ?? 'classic');
            this.applyVehicle(this.account?.equipped_vehicle ?? this.noVehicleId);
        } else {
            this.customMessage = '';
            this.player?.scale.setScalar(1);
            if (this.player) this.setPlayerSpawn();
        }

        this.syncSceneVisibility();
        if (updateHistory) this.pushViewPath(active ? '/custom' : '/');
        if (active && this.player) this.updateCustomCamera();
        this.changeDetector.detectChanges();
    }

    private setShopPage(active: boolean, updateHistory: boolean): void {
        this.isShopPage = active;
        if (active) {
            void this.flushCoinDeposits();
            this.isCustomPage = false;
            this.isAdminPage = false;
            this.shopTab = 'costume';
            this.selectedShopItemId = this.account?.equipped_costume ?? 'classic';
            this.started = false;
            this.paused = false;
            this.gameMenuOpen = false;
            this.gameOver = false;
            this.introMode = 'title';
            this.banner = '';
            this.bannerDetail = '';
            this.bannerTone = 'normal';
            this.clearControls();
            this.applyShopPreview();
        } else {
            this.applyCostume(this.account?.equipped_costume ?? 'classic');
            this.applyVehicle(this.account?.equipped_vehicle ?? this.noVehicleId);
            this.player?.scale.setScalar(1);
            if (this.player) this.setPlayerSpawn();
        }

        this.syncSceneVisibility();
        if (updateHistory) this.pushViewPath(active ? '/shop' : '/');
        if (active && this.player) this.updateShopCamera();
        this.changeDetector.detectChanges();
    }

    private setAdminPage(active: boolean, updateHistory: boolean): void {
        this.isAdminPage = active;
        if (active) {
            this.isCustomPage = false;
            this.isShopPage = false;
            this.started = false;
            this.paused = false;
            this.gameMenuOpen = false;
            this.gameOver = false;
            this.introMode = 'title';
            this.banner = '';
            this.bannerDetail = '';
            this.bannerTone = 'normal';
            this.clearControls();
            this.adminMessage = this.isAdmin ? '' : '관리자 권한이 필요합니다.';
            if (this.isAdmin) void this.loadAdminUsers();
        } else if (!this.isAdmin) {
            this.adminUsers = [];
        }

        this.syncSceneVisibility();
        if (updateHistory) this.pushViewPath(active ? '/admin' : '/');
        this.changeDetector.detectChanges();
    }

    private syncSceneVisibility(): void {
        const previewPage = this.isCustomPage || this.isShopPage;
        if (this.world) this.world.visible = !this.isMenuPage;
        if (this.scene) {
            this.scene.background = previewPage ? null : this.currentSceneBackground();
            this.scene.fog = previewPage ? null : this.currentSceneFog();
        }
        if (this.snow) this.snow.visible = !previewPage;
        if (this.stars) this.stars.visible = !previewPage;
        if (this.player) {
            this.player.visible = previewPage || !this.isAdminPage;
            this.player.scale.setScalar(this.isCustomPage ? this.customPreviewScale() : this.isShopPage ? this.shopPreviewScale() : 1);
        }
    }

    private currentSceneBackground(): THREE.Color {
        return this.currentLevelIndex === 16 ? this.mysticSceneBackground : this.sceneBackground;
    }

    private currentSceneFog(): THREE.Fog {
        return this.currentLevelIndex === 16 ? this.mysticSceneFog : this.sceneFog;
    }

    private applyStageAtmosphere(): void {
        if (!this.scene) return;
        const previewPage = this.isCustomPage || this.isShopPage;
        this.scene.background = previewPage ? null : this.currentSceneBackground();
        this.scene.fog = previewPage ? null : this.currentSceneFog();
    }

    private pushViewPath(path: '/' | '/custom' | '/shop' | '/admin'): void {
        if (typeof window === 'undefined') return;
        const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
        if (currentPath === path) return;
        const query = window.location.search || '';
        window.history.pushState({ snowView: path }, '', `${path}${query}`);
    }

    private async loadAccount(): Promise<void> {
        const result = await this.snowApi('me');
        if (result.ok) {
            this.applyAccountPayload(result.data);
        }
    }

    private async snowApi(path: string, data: Record<string, unknown> = {}): Promise<SnowApiResult> {
        const body = new URLSearchParams();
        Object.entries(data).forEach(([key, value]) => body.set(key, String(value ?? '')));

        try {
            const response = await fetch(`/api/snow/${path}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
                body
            });
            const text = await response.text();
            if (!text || text.trim().startsWith('<')) {
                return this.localSnowApi(path, data);
            }
            const parsed = JSON.parse(text);
            const code = Number(parsed.code ?? response.status);
            const payload = (parsed.data ?? parsed) as Record<string, unknown>;
            if (response.status === 404 && !payload.user && !payload.ranking) {
                return this.localSnowApi(path, data);
            }
            return {
                ok: code >= 200 && code < 300,
                data: payload,
                message: String(payload.message ?? parsed.message ?? ''),
                offline: false
            };
        } catch {
            return this.localSnowApi(path, data);
        }
    }

    private localSnowApi(path: string, data: Record<string, unknown> = {}): SnowApiResult {
        const users = this.readLocalUsers();
        const currentEmail = window.localStorage.getItem(this.localSessionKey) ?? '';
        const currentUser = currentEmail ? users[currentEmail] : undefined;
        const ranking = this.buildLocalRanking(users);
        if (currentUser) {
            currentUser.inventory ??= { classic: true };
            currentUser.items ??= {};
            currentUser.equipped_costume ??= 'classic';
            currentUser.equipped_vehicle ??= this.noVehicleId;
            if (this.refundRemovedLocalShopItems(currentUser)) {
                users[currentUser.email] = currentUser;
                this.writeLocalUsers(users);
            }
        }

        if (path === 'me') {
            return { ok: true, data: { user: currentUser ? this.publicLocalUser(currentUser) : null, ranking, shop: this.shopItems }, offline: true };
        }

        if (path === 'logout') {
            window.localStorage.removeItem(this.localSessionKey);
            return { ok: true, data: { ranking, shop: this.shopItems }, offline: true };
        }

        if (path === 'signup') {
            const email = String(data.email ?? '').trim().toLowerCase();
            const password = String(data.password ?? '');
            const name = String(data.name ?? '').trim();
            if (!email.includes('@') || !email.includes('.')) return { ok: false, data: {}, message: '이메일 형식이 올바르지 않습니다.', offline: true };
            if (password.length < 4) return { ok: false, data: {}, message: '비밀번호는 4자 이상 입력해주세요.', offline: true };
            if (name.length < 2) return { ok: false, data: {}, message: '닉네임은 2자 이상 입력해주세요.', offline: true };
            if (users[email]) return { ok: false, data: {}, message: '이미 가입된 이메일입니다.', offline: true };
            users[email] = {
                id: `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
                email,
                name,
                password,
                role: 'user',
                coins: 0,
                current_level: 1,
                best_stage: 1,
                best_score: 0,
                total_coins: 0,
                total_gifts: 0,
                inventory: { classic: true },
                items: {},
                equipped_costume: 'classic',
                equipped_vehicle: this.noVehicleId
            } as PlayerProfile & { password: string };
            this.writeLocalUsers(users);
            window.localStorage.setItem(this.localSessionKey, email);
            return { ok: true, data: { user: this.publicLocalUser(users[email]), ranking: this.buildLocalRanking(users), shop: this.shopItems }, offline: true };
        }

        if (path === 'login') {
            const email = String(data.email ?? '').trim().toLowerCase();
            const password = String(data.password ?? '');
            const user = users[email] as (PlayerProfile & { password?: string }) | undefined;
            if (!user || user.password !== password) return { ok: false, data: {}, message: '이메일 또는 비밀번호가 올바르지 않습니다.', offline: true };
            window.localStorage.setItem(this.localSessionKey, email);
            return { ok: true, data: { user: this.publicLocalUser(user), ranking, shop: this.shopItems }, offline: true };
        }

        if (path.startsWith('admin/')) {
            if (!currentUser || currentUser.role !== 'admin') return { ok: false, data: {}, message: '관리자 권한이 필요합니다.', offline: true };
            if (path === 'admin/users') {
                return {
                    ok: true,
                    data: { users: Object.values(users).filter((user) => user.role !== 'admin').map((user) => this.publicLocalUser(user)) },
                    offline: true
                };
            }
            const userId = String(data.user_id ?? '');
            const target = Object.values(users).find((user) => user.id === userId);
            if (!target || target.role === 'admin') return { ok: false, data: {}, message: '사용자를 찾을 수 없습니다.', offline: true };
            if (path === 'admin/update-user') {
                if (data.current_level !== undefined && data.current_level !== '') {
                    target.current_level = THREE.MathUtils.clamp(Number(data.current_level), 1, this.publicStageTotal);
                    target.best_stage = Math.max(target.best_stage, target.current_level);
                }
                if (data.coins !== undefined && data.coins !== '') target.coins = Math.max(0, Number(data.coins));
                users[target.email] = target;
                this.writeLocalUsers(users);
                return { ok: true, data: { users: Object.values(users).filter((user) => user.role !== 'admin').map((user) => this.publicLocalUser(user)) }, offline: true };
            }
            if (path === 'admin/reset-user') {
                target.coins = 0;
                target.current_level = 1;
                target.best_stage = 1;
                target.best_score = 0;
                target.total_coins = 0;
                target.total_gifts = 0;
                target.inventory = { classic: true };
                target.items = {};
                target.equipped_costume = 'classic';
                target.equipped_vehicle = this.noVehicleId;
                users[target.email] = target;
                this.writeLocalUsers(users);
                return { ok: true, data: { users: Object.values(users).filter((user) => user.role !== 'admin').map((user) => this.publicLocalUser(user)) }, offline: true };
            }
        }

        if (!currentUser) return { ok: false, data: {}, message: '로그인이 필요합니다.', offline: true };

        if (path === 'save') {
            const stageCap = this.visibleStageCount(currentUser.role);
            const stage = THREE.MathUtils.clamp(Number(data.stage ?? 1), 1, stageCap);
            const coins = Math.max(0, Number(data.coins ?? 0));
            const bankedCoins = THREE.MathUtils.clamp(Number(data.banked_coins ?? 0), 0, coins);
            const coinsToBank = Math.max(0, coins - bankedCoins);
            const gifts = Math.max(0, Number(data.gifts ?? 0));
            const timeLeft = Math.max(0, Number(data.time_left ?? 0));
            const cleared = String(data.cleared ?? 'false') === 'true';
            const score = this.calculateRunScore(stage, gifts, coins, timeLeft, cleared);
            currentUser.coins += coinsToBank;
            currentUser.total_coins += coinsToBank;
            currentUser.total_gifts += gifts;
            currentUser.best_score = Math.max(currentUser.best_score, score);
            currentUser.current_level = Math.min(stageCap, Math.max(currentUser.current_level, cleared ? Math.min(stageCap, stage + 1) : stage));
            currentUser.best_stage = Math.min(stageCap, Math.max(currentUser.best_stage, cleared ? stage : stage - 1, currentUser.current_level));
            users[currentUser.email] = currentUser;
            this.writeLocalUsers(users);
            return { ok: true, data: { user: this.publicLocalUser(currentUser), ranking: this.buildLocalRanking(users), shop: this.shopItems }, offline: true };
        }

        if (path === 'coins') {
            const coins = Math.max(0, Number(data.coins ?? 0));
            currentUser.coins += coins;
            currentUser.total_coins += coins;
            users[currentUser.email] = currentUser;
            this.writeLocalUsers(users);
            return { ok: true, data: { user: this.publicLocalUser(currentUser), ranking: this.buildLocalRanking(users), shop: this.shopItems }, offline: true };
        }

        if (path === 'purchase') {
            const itemId = String(data.item_id ?? '');
            const item = this.shopItems.find((candidate) => candidate.id === itemId);
            if (!item) return { ok: false, data: {}, message: '존재하지 않는 아이템입니다.', offline: true };
            if (item.type !== 'consumable' && currentUser.inventory[item.id]) return { ok: false, data: {}, message: '이미 보유한 아이템입니다.', offline: true };
            const pendingCoins = Math.max(0, Number(data.pending_coins ?? 0));
            const coins = currentUser.coins + pendingCoins;
            if (coins < item.price) return { ok: false, data: {}, message: '코인이 부족합니다.', offline: true };
            currentUser.coins = coins - item.price;
            currentUser.total_coins += pendingCoins;
            if (item.type === 'consumable') currentUser.items[item.id] = (currentUser.items[item.id] ?? 0) + 1;
            else currentUser.inventory[item.id] = true;
            users[currentUser.email] = currentUser;
            this.writeLocalUsers(users);
            return { ok: true, data: { user: this.publicLocalUser(currentUser), ranking: this.buildLocalRanking(users), shop: this.shopItems }, offline: true };
        }

        if (path === 'equip') {
            const itemType = String(data.item_type ?? 'costume');
            const itemId = String(data.item_id ?? data.costume_id ?? 'classic');
            if (itemType === 'vehicle') {
                if (itemId === this.noVehicleId) {
                    currentUser.equipped_vehicle = this.noVehicleId;
                } else {
                    const item = this.shopItems.find((candidate) => candidate.id === itemId && candidate.type === 'vehicle');
                    if (!item) return { ok: false, data: {}, message: '존재하지 않는 탈것입니다.', offline: true };
                    if (currentUser.role !== 'admin' && !currentUser.inventory[itemId]) return { ok: false, data: {}, message: '보유하지 않은 탈것입니다.', offline: true };
                    currentUser.equipped_vehicle = itemId;
                }
            } else {
                if (itemId !== 'classic' && currentUser.role !== 'admin' && !currentUser.inventory[itemId]) return { ok: false, data: {}, message: '보유하지 않은 코스튬입니다.', offline: true };
                currentUser.equipped_costume = itemId;
            }
            users[currentUser.email] = currentUser;
            this.writeLocalUsers(users);
            return { ok: true, data: { user: this.publicLocalUser(currentUser), ranking, shop: this.shopItems }, offline: true };
        }

        return { ok: false, data: {}, message: '지원하지 않는 요청입니다.', offline: true };
    }

    private readLocalUsers(): Record<string, PlayerProfile & { password?: string }> {
        try {
            return JSON.parse(window.localStorage.getItem(this.localUsersKey) ?? '{}');
        } catch {
            return {};
        }
    }

    private writeLocalUsers(users: Record<string, PlayerProfile & { password?: string }>): void {
        window.localStorage.setItem(this.localUsersKey, JSON.stringify(users));
    }

    private applyShopEntitlements(user: PlayerProfile): void {
        user.inventory = { classic: true, ...(user.inventory ?? {}) };
        user.items = { ...(user.items ?? {}) };
        if (user.role !== 'admin') return;

        this.shopItems.forEach((item) => {
            if (item.type === 'consumable') {
                const count = Number(user.items[item.id] ?? 0);
                user.items[item.id] = Number.isFinite(count) ? Math.max(1, count) : 1;
            } else {
                user.inventory[item.id] = true;
            }
        });
    }

    private publicLocalUser(user: PlayerProfile & { password?: string }): PlayerProfile {
        const clone = { ...user } as PlayerProfile & { password?: string };
        delete clone.password;
        clone.role ??= 'user';
        this.applyShopEntitlements(clone);
        clone.equipped_costume ??= 'classic';
        clone.equipped_vehicle ??= this.noVehicleId;
        const stageCap = this.visibleStageCount(clone.role);
        clone.current_level = THREE.MathUtils.clamp(Number(clone.current_level ?? 1), 1, stageCap);
        clone.best_stage = THREE.MathUtils.clamp(Number(clone.best_stage ?? 1), 1, stageCap);
        return clone;
    }

    private refundRemovedLocalShopItems(user: PlayerProfile & { password?: string }): boolean {
        let refund = 0;
        const inventory = user.inventory ?? {};
        Object.entries(this.removedShopRefunds).forEach(([itemId, price]) => {
            if (!inventory[itemId]) return;
            delete inventory[itemId];
            refund += price;
        });
        if (refund <= 0) return false;

        user.inventory = inventory;
        user.coins = Math.max(0, Number(user.coins ?? 0)) + refund;
        return true;
    }

    private buildLocalRanking(users: Record<string, PlayerProfile & { password?: string }>): RankingEntry[] {
        return Object.values(users)
            .filter((user) => user.role !== 'admin')
            .sort((a, b) => (
                (b.best_score - a.best_score) ||
                (b.best_stage - a.best_stage) ||
                (b.total_coins - a.total_coins)
            ))
            .slice(0, 50)
            .map((user, index) => ({
                rank: index + 1,
                id: user.id,
                name: user.name,
                best_stage: Math.min(this.publicStageTotal, user.best_stage),
                best_score: user.best_score,
                total_coins: user.total_coins
            }));
    }

    private calculateRunScore(stage: number, gifts: number, coins: number, timeLeft: number, cleared: boolean): number {
        const timeMultiplier = cleared ? this.scoreClearTimeMultiplier : this.scoreAttemptTimeMultiplier;
        return Math.floor(
            stage * this.scoreStageMultiplier
            + gifts * this.scoreGiftMultiplier
            + coins * this.scoreCoinMultiplier
            + timeLeft * timeMultiplier
            + (cleared ? this.scoreClearBonus : 0)
        );
    }

    private applyAccountPayload(data: Record<string, unknown>): void {
        const user = data.user as PlayerProfile | null | undefined;
        const ranking = data.ranking as RankingEntry[] | undefined;
        const shop = data.shop as ShopItem[] | undefined;
        if (Array.isArray(ranking)) this.ranking = ranking;
        if (Array.isArray(shop) && shop.length > 0) this.shopItems = shop;
        if (!user) {
            this.account = null;
            this.walletCoins = 0;
            this.saveMessage = 'Guest progress is temporary.';
            if (this.isCustomPage) this.customMessage = 'Guest closet';
            this.enforceVisibleStageAccess();
            return;
        }

        user.role ??= 'user';
        this.applyShopEntitlements(user);
        user.equipped_costume ??= 'classic';
        user.equipped_vehicle ??= this.noVehicleId;
        const stageCap = this.visibleStageCount(user.role);
        user.current_level = THREE.MathUtils.clamp(Number(user.current_level ?? 1), 1, stageCap);
        user.best_stage = THREE.MathUtils.clamp(Number(user.best_stage ?? 1), 1, stageCap);
        this.account = user;
        this.stageTotal = this.visibleStageCount();
        this.walletCoins = Number(user.coins ?? 0);
        this.saveMessage = `Saved account: ${user.name}`;
        if (this.isCustomPage) this.customMessage = '';
        this.applyEquippedLook();
        if (this.isShopPage) {
            this.syncShopSelection();
            this.applyShopPreview();
        }
        if (this.isAdminPage && this.isAdmin) void this.loadAdminUsers();

        const targetLevel = this.clampLevelIndexForRole(Number(user.current_level ?? 1) - 1);
        if (!this.started && targetLevel !== this.currentLevelIndex) {
            this.currentLevelIndex = targetLevel;
            this.createLevel();
            this.resetGame();
        } else {
            this.enforceVisibleStageAccess(false);
        }
    }

    private applyAdminPayload(data: Record<string, unknown>): void {
        const users = data.users as AdminUser[] | undefined;
        if (!Array.isArray(users)) return;
        this.adminUsers = users.map((user) => ({
            ...user,
            role: user.role ?? 'user',
            current_level: THREE.MathUtils.clamp(Number(user.current_level ?? 1), 1, this.publicStageTotal),
            best_stage: THREE.MathUtils.clamp(Number(user.best_stage ?? 1), 1, this.publicStageTotal),
            inventory: user.inventory ?? { classic: true },
            items: user.items ?? {},
            equipped_costume: user.equipped_costume ?? 'classic',
            equipped_vehicle: user.equipped_vehicle ?? this.noVehicleId
        }));
    }

    private grantAdminPurchase(item: ShopItem): void {
        if (!this.account) return;
        const inventory = { classic: true, ...(this.account.inventory ?? {}) };
        const items = { ...(this.account.items ?? {}) };
        if (item.type === 'consumable') {
            items[item.id] = (items[item.id] ?? 0) + 1;
        } else {
            inventory[item.id] = true;
        }
        this.account = { ...this.account, inventory, items };
    }

    private syncShopSelection(): void {
        const selected = this.shopCatalogItems.find((item) => item.id === this.selectedShopItemId);
        if (!selected || !this.itemMatchesShopTab(selected, this.shopTab)) {
            const currentVehicleId = this.account?.equipped_vehicle ?? this.noVehicleId;
            const preferred = this.shopTab === 'costume'
                ? this.shopCatalogItems.find((item) => item.id === (this.account?.equipped_costume ?? 'classic'))
                : this.shopTab === 'vehicle'
                    ? this.shopCatalogItems.find((item) => item.id === currentVehicleId && item.type === 'vehicle')
                    : undefined;
            this.selectedShopItemId = (preferred ?? this.shopTabCatalogItems[0] ?? this.classicCostume).id;
        }
    }

    private applyShopPreview(): void {
        this.syncShopSelection();
        const item = this.selectedShopItem;
        const costumeId = item.type === 'costume' ? item.id : this.account?.equipped_costume ?? 'classic';
        const vehicleId = item.type === 'vehicle' ? item.id : this.account?.equipped_vehicle ?? this.noVehicleId;
        this.applyCostume(costumeId);
        this.applyVehicle(vehicleId, this.isShopPage && vehicleId !== this.noVehicleId);
        if (this.player) this.player.scale.setScalar(this.shopPreviewScale());
    }

    private applyEquippedLook(): void {
        this.applyCostume(this.account?.equipped_costume ?? 'classic');
        this.applyVehicle(this.account?.equipped_vehicle ?? this.noVehicleId);
    }

    private shopPreviewVehicleId(): string {
        const item = this.selectedShopItem;
        if (item.type === 'vehicle') return item.id;
        return this.account?.equipped_vehicle ?? this.noVehicleId;
    }

    private shopPreviewHasVehicle(): boolean {
        return this.shopPreviewVehicleId() !== this.noVehicleId;
    }

    private customPreviewHasVehicle(): boolean {
        if (this.isAdmin) return true;
        return (this.account?.equipped_vehicle ?? this.noVehicleId) !== this.noVehicleId;
    }

    private queueCoinDeposit(coins = 1): void {
        if (!this.account || this.isAdmin || this.adminTestMode) return;
        this.coinDepositPending += Math.max(0, Math.floor(coins));
        if (this.coinDepositTimer) window.clearTimeout(this.coinDepositTimer);
        this.coinDepositTimer = window.setTimeout(() => {
            this.coinDepositTimer = 0;
            void this.flushCoinDeposits();
        }, 180);
    }

    private async flushCoinDeposits(): Promise<number> {
        if (this.coinDepositTimer) {
            window.clearTimeout(this.coinDepositTimer);
            this.coinDepositTimer = 0;
        }
        if (!this.account || this.isAdmin || this.adminTestMode) {
            this.coinDepositPending = 0;
            return 0;
        }
        if (this.coinDepositPromise) return this.coinDepositPromise;

        this.coinDepositPromise = this.drainCoinDeposits();
        try {
            return await this.coinDepositPromise;
        } finally {
            this.coinDepositPromise = null;
        }
    }

    private async drainCoinDeposits(): Promise<number> {
        let deposited = 0;
        while (this.coinDepositPending > 0 && this.account && !this.isAdmin && !this.adminTestMode) {
            const coins = this.coinDepositPending;
            this.coinDepositPending = 0;
            const result = await this.snowApi('coins', { coins });
            if (!result.ok) {
                this.coinDepositPending += coins;
                this.saveMessage = result.message ?? 'Coin save failed';
                this.changeDetector.detectChanges();
                break;
            }

            deposited += coins;
            this.bankedCoinsThisRun += coins;
            this.applyAccountPayload(result.data);
            this.saveMessage = result.offline ? 'Coin saved locally' : 'Coin saved';
        }
        if (deposited > 0) this.changeDetector.detectChanges();
        return deposited;
    }

    private async takePendingCoinsForPurchase(item: ShopItem): Promise<number> {
        if (!this.account || this.isAdmin || this.adminTestMode) return 0;
        if (this.coinDepositTimer) {
            window.clearTimeout(this.coinDepositTimer);
            this.coinDepositTimer = 0;
        }
        if (this.coinDepositPromise) await this.coinDepositPromise;

        const pendingCoins = Math.max(0, Math.floor(Math.max(this.coinDepositPending, this.unbankedRunCoins)));
        if (pendingCoins <= 0) return 0;
        if (this.walletCoins >= item.price) return 0;
        if (this.walletCoins + pendingCoins < item.price) return 0;

        this.coinDepositPending = 0;
        return pendingCoins;
    }

    private restorePendingCoinsAfterPurchase(coins: number): void {
        if (coins <= 0 || !this.account || this.isAdmin || this.adminTestMode) return;
        this.coinDepositPending += coins;
        if (!this.coinDepositTimer) {
            this.coinDepositTimer = window.setTimeout(() => {
                this.coinDepositTimer = 0;
                void this.flushCoinDeposits();
            }, 300);
        }
    }

    private async adminUpdateUser(user: AdminUser, patch: Partial<Pick<AdminUser, 'current_level' | 'coins'>>): Promise<void> {
        if (!this.isAdmin) return;
        this.adminLoading = true;
        this.adminMessage = '';
        this.changeDetector.detectChanges();

        const result = await this.snowApi('admin/update-user', {
            user_id: user.id,
            current_level: patch.current_level ?? '',
            coins: patch.coins ?? '',
            limit: 200
        });
        this.adminLoading = false;
        if (!result.ok) {
            this.adminMessage = result.message ?? '사용자 변경에 실패했습니다.';
            this.changeDetector.detectChanges();
            return;
        }

        this.applyAdminPayload(result.data);
        this.adminMessage = `${user.name} updated`;
        this.changeDetector.detectChanges();
    }

    private applyCostume(costumeId: string): void {
        const activeCostumeId = costumeId || 'classic';
        const adminActive = this.isAdmin && !this.isShopPage && !this.isCustomPage && activeCostumeId === 'classic';
        const palette: Record<string, { coat: number; dark: number; emissive?: number; darkEmissive?: number; emissiveIntensity?: number; darkEmissiveIntensity?: number }> = {
            classic: { coat: 0xd21f2b, dark: 0x9b171e },
            blue_costume: { coat: 0x2d69e8, dark: 0x173c99 },
            gold_costume: { coat: 0xf0b72d, dark: 0x9f6612 },
            admin: {
                coat: 0x1b2438,
                dark: 0x152734,
                emissive: 0x0c2f56,
                darkEmissive: 0x0b5160,
                emissiveIntensity: 0.1,
                darkEmissiveIntensity: 0.12
            }
        };
        const costumeVisualId = adminActive ? 'classic' : activeCostumeId;
        const characterCostumeActive = this.isCharacterCostume(costumeVisualId);
        const colors = palette[adminActive ? 'admin' : costumeVisualId] ?? palette.classic;
        this.redMaterial.color.setHex(colors.coat);
        this.darkRedMaterial.color.setHex(colors.dark);
        this.redMaterial.emissive.setHex(colors.emissive ?? 0x000000);
        this.redMaterial.emissiveIntensity = colors.emissiveIntensity ?? 0;
        this.darkRedMaterial.emissive.setHex(colors.darkEmissive ?? 0x000000);
        this.darkRedMaterial.emissiveIntensity = colors.darkEmissiveIntensity ?? 0;
        if (this.baseAvatar) this.baseAvatar.visible = !characterCostumeActive;
        this.setCharacterCostume(costumeVisualId, characterCostumeActive);
        this.setAdminOutfitVisible(adminActive);
    }

    private isCharacterCostume(costumeId: string): boolean {
        return [
            'alex_costume',
            'steve_costume',
            'mario_costume',
            'among_crewmate_costume',
            'sonic_costume',
            'frog_costume'
        ].includes(costumeId);
    }

    private setCharacterCostume(costumeId: string, active: boolean): void {
        if (!this.costumeOutfits) return;
        this.costumeOutfits.visible = active;
        this.costumeOutfits.children.forEach((child) => {
            child.visible = active && child.name === costumeId;
        });
    }

    private setAdminOutfitVisible(active: boolean): void {
        if (this.adminOutfit) this.adminOutfit.visible = active;
        this.updateRideVisualState();
    }

    private applyVehicle(vehicleId: string, forceVehiclePreview = false): void {
        const nextVehicleId = vehicleId || this.noVehicleId;
        if (!this.vehicleMount) return;
        this.vehicleMount.clear();
        if (nextVehicleId !== this.noVehicleId) {
            const vehicle = this.createVehicleMount(nextVehicleId);
            if (vehicle) this.vehicleMount.add(vehicle);
        }
        this.updateRideVisualState(nextVehicleId, forceVehiclePreview);
    }

    private updateRideVisualState(vehicleId = this.account?.equipped_vehicle ?? this.noVehicleId, forceVehiclePreview = false): void {
        const vehiclePreviewActive = forceVehiclePreview && vehicleId !== this.noVehicleId;
        const adminActive = this.isAdmin && !vehiclePreviewActive && vehicleId === this.noVehicleId;
        if (this.adminMount) this.adminMount.visible = adminActive;
        if (this.vehicleMount) this.vehicleMount.visible = (!this.isAdmin || vehiclePreviewActive || !adminActive) && vehicleId !== this.noVehicleId;
        if (this.santaRider) {
            const rideHeight = adminActive ? this.vehicleRideHeights.rudolph_vehicle : this.vehicleRideHeights[vehicleId] ?? 0;
            this.santaRider.position.y = rideHeight;
        }
    }

    private rideGroundLift(): number {
        if (this.isAdmin) return 0.18;
        const vehicleId = this.account?.equipped_vehicle ?? this.noVehicleId;
        return vehicleId === this.noVehicleId ? 0 : 0.14;
    }

    private initScene(): void {
        this.scene = new THREE.Scene();
        this.scene.background = this.sceneBackground;
        this.scene.fog = this.sceneFog;
        this.world = new THREE.Group();
        this.scene.add(this.world);
        this.scenery = new THREE.Group();
        this.world.add(this.scenery);

        this.camera = new THREE.PerspectiveCamera(56, 1, 0.1, 240);
        this.renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
        this.renderer.setPixelRatio(1);
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.BasicShadowMap;
        this.viewportElement.appendChild(this.renderer.domElement);

        this.addLighting();
        this.addStars();
        this.createLevel();
        this.player = this.createSanta();
        this.scene.add(this.player);
        this.addSnow();

        window.addEventListener('keydown', this.onKeyDown, { passive: false });
        window.addEventListener('keyup', this.onKeyUp);
        window.addEventListener('resize', this.onResize);
        window.addEventListener('blur', this.clearControls);
        window.addEventListener('pointermove', this.onPointerMove, { passive: true });
        window.addEventListener('pointerup', this.clearTouchControls);
        window.addEventListener('pointercancel', this.clearTouchControls);
        window.addEventListener('popstate', this.onPopState);
        this.viewportElement.addEventListener('pointerleave', this.onPointerLeave);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.onResize();
    }

    private addLighting(): void {
        const hemi = new THREE.HemisphereLight(0x89a8ff, 0x05060c, 1.08);
        this.scene.add(hemi);

        const sun = new THREE.DirectionalLight(0xdde8ff, 2.05);
        sun.position.set(-7, 16, 7);
        sun.castShadow = true;
        sun.shadow.mapSize.set(2048, 2048);
        sun.shadow.camera.left = -34;
        sun.shadow.camera.right = 34;
        sun.shadow.camera.top = 34;
        sun.shadow.camera.bottom = -34;
        this.scene.add(sun);

        const fill = new THREE.DirectionalLight(0x3e64c9, 0.45);
        fill.position.set(9, 7, -12);
        this.scene.add(fill);
    }

    private createLevel(): void {
        this.currentLevelIndex = this.clampLevelIndexForRole(this.currentLevelIndex);
        this.stageTotal = this.visibleStageCount();
        this.clearLevel();
        const level = this.buildExpandedLevel(this.levels[this.currentLevelIndex], this.currentLevelIndex);
        this.activeLevel = level;
        this.stage = this.currentLevelIndex + 1;
        this.applyStageAtmosphere();
        this.saveLevelIndex();
        this.spawn.set(level.spawn[0], 0, level.spawn[1]);

        level.platforms.forEach((platform) => {
            this.addPlatform(platform.x, platform.y, platform.z, platform.w, platform.h ?? 0.75, platform.d, platform);
        });
        level.gifts.forEach(([x, z]) => this.addGift(x, z));
        (level.checkpoints ?? []).forEach(([x, z], index) => this.addCheckpoint(x, z, index));
        this.addCoinsForLevel();
        this.addHeartsForLevel(level.goal);
        level.enemies.forEach((enemy) => this.addEnemy(enemy.x, enemy.z, enemy.axis, enemy.distance, enemy.speed, enemy.phase ?? 0, enemy));
        this.addGoal(level.goal[0], level.goal[1]);
        this.addStageSpecialObjects();
        this.addSceneryForLevel();
        this.rebuildMinimap();
    }

    private clearLevel(): void {
        this.platforms.forEach((platform) => this.world.remove(platform.mesh));
        this.gifts.forEach((gift) => this.world.remove(gift.mesh));
        this.coins.forEach((coin) => this.world.remove(coin.mesh));
        this.hearts.forEach((heart) => this.world.remove(heart.mesh));
        this.checkpoints.forEach((checkpoint) => this.world.remove(checkpoint.mesh));
        this.giftBursts.forEach((burst) => this.world.remove(burst.group));
        this.enemies.forEach((enemy) => this.world.remove(enemy.mesh));
        this.meteors.forEach((meteor) => this.world.remove(meteor.group));
        this.dimensionPortals.forEach((portal) => this.world.remove(portal.group));
        if (this.goal) this.world.remove(this.goal);
        this.scenery?.clear();
        this.platforms = [];
        this.gifts = [];
        this.coins = [];
        this.hearts = [];
        this.checkpoints = [];
        this.giftBursts = [];
        this.enemies = [];
        this.meteors = [];
        this.dimensionPortals = [];
        this.dimensionPortalCooldown = 0;
        this.meteorTimer = 0;
        this.giftsCollected = 0;
        this.giftTotal = 0;
        this.coinsCollected = 0;
        this.bankedCoinsThisRun = 0;
        this.coinTotal = 0;
        this.activeCheckpointIndex = -1;
        this.minimapPlatforms = [];
        this.minimapGifts = [];
        this.minimapHearts = [];
        this.minimapCheckpoints = [];
    }

    private buildExpandedLevel(base: LevelLayout, levelIndex: number): LevelLayout {
        const nodes = this.getStageNodes(levelIndex);
        const lateStageTightSpacing = levelIndex >= 8;
        const stageWideSlippery = levelIndex === 11;
        const stageTwelveRoomierSpacing = levelIndex === 11;
        const nightmareSpacing = levelIndex >= 12;
        const castleCourtSpacing = levelIndex === 14;
        const castleLeapSpacing = levelIndex === 15;
        const mysticSanctumSpacing = levelIndex === 16;
        const cellX = levelIndex === 1 ? 8.6 : stageTwelveRoomierSpacing ? 7.0 : mysticSanctumSpacing ? 7.35 : castleLeapSpacing ? 8.3 : castleCourtSpacing ? 7.25 : nightmareSpacing ? 6.85 : lateStageTightSpacing ? 6.4 : 11.3;
        const cellZ = levelIndex === 1 ? 7.8 : stageTwelveRoomierSpacing ? 8.85 : mysticSanctumSpacing ? 10.75 : castleLeapSpacing ? 13.1 : castleCourtSpacing ? 9.65 : nightmareSpacing ? 9.05 : lateStageTightSpacing ? 8.2 : 10.2;
        const platforms: PlatformSpec[] = [];
        const gifts: Array<[number, number]> = [];
        const enemies: EnemySpec[] = [];

        nodes.forEach((node, index) => {
            const x = Number((node.gx * cellX).toFixed(2));
            const z = Number((node.gz * cellZ).toFixed(2));
            const platform: PlatformSpec = {
                x,
                y: Number((node.y ?? (index * 0.12 + levelIndex * 0.05)).toFixed(2)),
                z,
                w: node.w,
                h: 0.85,
                d: node.d,
                kind: node.kind ?? (stageWideSlippery ? 'slipperyIce' : undefined),
                tower: node.tower,
                axis: node.axis,
                distance: node.distance,
                moveX: node.moveX,
                moveZ: node.moveZ,
                speed: node.speed,
                phase: node.phase,
                shakePower: node.shakePower,
                slippery: stageWideSlippery || node.kind === 'slipperyIce',
                fireCycle: node.fireCycle,
                fireOffDuration: node.fireOffDuration,
                firePhase: node.firePhase,
                bouncePad: node.kind === 'bouncePad',
                bouncePower: node.bouncePower,
                breakLimit: node.breakLimit
            };
            platforms.push(platform);

            const safeX = Math.max(0.7, node.w / 2 - 1.45);
            const safeZ = Math.max(0.7, node.d / 2 - 1.45);
            const giftSlots: Array<[number, number]> = [
                [x - safeX * 0.58, z + safeZ * 0.48],
                [x + safeX * 0.58, z - safeZ * 0.48],
                [x, z],
                [x - safeX * 0.48, z - safeZ * 0.58],
                [x + safeX * 0.42, z + safeZ * 0.54]
            ];
            const giftCount = node.gifts ?? (node.kind === 'woodDisk' ? 1 : levelIndex >= 4 ? 3 : 2);
            giftSlots.slice(0, giftCount).forEach((slot) => gifts.push(slot));

            if (node.enemyAxis) {
                const enemyAxis = node.enemyAxis === 'both' ? (index % 2 === 0 ? 'x' : 'z') : node.enemyAxis;
                enemies.push({
                    x,
                    z,
                    axis: enemyAxis,
                    distance: 1.7 + levelIndex * 0.2 + (index % 3) * 0.2,
                    speed: 1.0 + levelIndex * 0.13 + (index % 4) * 0.04,
                    phase: index * 0.55
                });
                if (node.enemyAxis === 'both') {
                    enemies.push({
                        x: x + Math.min(2.1, safeX),
                        z: z - Math.min(1.6, safeZ),
                        axis: enemyAxis === 'x' ? 'z' : 'x',
                        distance: 1.35 + levelIndex * 0.16,
                        speed: 1.08 + levelIndex * 0.12,
                        phase: index * 0.77 + 0.4
                    });
                }
            }
        });

        const firstPlatform = platforms[0];
        const finalPlatform = platforms[platforms.length - 1];
        if (levelIndex === 2) this.addStageThreeBridgeTiles(platforms, cellX, cellZ);
        const spawn: [number, number] = [
            firstPlatform.x,
            firstPlatform.z + Math.min(2.2, firstPlatform.d / 3)
        ];
        this.applyStageHazards(platforms, enemies, levelIndex, spawn, [finalPlatform.x, finalPlatform.z]);
        this.applyNightmareStageHazards(platforms, enemies, levelIndex, spawn, [finalPlatform.x, finalPlatform.z]);
        this.removeStageFourteenFireApproachEnemies(platforms, enemies, levelIndex);
        const checkpoints = this.buildCheckpointPositions(platforms, levelIndex);
        const spreadGifts = this.spreadGiftPositions(gifts, platforms);
        const stageGifts = (levelIndex === 0 ? spreadGifts.slice(0, 32) : spreadGifts)
            .filter(([x, z]) => checkpoints.every(([checkpointX, checkpointZ]) => Math.hypot(checkpointX - x, checkpointZ - z) > 1.65));
        const baseTime = base.time + Math.max(0, nodes.length - 14) * 5 + levelIndex * 14;
        const nightmareTime = levelIndex === 12 ? 250 : levelIndex === 13 ? 265 : levelIndex === 14 ? 245 : levelIndex === 15 ? 260 : levelIndex === 16 ? 285 : undefined;

        return {
            title: base.title,
            time: nightmareTime ?? baseTime,
            spawn,
            goal: [finalPlatform.x, finalPlatform.z],
            platforms,
            gifts: stageGifts,
            checkpoints,
            enemies
        };
    }

    private addStageThreeBridgeTiles(platforms: PlatformSpec[], cellX: number, cellZ: number): void {
        [
            { gx: 3, gz: -4, y: 1.54 },
            { gx: 1, gz: -4, y: 1.54 }
        ].forEach((bridge) => {
            platforms.push({
                x: Number((bridge.gx * cellX).toFixed(2)),
                y: bridge.y,
                z: Number((bridge.gz * cellZ).toFixed(2)),
                w: 6.6,
                h: 0.85,
                d: 6.6,
                kind: 'fragileIce',
                fragile: true,
                breakLimit: 5
            });
        });
    }

    private buildCheckpointPositions(platforms: PlatformSpec[], levelIndex: number): Array<[number, number]> {
        const positions: Array<[number, number]> = [];
        const checkpointsWanted = levelIndex >= 12 ? 2 : levelIndex >= 11 ? 4 : levelIndex === 10 ? 3 : 2;
        const candidates = platforms
            .map((platform, index) => ({ platform, index }))
            .filter(({ platform, index }) => (
                index > 0 &&
                index < platforms.length - 1 &&
                platform.kind !== 'woodDisk' &&
                platform.kind !== 'shakyIce' &&
                platform.kind !== 'fireBridge' &&
                platform.kind !== 'bouncePad' &&
                (levelIndex >= 11 || platform.kind !== 'slipperyIce') &&
                !platform.fragile &&
                !platform.tower &&
                !platform.axis &&
                platform.moveX === undefined &&
                platform.moveZ === undefined &&
                platform.w >= 8 &&
                platform.d >= 6
            ));

        for (let checkpoint = 1; checkpoint <= checkpointsWanted; checkpoint++) {
            const targetIndex = Math.round((platforms.length - 1) * (checkpoint / (checkpointsWanted + 1)));
            const candidate = candidates
                .filter(({ platform }) => positions.every(([x, z]) => Math.hypot(platform.x - x, platform.z - z) > 8))
                .sort((a, b) => Math.abs(a.index - targetIndex) - Math.abs(b.index - targetIndex))[0];
            if (!candidate) continue;

            const { platform } = candidate;
            const safeZ = Math.max(0.5, platform.d / 2 - 1.6);
            const offsetZ = checkpoint % 2 === 0 ? -safeZ * 0.46 : safeZ * 0.46;
            positions.push([
                Number(platform.x.toFixed(2)),
                Number((platform.z + offsetZ).toFixed(2))
            ]);
        }

        return positions;
    }

    private getStageNodes(levelIndex: number): StageNode[] {
        const maps: StageNode[][] = [
            [
                { gx: 0, gz: 0, w: 10.8, d: 8.4, gifts: 3 },
                { gx: 0.45, gz: -1, w: 5.8, d: 5.8, kind: 'woodDisk', moveX: 5.0, moveZ: -3.4, speed: 0.78, gifts: 1 },
                { gx: 1, gz: -2, w: 9.0, d: 7.8 },
                { gx: 2, gz: -2, w: 10.0, d: 7.8, enemyAxis: 'x' },
                { gx: 2, gz: -3, w: 5.8, d: 5.8, kind: 'woodDisk', axis: 'z', distance: 3.4, speed: 0.82, phase: 0.6, gifts: 1 },
                { gx: 2, gz: -4, w: 9.6, d: 8.0 },
                { gx: 1, gz: -5, w: 8.8, d: 7.6 },
                { gx: 0, gz: -5, w: 10.2, d: 8.0, enemyAxis: 'z' },
                { gx: -0.45, gz: -6, w: 5.8, d: 5.8, kind: 'woodDisk', moveX: 5.0, moveZ: 3.7, speed: 0.86, phase: 1.2, gifts: 1 },
                { gx: -1, gz: -7, w: 9.4, d: 7.8 },
                { gx: -2, gz: -7, w: 8.8, d: 7.4 },
                { gx: -2, gz: -8, w: 9.2, d: 7.6 },
                { gx: -1, gz: -9, w: 5.8, d: 5.8, kind: 'woodDisk', moveX: 7.5, moveZ: -4.55, speed: 0.86, phase: 1.8, gifts: 1 },
                { gx: 0, gz: -10, w: 9.6, d: 7.8, enemyAxis: 'x' },
                { gx: 1, gz: -10, w: 8.8, d: 7.4 },
                { gx: 2, gz: -11, w: 9.2, d: 7.6 },
                { gx: 2, gz: -12, w: 10.0, d: 8.0, gifts: 3 }
            ],
            [
                { gx: 0, gz: 0, w: 10.4, d: 8.4, gifts: 3 },
                { gx: 0, gz: -1, w: 5.8, d: 5.8, kind: 'woodDisk', axis: 'x', distance: 3.6, speed: 0.82, gifts: 1 },
                { gx: -1, gz: -2, w: 8.6, d: 7.6 },
                { gx: 1, gz: -2, w: 8.6, d: 7.6 },
                { gx: -2, gz: -3, w: 9.2, d: 7.6, enemyAxis: 'z' },
                { gx: 0, gz: -3, w: 9.8, d: 7.8 },
                { gx: 2, gz: -3, w: 9.2, d: 7.6, enemyAxis: 'x' },
                { gx: -2, gz: -4, w: 5.6, d: 5.6, kind: 'woodDisk', axis: 'z', distance: 1.0, speed: 0.84, phase: 0.7, gifts: 1 },
                { gx: 0, gz: -4.05, w: 5.8, d: 5.8, kind: 'woodDisk', axis: 'z', distance: 1.35, speed: 0.88, phase: 1.05, gifts: 0 },
                { gx: -3, gz: -5, w: 8.8, d: 7.6 },
                { gx: -1, gz: -5, w: 9.4, d: 7.8 },
                { gx: 1, gz: -5, w: 9.4, d: 7.8 },
                { gx: 3, gz: -5, w: 8.8, d: 7.6 },
                { gx: 2, gz: -6, w: 5.6, d: 5.6, kind: 'woodDisk', axis: 'z', distance: 1.05, speed: 0.88, phase: 1.4, gifts: 1 },
                { gx: 1, gz: -7, w: 9.2, d: 7.8, enemyAxis: 'both' },
                { gx: 0, gz: -8, w: 8.8, d: 7.6 },
                { gx: -1, gz: -8, w: 9.4, d: 7.8 },
                { gx: -2, gz: -7, w: 8.8, d: 7.6 },
                { gx: -3, gz: -8, w: 9.0, d: 7.6, enemyAxis: 'x' },
                { gx: -2, gz: -9, w: 5.6, d: 5.6, kind: 'woodDisk', axis: 'x', distance: 1.1, speed: 0.88, phase: 2.2, gifts: 1 },
                { gx: -1, gz: -10, w: 10.0, d: 8.0, gifts: 3 }
            ],
            [
                { gx: 0, gz: 0, w: 10.8, d: 8.6, gifts: 3 },
                { gx: 1, gz: -1, w: 8.8, d: 7.6 },
                { gx: 2, gz: -1, w: 9.2, d: 7.6 },
                { gx: 3, gz: -1, w: 9.2, d: 7.6, enemyAxis: 'x' },
                { gx: 4, gz: -2, w: 8.8, d: 7.6 },
                { gx: 4, gz: -3, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'z', distance: 1.3, speed: 1.02, gifts: 1 },
                { gx: 4, gz: -4, w: 9.2, d: 7.6, enemyAxis: 'z' },
                { gx: 3, gz: -5, w: 8.8, d: 7.6 },
                { gx: 2, gz: -5, w: 9.6, d: 7.8 },
                { gx: 1, gz: -5, w: 9.0, d: 7.6 },
                { gx: 0, gz: -5, w: 9.2, d: 7.6, enemyAxis: 'x' },
                { gx: -1, gz: -4, w: 8.8, d: 7.6 },
                { gx: -1, gz: -3, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'x', distance: 1.2, speed: 1.0, phase: 1.2, gifts: 1 },
                { gx: -1, gz: -2, w: 8.8, d: 7.6 },
                { gx: 0, gz: -2, w: 9.8, d: 7.8 },
                { gx: 1, gz: -3, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'z', distance: 1.25, speed: 1.06, phase: 2.1, gifts: 1 },
                { gx: 2, gz: -3, w: 10.6, d: 8.4, enemyAxis: 'both' },
                { gx: 3, gz: -3, w: 9.0, d: 7.8 },
                { gx: 2, gz: -6, w: 8.8, d: 7.6 },
                { gx: 1, gz: -7, w: 8.8, d: 7.6 },
                { gx: 0, gz: -8, w: 10.0, d: 8.0, gifts: 3 }
            ],
            [
                { gx: 0, gz: 0, w: 10.8, d: 8.6, gifts: 3 },
                { gx: 0, gz: -1, w: 8.8, d: 7.4 },
                { gx: 0, gz: -2, w: 11.0, d: 8.6, enemyAxis: 'both' },
                { gx: -1, gz: -2, w: 8.8, d: 7.4 },
                { gx: -2, gz: -2, w: 8.6, d: 7.4, enemyAxis: 'z' },
                { gx: -3, gz: -3, w: 9.4, d: 7.8 },
                { gx: 1, gz: -2, w: 8.8, d: 7.4 },
                { gx: 2, gz: -2, w: 8.6, d: 7.4, enemyAxis: 'x' },
                { gx: 3, gz: -3, w: 9.4, d: 7.8 },
                { gx: 0, gz: -3, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'x', distance: 1.35, speed: 1.04, gifts: 1 },
                { gx: 0, gz: -4, w: 9.4, d: 7.8 },
                { gx: -1, gz: -5, w: 8.8, d: 7.4 },
                { gx: 1, gz: -5, w: 8.8, d: 7.4 },
                { gx: 0, gz: -6, w: 10.4, d: 8.0, enemyAxis: 'x' },
                { gx: -2, gz: -6, w: 8.6, d: 7.4 },
                { gx: 2, gz: -6, w: 8.6, d: 7.4 },
                { gx: 0, gz: -7, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'z', distance: 1.4, speed: 1.08, phase: 1.6, gifts: 1 },
                { gx: 0, gz: -8, w: 9.4, d: 7.8 },
                { gx: 0.45, gz: -8.85, w: 8.8, d: 7.6 },
                { gx: -0.35, gz: -9.7, w: 8.8, d: 7.6, enemyAxis: 'z' },
                { gx: 0, gz: -10.55, w: 10.2, d: 8.2, gifts: 3 }
            ],
            [
                { gx: 0, gz: 0, w: 11.0, d: 8.8, gifts: 3 },
                { gx: 1, gz: -1, w: 8.8, d: 7.6 },
                { gx: 2, gz: -1, w: 9.2, d: 7.8, enemyAxis: 'x' },
                { gx: 3, gz: -2, w: 8.8, d: 7.6 },
                { gx: 4, gz: -3, w: 9.2, d: 7.8, enemyAxis: 'z' },
                { gx: 4, gz: -4, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'z', distance: 1.4, speed: 1.12, gifts: 1 },
                { gx: 4, gz: -5, w: 9.4, d: 7.8 },
                { gx: 3, gz: -6, w: 9.0, d: 7.6 },
                { gx: 2, gz: -6, w: 10.4, d: 8.4, enemyAxis: 'both' },
                { gx: 1, gz: -6, w: 9.2, d: 7.8 },
                { gx: 0, gz: -6, w: 9.2, d: 7.8 },
                { gx: -1, gz: -5, w: 8.8, d: 7.6, enemyAxis: 'z' },
                { gx: -2, gz: -4, w: 9.0, d: 7.6 },
                { gx: -2, gz: -3, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'x', distance: 1.35, speed: 1.1, phase: 1.4, gifts: 1 },
                { gx: -2, gz: -2, w: 9.0, d: 7.6 },
                { gx: -1, gz: -1, w: 8.8, d: 7.6 },
                { gx: 0, gz: -3, w: 10.6, d: 8.4, enemyAxis: 'both' },
                { gx: 1, gz: -3, w: 9.2, d: 7.8 },
                { gx: 2, gz: -4, w: 8.8, d: 7.6 },
                { gx: 1, gz: -5, w: 8.8, d: 7.6 },
                { gx: 3, gz: -7, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'x', distance: 1.45, speed: 1.16, phase: 2.3, gifts: 1 },
                { gx: 2, gz: -8, w: 9.0, d: 7.8 },
                { gx: 1, gz: -9, w: 9.2, d: 7.8, enemyAxis: 'x' },
                { gx: 0, gz: -10, w: 10.2, d: 8.2, gifts: 3 }
            ],
            [
                { gx: 0, gz: 0, w: 10.8, d: 8.6, gifts: 3 },
                { gx: 1, gz: -1, w: 5.2, d: 5.2, kind: 'woodDisk', axis: 'x', distance: 1.5, speed: 1.18, gifts: 1 },
                { gx: 2, gz: -1, w: 9.0, d: 7.6 },
                { gx: 3, gz: -2, w: 5.2, d: 5.2, kind: 'woodDisk', axis: 'z', distance: 1.55, speed: 1.2, phase: 0.8, gifts: 1 },
                { gx: 4, gz: -2, w: 9.2, d: 7.8, enemyAxis: 'x' },
                { gx: 4, gz: -3, w: 8.8, d: 7.6 },
                { gx: 3, gz: -4, w: 9.2, d: 7.8, enemyAxis: 'both' },
                { gx: 2, gz: -4, w: 5.2, d: 5.2, kind: 'woodDisk', axis: 'x', distance: 1.55, speed: 1.22, phase: 1.3, gifts: 1 },
                { gx: 1, gz: -4, w: 8.8, d: 7.6 },
                { gx: 0, gz: -5, w: 9.2, d: 7.8 },
                { gx: -1, gz: -5, w: 5.2, d: 5.2, kind: 'woodDisk', axis: 'z', distance: 1.6, speed: 1.26, phase: 1.9, gifts: 1 },
                { gx: -2, gz: -6, w: 9.2, d: 7.8, enemyAxis: 'z' },
                { gx: -3, gz: -6, w: 8.8, d: 7.6 },
                { gx: -3, gz: -7, w: 5.2, d: 5.2, kind: 'woodDisk', axis: 'x', distance: 1.65, speed: 1.3, phase: 2.4, gifts: 1 },
                { gx: -2, gz: -8, w: 9.0, d: 7.8 },
                { gx: -1, gz: -8, w: 9.2, d: 7.8, enemyAxis: 'x' },
                { gx: 0, gz: -9, w: 9.0, d: 7.6 },
                { gx: 1, gz: -9, w: 5.2, d: 5.2, kind: 'woodDisk', axis: 'z', distance: 1.7, speed: 1.34, phase: 3.0, gifts: 1 },
                { gx: 2, gz: -10, w: 9.2, d: 7.8, enemyAxis: 'both' },
                { gx: 3, gz: -10, w: 8.8, d: 7.6 },
                { gx: 2, gz: -11, w: 9.0, d: 7.6 },
                { gx: 1, gz: -12, w: 8.8, d: 7.6, enemyAxis: 'z' },
                { gx: 0, gz: -12, w: 10.0, d: 8.0, gifts: 3 }
            ],
            [
                { gx: 0, gz: 0, w: 10.8, d: 8.6, gifts: 3 },
                { gx: 0, gz: -1, w: 8.8, d: 7.6 },
                { gx: 1, gz: -2, w: 9.4, d: 7.8 },
                { gx: 2, gz: -2, w: 10.8, d: 8.6, enemyAxis: 'both' },
                { gx: 3, gz: -2, w: 9.4, d: 7.8 },
                { gx: 2, gz: -3, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'x', distance: 1.55, speed: 1.26, gifts: 1 },
                { gx: 1, gz: -4, w: 9.2, d: 7.8 },
                { gx: 0, gz: -4, w: 10.4, d: 8.4, enemyAxis: 'x' },
                { gx: -1, gz: -4, w: 9.2, d: 7.8 },
                { gx: -2, gz: -4, w: 9.0, d: 7.6, enemyAxis: 'z' },
                { gx: -3, gz: -5, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'z', distance: 1.65, speed: 1.3, phase: 1.2, gifts: 1 },
                { gx: -2, gz: -6, w: 9.2, d: 7.8 },
                { gx: -1, gz: -6, w: 9.8, d: 8.0, enemyAxis: 'both' },
                { gx: 0, gz: -6, w: 9.2, d: 7.8 },
                { gx: 1, gz: -6, w: 9.2, d: 7.8 },
                { gx: 2, gz: -6, w: 10.4, d: 8.4, enemyAxis: 'x' },
                { gx: 3, gz: -7, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'x', distance: 1.7, speed: 1.34, phase: 2.1, gifts: 1 },
                { gx: 2, gz: -8, w: 9.2, d: 7.8 },
                { gx: 1, gz: -8, w: 9.2, d: 7.8, enemyAxis: 'z' },
                { gx: 0, gz: -8, w: 9.2, d: 7.8 },
                { gx: -1, gz: -9, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'z', distance: 1.7, speed: 1.38, phase: 2.9, gifts: 1 },
                { gx: -2, gz: -10, w: 9.4, d: 7.8, enemyAxis: 'both' },
                { gx: -1, gz: -11, w: 9.0, d: 7.6 },
                { gx: 0, gz: -11, w: 9.2, d: 7.8 },
                { gx: 1, gz: -12, w: 9.0, d: 7.6, enemyAxis: 'x' },
                { gx: 2, gz: -12, w: 9.4, d: 7.8 },
                { gx: 3, gz: -13, w: 10.4, d: 8.2, gifts: 3 }
            ],
            [
                { gx: 0, gz: 0, w: 11.0, d: 8.8, gifts: 3 },
                { gx: 1, gz: 0, w: 8.8, d: 7.6 },
                { gx: 2, gz: -1, w: 9.0, d: 7.8, enemyAxis: 'x' },
                { gx: 3, gz: -1, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'z', distance: 1.65, speed: 1.36, gifts: 1 },
                { gx: 4, gz: -2, w: 9.4, d: 7.8 },
                { gx: 4, gz: -3, w: 9.2, d: 7.8, enemyAxis: 'both' },
                { gx: 3, gz: -4, w: 9.0, d: 7.6 },
                { gx: 2, gz: -4, w: 9.2, d: 7.8 },
                { gx: 1, gz: -4, w: 10.4, d: 8.4, enemyAxis: 'z' },
                { gx: 0, gz: -4, w: 9.2, d: 7.8 },
                { gx: -1, gz: -5, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'x', distance: 1.75, speed: 1.42, phase: 1.1, gifts: 1 },
                { gx: -2, gz: -6, w: 9.2, d: 7.8, enemyAxis: 'both' },
                { gx: -2, gz: -7, w: 8.8, d: 7.6 },
                { gx: -1, gz: -8, w: 9.0, d: 7.8 },
                { gx: 0, gz: -8, w: 10.6, d: 8.4, enemyAxis: 'x' },
                { gx: 1, gz: -8, w: 9.0, d: 7.8 },
                { gx: 2, gz: -8, w: 9.2, d: 7.8 },
                { gx: 3, gz: -9, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'z', distance: 1.8, speed: 1.46, phase: 2.0, gifts: 1 },
                { gx: 4, gz: -10, w: 9.4, d: 7.8, enemyAxis: 'z' },
                { gx: 4, gz: -11, w: 9.2, d: 7.8 },
                { gx: 3, gz: -12, w: 9.0, d: 7.6, enemyAxis: 'both' },
                { gx: 2, gz: -12, w: 9.2, d: 7.8 },
                { gx: 1, gz: -12, w: 9.0, d: 7.8 },
                { gx: 0, gz: -12, w: 10.2, d: 8.2, enemyAxis: 'x' },
                { gx: -1, gz: -13, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'x', distance: 1.85, speed: 1.5, phase: 2.8, gifts: 1 },
                { gx: -2, gz: -14, w: 9.2, d: 7.8, enemyAxis: 'both' },
                { gx: -1, gz: -15, w: 9.0, d: 7.6 },
                { gx: 0, gz: -15, w: 9.2, d: 7.8, enemyAxis: 'z' },
                { gx: 1, gz: -16, w: 9.0, d: 7.6 },
                { gx: 2, gz: -16, w: 9.2, d: 7.8 },
                { gx: 3, gz: -17, w: 10.6, d: 8.4, gifts: 3, enemyAxis: 'both' }
            ],
            [
                { gx: 0, gz: 0, w: 11.2, d: 8.8, gifts: 3 },
                { gx: 0, gz: -1, w: 8.8, d: 7.4, enemyAxis: 'x' },
                { gx: -1, gz: -2, w: 6.8, d: 6.3, kind: 'shakyIce', speed: 5.4, phase: 0.2, shakePower: 0.18, gifts: 2 },
                { gx: 1, gz: -2, w: 6.8, d: 6.3, kind: 'shakyIce', speed: 5.9, phase: 1.4, shakePower: 0.2, gifts: 2 },
                { gx: 0, gz: -3, w: 11.0, d: 7.8, enemyAxis: 'both' },
                { gx: 1, gz: -4, w: 5.2, d: 5.2, kind: 'woodDisk', axis: 'z', distance: 1.8, speed: 1.32, gifts: 1 },
                { gx: 2, gz: -5, w: 8.8, d: 7.4 },
                { gx: 2, gz: -6, w: 6.6, d: 6.1, kind: 'shakyIce', speed: 6.2, phase: 2.3, shakePower: 0.22, gifts: 2 },
                { gx: 1, gz: -7, w: 9.4, d: 7.6, enemyAxis: 'z' },
                { gx: 0, gz: -8, w: 6.6, d: 6.2, kind: 'shakyIce', speed: 5.7, phase: 3.1, shakePower: 0.2, gifts: 2 },
                { gx: -1, gz: -9, w: 9.2, d: 7.8 },
                { gx: -2, gz: -10, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'x', distance: 1.9, speed: 1.45, phase: 0.9, gifts: 1 },
                { gx: -1, gz: -11, w: 9.6, d: 7.8, enemyAxis: 'both' },
                { gx: 0, gz: -12, w: 10.4, d: 8.0, gifts: 3 }
            ],
            [
                { gx: 0, gz: 0, w: 11.4, d: 8.8, gifts: 3 },
                { gx: -1, gz: -1, w: 9.0, d: 7.4 },
                { gx: -2, gz: -2, w: 7.0, d: 6.4, kind: 'shakyIce', speed: 5.8, phase: 0.7, shakePower: 0.18, gifts: 2 },
                { gx: -2, gz: -3, w: 2.7, d: 9.6, kind: 'fireBridge', fireOffDuration: 3, fireCycle: 6.4, firePhase: 0.1, gifts: 0 },
                { gx: -2, gz: -4, w: 9.2, d: 7.6, enemyAxis: 'x' },
                { gx: 0, gz: -4, w: 8.8, d: 7.6 },
                { gx: 2, gz: -4, w: 9.0, d: 7.4 },
                { gx: 2, gz: -5, w: 7.2, d: 6.5, kind: 'shakyIce', speed: 6.1, phase: 1.6, shakePower: 0.2, gifts: 2 },
                { gx: 1, gz: -6, w: 2.6, d: 9.8, kind: 'fireBridge', fireOffDuration: 3, fireCycle: 6.2, firePhase: 3.0, gifts: 0 },
                { gx: 1, gz: -7, w: 9.4, d: 7.6, enemyAxis: 'z' },
                { gx: -1, gz: -8, w: 9.2, d: 7.8 },
                { gx: -2, gz: -9, w: 6.8, d: 6.2, kind: 'shakyIce', speed: 6.5, phase: 2.8, shakePower: 0.21, gifts: 2 },
                { gx: 0, gz: -10, w: 9.4, d: 7.8, enemyAxis: 'both' },
                { gx: 2, gz: -11, w: 5.4, d: 5.4, kind: 'woodDisk', axis: 'x', distance: 2.0, speed: 1.5, phase: 1.2, gifts: 1 },
                { gx: 3, gz: -12, w: 10.2, d: 8.2, gifts: 3 }
            ],
            [
                { gx: 0, gz: 0, w: 12.0, d: 9.0, gifts: 3 },
                { gx: 0, gz: -1, w: 2.4, d: 10.2, gifts: 1 },
                { gx: 0, gz: -2, w: 2.2, d: 10.4, kind: 'shakyIce', speed: 6.4, phase: 0.4, shakePower: 0.16, gifts: 1 },
                { gx: 0, gz: -3, w: 2.15, d: 10.2, gifts: 1 },
                { gx: -1, gz: -4, w: 9.6, d: 7.8, kind: 'slipperyIce', gifts: 3, enemyAxis: 'z' },
                { gx: 1, gz: -4, w: 9.6, d: 7.8, kind: 'slipperyIce', gifts: 3 },
                { gx: 2, gz: -5, w: 6.8, d: 6.2, kind: 'shakyIce', speed: 5.9, phase: 1.9, shakePower: 0.19, gifts: 2 },
                { gx: 1.2, gz: -6.1, w: 2.3, d: 8.6, gifts: 1 },
                { gx: 0.45, gz: -7.1, w: 2.3, d: 8.6, kind: 'slipperyIce', gifts: 1 },
                { gx: -0.35, gz: -8.1, w: 2.3, d: 8.6, gifts: 1 },
                { gx: -1.1, gz: -9.1, w: 7.0, d: 6.3, kind: 'shakyIce', speed: 6.2, phase: 2.7, shakePower: 0.2, gifts: 2 },
                { gx: 0.8, gz: -10.1, w: 9.4, d: 7.8, enemyAxis: 'both' },
                { gx: 2.1, gz: -11.1, w: 10.6, d: 8.2, gifts: 3 },
                { gx: 3.0, gz: -12.2, w: 6.8, d: 6.2, kind: 'shakyIce', speed: 6.4, phase: 3.4, shakePower: 0.2, gifts: 2 },
                { gx: 2.25, gz: -13.25, w: 2.35, d: 8.8, gifts: 1 },
                { gx: 1.45, gz: -14.25, w: 2.25, d: 8.8, kind: 'slipperyIce', gifts: 1 },
                { gx: 0.55, gz: -15.25, w: 9.6, d: 7.8, enemyAxis: 'x', gifts: 3 },
                { gx: -0.55, gz: -16.25, w: 2.3, d: 8.8, gifts: 1 },
                { gx: -1.45, gz: -17.25, w: 6.8, d: 6.2, kind: 'shakyIce', speed: 6.7, phase: 4.1, shakePower: 0.22, gifts: 2 },
                { gx: -2.2, gz: -18.25, w: 9.4, d: 7.8, kind: 'slipperyIce', enemyAxis: 'z', gifts: 3 },
                { gx: -1.2, gz: -19.25, w: 2.3, d: 8.6, gifts: 1 },
                { gx: -0.2, gz: -20.25, w: 2.25, d: 8.6, kind: 'slipperyIce', gifts: 1 },
                { gx: 0.85, gz: -21.25, w: 6.8, d: 6.2, kind: 'shakyIce', speed: 6.9, phase: 5.0, shakePower: 0.23, gifts: 2 },
                { gx: 1.85, gz: -22.25, w: 9.8, d: 7.8, enemyAxis: 'both', gifts: 3 },
                { gx: 0.9, gz: -23.25, w: 2.4, d: 8.8, gifts: 1 },
                { gx: 0.0, gz: -24.25, w: 11.4, d: 8.8, kind: 'slipperyIce', gifts: 4, enemyAxis: 'x' }
            ],
            [
                { gx: 0, gz: 0, w: 14.0, d: 10.0, gifts: 4 },
                { gx: 0, gz: -1, w: 4.0, d: 10.0, gifts: 1 },
                { gx: -1, gz: -2, w: 10.4, d: 8.2, gifts: 3 },
                { gx: 1, gz: -2, w: 10.4, d: 8.2, gifts: 3, enemyAxis: 'x' },
                { gx: 0, gz: -3, w: 3.2, d: 10.4, kind: 'fireBridge', fireOffDuration: 3.1, fireCycle: 6.8, firePhase: 0.4, gifts: 0 },
                { gx: 0, gz: -4, w: 12.4, d: 9.2, gifts: 4 },
                { gx: 1.1, gz: -5, w: 4.0, d: 9.4, gifts: 1 },
                { gx: 2, gz: -6, w: 10.0, d: 8.2, gifts: 3, enemyAxis: 'z' },
                { gx: 1, gz: -7, w: 3.1, d: 10.4, kind: 'fireBridge', fireOffDuration: 3.2, fireCycle: 6.6, firePhase: 2.0, gifts: 0 },
                { gx: 0, gz: -8, w: 12.4, d: 9.2, gifts: 4 },
                { gx: -1, gz: -9, w: 4.0, d: 9.4, gifts: 1 },
                { gx: -2, gz: -10, w: 10.2, d: 8.2, gifts: 3, enemyAxis: 'x' },
                { gx: -1, gz: -11, w: 4.0, d: 9.4, gifts: 1 },
                { gx: 0, gz: -12, w: 3.2, d: 10.4, kind: 'fireBridge', fireOffDuration: 3.0, fireCycle: 6.7, firePhase: 3.3, gifts: 0 },
                { gx: 1, gz: -13, w: 10.0, d: 8.2, gifts: 3 },
                { gx: 2, gz: -14, w: 10.2, d: 8.4, gifts: 3, enemyAxis: 'both' },
                { gx: 2, gz: -15, w: 4.0, d: 9.4, gifts: 1 },
                { gx: 1, gz: -16, w: 12.0, d: 9.2, gifts: 4 },
                { gx: 0, gz: -17, w: 3.2, d: 10.4, kind: 'fireBridge', fireOffDuration: 3.1, fireCycle: 6.8, firePhase: 1.2, gifts: 0 },
                { gx: -1, gz: -18, w: 10.0, d: 8.2, gifts: 3, enemyAxis: 'z' },
                { gx: -2, gz: -19, w: 10.0, d: 8.2, gifts: 3 },
                { gx: -1, gz: -20, w: 4.0, d: 9.4, gifts: 1 },
                { gx: 0, gz: -21, w: 12.4, d: 9.2, gifts: 4, enemyAxis: 'x' },
                { gx: 1, gz: -22, w: 3.2, d: 10.4, kind: 'fireBridge', fireOffDuration: 3.2, fireCycle: 6.9, firePhase: 4.0, gifts: 0 },
                { gx: 2, gz: -23, w: 10.0, d: 8.2, gifts: 3 },
                { gx: 1, gz: -24, w: 4.0, d: 9.4, gifts: 1 },
                { gx: 0, gz: -25, w: 12.4, d: 9.2, gifts: 4, enemyAxis: 'both' },
                { gx: 0, gz: -26, w: 4.2, d: 9.4, gifts: 1 },
                { gx: 0, gz: -27, w: 14.0, d: 10.0, gifts: 5 }
            ],
            [
                { gx: 0, gz: 0, w: 12.8, d: 9.2, gifts: 4 },
                { gx: 0.4, gz: -1, w: 8.8, d: 7.2, kind: 'slipperyIce', gifts: 3 },
                { gx: 1.3, gz: -2, w: 5.8, d: 5.8, kind: 'bouncePad', bouncePower: 15.6, gifts: 1 },
                { gx: 2.2, gz: -3, w: 8.8, d: 7.2, kind: 'fragileIce', gifts: 2, enemyAxis: 'x' },
                { gx: 2.8, gz: -4, w: 3.0, d: 10.0, kind: 'fireBridge', fireOffDuration: 3, fireCycle: 6.5, firePhase: 0.5, gifts: 0 },
                { gx: 2.0, gz: -5, w: 10.2, d: 8.0, kind: 'slipperyIce', gifts: 3 },
                { gx: 1.0, gz: -6, w: 5.8, d: 5.8, kind: 'bouncePad', bouncePower: 16.4, gifts: 1 },
                { gx: 0, gz: -7, w: 8.8, d: 7.2, kind: 'fragileIce', gifts: 2, enemyAxis: 'z' },
                { gx: -1, gz: -8, w: 10.0, d: 8.0, kind: 'slipperyIce', gifts: 3 },
                { gx: -1.8, gz: -9, w: 3.0, d: 10.0, kind: 'fireBridge', fireOffDuration: 3.1, fireCycle: 6.6, firePhase: 2.2, gifts: 0 },
                { gx: -1.2, gz: -10, w: 5.8, d: 5.8, kind: 'bouncePad', bouncePower: 17.2, gifts: 1 },
                { gx: -0.2, gz: -11, w: 9.4, d: 7.6, kind: 'slipperyIce', gifts: 3, enemyAxis: 'both' },
                { gx: 1, gz: -12, w: 7.8, d: 6.8, kind: 'fragileIce', gifts: 2 },
                { gx: 2, gz: -13, w: 5.8, d: 5.8, kind: 'bouncePad', bouncePower: 16.8, gifts: 1 },
                { gx: 2.8, gz: -14, w: 10.0, d: 8.0, kind: 'slipperyIce', gifts: 3, enemyAxis: 'x' },
                { gx: 2.0, gz: -15, w: 3.0, d: 10.0, kind: 'fireBridge', fireOffDuration: 3, fireCycle: 6.4, firePhase: 4.0, gifts: 0 },
                { gx: 1.0, gz: -16, w: 8.8, d: 7.2, kind: 'fragileIce', gifts: 2 },
                { gx: 0, gz: -17, w: 5.8, d: 5.8, kind: 'bouncePad', bouncePower: 17.5, gifts: 1 },
                { gx: -1, gz: -18, w: 10.2, d: 8.0, kind: 'slipperyIce', gifts: 3, enemyAxis: 'both' },
                { gx: -2, gz: -19, w: 8.8, d: 7.2, kind: 'fragileIce', gifts: 2 },
                { gx: -1.2, gz: -20, w: 3.0, d: 10.0, kind: 'fireBridge', fireOffDuration: 3.2, fireCycle: 6.8, firePhase: 1.5, gifts: 0 },
                { gx: -0.2, gz: -21, w: 5.8, d: 5.8, kind: 'bouncePad', bouncePower: 18.0, gifts: 1 },
                { gx: 0.8, gz: -22, w: 11.2, d: 8.6, kind: 'slipperyIce', gifts: 4, enemyAxis: 'z' },
                { gx: 0, gz: -23, w: 13.2, d: 9.2, kind: 'slipperyIce', gifts: 5 }
            ],
            [
                { gx: 0, gz: 0, y: 0.65, w: 13.0, d: 9.4, gifts: 4 },
                { gx: 0, gz: -1, y: 0.78, w: 2.6, d: 11.2, gifts: 1, enemyAxis: 'z' },
                { gx: -1, gz: -2, y: 0.94, w: 9.0, d: 7.2, kind: 'slipperyIce', gifts: 3 },
                { gx: -2, gz: -3, y: 1.1, w: 8.4, d: 6.8, kind: 'fragileIce', gifts: 2 },
                { gx: -1, gz: -4, y: 1.26, w: 2.8, d: 10.8, kind: 'fireBridge', fireOffDuration: 3.0, fireCycle: 6.5, firePhase: 0.7, gifts: 0 },
                { gx: 0, gz: -5, y: 1.46, w: 9.2, d: 7.4, gifts: 3 },
                { gx: -0.7, gz: -6, y: 2.12, w: 6.8, d: 6.0, kind: 'slipperyIce', gifts: 2 },
                { gx: -1.3, gz: -7, y: 2.88, w: 6.2, d: 5.8, kind: 'fragileIce', gifts: 2 },
                { gx: -1.8, gz: -8, y: 3.62, w: 8.8, d: 7.0, tower: true, gifts: 5 },
                { gx: -0.7, gz: -9, y: 3.7, w: 2.5, d: 11.4, gifts: 1, enemyAxis: 'z' },
                { gx: 0.6, gz: -10, y: 3.52, w: 9.0, d: 7.2, kind: 'fireBridge', fireOffDuration: 3.1, fireCycle: 6.8, firePhase: 2.2, gifts: 0 },
                { gx: 1.6, gz: -11, y: 3.32, w: 8.6, d: 6.8, kind: 'slipperyIce', gifts: 3 },
                { gx: 2.4, gz: -12, y: 4.08, w: 6.5, d: 6.0, kind: 'fragileIce', gifts: 2 },
                { gx: 3.0, gz: -13, y: 4.84, w: 8.8, d: 7.0, tower: true, gifts: 5 },
                { gx: 2.0, gz: -14, y: 4.66, w: 2.5, d: 11.6, gifts: 1, enemyAxis: 'z' },
                { gx: 1.0, gz: -15, y: 4.36, w: 9.0, d: 7.4, kind: 'slipperyIce', gifts: 3 },
                { gx: 0, gz: -16, y: 4.12, w: 2.7, d: 10.8, kind: 'fireBridge', fireOffDuration: 3.0, fireCycle: 6.4, firePhase: 4.1, gifts: 0 },
                { gx: -1, gz: -17, y: 4.34, w: 8.6, d: 6.8, kind: 'fragileIce', gifts: 2 },
                { gx: -0.4, gz: -18, y: 5.1, w: 6.5, d: 6.0, kind: 'slipperyIce', gifts: 2 },
                { gx: 0.2, gz: -19, y: 5.84, w: 8.8, d: 7.0, tower: true, gifts: 0 }
            ],
            [
                { gx: 0, gz: 0, y: 0.72, w: 12.2, d: 8.6, kind: 'slipperyIce', gifts: 3 },
                { gx: 0.1, gz: -1, y: 0.92, w: 3.4, d: 10.2, kind: 'slipperyIce', gifts: 1, enemyAxis: 'z' },
                { gx: -1.15, gz: -2, y: 1.14, w: 8.2, d: 6.4, kind: 'slipperyIce', gifts: 2, enemyAxis: 'z' },
                { gx: 1.15, gz: -2, y: 1.14, w: 7.6, d: 6.0, kind: 'fragileIce', breakLimit: 0.48, gifts: 2, enemyAxis: 'x' },
                { gx: 0, gz: -3, y: 1.42, w: 10.8, d: 7.8, kind: 'shakyIce', tower: true, speed: 8.2, shakePower: 0.31, gifts: 4, enemyAxis: 'both' },
                { gx: -1, gz: -4, y: 1.54, w: 2.6, d: 10.4, kind: 'fireBridge', fireOffDuration: 1.15, fireCycle: 3.5, firePhase: 0.4, gifts: 0 },
                { gx: -2.1, gz: -5, y: 1.9, w: 7.2, d: 6.2, kind: 'fragileIce', tower: true, breakLimit: 0.44, gifts: 3, enemyAxis: 'both' },
                { gx: -1.0, gz: -6, y: 2.04, w: 2.5, d: 10.5, kind: 'slipperyIce', gifts: 1, enemyAxis: 'z' },
                { gx: 0, gz: -7, y: 2.28, w: 9.4, d: 7.0, kind: 'shakyIce', speed: 8.6, shakePower: 0.33, gifts: 3, enemyAxis: 'both' },
                { gx: 1.1, gz: -8, y: 2.52, w: 2.4, d: 10.4, kind: 'fireBridge', fireOffDuration: 1.0, fireCycle: 3.35, firePhase: 2.2, gifts: 0 },
                { gx: 2.1, gz: -9, y: 2.92, w: 7.2, d: 6.0, kind: 'fragileIce', breakLimit: 0.42, gifts: 2, enemyAxis: 'both' },
                { gx: 1.1, gz: -10, y: 3.16, w: 2.5, d: 10.6, kind: 'slipperyIce', gifts: 1, enemyAxis: 'z' },
                { gx: 0, gz: -11, y: 3.42, w: 10.4, d: 7.5, kind: 'shakyIce', tower: true, speed: 9.0, shakePower: 0.34, gifts: 4, enemyAxis: 'both' },
                { gx: -1.2, gz: -12, y: 3.7, w: 7.6, d: 6.2, kind: 'slipperyIce', gifts: 2, enemyAxis: 'x' },
                { gx: -2.0, gz: -13, y: 3.98, w: 3.0, d: 10.5, kind: 'fireBridge', fireOffDuration: 0.95, fireCycle: 3.2, firePhase: 3.9, gifts: 0 },
                { gx: -1.0, gz: -14, y: 4.24, w: 7.0, d: 6.0, kind: 'fragileIce', breakLimit: 0.4, gifts: 2, enemyAxis: 'both' },
                { gx: 0, gz: -15, y: 4.52, w: 3.0, d: 10.6, kind: 'slipperyIce', gifts: 1, enemyAxis: 'z' },
                { gx: 1.0, gz: -16, y: 4.82, w: 7.2, d: 6.1, kind: 'shakyIce', speed: 9.2, shakePower: 0.35, gifts: 3, enemyAxis: 'both' },
                { gx: 2.0, gz: -17, y: 5.14, w: 7.0, d: 6.0, kind: 'fragileIce', tower: true, breakLimit: 0.38, gifts: 3 },
                { gx: 1.0, gz: -18, y: 5.42, w: 3.0, d: 10.5, kind: 'fireBridge', fireOffDuration: 0.9, fireCycle: 3.1, firePhase: 1.4, gifts: 0 },
                { gx: 0, gz: -19, y: 5.74, w: 10.2, d: 7.4, kind: 'shakyIce', tower: true, speed: 9.4, shakePower: 0.36, gifts: 4, enemyAxis: 'both' },
                { gx: 0, gz: -20, y: 6.06, w: 7.2, d: 6.2, kind: 'slipperyIce', gifts: 2, enemyAxis: 'z' },
                { gx: -0.6, gz: -21, y: 6.38, w: 7.4, d: 6.0, kind: 'fragileIce', breakLimit: 0.36, gifts: 2, enemyAxis: 'both' },
                { gx: 0, gz: -22, y: 6.72, w: 11.2, d: 8.0, tower: true, gifts: 0 }
            ],
            [
                { gx: 0, gz: 0, y: 0.82, w: 7.8, d: 5.8, tower: true, gifts: 2 },
                { gx: 0.68, gz: -1.15, y: 1.18, w: 4.4, d: 3.9, kind: 'slipperyIce', gifts: 1 },
                { gx: -0.66, gz: -2.3, y: 1.52, w: 4.2, d: 3.8, kind: 'fragileIce', breakLimit: 0.42, gifts: 1 },
                { gx: 0.92, gz: -3.45, y: 1.86, w: 4.3, d: 3.9, kind: 'shakyIce', speed: 8.4, shakePower: 0.34, gifts: 1, enemyAxis: 'x' },
                { gx: -0.86, gz: -4.6, y: 2.2, w: 3.8, d: 4.1, kind: 'fireBridge', fireOffDuration: 0.85, fireCycle: 3.0, firePhase: 0.3, gifts: 0 },
                { gx: 0.58, gz: -5.75, y: 2.5, w: 4.8, d: 4.0, kind: 'shakyIce', tower: true, speed: 8.8, shakePower: 0.35, gifts: 2 },
                { gx: 1.54, gz: -6.9, y: 2.84, w: 4.1, d: 3.8, kind: 'fragileIce', breakLimit: 0.4, gifts: 1 },
                { gx: 0.18, gz: -8.05, y: 3.16, w: 4.0, d: 3.8, kind: 'slipperyIce', gifts: 1, enemyAxis: 'z' },
                { gx: -1.02, gz: -9.2, y: 3.5, w: 4.0, d: 3.8, kind: 'fragileIce', breakLimit: 0.38, gifts: 1, enemyAxis: 'both' },
                { gx: 0.42, gz: -10.35, y: 3.86, w: 3.9, d: 3.9, kind: 'fireBridge', fireOffDuration: 0.78, fireCycle: 2.9, firePhase: 1.8, gifts: 0 },
                { gx: 1.36, gz: -11.5, y: 4.18, w: 4.6, d: 4.0, kind: 'shakyIce', tower: true, speed: 9.1, shakePower: 0.36, gifts: 2, enemyAxis: 'x' },
                { gx: 0.02, gz: -12.65, y: 4.54, w: 3.9, d: 3.7, kind: 'slipperyIce', gifts: 1 },
                { gx: -1.16, gz: -13.8, y: 4.9, w: 4.0, d: 3.8, kind: 'shakyIce', speed: 9.3, shakePower: 0.37, gifts: 1, enemyAxis: 'both' },
                { gx: 0.16, gz: -14.95, y: 5.24, w: 3.8, d: 3.9, kind: 'fireBridge', fireOffDuration: 0.75, fireCycle: 2.85, firePhase: 2.6, gifts: 0 },
                { gx: 1.14, gz: -16.1, y: 5.6, w: 4.1, d: 3.7, kind: 'fragileIce', breakLimit: 0.36, gifts: 1, enemyAxis: 'z' },
                { gx: -0.2, gz: -17.25, y: 5.96, w: 4.6, d: 4.0, kind: 'shakyIce', tower: true, speed: 9.6, shakePower: 0.38, gifts: 2 },
                { gx: -1.28, gz: -18.4, y: 6.32, w: 3.9, d: 3.8, kind: 'slipperyIce', gifts: 1, enemyAxis: 'x' },
                { gx: -0.04, gz: -19.55, y: 6.68, w: 3.8, d: 3.9, kind: 'fireBridge', fireOffDuration: 0.72, fireCycle: 2.8, firePhase: 3.35, gifts: 0 },
                { gx: 1.24, gz: -20.7, y: 7.04, w: 4.0, d: 3.8, kind: 'fragileIce', breakLimit: 0.35, gifts: 1, enemyAxis: 'both' },
                { gx: 0.08, gz: -21.85, y: 7.4, w: 3.9, d: 3.7, kind: 'shakyIce', speed: 9.9, shakePower: 0.4, gifts: 1 },
                { gx: -1.08, gz: -23.0, y: 7.76, w: 4.0, d: 3.8, kind: 'slipperyIce', gifts: 1, enemyAxis: 'z' },
                { gx: 0.24, gz: -24.15, y: 8.12, w: 3.8, d: 3.8, kind: 'fireBridge', fireOffDuration: 0.7, fireCycle: 2.75, firePhase: 0.9, gifts: 0 },
                { gx: 1.22, gz: -25.3, y: 8.48, w: 4.0, d: 3.7, kind: 'fragileIce', breakLimit: 0.34, gifts: 1, enemyAxis: 'x' },
                { gx: 0.0, gz: -26.45, y: 8.86, w: 7.6, d: 5.6, tower: true, gifts: 0 }
            ],
            [
                { gx: 0, gz: 0, y: 0.9, w: 10.0, d: 7.2, tower: true, gifts: 3 },
                { gx: 0.75, gz: -1.05, y: 1.2, w: 4.6, d: 4.1, kind: 'slipperyIce', gifts: 1 },
                { gx: 0.55, gz: -2.0, y: 1.56, w: 4.2, d: 3.9, kind: 'fragileIce', breakLimit: 0.34, gifts: 1, enemyAxis: 'x' },
                { gx: 0.95, gz: -3.15, y: 1.92, w: 3.6, d: 9.2, kind: 'fireBridge', fireOffDuration: 0.66, fireCycle: 2.65, firePhase: 0.2, gifts: 0 },
                { gx: 1.8, gz: -4.2, y: 2.32, w: 6.2, d: 5.5, kind: 'shakyIce', speed: 10.0, phase: 0.6, shakePower: 0.38, gifts: 2, enemyAxis: 'both' },
                { gx: 0.45, gz: -5.2, y: 2.72, w: 4.4, d: 4.0, kind: 'bouncePad', bouncePower: 19.4, gifts: 1 },
                { gx: -0.85, gz: -6.35, y: 3.18, w: 7.4, d: 6.2, kind: 'slipperyIce', gifts: 2, enemyAxis: 'z' },
                { gx: -1.8, gz: -7.45, y: 3.56, w: 3.2, d: 9.6, kind: 'fireBridge', fireOffDuration: 0.62, fireCycle: 2.55, firePhase: 1.4, gifts: 0 },
                { gx: -0.65, gz: -8.55, y: 3.96, w: 7.8, d: 6.4, tower: true, gifts: 3 },
                { gx: 0.75, gz: -9.55, y: 4.24, w: 8.8, d: 6.8, gifts: 3, enemyAxis: 'x' },
                { gx: 1.85, gz: -10.6, y: 4.58, w: 4.0, d: 4.0, kind: 'fragileIce', breakLimit: 0.33, gifts: 1 },
                { gx: 0.4, gz: -11.65, y: 4.96, w: 3.2, d: 9.8, kind: 'fireBridge', fireOffDuration: 0.6, fireCycle: 2.5, firePhase: 2.7, gifts: 0 },
                { gx: -0.9, gz: -12.7, y: 5.34, w: 5.2, d: 4.8, kind: 'woodDisk', axis: 'x', distance: 2.4, speed: 1.8, phase: 1.2, gifts: 1 },
                { gx: -2.0, gz: -13.8, y: 5.72, w: 7.8, d: 6.2, kind: 'shakyIce', speed: 10.2, phase: 2.0, shakePower: 0.4, gifts: 2, enemyAxis: 'both' },
                { gx: -0.6, gz: -14.9, y: 6.08, w: 5.0, d: 4.6, kind: 'bouncePad', bouncePower: 20.2, gifts: 1 },
                { gx: 0.85, gz: -16.0, y: 6.48, w: 8.4, d: 6.5, kind: 'slipperyIce', gifts: 3, enemyAxis: 'both' },
                { gx: 2.0, gz: -17.05, y: 6.86, w: 3.1, d: 9.8, kind: 'fireBridge', fireOffDuration: 0.58, fireCycle: 2.45, firePhase: 3.6, gifts: 0 },
                { gx: 0.75, gz: -18.1, y: 7.24, w: 4.2, d: 3.9, kind: 'fragileIce', breakLimit: 0.32, gifts: 1, enemyAxis: 'x' },
                { gx: -0.55, gz: -19.15, y: 7.62, w: 6.8, d: 5.8, kind: 'shakyIce', speed: 10.5, phase: 4.0, shakePower: 0.42, gifts: 2 },
                { gx: -1.65, gz: -20.2, y: 8.0, w: 8.6, d: 6.8, gifts: 3, enemyAxis: 'z' },
                { gx: -0.25, gz: -21.25, y: 8.42, w: 3.2, d: 10.0, kind: 'fireBridge', fireOffDuration: 0.56, fireCycle: 2.4, firePhase: 4.9, gifts: 0 },
                { gx: 1.2, gz: -22.35, y: 8.8, w: 5.0, d: 4.4, kind: 'bouncePad', bouncePower: 20.8, gifts: 1 },
                { gx: 2.15, gz: -23.45, y: 9.16, w: 4.2, d: 3.8, kind: 'fragileIce', breakLimit: 0.31, gifts: 1, enemyAxis: 'z' },
                { gx: 0.9, gz: -24.55, y: 9.54, w: 7.2, d: 6.2, kind: 'slipperyIce', gifts: 2, enemyAxis: 'both' },
                { gx: -0.35, gz: -25.65, y: 9.92, w: 3.4, d: 9.8, kind: 'fireBridge', fireOffDuration: 0.54, fireCycle: 2.35, firePhase: 2.1, gifts: 0 },
                { gx: -1.45, gz: -26.75, y: 10.28, w: 4.4, d: 4.0, kind: 'shakyIce', speed: 10.8, phase: 5.1, shakePower: 0.44, gifts: 1 },
                { gx: -0.2, gz: -27.85, y: 10.62, w: 5.0, d: 4.4, kind: 'fragileIce', breakLimit: 0.3, gifts: 1, enemyAxis: 'both' },
                { gx: 1.0, gz: -28.95, y: 10.98, w: 7.4, d: 5.8, tower: true, gifts: 2 },
                { gx: 0, gz: -30.1, y: 11.38, w: 10.2, d: 7.4, tower: true, gifts: 0 }
            ]
        ];

        return maps[levelIndex] ?? maps[maps.length - 1];
    }

    private loadSavedLevelIndex(): number {
        if (typeof window === 'undefined') return 0;

        try {
            const savedValue = window.localStorage.getItem(this.progressStorageKey);
            const savedIndex = Number.parseInt(savedValue ?? '', 10);
            if (!Number.isFinite(savedIndex)) return 0;
            return THREE.MathUtils.clamp(savedIndex, 0, this.publicStageTotal - 1);
        } catch {
            return 0;
        }
    }

    private saveLevelIndex(): void {
        if (typeof window === 'undefined') return;

        try {
            window.localStorage.setItem(this.progressStorageKey, this.currentLevelIndex.toString());
        } catch {
            // Storage can be unavailable in restricted iframe/private browsing contexts.
        }
    }

    private applyStageHazards(
        platforms: PlatformSpec[],
        enemies: EnemySpec[],
        levelIndex: number,
        spawn: [number, number],
        goal: [number, number]
    ): void {
        if (levelIndex >= 2) {
            const speedBoost = 1.34 + Math.min(levelIndex - 2, 5) * 0.13;
            platforms.forEach((platform) => {
                if (platform.kind !== 'woodDisk' && !platform.axis && !platform.moveX && !platform.moveZ) return;
                platform.speed = (platform.speed ?? 0.8) * speedBoost;
                if (platform.axis) platform.distance = (platform.distance ?? 1.1) + 0.15 + Math.min(levelIndex, 7) * 0.04;
            });
        }

        if (levelIndex >= 4 && enemies.length > 0 && levelIndex !== 13) {
            const chaserCount = levelIndex >= 14 ? 1 : levelIndex >= 12 ? 2 : levelIndex >= 6 ? 2 : 1;
            const step = Math.max(1, Math.floor(enemies.length / (chaserCount + 1)));
            for (let index = 0; index < chaserCount; index++) {
                const enemy = enemies[Math.min(enemies.length - 1, step * (index + 1))];
                enemy.chasing = true;
                enemy.chaseSpeed = levelIndex >= 12 ? 2.15 + (levelIndex - 12) * 0.16 + index * 0.14 : 2.05 + levelIndex * 0.18 + index * 0.22;
                enemy.chaseRange = levelIndex >= 12 ? 11 + (levelIndex - 12) * 0.9 : 9.5 + levelIndex * 0.85;
            }
        }

        if (levelIndex >= 5 && levelIndex !== 13) {
            const fragileCount = levelIndex >= 7 ? 2 : 1;
            const candidates = platforms.filter((platform) => (
                !platform.axis &&
                platform.kind !== 'woodDisk' &&
                platform.kind !== 'shakyIce' &&
                platform.kind !== 'fireBridge' &&
                platform.kind !== 'slipperyIce' &&
                platform.kind !== 'bouncePad' &&
                platform.w >= 8 &&
                platform.d >= 6 &&
                Math.hypot(platform.x - spawn[0], platform.z - spawn[1]) > 9 &&
                Math.hypot(platform.x - goal[0], platform.z - goal[1]) > 8
            ));

            for (let index = 0; index < Math.min(fragileCount, candidates.length); index++) {
                const candidate = candidates[Math.floor((candidates.length * (index + 1)) / (fragileCount + 1))];
                candidate.fragile = true;
                candidate.kind = 'fragileIce';
            }
        }

        if (levelIndex >= 7) {
            enemies.forEach((enemy) => {
                const lateStageEnemyTune = levelIndex >= 12;
                enemy.speed *= lateStageEnemyTune ? 0.96 : 1.12;
                enemy.distance += lateStageEnemyTune ? 0.12 : 0.28;
                if (enemy.chasing) enemy.chaseSpeed = (enemy.chaseSpeed ?? 2.6) + (lateStageEnemyTune ? 0.12 : 0.35);
            });
        }
    }

    private applyNightmareStageHazards(
        platforms: PlatformSpec[],
        enemies: EnemySpec[],
        levelIndex: number,
        spawn: [number, number],
        goal: [number, number]
    ): void {
        if (levelIndex < 12) return;

        const tier = levelIndex - 12;
        const extremeTier = Math.max(0, levelIndex - 13);
        platforms.forEach((platform, index) => {
            const nearSpawn = index === 0 || Math.hypot(platform.x - spawn[0], platform.z - spawn[1]) < 7.2;
            const nearGoal = index === platforms.length - 1 || Math.hypot(platform.x - goal[0], platform.z - goal[1]) < 5.6;

            if (platform.kind === 'fireBridge' || platform.fireBridge) {
                platform.kind = 'fireBridge';
                platform.fireBridge = true;
                const fireTiming = levelIndex >= 13
                    ? this.lateStageFireTiming(
                        levelIndex,
                        (platform.fireOffDuration ?? 3) - (0.7 + tier * 0.1 + extremeTier * 0.08),
                        (platform.fireCycle ?? 6.4) - (0.95 + tier * 0.1 + extremeTier * 0.12)
                    )
                    : {
                        offDuration: Math.max(1.05, (platform.fireOffDuration ?? 3) - (1.18 + tier * 0.22 + extremeTier * 0.22)),
                        cycle: Math.max(3.75, (platform.fireCycle ?? 6.4) - (1.65 + tier * 0.22 + extremeTier * 0.34))
                    };
                platform.fireOffDuration = fireTiming.offDuration;
                platform.fireCycle = fireTiming.cycle;
                platform.firePhase = (platform.firePhase ?? index * 0.43) + tier * 0.37;
            }

            if (platform.kind === 'fragileIce' || platform.fragile) {
                platform.kind = 'fragileIce';
                platform.fragile = true;
                const breakLimit = levelIndex >= 13
                    ? Math.min(platform.breakLimit ?? 1.48, Math.max(levelIndex >= 14 ? 0.52 : 0.68, 1.02 - tier * 0.05 - extremeTier * 0.07))
                    : Math.min(platform.breakLimit ?? 1.48, Math.max(levelIndex >= 14 ? 0.34 : 0.5, 0.82 - tier * 0.08 - extremeTier * 0.1));
                platform.breakLimit = this.lateStageBreakLimit(levelIndex, breakLimit);
            }

            if (platform.kind === 'shakyIce' || platform.shaky) {
                platform.kind = 'shakyIce';
                platform.shaky = true;
                platform.speed = (platform.speed ?? 5.8) + 1.2 + tier * 0.55 + extremeTier * 0.8;
                platform.shakePower = (platform.shakePower ?? 0.18) + 0.06 + tier * 0.025 + extremeTier * 0.035;
            }

            if (platform.kind === 'bouncePad' || platform.bouncePad) {
                platform.kind = 'bouncePad';
                platform.bouncePad = true;
                platform.bouncePower = (platform.bouncePower ?? 16) + 1.1 + tier * 0.65;
            }

            if (nearSpawn || nearGoal || platform.kind === 'woodDisk' || platform.kind === 'bouncePad') return;

            const isNarrowBridge = platform.w <= 4.6 && platform.d >= 8.4;
            const isWideSafePatch = platform.w >= 8 && platform.d >= 6.4;

            if (levelIndex > 14 && (isWideSafePatch || platform.tower) && index % 5 === 0) {
                platform.kind = 'shakyIce';
                platform.shaky = true;
                platform.speed = 8.2 + extremeTier * 0.9 + index * 0.035;
                platform.shakePower = 0.26 + extremeTier * 0.045;
                return;
            }

            if (levelIndex >= 14 && isWideSafePatch && index % 2 === 0) {
                platform.kind = 'fragileIce';
                platform.fragile = true;
                platform.breakLimit = this.lateStageBreakLimit(levelIndex, Math.max(0.52, 0.72 - extremeTier * 0.04));
                return;
            }

            if (isNarrowBridge && index % 2 === 1) {
                platform.kind = 'fireBridge';
                platform.fireBridge = true;
                const fireTiming = levelIndex >= 13
                    ? this.lateStageFireTiming(
                        levelIndex,
                        1.72 - tier * 0.05 - extremeTier * 0.04,
                        4.85 - tier * 0.08 - extremeTier * 0.08
                    )
                    : {
                        offDuration: Math.max(levelIndex >= 14 ? 0.76 : 1.0, 1.42 - tier * 0.12 - extremeTier * 0.16),
                        cycle: Math.max(levelIndex >= 14 ? 2.9 : 3.65, 4.25 - tier * 0.15 - extremeTier * 0.26)
                    };
                platform.fireOffDuration = fireTiming.offDuration;
                platform.fireCycle = fireTiming.cycle;
                platform.firePhase = index * 0.48 + tier * 0.55;
                return;
            }

            if (isWideSafePatch && index % (levelIndex === 12 ? 5 : 4) === 2) {
                platform.kind = 'fragileIce';
                platform.fragile = true;
                platform.breakLimit = this.lateStageBreakLimit(levelIndex, levelIndex === 12 ? 0.88 : 0.9);
                return;
            }

            if (isWideSafePatch && index % 3 === 0) {
                platform.kind = 'slipperyIce';
                platform.slippery = true;
            }
        });

        const extraChasersWanted = levelIndex === 12 ? 4 : levelIndex === 13 ? 5 : levelIndex === 14 ? 6 : 7;
        const enemyPlatforms = platforms
            .map((platform, index) => ({ platform, index }))
            .filter(({ platform, index }) => (
                index > 1 &&
                index < platforms.length - 1 &&
                platform.kind !== 'woodDisk' &&
                platform.kind !== 'fireBridge' &&
                platform.kind !== 'bouncePad' &&
                platform.kind !== 'fragileIce' &&
                platform.kind !== 'shakyIce' &&
                platform.w >= 5.4 &&
                platform.d >= 5.2 &&
                Math.hypot(platform.x - spawn[0], platform.z - spawn[1]) > 10 &&
                Math.hypot(platform.x - goal[0], platform.z - goal[1]) > 7
            ));

        for (let index = 0; index < Math.min(extraChasersWanted, enemyPlatforms.length); index++) {
            const chosen = enemyPlatforms[Math.floor(((index + 1) * enemyPlatforms.length) / (extraChasersWanted + 1))];
            if (!chosen) continue;

            const { platform } = chosen;
            const safeX = Math.max(0.45, platform.w / 2 - 1.4);
            const safeZ = Math.max(0.45, platform.d / 2 - 1.4);
            const axis: 'x' | 'z' = index % 2 === 0 ? 'x' : 'z';
            enemies.push({
                x: Number((platform.x + THREE.MathUtils.clamp(Math.sin(index * 1.7) * 1.1, -safeX, safeX)).toFixed(2)),
                z: Number((platform.z + THREE.MathUtils.clamp(Math.cos(index * 1.3) * 1.0, -safeZ, safeZ)).toFixed(2)),
                axis,
                distance: Math.min(axis === 'x' ? safeX : safeZ, 2.15 + tier * 0.18),
                speed: 1.75 + tier * 0.12 + index * 0.04,
                phase: index * 0.63,
                chasing: index < (levelIndex === 13 ? 1 : 2 + Math.min(tier, 1)),
                chaseSpeed: 2.45 + tier * 0.14 + index * 0.025,
                chaseRange: 11.5 + tier * 1.1
            });
        }

        enemies.forEach((enemy, index) => {
            enemy.speed *= 0.88 + tier * 0.04 + extremeTier * 0.03;
            enemy.distance += 0.18 + tier * 0.08 + extremeTier * 0.08;
            if (enemy.chasing) {
                enemy.chaseSpeed = (enemy.chaseSpeed ?? 2.7) + 0.2 + tier * 0.08 + extremeTier * 0.12;
                enemy.chaseRange = (enemy.chaseRange ?? 12) + 0.8 + tier * 0.45 + extremeTier * 0.5;
            } else if (index % 7 === 0 && levelIndex !== 13 && levelIndex < 14) {
                enemy.chasing = true;
                enemy.chaseSpeed = 2.45 + tier * 0.12 + extremeTier * 0.12;
                enemy.chaseRange = 11.5 + tier * 0.8 + extremeTier * 0.9;
            }
        });
    }

    private lateStageFireTiming(levelIndex: number, offDuration: number, cycle: number): { offDuration: number; cycle: number } {
        if (levelIndex < 13) return { offDuration, cycle };

        const floor = levelIndex >= 15
            ? { offDuration: 2.75, cycle: 6.6 }
            : levelIndex >= 14
                ? { offDuration: 2.85, cycle: 6.8 }
                : { offDuration: 2.25, cycle: 5.7 };
        const tunedOffDuration = Math.max(floor.offDuration, offDuration);
        const tunedCycle = Math.max(floor.cycle, cycle, tunedOffDuration + 3.05);
        return {
            offDuration: Number(tunedOffDuration.toFixed(2)),
            cycle: Number(tunedCycle.toFixed(2))
        };
    }

    private lateStageBreakLimit(levelIndex: number, breakLimit: number): number {
        if (levelIndex < 13) return breakLimit;

        const minimum = levelIndex >= 15 ? 1.25 : levelIndex >= 14 ? 1.45 : 1.55;
        return Number(Math.max(minimum, breakLimit).toFixed(2));
    }

    private removeStageFourteenFireApproachEnemies(platforms: PlatformSpec[], enemies: EnemySpec[], levelIndex: number): void {
        if (levelIndex !== 13) return;

        const fireBridges = platforms.filter((platform) => platform.kind === 'fireBridge' || platform.fireBridge);
        if (fireBridges.length === 0) return;

        for (let index = enemies.length - 1; index >= 0; index--) {
            const enemy = enemies[index];
            const nearFireBridge = fireBridges.some((platform) => Math.hypot(enemy.x - platform.x, enemy.z - platform.z) <= 11.8);
            if (nearFireBridge) enemies.splice(index, 1);
        }
    }

    private spreadGiftPositions(gifts: Array<[number, number]>, platforms: PlatformSpec[]): Array<[number, number]> {
        const placed: Array<[number, number]> = [];
        const minimumDistance = 3.15;

        gifts.forEach(([startX, startZ], index) => {
            const platform = this.findPlatformForPoint(platforms, startX, startZ);
            const candidates: Array<[number, number]> = [[startX, startZ]];

            if (platform) {
                const safeX = Math.max(0.4, platform.w / 2 - 1.55);
                const safeZ = Math.max(0.4, platform.d / 2 - 1.55);
                const ring = 1.65 + (index % 3) * 0.55;
                const angles = [0, Math.PI / 2, Math.PI, Math.PI * 1.5, Math.PI / 4, Math.PI * 0.75, Math.PI * 1.25, Math.PI * 1.75];
                angles.forEach((angle) => {
                    candidates.push([
                        platform.x + THREE.MathUtils.clamp(startX - platform.x + Math.cos(angle) * ring, -safeX, safeX),
                        platform.z + THREE.MathUtils.clamp(startZ - platform.z + Math.sin(angle) * ring, -safeZ, safeZ)
                    ]);
                });
            }

            const best = candidates.find(([x, z]) => placed.every(([otherX, otherZ]) => Math.hypot(x - otherX, z - otherZ) >= minimumDistance));
            placed.push(best ?? candidates[0]);
        });

        return placed;
    }

    private findPlatformForPoint(platforms: PlatformSpec[], x: number, z: number): PlatformSpec | undefined {
        let match: PlatformSpec | undefined;
        platforms.forEach((platform) => {
            const inside =
                Math.abs(x - platform.x) <= platform.w / 2 &&
                Math.abs(z - platform.z) <= platform.d / 2;
            if (!inside) return;
            if (!match || platform.y > match.y) match = platform;
        });
        return match;
    }

    private rebuildMinimap(): void {
        if (this.platforms.length === 0) return;

        let minX = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let minZ = Number.POSITIVE_INFINITY;
        let maxZ = Number.NEGATIVE_INFINITY;

        this.platforms.forEach((platform) => {
            minX = Math.min(minX, platform.x - platform.w / 2);
            maxX = Math.max(maxX, platform.x + platform.w / 2);
            minZ = Math.min(minZ, platform.z - platform.d / 2);
            maxZ = Math.max(maxZ, platform.z + platform.d / 2);
        });

        this.gifts.forEach((gift) => {
            minX = Math.min(minX, gift.x);
            maxX = Math.max(maxX, gift.x);
            minZ = Math.min(minZ, gift.z);
            maxZ = Math.max(maxZ, gift.z);
        });

        this.hearts.forEach((heart) => {
            minX = Math.min(minX, heart.x);
            maxX = Math.max(maxX, heart.x);
            minZ = Math.min(minZ, heart.z);
            maxZ = Math.max(maxZ, heart.z);
        });

        this.checkpoints.forEach((checkpoint) => {
            minX = Math.min(minX, checkpoint.x);
            maxX = Math.max(maxX, checkpoint.x);
            minZ = Math.min(minZ, checkpoint.z);
            maxZ = Math.max(maxZ, checkpoint.z);
        });

        minX = Math.min(minX, this.spawn.x, this.goalPosition.x) - 4;
        maxX = Math.max(maxX, this.spawn.x, this.goalPosition.x) + 4;
        minZ = Math.min(minZ, this.spawn.z, this.goalPosition.z) - 4;
        maxZ = Math.max(maxZ, this.spawn.z, this.goalPosition.z) + 4;
        this.minimapBounds = { minX, maxX, minZ, maxZ };

        this.minimapPlatforms = this.platforms.map((platform) => {
            const left = this.mapToMinimapX(platform.x - platform.w / 2);
            const right = this.mapToMinimapX(platform.x + platform.w / 2);
            const top = this.mapToMinimapY(platform.z - platform.d / 2);
            const bottom = this.mapToMinimapY(platform.z + platform.d / 2);
            return {
                left,
                top,
                width: Math.max(1.4, right - left),
                height: Math.max(1.4, bottom - top),
                kind: platform.kind
            };
        });

        this.minimapGifts = this.gifts.map((gift) => ({
            x: this.mapToMinimapX(gift.x),
            y: this.mapToMinimapY(gift.z),
            collected: gift.collected
        }));
        this.minimapHearts = this.hearts.map((heart) => ({
            x: this.mapToMinimapX(heart.x),
            y: this.mapToMinimapY(heart.z),
            collected: heart.collected
        }));
        this.minimapCheckpoints = this.checkpoints.map((checkpoint) => ({
            x: this.mapToMinimapX(checkpoint.x),
            y: this.mapToMinimapY(checkpoint.z),
            collected: checkpoint.reached
        }));
        this.minimapGoal = {
            x: this.mapToMinimapX(this.goalPosition.x),
            y: this.mapToMinimapY(this.goalPosition.z)
        };
        this.updateMinimap();
    }

    private updateMinimap(): void {
        this.minimapPlayer = {
            x: this.mapToMinimapX(this.playerPosition.x),
            y: this.mapToMinimapY(this.playerPosition.z)
        };
        this.minimapGifts.forEach((marker, index) => {
            marker.collected = this.gifts[index]?.collected ?? marker.collected;
        });
        this.minimapHearts.forEach((marker, index) => {
            marker.collected = this.hearts[index]?.collected ?? marker.collected;
        });
        this.minimapCheckpoints.forEach((marker, index) => {
            marker.collected = this.checkpoints[index]?.reached ?? marker.collected;
        });
    }

    private mapToMinimapX(x: number): number {
        const width = Math.max(1, this.minimapBounds.maxX - this.minimapBounds.minX);
        return THREE.MathUtils.clamp(((x - this.minimapBounds.minX) / width) * 100, 0, 100);
    }

    private mapToMinimapY(z: number): number {
        const depth = Math.max(1, this.minimapBounds.maxZ - this.minimapBounds.minZ);
        return THREE.MathUtils.clamp(((z - this.minimapBounds.minZ) / depth) * 100, 0, 100);
    }

    private teleportPlayerToMapPoint(x: number, z: number): boolean {
        const target = this.closestPlayablePoint(x, z);
        if (!target) return false;
        this.playerPosition.set(target.x, target.y, target.z);
        this.playerVelocity.set(0, 0, 0);
        this.slideDrift.set(0, 0);
        this.airJumpsUsed = 0;
        this.clearControls();
        this.coyoteTimer = 0.12;
        this.banner = 'ADMIN WARP';
        this.bannerDetail = 'Map double-click teleport';
        this.bannerTone = 'normal';
        this.damageBannerTimer = 0;
        this.updateHud();
        this.updateCamera();
        this.changeDetector.detectChanges();
        return true;
    }

    private closestPlayablePoint(x: number, z: number): { x: number; y: number; z: number } | undefined {
        let best: { x: number; y: number; z: number; distance: number } | undefined;
        this.platforms.forEach((platform) => {
            if (platform.broken || !platform.mesh.visible) return;
            const halfW = Math.max(0.08, platform.w / 2 - 0.32);
            const halfD = Math.max(0.08, platform.d / 2 - 0.32);
            const safeX = THREE.MathUtils.clamp(x, platform.x - halfW, platform.x + halfW);
            const safeZ = THREE.MathUtils.clamp(z, platform.z - halfD, platform.z + halfD);
            const distance = Math.hypot(x - safeX, z - safeZ);
            const y = platform.y + platform.h * 0.7;
            if (!best || distance < best.distance || (distance === best.distance && y > best.y)) {
                best = { x: safeX, y, z: safeZ, distance };
            }
        });
        return best ? { x: best.x, y: best.y, z: best.z } : undefined;
    }

    private addPlatform(x: number, y: number, z: number, w: number, h: number, d: number, options: Partial<Platform> = {}): void {
        const group = new THREE.Group();
        group.position.set(x, y, z);
        const isWoodDisk = options.kind === 'woodDisk' || !!options.axis || options.moveX !== undefined || options.moveZ !== undefined;
        const isFragile = !!options.fragile || options.kind === 'fragileIce';
        const isShaky = !!options.shaky || options.kind === 'shakyIce';
        const isSlippery = !!options.slippery || options.kind === 'slipperyIce';
        const isFireBridge = !!options.fireBridge || options.kind === 'fireBridge';
        const isBouncePad = !!options.bouncePad || options.kind === 'bouncePad';
        const visualW = Math.max(1.4, w - 0.72);
        const visualD = Math.max(1.4, d - 0.72);

        if (isWoodDisk) {
            this.addWoodDiskVisual(group, visualW, h, visualD);
            this.world.add(group);
            this.platforms.push({
                mesh: group,
                x,
                y,
                z,
                w,
                h,
                d,
                startX: x,
                startY: y,
                startZ: z,
                axis: options.axis,
                distance: options.distance,
                moveX: options.moveX,
                moveZ: options.moveZ,
                speed: options.speed,
                phase: options.phase || 0,
                kind: options.kind ?? 'woodDisk',
                shaky: isShaky,
                shakePower: options.shakePower,
                slippery: isSlippery,
                fireBridge: isFireBridge,
                fireCycle: options.fireCycle,
                fireOffDuration: options.fireOffDuration,
                firePhase: options.firePhase,
                bouncePad: isBouncePad,
                bouncePower: isBouncePad ? options.bouncePower ?? 15.5 : undefined,
                tower: !!options.tower,
                broken: false
            });
            return;
        }

        const body = new THREE.Mesh(new THREE.BoxGeometry(visualW * 0.96, h * 0.88, visualD * 0.96), isSlippery ? this.iceMaterial : this.sideMaterial);
        body.position.y = -h * 0.1;
        body.castShadow = true;
        body.receiveShadow = true;
        group.add(body);

        const snowBase = new THREE.Mesh(new THREE.BoxGeometry(visualW, h * 0.16, visualD), isSlippery ? this.slipperyIceMaterial : this.snowMaterial);
        snowBase.position.y = h * 0.42;
        snowBase.castShadow = true;
        snowBase.receiveShadow = true;
        group.add(snowBase);

        const pathWidth = Math.max(1.6, visualW - 1.15);
        const pathDepth = Math.max(1.6, visualD - 1.05);
        if (isSlippery) {
            const iceSheet = new THREE.Mesh(new THREE.BoxGeometry(pathWidth, h * 0.09, pathDepth), this.slipperyIceMaterial);
            iceSheet.position.y = h * 0.58;
            iceSheet.castShadow = true;
            iceSheet.receiveShadow = true;
            group.add(iceSheet);
            this.addSlipperyIceVisual(group, pathWidth, pathDepth, h * 0.69);
        } else {
            const pathBed = new THREE.Mesh(new THREE.BoxGeometry(pathWidth, h * 0.08, pathDepth), this.stoneLineMaterial);
            pathBed.position.y = h * 0.55;
            pathBed.castShadow = true;
            pathBed.receiveShadow = true;
            group.add(pathBed);
            this.addStonePattern(group, pathWidth, pathDepth, h * 0.63);
        }

        const edgeHeight = h * 0.16;
        const edgeY = h * 0.62;
        [
            { width: visualW, depth: 0.35, x: 0, z: visualD / 2 - 0.18 },
            { width: visualW, depth: 0.35, x: 0, z: -visualD / 2 + 0.18 },
            { width: 0.35, depth: visualD, x: -visualW / 2 + 0.18, z: 0 },
            { width: 0.35, depth: visualD, x: visualW / 2 - 0.18, z: 0 }
        ].forEach((edge) => {
            const snowEdge = new THREE.Mesh(new THREE.BoxGeometry(edge.width, edgeHeight, edge.depth), isSlippery ? this.slipperyIceMaterial : this.snowMaterial);
            snowEdge.position.set(edge.x, edgeY, edge.z);
            snowEdge.castShadow = true;
            snowEdge.receiveShadow = true;
            group.add(snowEdge);
        });

        const side = new THREE.Mesh(new THREE.BoxGeometry(visualW * 0.9, h * 0.28, visualD * 0.92), this.iceMaterial);
        side.position.y = -h * 0.36;
        side.castShadow = true;
        side.receiveShadow = true;
        group.add(side);

        const skirt = new THREE.Mesh(new THREE.BoxGeometry(visualW * 0.92, h * 0.12, visualD * 0.92), this.woodMaterial);
        skirt.position.y = -h * 0.52;
        skirt.receiveShadow = true;
        group.add(skirt);
        if (options.tower) this.addTowerPillarVisual(group, y, h, visualW, visualD);
        const warningGroup = isFragile ? this.addFragileIceVisual(group, visualW, h, visualD) : undefined;
        const shakeGroup = isShaky ? this.addShakyTileVisual(group, visualW, h, visualD) : undefined;
        const fireGroup = isFireBridge ? this.addFireBridgeVisual(group, visualW, h, visualD) : undefined;
        const bounceGroup = isBouncePad ? this.addBouncePadVisual(group, visualW, h, visualD) : undefined;

        this.world.add(group);
        this.platforms.push({
            mesh: group,
            x,
            y,
            z,
            w,
            h,
            d,
            startX: x,
            startY: y,
            startZ: z,
            axis: options.axis,
            distance: options.distance,
            moveX: options.moveX,
            moveZ: options.moveZ,
            speed: options.speed,
            phase: options.phase || 0,
            kind: isFragile ? 'fragileIce' : isFireBridge ? 'fireBridge' : isShaky ? 'shakyIce' : isBouncePad ? 'bouncePad' : isSlippery ? 'slipperyIce' : options.kind ?? 'ice',
            shaky: isShaky,
            shakePower: options.shakePower ?? (isShaky ? 0.16 : undefined),
            shakeGroup,
            slippery: isSlippery,
            fireBridge: isFireBridge,
            fireCycle: isFireBridge ? options.fireCycle ?? 6.4 : undefined,
            fireOffDuration: isFireBridge ? options.fireOffDuration ?? 3 : undefined,
            firePhase: isFireBridge ? options.firePhase ?? options.phase ?? 0 : undefined,
            fireGroup,
            bouncePad: isBouncePad,
            bouncePower: isBouncePad ? options.bouncePower ?? 15.5 : undefined,
            bounceGroup,
            tower: !!options.tower,
            fragile: isFragile,
            fragileTimer: 0,
            breakLimit: isFragile ? options.breakLimit ?? 1.48 : undefined,
            broken: false,
            respawnTimer: 0,
            warningGroup
        });
    }

    private addFragileIceVisual(group: THREE.Group, visualW: number, h: number, visualD: number): THREE.Group {
        const cracks = new THREE.Group();
        cracks.visible = false;

        const glowMaterial = new THREE.MeshBasicMaterial({ color: 0x6ee7ff, transparent: true, opacity: 0.12, depthWrite: false });
        const crackMaterial = new THREE.MeshBasicMaterial({ color: 0xff3d4c, transparent: true, opacity: 0.2, depthWrite: false });
        const plate = new THREE.Mesh(new THREE.BoxGeometry(visualW * 0.72, 0.045, visualD * 0.64), glowMaterial);
        plate.position.y = h * 0.75;
        cracks.add(plate);

        [
            { x: -0.22, z: 0, w: visualW * 0.46, d: 0.08, r: 0.25 },
            { x: 0.2, z: -0.34, w: visualW * 0.32, d: 0.07, r: -0.45 },
            { x: 0.16, z: 0.42, w: visualW * 0.28, d: 0.07, r: 0.68 },
            { x: -0.58, z: 0.24, w: visualW * 0.18, d: 0.07, r: -0.7 }
        ].forEach((piece) => {
            const crack = new THREE.Mesh(new THREE.BoxGeometry(piece.w, 0.055, piece.d), crackMaterial.clone());
            crack.position.set(piece.x, h * 0.8, piece.z);
            crack.rotation.y = piece.r;
            cracks.add(crack);
        });

        group.add(cracks);
        return cracks;
    }

    private addShakyTileVisual(group: THREE.Group, visualW: number, h: number, visualD: number): THREE.Group {
        const marker = new THREE.Group();
        const stripMaterial = this.shakyWarningMaterial.clone();
        const stripDepth = Math.min(0.22, visualD * 0.12);
        const stripWidth = Math.min(0.22, visualW * 0.12);

        [
            { width: visualW * 0.74, depth: stripDepth, x: 0, z: visualD * 0.32 },
            { width: visualW * 0.74, depth: stripDepth, x: 0, z: -visualD * 0.32 },
            { width: stripWidth, depth: visualD * 0.58, x: visualW * 0.34, z: 0 },
            { width: stripWidth, depth: visualD * 0.58, x: -visualW * 0.34, z: 0 }
        ].forEach((strip) => {
            const mesh = new THREE.Mesh(new THREE.BoxGeometry(strip.width, 0.05, strip.depth), stripMaterial.clone());
            mesh.position.set(strip.x, h * 0.83, strip.z);
            marker.add(mesh);
        });

        const center = new THREE.Mesh(new THREE.BoxGeometry(visualW * 0.26, 0.06, visualD * 0.2), this.emberMaterial.clone());
        center.position.y = h * 0.86;
        marker.add(center);
        group.add(marker);
        return marker;
    }

    private addSlipperyIceVisual(group: THREE.Group, visualW: number, visualD: number, y: number): void {
        const baseGlow = new THREE.Mesh(new THREE.BoxGeometry(visualW * 0.92, 0.035, visualD * 0.9), this.iceGlareMaterial.clone());
        baseGlow.position.y = y + 0.01;
        group.add(baseGlow);

        [
            { x: -visualW * 0.18, z: -visualD * 0.16, w: 0.12, d: visualD * 0.68, r: -0.42, material: this.iceGlareMaterial },
            { x: visualW * 0.18, z: visualD * 0.05, w: 0.1, d: visualD * 0.5, r: 0.48, material: this.iceGlareMaterial },
            { x: -visualW * 0.28, z: visualD * 0.28, w: visualW * 0.36, d: 0.08, r: 0.18, material: this.iceVeinMaterial },
            { x: visualW * 0.22, z: -visualD * 0.32, w: visualW * 0.28, d: 0.075, r: -0.22, material: this.iceVeinMaterial },
            { x: 0, z: 0, w: visualW * 0.52, d: 0.055, r: 0.68, material: this.iceVeinMaterial }
        ].forEach((piece) => {
            const glint = new THREE.Mesh(new THREE.BoxGeometry(piece.w, 0.045, piece.d), piece.material.clone());
            glint.position.set(piece.x, y, piece.z);
            glint.rotation.y = piece.r;
            group.add(glint);
        });

        [
            { x: -visualW * 0.34, z: -visualD * 0.38 },
            { x: visualW * 0.32, z: visualD * 0.36 },
            { x: visualW * 0.08, z: -visualD * 0.05 }
        ].forEach((spot, index) => {
            const sparkle = new THREE.Mesh(new THREE.BoxGeometry(0.18 + index * 0.04, 0.05, 0.18 + index * 0.04), this.iceGlareMaterial.clone());
            sparkle.position.set(spot.x, y + 0.035, spot.z);
            sparkle.rotation.y = Math.PI / 4;
            group.add(sparkle);
        });
    }

    private addTowerPillarVisual(group: THREE.Group, platformY: number, h: number, visualW: number, visualD: number): void {
        const bottomWorldY = -0.65;
        const topLocalY = -h * 0.46;
        const bottomLocalY = bottomWorldY - platformY;
        const pillarHeight = Math.max(1.1, topLocalY - bottomLocalY);
        const pillarW = Math.min(visualW * 0.5, 3.6);
        const pillarD = Math.min(visualD * 0.5, 3.6);
        const pillar = new THREE.Mesh(new THREE.BoxGeometry(pillarW, pillarHeight, pillarD), this.sideMaterial);
        pillar.position.y = bottomLocalY + pillarHeight / 2;
        pillar.castShadow = true;
        pillar.receiveShadow = true;
        group.add(pillar);

        const bandCount = Math.max(3, Math.floor(pillarHeight / 0.72));
        for (let index = 0; index < bandCount; index++) {
            const t = (index + 0.5) / bandCount;
            const band = new THREE.Mesh(new THREE.BoxGeometry(pillarW * 1.08, 0.08, pillarD * 1.08), this.stoneLineMaterial);
            band.position.y = bottomLocalY + pillarHeight * t;
            band.castShadow = true;
            band.receiveShadow = true;
            group.add(band);
        }

        [
            { x: -pillarW * 0.58, z: -pillarD * 0.58 },
            { x: pillarW * 0.58, z: -pillarD * 0.58 },
            { x: -pillarW * 0.58, z: pillarD * 0.58 },
            { x: pillarW * 0.58, z: pillarD * 0.58 }
        ].forEach((corner) => {
            const cap = new THREE.Mesh(new THREE.BoxGeometry(0.24, pillarHeight * 0.92, 0.24), this.iceMaterial);
            cap.position.set(corner.x, bottomLocalY + pillarHeight * 0.46, corner.z);
            cap.castShadow = true;
            cap.receiveShadow = true;
            group.add(cap);
        });

        const lamp = new THREE.PointLight(0xffe58f, 0.55, 5.2);
        lamp.position.set(0, h * 0.78, Math.min(visualD * 0.28, 1.7));
        group.add(lamp);
    }

    private addBouncePadVisual(group: THREE.Group, visualW: number, h: number, visualD: number): THREE.Group {
        const pad = new THREE.Group();
        const padW = Math.min(visualW * 0.7, 4.6);
        const padD = Math.min(visualD * 0.7, 4.6);

        const base = new THREE.Mesh(new THREE.BoxGeometry(padW, 0.12, padD), this.bouncePadMaterial);
        base.position.y = h * 0.83;
        base.castShadow = true;
        pad.add(base);

        const core = new THREE.Mesh(new THREE.BoxGeometry(padW * 0.54, 0.07, padD * 0.54), this.bouncePadCoreMaterial.clone());
        core.position.y = h * 0.93;
        pad.add(core);

        const arrow = new THREE.Mesh(new THREE.ConeGeometry(0.46, 0.8, 4), this.bouncePadCoreMaterial.clone());
        arrow.position.y = h * 1.35;
        arrow.rotation.y = Math.PI / 4;
        pad.add(arrow);

        const light = new THREE.PointLight(0x55f7a0, 0.95, 5.5);
        light.position.y = h * 1.15;
        pad.add(light);

        pad.userData = { arrow, light };
        group.add(pad);
        return pad;
    }

    private addFireBridgeVisual(group: THREE.Group, visualW: number, h: number, visualD: number): THREE.Group {
        const flames = new THREE.Group();
        flames.visible = false;

        const coal = new THREE.Mesh(new THREE.BoxGeometry(visualW * 0.72, 0.08, visualD * 0.72), this.meteorMaterial);
        coal.position.y = h * 0.82;
        group.add(coal);

        const columns = Math.max(3, Math.floor(visualD / 2.2));
        for (let index = 0; index < columns; index++) {
            const z = -visualD * 0.34 + (visualD * 0.68 * index) / Math.max(1, columns - 1);
            [-visualW * 0.28, visualW * 0.28].forEach((x, sideIndex) => {
                const flame = new THREE.Group();
                flame.position.set(x, h * 0.96, z);
                flame.userData['baseY'] = flame.position.y;
                flame.userData['phase'] = index * 0.7 + sideIndex * 1.3;

                const outer = new THREE.Mesh(new THREE.ConeGeometry(0.26, 0.82, 4), this.fireMaterial.clone());
                outer.position.y = 0.32;
                outer.rotation.y = Math.PI / 4;
                flame.add(outer);

                const core = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.56, 4), this.fireCoreMaterial.clone());
                core.position.y = 0.32;
                core.rotation.y = Math.PI / 4;
                flame.add(core);

                flames.add(flame);
            });
        }

        const light = new THREE.PointLight(0xff6a1f, 1.25, Math.max(4, visualD * 0.7));
        light.position.y = h * 1.25;
        light.userData['baseY'] = light.position.y;
        flames.add(light);

        group.add(flames);
        return flames;
    }

    private addWoodDiskVisual(group: THREE.Group, visualW: number, h: number, visualD: number): void {
        const radius = Math.max(1.55, Math.min(visualW, visualD) / 2);
        const side = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius * 0.94, h * 0.62, 12), this.woodMaterial);
        side.position.y = -h * 0.04;
        side.castShadow = true;
        side.receiveShadow = true;
        group.add(side);

        const darkBowl = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.98, radius * 0.82, h * 0.24, 12), this.woodDarkMaterial);
        darkBowl.position.y = -h * 0.43;
        darkBowl.castShadow = true;
        darkBowl.receiveShadow = true;
        group.add(darkBowl);

        const top = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.98, radius * 0.98, h * 0.14, 12), this.woodTopMaterial);
        top.position.y = h * 0.32;
        top.castShadow = true;
        top.receiveShadow = true;
        group.add(top);

        for (let index = -2; index <= 2; index++) {
            const plank = new THREE.Mesh(new THREE.BoxGeometry(radius * 0.34, h * 0.08, radius * 1.55), index % 2 === 0 ? this.woodTopMaterial : this.woodRingMaterial);
            plank.position.set(index * radius * 0.34, h * 0.43, 0);
            plank.castShadow = true;
            plank.receiveShadow = true;
            group.add(plank);
        }

        [-1.5, -0.5, 0.5, 1.5].forEach((offset) => {
            const seam = new THREE.Mesh(new THREE.BoxGeometry(0.045, h * 0.09, radius * 1.55), this.woodDarkMaterial);
            seam.position.set(offset * radius * 0.34, h * 0.49, 0);
            seam.castShadow = true;
            group.add(seam);
        });

        [0.42, 0.68, 0.9].forEach((scale, index) => {
            const ring = new THREE.Mesh(new THREE.TorusGeometry(radius * scale, 0.025 + index * 0.005, 4, 12), this.woodDarkMaterial);
            ring.rotation.x = Math.PI / 2;
            ring.position.y = h * 0.53 + index * 0.012;
            group.add(ring);
        });
    }

    private addStonePattern(group: THREE.Group, w: number, d: number, y: number): void {
        const rowCount = Math.max(3, Math.floor(d / 0.58));
        const columnCount = Math.max(3, Math.floor(w / 0.62));
        const cellW = w / columnCount;
        const cellD = d / rowCount;

        for (let row = 0; row < rowCount; row++) {
            for (let column = 0; column < columnCount; column++) {
                const wobble = Math.sin((row + 1) * 13.13 + (column + 2) * 7.71);
                const width = cellW * (0.68 + Math.abs(wobble) * 0.22);
                const depth = cellD * (0.64 + Math.abs(Math.cos(wobble * 4.2)) * 0.2);
                const stone = new THREE.Mesh(
                    new THREE.BoxGeometry(width, 0.06 + Math.abs(wobble) * 0.025, depth),
                    this.stoneMaterials[(row + column * 2) % this.stoneMaterials.length]
                );
                const offsetX = Math.sin(row * 4.1 + column) * cellW * 0.07;
                const offsetZ = Math.cos(column * 5.3 + row) * cellD * 0.07;
                stone.position.set(
                    -w / 2 + cellW * (column + 0.5) + offsetX,
                    y + Math.abs(wobble) * 0.014,
                    -d / 2 + cellD * (row + 0.5) + offsetZ
                );
                stone.rotation.y = wobble * 0.08;
                stone.castShadow = true;
                stone.receiveShadow = true;
                group.add(stone);
            }
        }
    }

    private addGift(x: number, z: number): void {
        const top = this.platformTopAt(x, z) ?? 0;
        const group = this.createGift();
        const y = top + 0.72;
        group.position.set(x, y, z);
        this.world.add(group);
        this.gifts.push({ mesh: group, x, y, z, baseX: x, baseY: y, baseZ: z, collected: false });
        this.giftTotal = this.gifts.length;
    }

    private addCoinsForLevel(): void {
        const candidates = this.platforms.filter((platform) => (
            platform.kind !== 'woodDisk' &&
            platform.kind !== 'fireBridge' &&
            platform.w >= 5.4 &&
            platform.d >= 4.8
        ));
        const desired = Math.min(18 + this.currentLevelIndex * 4, Math.max(10, candidates.length * 2));

        for (let index = 0; index < desired; index++) {
            const platform = candidates[(index * 3 + this.currentLevelIndex) % candidates.length];
            if (!platform) continue;
            const ring = index % 3;
            const safeX = Math.max(0.9, platform.w / 2 - 1.25);
            const safeZ = Math.max(0.9, platform.d / 2 - 1.25);
            const offsetX = THREE.MathUtils.clamp(Math.sin(index * 1.7 + this.currentLevelIndex) * (1.2 + ring * 0.75), -safeX, safeX);
            const offsetZ = THREE.MathUtils.clamp(Math.cos(index * 1.23 + this.currentLevelIndex * 0.7) * (1.0 + ring * 0.7), -safeZ, safeZ);
            const x = platform.x + offsetX;
            const z = platform.z + offsetZ;
            const tooCloseToGift = this.gifts.some((gift) => Math.hypot(gift.x - x, gift.z - z) < 1.35);
            if (tooCloseToGift) continue;
            this.addCoin(x, z);
        }
    }

    private addCoin(x: number, z: number): void {
        const top = this.platformTopAt(x, z) ?? 0;
        const group = this.createCoin();
        const y = top + 0.62;
        group.position.set(x, y, z);
        this.world.add(group);
        this.coins.push({ mesh: group, x, y, z, baseX: x, baseY: y, baseZ: z, collected: false });
        this.coinTotal = this.coins.length;
    }

    private addHeartsForLevel(goal: [number, number]): void {
        const candidates = this.platforms.filter((platform) => (
            platform.kind !== 'woodDisk' &&
            platform.kind !== 'shakyIce' &&
            platform.kind !== 'fireBridge' &&
            platform.kind !== 'slipperyIce' &&
            !platform.fragile &&
            platform.w >= 7.6 &&
            platform.d >= 6 &&
            Math.hypot(platform.x - this.spawn.x, platform.z - this.spawn.z) > 10 &&
            Math.hypot(platform.x - goal[0], platform.z - goal[1]) > 8
        ));
        const desired = this.currentLevelIndex >= 14
            ? 0
            : this.currentLevelIndex >= 12
                ? 1
                : Math.min(2 + Math.floor(this.currentLevelIndex / 2), 4);

        for (let index = 0; index < desired; index++) {
            const platform = candidates[Math.floor(((index + 1) * candidates.length) / (desired + 1))];
            if (!platform) continue;

            const safeX = Math.max(0.7, platform.w / 2 - 1.7);
            const safeZ = Math.max(0.7, platform.d / 2 - 1.7);
            const offsetX = THREE.MathUtils.clamp(Math.sin((index + 1) * 2.4 + this.currentLevelIndex) * safeX * 0.55, -safeX, safeX);
            const offsetZ = THREE.MathUtils.clamp(Math.cos((index + 1) * 1.9 + this.currentLevelIndex * 0.6) * safeZ * 0.48, -safeZ, safeZ);
            const x = Number((platform.x + offsetX).toFixed(2));
            const z = Number((platform.z + offsetZ).toFixed(2));
            const blocked = (
                this.gifts.some((gift) => Math.hypot(gift.x - x, gift.z - z) < 1.5) ||
                this.coins.some((coin) => Math.hypot(coin.x - x, coin.z - z) < 1.25) ||
                this.checkpoints.some((checkpoint) => Math.hypot(checkpoint.x - x, checkpoint.z - z) < 2.2) ||
                this.hearts.some((heart) => Math.hypot(heart.x - x, heart.z - z) < 5)
            );
            if (blocked) continue;
            this.addHeart(x, z);
        }
    }

    private addHeart(x: number, z: number): void {
        const top = this.platformTopAt(x, z) ?? 0;
        const group = this.createHeart();
        group.position.set(x, top + 0.76, z);
        this.world.add(group);
        this.hearts.push({ mesh: group, x, y: top + 0.76, z, collected: false });
    }

    private addCheckpoint(x: number, z: number, index: number): void {
        const top = this.platformTopAt(x, z) ?? 0;
        const group = this.createCheckpoint(index);
        group.position.set(x, top + 0.12, z);
        this.world.add(group);
        this.checkpoints.push({ mesh: group, x, y: top, z, index, reached: false });
    }

    private addEnemy(x: number, z: number, axis: 'x' | 'z', distance: number, speed: number, phase = 0, options: Partial<Enemy> = {}): void {
        const y = (this.platformTopAt(x, z) ?? 0) + 0.52;
        const mesh = this.createSnowman();
        mesh.position.set(x, y, z);
        this.world.add(mesh);
        this.enemies.push({
            mesh,
            x,
            z,
            baseX: x,
            baseZ: z,
            y,
            axis,
            distance,
            speed,
            phase,
            chasing: options.chasing,
            chaseSpeed: options.chaseSpeed,
            chaseRange: options.chaseRange
        });
    }

    private addGoal(x: number, z: number): void {
        const top = this.platformTopAt(x, z) ?? 9.7;
        this.goal = new THREE.Group();
        this.goal.position.set(x, top + 0.08, z);

        const base = new THREE.Mesh(new THREE.CylinderGeometry(1.35, 1.55, 0.22, 8), this.stoneMaterials[1]);
        base.position.y = 0.08;
        base.castShadow = true;
        base.receiveShadow = true;
        this.goal.add(base);

        const baseSnow = new THREE.Mesh(new THREE.CylinderGeometry(1.45, 1.45, 0.1, 8), this.snowMaterial);
        baseSnow.position.y = 0.24;
        baseSnow.castShadow = true;
        this.goal.add(baseSnow);

        const outerRing = new THREE.Mesh(new THREE.TorusGeometry(1.05, 0.1, 4, 4), this.portalRingMaterial);
        outerRing.position.y = 1.55;
        outerRing.rotation.z = Math.PI / 4;
        outerRing.castShadow = true;
        this.goal.add(outerRing);

        const innerRing = new THREE.Mesh(new THREE.TorusGeometry(0.72, 0.06, 4, 4), this.portalInnerMaterial);
        innerRing.position.y = 1.55;
        innerRing.rotation.z = Math.PI / 4;
        innerRing.castShadow = true;
        this.goal.add(innerRing);

        const closedSeal = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.18, 0.14), this.portalClosedMaterial);
        closedSeal.position.y = 1.55;
        closedSeal.rotation.z = Math.PI / 4;
        closedSeal.castShadow = true;
        this.goal.add(closedSeal);

        const core = new THREE.Mesh(new THREE.PlaneGeometry(1.28, 1.28), this.portalCoreMaterial);
        core.position.y = 1.55;
        core.rotation.z = Math.PI / 4;
        core.visible = false;
        this.goal.add(core);

        const sparks: THREE.Mesh[] = [];
        for (let index = 0; index < 10; index++) {
            const spark = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, 0.08), index % 2 === 0 ? this.portalRingMaterial : this.portalInnerMaterial);
            spark.position.y = 1.55;
            spark.visible = false;
            this.goal.add(spark);
            sparks.push(spark);
        }

        const arrow = new THREE.Mesh(new THREE.ConeGeometry(0.24, 0.62, 4), this.portalRingMaterial);
        arrow.position.set(0, 3.0, 0);
        arrow.rotation.z = Math.PI / 4;
        arrow.castShadow = true;
        this.goal.add(arrow);

        this.goal.userData = { outerRing, innerRing, closedSeal, core, sparks, arrow };
        this.goalLight = new THREE.PointLight(0x68dbff, 0.45, 10);
        this.goalLight.position.set(0, 1.7, 0);
        this.goal.add(this.goalLight);
        this.world.add(this.goal);
        this.goalPosition.set(x, top + 0.18, z);
    }

    private addStageSpecialObjects(): void {
        if (this.currentLevelIndex === 9) {
            this.addDimensionPortalPair(4, 10, { x: -2.4, z: 1.3 }, { x: 2.2, z: -1.3 });
            this.addDimensionPortalPair(6, 12, { x: 1.7, z: 0.8 }, { x: -2.0, z: 1.1 });
        }
        if (this.currentLevelIndex === 16) {
            this.addDimensionPortalPair(5, 13, { x: 0.9, z: -0.7 }, { x: -1.2, z: 0.8 });
            this.addDimensionPortalPair(15, 23, { x: -1.5, z: 1.0 }, { x: 1.3, z: -0.9 });
        }
    }

    private addDimensionPortalPair(
        firstIndex: number,
        secondIndex: number,
        firstOffset: { x: number; z: number },
        secondOffset: { x: number; z: number }
    ): void {
        const first = this.platforms[firstIndex];
        const second = this.platforms[secondIndex];
        if (!first || !second) return;

        const firstPoint = this.platformPoint(first, firstOffset.x, firstOffset.z);
        const secondPoint = this.platformPoint(second, secondOffset.x, secondOffset.z);
        const firstExit = this.platformPoint(first, -firstOffset.x * 0.55, -firstOffset.z * 0.55);
        const secondExit = this.platformPoint(second, -secondOffset.x * 0.55, -secondOffset.z * 0.55);
        this.addDimensionPortal(firstPoint, secondExit);
        this.addDimensionPortal(secondPoint, firstExit);
    }

    private platformPoint(platform: Platform, offsetX: number, offsetZ: number): THREE.Vector3 {
        const safeX = Math.max(0.2, platform.w / 2 - 1.2);
        const safeZ = Math.max(0.2, platform.d / 2 - 1.2);
        const x = platform.x + THREE.MathUtils.clamp(offsetX, -safeX, safeX);
        const z = platform.z + THREE.MathUtils.clamp(offsetZ, -safeZ, safeZ);
        return new THREE.Vector3(x, platform.y + platform.h * 0.7, z);
    }

    private addDimensionPortal(point: THREE.Vector3, target: THREE.Vector3): void {
        const group = this.createDimensionPortal();
        group.position.set(point.x, point.y + 0.1, point.z);
        this.world.add(group);
        this.dimensionPortals.push({
            group,
            x: point.x,
            y: point.y,
            z: point.z,
            target: target.clone(),
            cooldown: 0
        });
    }

    private createDimensionPortal(): THREE.Group {
        const portal = new THREE.Group();
        const outer = new THREE.Mesh(new THREE.TorusGeometry(0.82, 0.08, 4, 8), this.portalRingMaterial);
        outer.position.y = 1.0;
        outer.rotation.z = Math.PI / 4;
        portal.add(outer);

        const inner = new THREE.Mesh(new THREE.TorusGeometry(0.52, 0.055, 4, 8), this.portalInnerMaterial);
        inner.position.y = 1.0;
        inner.rotation.z = Math.PI / 4;
        portal.add(inner);

        const core = new THREE.Mesh(new THREE.PlaneGeometry(0.92, 0.92), this.portalCoreMaterial.clone());
        core.position.y = 1.0;
        core.rotation.z = Math.PI / 4;
        portal.add(core);
        portal.userData['core'] = core;

        for (let index = 0; index < 6; index++) {
            const spark = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.09, 0.09), index % 2 === 0 ? this.portalRingMaterial : this.portalInnerMaterial);
            const angle = index * Math.PI / 3;
            spark.position.set(Math.cos(angle) * 0.82, 1.0 + Math.sin(angle) * 0.82, 0.02);
            portal.add(spark);
        }

        const base = new THREE.Mesh(new THREE.CylinderGeometry(0.92, 1.08, 0.16, 8), this.stoneMaterials[2]);
        base.position.y = 0.08;
        base.castShadow = true;
        base.receiveShadow = true;
        portal.add(base);

        const light = new THREE.PointLight(0x7b5cff, 1.1, 7);
        light.position.y = 1.05;
        portal.add(light);
        return portal;
    }

    private addSceneryForLevel(): void {
        this.addMountainRidge();
        if (this.currentLevelIndex === 16) {
            this.addMysticSceneryForLevel();
            return;
        }
        if (this.currentLevelIndex === 15) {
            this.addCastleLeapSceneryForLevel();
            return;
        }
        if (this.currentLevelIndex === 14) {
            this.addCastleSceneryForLevel();
            return;
        }

        let treeCount = 0;
        this.platforms.forEach((platform, index) => {
            if (treeCount >= 14 || index % 4 !== 1 || platform.kind === 'woodDisk' || platform.w < 8.5 || platform.d < 7.2) return;
            const top = platform.y + platform.h / 2;
            const side = platform.x >= 0 ? 1 : -1;
            const treeX = platform.x + side * (platform.w / 2 + THREE.MathUtils.randFloat(4.2, 6.4));
            const treeZ = platform.z + THREE.MathUtils.randFloat(-platform.d * 0.28, platform.d * 0.28);
            const blocksPlatform = this.platforms.some((other) => (
                Math.abs(treeX - other.x) <= other.w / 2 + 1.8 &&
                Math.abs(treeZ - other.z) <= other.d / 2 + 1.8
            ));
            if (blocksPlatform) return;

            const tree = this.createPineTree(THREE.MathUtils.randFloat(0.92, 1.26));
            tree.position.set(treeX, top - 0.08, treeZ);
            this.scenery.add(tree);
            treeCount += 1;

            if (treeCount < 14 && index % 8 === 1) {
                const farTreeX = platform.x - side * (platform.w / 2 + THREE.MathUtils.randFloat(6.4, 8.8));
                const farTreeZ = platform.z + THREE.MathUtils.randFloat(-1.8, 1.8);
                const farTreeBlocksPlatform = this.platforms.some((other) => (
                    Math.abs(farTreeX - other.x) <= other.w / 2 + 1.8 &&
                    Math.abs(farTreeZ - other.z) <= other.d / 2 + 1.8
                ));
                if (farTreeBlocksPlatform) return;

                const farTree = this.createPineTree(THREE.MathUtils.randFloat(0.72, 1.05));
                farTree.position.set(farTreeX, top - 0.16, farTreeZ);
                this.scenery.add(farTree);
                treeCount += 1;
            }
        });

        const startTop = this.platformTopAt(this.spawn.x, this.spawn.z) ?? 0;
        const startHouse = this.createHouse(1.05);
        startHouse.position.set(this.spawn.x - 8.2, startTop - 0.24, this.spawn.z - 8.4);
        startHouse.rotation.y = 0.38;
        this.scenery.add(startHouse);

        const goalTop = this.platformTopAt(this.goalPosition.x, this.goalPosition.z) ?? startTop;
        const goalHouse = this.createHouse(0.82);
        goalHouse.position.set(this.goalPosition.x + 6.6, goalTop - 0.2, this.goalPosition.z + 2.8);
        goalHouse.rotation.y = -0.52;
        this.scenery.add(goalHouse);
    }

    private addMysticSceneryForLevel(): void {
        const placeOnPlatform = (platformIndex: number, object: THREE.Group, offsetX = 0, offsetZ = 0, lift = 0): void => {
            const platform = this.platforms[platformIndex];
            if (!platform) return;
            const safeX = Math.max(0, platform.w / 2 - 1.0);
            const safeZ = Math.max(0, platform.d / 2 - 1.0);
            const x = platform.x + THREE.MathUtils.clamp(offsetX, -safeX, safeX);
            const z = platform.z + THREE.MathUtils.clamp(offsetZ, -safeZ, safeZ);
            object.position.set(x, platform.y + platform.h * 0.64 + lift, z);
            this.scenery.add(object);
        };

        const placeBesidePlatform = (platformIndex: number, object: THREE.Group, side: -1 | 1, offsetZ = 0): void => {
            const platform = this.platforms[platformIndex];
            if (!platform) return;
            const x = platform.x + side * (platform.w / 2 + 1.25);
            const z = platform.z + THREE.MathUtils.clamp(offsetZ, -platform.d / 2, platform.d / 2);
            object.position.set(x, platform.y + platform.h * 0.55, z);
            this.scenery.add(object);
        };

        [0, 6, 9, 15, 19, 23, 28].forEach((platformIndex, index) => {
            const crystal = this.createMysticCrystal(0.72 + (index % 3) * 0.1);
            placeBesidePlatform(platformIndex, crystal, index % 2 === 0 ? -1 : 1, index % 2 === 0 ? -0.7 : 0.7);
        });

        [4, 11, 18, 24, 27].forEach((platformIndex, index) => {
            const rune = this.createMysticRune(0.68 + (index % 2) * 0.08);
            placeOnPlatform(platformIndex, rune, index % 2 === 0 ? -1.1 : 1.1, index % 3 === 0 ? 0.8 : -0.8, 0.18);
        });

        const goalRune = this.createMysticRune(1.18);
        goalRune.position.set(this.goalPosition.x, this.goalPosition.y + 0.3, this.goalPosition.z - 1.35);
        this.scenery.add(goalRune);

        const goalGlow = new THREE.PointLight(0x9f78ff, 1.45, 18);
        goalGlow.position.set(this.goalPosition.x, this.goalPosition.y + 2.1, this.goalPosition.z);
        this.scenery.add(goalGlow);
    }

    private createMysticCrystal(scale = 1): THREE.Group {
        const crystal = new THREE.Group();
        const crystalMaterial = pixelMaterial({ color: 0x8df8ff, roughness: 0.32, metalness: 0.04, emissive: 0x26d8ff, emissiveIntensity: 0.95 });
        const violetMaterial = pixelMaterial({ color: 0xb487ff, roughness: 0.36, emissive: 0x6d3cff, emissiveIntensity: 0.75 });

        const base = new THREE.Mesh(new THREE.CylinderGeometry(0.54, 0.7, 0.22, 6), this.stoneMaterials[3]);
        base.position.y = 0.12;
        base.castShadow = true;
        base.receiveShadow = true;
        crystal.add(base);

        const lower = new THREE.Mesh(new THREE.ConeGeometry(0.42, 0.84, 4), violetMaterial);
        lower.position.y = 0.55;
        lower.rotation.x = Math.PI;
        lower.rotation.y = Math.PI / 4;
        lower.castShadow = true;
        crystal.add(lower);

        const upper = new THREE.Mesh(new THREE.ConeGeometry(0.46, 1.24, 4), crystalMaterial);
        upper.position.y = 1.03;
        upper.rotation.y = Math.PI / 4;
        upper.castShadow = true;
        crystal.add(upper);

        const halo = new THREE.Mesh(new THREE.TorusGeometry(0.62, 0.035, 4, 8), this.portalInnerMaterial.clone());
        halo.position.y = 1.18;
        halo.rotation.x = Math.PI / 2;
        halo.rotation.z = Math.PI / 4;
        crystal.add(halo);

        const light = new THREE.PointLight(0x75ecff, 0.85, 7);
        light.position.y = 1.1;
        crystal.add(light);
        crystal.scale.setScalar(scale);
        return crystal;
    }

    private createMysticRune(scale = 1): THREE.Group {
        const rune = new THREE.Group();
        const ringMaterial = this.portalInnerMaterial.clone();
        ringMaterial.emissiveIntensity = 0.95;
        const chipMaterial = this.portalRingMaterial.clone();
        chipMaterial.emissiveIntensity = 1.05;

        const outer = new THREE.Mesh(new THREE.TorusGeometry(0.78, 0.052, 4, 8), ringMaterial);
        outer.position.y = 1.15;
        outer.rotation.z = Math.PI / 4;
        rune.add(outer);

        const inner = new THREE.Mesh(new THREE.TorusGeometry(0.46, 0.035, 4, 8), chipMaterial);
        inner.position.y = 1.15;
        inner.rotation.z = Math.PI / 4;
        rune.add(inner);

        for (let index = 0; index < 4; index++) {
            const angle = index * Math.PI / 2 + Math.PI / 4;
            const chip = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.16, 0.1), chipMaterial.clone());
            chip.position.set(Math.cos(angle) * 0.78, 1.15 + Math.sin(angle) * 0.78, 0.02);
            chip.rotation.z = angle;
            rune.add(chip);
        }

        const base = new THREE.Mesh(new THREE.CylinderGeometry(0.52, 0.64, 0.14, 8), this.stoneMaterials[4]);
        base.position.y = 0.08;
        base.castShadow = true;
        base.receiveShadow = true;
        rune.add(base);

        const light = new THREE.PointLight(0xa783ff, 0.8, 6.5);
        light.position.y = 1.15;
        rune.add(light);
        rune.scale.setScalar(scale);
        return rune;
    }

    private addCastleLeapSceneryForLevel(): void {
        const placeOnPlatform = (platformIndex: number, object: THREE.Group, offsetX = 0, offsetZ = 0, lift = 0): void => {
            const platform = this.platforms[platformIndex];
            if (!platform) return;
            const safeX = Math.max(0, platform.w / 2 - 1.1);
            const safeZ = Math.max(0, platform.d / 2 - 1.1);
            const x = platform.x + THREE.MathUtils.clamp(offsetX, -safeX, safeX);
            const z = platform.z + THREE.MathUtils.clamp(offsetZ, -safeZ, safeZ);
            object.position.set(x, platform.y + platform.h * 0.62 + lift, z);
            this.scenery.add(object);
        };

        const placeBesidePlatform = (platformIndex: number, object: THREE.Group, side = 1, offsetZ = 0): void => {
            const platform = this.platforms[platformIndex];
            if (!platform) return;
            object.position.set(
                platform.x + side * (platform.w / 2 + 2.55),
                platform.y + platform.h * 0.58,
                platform.z + THREE.MathUtils.clamp(offsetZ, -platform.d * 0.45, platform.d * 0.45)
            );
            this.scenery.add(object);
        };

        [0, 5, 10, 15, 23].forEach((index) => {
            const platform = this.platforms[index];
            if (!platform) return;
            this.addCastleFence(platform, 'x', platform.d / 2 - 0.34, Math.max(2.8, platform.w - 0.7));
            this.addCastleFence(platform, 'x', -platform.d / 2 + 0.34, Math.max(2.8, platform.w - 0.7));
        });

        placeOnPlatform(0, this.createCastleGate(0.72), 0, -1.0);
        placeOnPlatform(5, this.createCastleBanner(0.76), 1.5, -0.8, 0.12);
        placeOnPlatform(10, this.createSpiralCastleStairs(0.72), -1.25, 0.45);
        placeOnPlatform(15, this.createCastleBanner(0.82), -1.55, 0.65, 0.1);
        placeOnPlatform(23, this.createCastleGate(0.82), 0, -0.95);

        [2, 6, 10, 14, 18, 22].forEach((platformIndex, index) => {
            placeBesidePlatform(platformIndex, this.createCastleTower(0.58 + (index % 2) * 0.08), index % 2 === 0 ? -1 : 1, index % 3 === 0 ? -0.55 : 0.55);
        });

        [1, 4, 7, 11, 16, 20].forEach((platformIndex) => {
            const platform = this.platforms[platformIndex];
            if (!platform) return;
            [-1, 1].forEach((side) => {
                const torch = this.createCastleTorch(0.72);
                torch.position.set(platform.x + side * (platform.w / 2 + 0.2), platform.y + platform.h * 0.7, platform.z);
                this.scenery.add(torch);
            });
        });

        for (let index = 0; index < 14; index++) {
            const tree = this.createPineTree(0.62 + (index % 3) * 0.08);
            const side = index % 2 === 0 ? -1 : 1;
            const pathIndex = Math.min(this.platforms.length - 1, 2 + index * 1.4);
            const platform = this.platforms[Math.floor(pathIndex)];
            if (!platform) continue;
            tree.position.set(platform.x + side * (platform.w / 2 + 5.2 + (index % 3)), platform.y - 0.1, platform.z + (index % 2 === 0 ? -2.8 : 2.4));
            this.scenery.add(tree);
        }
    }

    private addCastleSceneryForLevel(): void {
        const placeOnPlatform = (platformIndex: number, object: THREE.Group, offsetX = 0, offsetZ = 0): void => {
            const platform = this.platforms[platformIndex];
            if (!platform) return;
            const safeX = Math.max(0, platform.w / 2 - 1.8);
            const safeZ = Math.max(0, platform.d / 2 - 1.8);
            const x = platform.x + THREE.MathUtils.clamp(offsetX, -safeX, safeX);
            const z = platform.z + THREE.MathUtils.clamp(offsetZ, -safeZ, safeZ);
            object.position.set(x, platform.y + platform.h * 0.62, z);
            this.scenery.add(object);
        };

        [0, 4, 8, 12, 20, 23].forEach((index) => {
            const platform = this.platforms[index];
            if (!platform) return;
            this.addCastleFence(platform, 'x', platform.d / 2 - 0.38, platform.w - 1.0);
            this.addCastleFence(platform, 'x', -platform.d / 2 + 0.38, platform.w - 1.0);
            if (platform.d > 8.6) {
                this.addCastleFence(platform, 'z', platform.w / 2 - 0.38, platform.d - 1.0);
                this.addCastleFence(platform, 'z', -platform.w / 2 + 0.38, platform.d - 1.0);
            }
        });

        placeOnPlatform(4, this.createCastleGate(1.05), 0, -2.0);
        placeOnPlatform(6, this.createCastleTower(1.0), -1.8, -0.35);
        placeOnPlatform(12, this.createCastleKeep(1.08), 0, -1.2);
        placeOnPlatform(18, this.createCastleTower(0.95), 1.45, -0.4);
        placeOnPlatform(20, this.createCastleKeep(0.96), -2.0, -1.3);
        placeOnPlatform(23, this.createCastleGate(1.18), 0, -1.7);

        placeOnPlatform(3, this.createCastleBanner(1.0), 2.6, -1.9);
        placeOnPlatform(8, this.createCastleBanner(0.92), -3.8, 1.9);
        placeOnPlatform(16, this.createCastleBanner(0.9), 1.2, 2.3);
        placeOnPlatform(21, this.createCastleBanner(1.0), -3.1, -1.7);
        placeOnPlatform(4, this.createSpiralCastleStairs(1.0), -4.2, 0.9);

        [5, 9, 14, 19].forEach((platformIndex) => {
            const platform = this.platforms[platformIndex];
            if (!platform) return;
            [-1, 1].forEach((side) => {
                const torch = this.createCastleTorch(0.9);
                torch.position.set(platform.x + side * Math.max(0.72, platform.w / 2 - 0.58), platform.y + platform.h * 0.68, platform.z);
                this.scenery.add(torch);
            });
        });

        for (let index = 0; index < 11; index++) {
            const tree = this.createPineTree(0.74 + (index % 3) * 0.09);
            const angle = (index / 11) * Math.PI * 2;
            tree.position.set(Math.cos(angle) * 35, -0.25, -82 + Math.sin(angle) * 14);
            this.scenery.add(tree);
        }
    }

    private addCastleFence(platform: Platform, orientation: 'x' | 'z', offset: number, length: number): void {
        const fence = this.createCastleFence(length);
        fence.position.set(
            platform.x + (orientation === 'z' ? offset : 0),
            platform.y + platform.h * 0.69,
            platform.z + (orientation === 'x' ? offset : 0)
        );
        if (orientation === 'z') fence.rotation.y = Math.PI / 2;
        this.scenery.add(fence);
    }

    private addMountainRidge(): void {
        for (let index = 0; index < 9; index++) {
            const mountain = new THREE.Mesh(
                new THREE.ConeGeometry(7 + (index % 3) * 2.2, 8 + (index % 2) * 2.4, 4),
                this.mountainMaterial
            );
            mountain.position.set(-42 + index * 11, -5.4, -78 - (index % 2) * 8);
            mountain.rotation.y = Math.PI / 4;
            mountain.scale.z = 0.55;
            mountain.receiveShadow = true;
            this.scenery.add(mountain);
        }
    }

    private createHouse(scale = 1): THREE.Group {
        const house = new THREE.Group();
        house.scale.setScalar(scale);

        const body = new THREE.Mesh(new THREE.BoxGeometry(3.0, 2.0, 2.4), new THREE.MeshStandardMaterial({ color: 0x8ca0ba, roughness: 0.78 }));
        body.position.y = 1.0;
        body.castShadow = true;
        body.receiveShadow = true;
        house.add(body);

        const roof = new THREE.Mesh(new THREE.ConeGeometry(2.55, 1.35, 4), this.roofMaterial);
        roof.position.y = 2.55;
        roof.rotation.y = Math.PI / 4;
        roof.castShadow = true;
        house.add(roof);

        const snowRoof = new THREE.Mesh(new THREE.ConeGeometry(2.72, 0.28, 4), this.snowMaterial);
        snowRoof.position.y = 2.94;
        snowRoof.rotation.y = Math.PI / 4;
        snowRoof.castShadow = true;
        house.add(snowRoof);

        const chimney = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.95, 0.42), new THREE.MeshStandardMaterial({ color: 0x5e4a58, roughness: 0.82 }));
        chimney.position.set(-0.86, 2.92, -0.35);
        chimney.castShadow = true;
        house.add(chimney);

        const windowGeometry = new THREE.BoxGeometry(0.55, 0.72, 0.06);
        const leftWindow = new THREE.Mesh(windowGeometry, this.windowMaterial);
        leftWindow.position.set(-0.75, 1.1, 1.23);
        house.add(leftWindow);
        const rightWindow = leftWindow.clone();
        rightWindow.position.x = 0.75;
        house.add(rightWindow);

        return house;
    }

    private createPineTree(scale = 1): THREE.Group {
        const tree = new THREE.Group();
        tree.scale.setScalar(scale);

        const trunk = new THREE.Mesh(new THREE.BoxGeometry(0.34, 1.05, 0.34), this.woodMaterial);
        trunk.position.y = 0.5;
        trunk.castShadow = true;
        tree.add(trunk);

        const layers = [
            { width: 2.05, depth: 1.8, y: 1.06 },
            { width: 1.55, depth: 1.38, y: 1.62 },
            { width: 1.08, depth: 1.0, y: 2.12 }
        ];
        layers.forEach((layer) => {
            const needles = new THREE.Mesh(new THREE.BoxGeometry(layer.width, 0.54, layer.depth), this.pineMaterial);
            needles.position.y = layer.y;
            needles.castShadow = true;
            tree.add(needles);

            const snow = new THREE.Mesh(new THREE.BoxGeometry(layer.width * 0.78, 0.14, layer.depth * 0.76), this.snowMaterial);
            snow.position.y = layer.y + 0.34;
            snow.castShadow = true;
            tree.add(snow);
        });

        return tree;
    }

    private createCastleTower(scale = 1): THREE.Group {
        const tower = new THREE.Group();
        tower.scale.setScalar(scale);

        const height = 5.1;
        const body = new THREE.Mesh(new THREE.BoxGeometry(2.6, height, 2.6), this.stoneMaterials[0]);
        body.position.y = height / 2;
        body.castShadow = true;
        body.receiveShadow = true;
        tower.add(body);

        for (let index = 0; index < 5; index++) {
            const band = new THREE.Mesh(new THREE.BoxGeometry(2.82, 0.08, 2.82), this.stoneLineMaterial);
            band.position.y = 0.72 + index * 0.92;
            band.castShadow = true;
            tower.add(band);
        }

        [-0.52, 0.52].forEach((x) => {
            const window = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.72, 0.08), this.windowMaterial);
            window.position.set(x, 2.8, -1.34);
            tower.add(window);
        });

        const roof = new THREE.Mesh(new THREE.ConeGeometry(2.05, 2.35, 4), this.castleRoofMaterial);
        roof.position.y = height + 1.05;
        roof.rotation.y = Math.PI / 4;
        roof.castShadow = true;
        tower.add(roof);

        const snowCap = new THREE.Mesh(new THREE.ConeGeometry(2.16, 0.32, 4), this.snowMaterial);
        snowCap.position.y = height + 2.04;
        snowCap.rotation.y = Math.PI / 4;
        snowCap.castShadow = true;
        tower.add(snowCap);

        const light = new THREE.PointLight(0xffd88a, 0.42, 6.4);
        light.position.set(0, 2.8, -1.42);
        tower.add(light);
        return tower;
    }

    private createCastleKeep(scale = 1): THREE.Group {
        const keep = new THREE.Group();
        keep.scale.setScalar(scale);

        const body = new THREE.Mesh(new THREE.BoxGeometry(5.2, 3.4, 3.3), this.stoneMaterials[1]);
        body.position.y = 1.72;
        body.castShadow = true;
        body.receiveShadow = true;
        keep.add(body);

        const roof = new THREE.Mesh(new THREE.ConeGeometry(3.7, 1.8, 4), this.castleRoofMaterial);
        roof.position.y = 4.2;
        roof.rotation.y = Math.PI / 4;
        roof.scale.x = 1.12;
        roof.castShadow = true;
        keep.add(roof);

        [-1.65, 0, 1.65].forEach((x) => {
            const window = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.72, 0.08), this.windowMaterial);
            window.position.set(x, 2.16, -1.7);
            keep.add(window);
        });

        const door = new THREE.Mesh(new THREE.BoxGeometry(0.8, 1.28, 0.1), this.woodDarkMaterial);
        door.position.set(0, 0.82, -1.72);
        keep.add(door);

        [-2.25, 2.25].forEach((x) => {
            const turret = this.createCastleTower(0.42);
            turret.position.set(x, 0.02, -0.15);
            keep.add(turret);
        });

        return keep;
    }

    private createCastleGate(scale = 1): THREE.Group {
        const gate = new THREE.Group();
        gate.scale.setScalar(scale);

        const leftTower = this.createCastleTower(0.62);
        leftTower.position.x = -2.2;
        gate.add(leftTower);

        const rightTower = this.createCastleTower(0.62);
        rightTower.position.x = 2.2;
        gate.add(rightTower);

        const wall = new THREE.Mesh(new THREE.BoxGeometry(5.2, 2.6, 1.0), this.stoneMaterials[2]);
        wall.position.y = 1.3;
        wall.castShadow = true;
        wall.receiveShadow = true;
        gate.add(wall);

        const arch = new THREE.Mesh(new THREE.BoxGeometry(1.5, 1.45, 1.08), this.woodDarkMaterial);
        arch.position.set(0, 0.72, -0.04);
        gate.add(arch);

        const banner = this.createCastleBanner(0.55);
        banner.position.set(0, 2.25, -0.62);
        gate.add(banner);

        return gate;
    }

    private createCastleFence(length: number): THREE.Group {
        const fence = new THREE.Group();
        const postCount = Math.max(4, Math.floor(length / 0.72));
        const spacing = length / Math.max(1, postCount - 1);

        for (let index = 0; index < postCount; index++) {
            const x = -length / 2 + spacing * index;
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.86, 0.18), this.woodMaterial);
            post.position.set(x, 0.43, 0);
            post.castShadow = true;
            fence.add(post);

            const cap = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.14, 0.22), this.snowMaterial);
            cap.position.set(x, 0.92, 0);
            cap.castShadow = true;
            fence.add(cap);
        }

        [-0.12, 0.32].forEach((y) => {
            const rail = new THREE.Mesh(new THREE.BoxGeometry(length + 0.18, 0.14, 0.16), this.woodTopMaterial);
            rail.position.set(0, y + 0.46, 0);
            rail.castShadow = true;
            fence.add(rail);
        });

        return fence;
    }

    private createCastleTorch(scale = 1): THREE.Group {
        const torch = new THREE.Group();
        torch.scale.setScalar(scale);

        const pole = new THREE.Mesh(new THREE.BoxGeometry(0.16, 1.25, 0.16), this.torchPoleMaterial);
        pole.position.y = 0.62;
        pole.castShadow = true;
        torch.add(pole);

        const bowl = new THREE.Mesh(new THREE.BoxGeometry(0.54, 0.18, 0.54), this.meteorMaterial);
        bowl.position.y = 1.25;
        bowl.castShadow = true;
        torch.add(bowl);

        const flame = new THREE.Group();
        flame.position.y = 1.52;
        const outer = new THREE.Mesh(new THREE.ConeGeometry(0.24, 0.72, 4), this.fireMaterial.clone());
        outer.position.y = 0.28;
        outer.rotation.y = Math.PI / 4;
        flame.add(outer);
        const core = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.46, 4), this.fireCoreMaterial.clone());
        core.position.y = 0.26;
        core.rotation.y = Math.PI / 4;
        flame.add(core);
        torch.add(flame);

        const light = new THREE.PointLight(0xff8a2a, 0.8, 6.2);
        light.position.y = 1.55;
        torch.add(light);
        return torch;
    }

    private createCastleBanner(scale = 1): THREE.Group {
        const banner = new THREE.Group();
        banner.scale.setScalar(scale);

        const board = new THREE.Mesh(new THREE.BoxGeometry(1.8, 1.0, 0.12), this.castleBannerMaterial);
        board.position.y = 0.7;
        board.castShadow = true;
        banner.add(board);

        [-0.42, 0.42].forEach((x) => {
            const star = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.18, 5), this.goldMaterial);
            star.position.set(x, 0.78, -0.08);
            star.rotation.z = Math.PI / 2;
            star.castShadow = true;
            banner.add(star);
        });

        const pole = new THREE.Mesh(new THREE.BoxGeometry(0.14, 1.75, 0.14), this.torchPoleMaterial);
        pole.position.set(-1.05, 0.76, 0);
        pole.castShadow = true;
        banner.add(pole);

        return banner;
    }

    private createSpiralCastleStairs(scale = 1): THREE.Group {
        const stairs = new THREE.Group();
        stairs.scale.setScalar(scale);

        const column = new THREE.Mesh(new THREE.BoxGeometry(0.5, 4.2, 0.5), this.stoneLineMaterial);
        column.position.y = 2.1;
        column.castShadow = true;
        stairs.add(column);

        for (let index = 0; index < 10; index++) {
            const angle = index * 0.72;
            const step = new THREE.Mesh(new THREE.BoxGeometry(1.55, 0.18, 0.86), index % 2 === 0 ? this.stoneMaterials[0] : this.stoneMaterials[2]);
            step.position.set(Math.cos(angle) * 1.08, 0.34 + index * 0.34, Math.sin(angle) * 1.08);
            step.rotation.y = -angle;
            step.castShadow = true;
            step.receiveShadow = true;
            stairs.add(step);
        }

        return stairs;
    }

    private createSanta(): THREE.Group {
        const santa = new THREE.Group();
        const rider = new THREE.Group();
        const baseAvatar = new THREE.Group();
        baseAvatar.name = 'baseAvatar';
        this.santaRider = rider;
        this.baseAvatar = baseAvatar;

        const coat = new THREE.Mesh(new THREE.BoxGeometry(0.86, 0.92, 0.76), this.redMaterial);
        coat.position.y = 0.55;
        coat.castShadow = true;
        baseAvatar.add(coat);

        const trim = new THREE.Mesh(new THREE.BoxGeometry(0.96, 0.16, 0.82), this.whiteMaterial);
        trim.position.y = 0.14;
        trim.castShadow = true;
        baseAvatar.add(trim);

        const belt = new THREE.Mesh(new THREE.BoxGeometry(0.98, 0.14, 0.84), this.blackMaterial);
        belt.position.y = 0.48;
        belt.castShadow = true;
        baseAvatar.add(belt);

        const sack = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.82, 0.46), this.sackMaterial);
        sack.position.set(0.48, 0.82, 0.46);
        sack.rotation.z = -0.08;
        sack.castShadow = true;
        baseAvatar.add(sack);

        const sackTie = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.18, 0.12), this.goldMaterial);
        sackTie.position.set(0.12, 1.1, 0.42);
        sackTie.castShadow = true;
        baseAvatar.add(sackTie);

        const head = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.5, 0.54), this.faceMaterial);
        head.position.y = 1.14;
        head.castShadow = true;
        baseAvatar.add(head);

        const beard = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.34, 0.16), this.whiteMaterial);
        beard.position.set(0, 0.92, -0.34);
        beard.castShadow = true;
        baseAvatar.add(beard);

        const hatBase = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.16, 0.62), this.whiteMaterial);
        hatBase.position.y = 1.43;
        hatBase.castShadow = true;
        baseAvatar.add(hatBase);

        const hatMid = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.36, 0.46), this.redMaterial);
        hatMid.position.set(-0.05, 1.66, 0);
        hatMid.castShadow = true;
        baseAvatar.add(hatMid);

        const hatTip = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.26, 0.3), this.redMaterial);
        hatTip.position.set(-0.14, 1.96, 0);
        hatTip.castShadow = true;
        baseAvatar.add(hatTip);

        const pom = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.18, 0.18), this.whiteMaterial);
        pom.position.set(-0.26, 2.14, 0);
        pom.castShadow = true;
        baseAvatar.add(pom);

        const armGeometry = new THREE.BoxGeometry(0.18, 0.54, 0.22);
        const leftArm = new THREE.Mesh(armGeometry, this.darkRedMaterial);
        leftArm.position.set(-0.56, 0.56, 0);
        leftArm.rotation.z = -0.28;
        leftArm.castShadow = true;
        baseAvatar.add(leftArm);
        const rightArm = leftArm.clone();
        rightArm.position.x = 0.55;
        rightArm.rotation.z = 0.28;
        baseAvatar.add(rightArm);

        const footGeometry = new THREE.BoxGeometry(0.26, 0.16, 0.48);
        const leftFoot = new THREE.Mesh(footGeometry, this.blackMaterial);
        leftFoot.position.set(-0.23, 0.02, -0.05);
        leftFoot.castShadow = true;
        baseAvatar.add(leftFoot);
        const rightFoot = leftFoot.clone();
        rightFoot.position.x = 0.23;
        baseAvatar.add(rightFoot);

        rider.add(baseAvatar);

        const costumeOutfits = this.createCharacterCostumes();
        rider.add(costumeOutfits);
        this.costumeOutfits = costumeOutfits;

        const adminOutfit = this.createAdminOutfit();
        rider.add(adminOutfit);
        this.adminOutfit = adminOutfit;

        const adminMount = this.createRudolphMount();
        santa.add(adminMount);
        this.adminMount = adminMount;
        const vehicleMount = new THREE.Group();
        vehicleMount.name = 'vehicleMount';
        santa.add(vehicleMount);
        this.vehicleMount = vehicleMount;
        santa.add(rider);

        return santa;
    }

    private createCharacterCostumes(): THREE.Group {
        const outfits = new THREE.Group();
        outfits.name = 'characterCostumes';
        outfits.visible = false;

        const characterCostumes: Array<[string, THREE.Group]> = [
            ['alex_costume', this.createBlockHumanCostume({
                skin: this.faceMaterial,
                hair: this.alexHairMaterial,
                hairDark: this.alexHairDarkMaterial,
                shirt: this.alexShirtMaterial,
                pants: this.alexPantsMaterial,
                shoes: this.stoneMaterials[3],
                eye: this.frogDarkGreenMaterial,
                alexBraid: true
            })],
            ['steve_costume', this.createBlockHumanCostume({
                skin: this.skinTanMaterial,
                hair: this.steveHairMaterial,
                shirt: this.steveShirtMaterial,
                pants: this.stevePantsMaterial,
                shoes: this.blackMaterial,
                eye: this.marioBlueMaterial,
                beard: true
            })],
            ['mario_costume', this.createMarioCostume()],
            ['among_crewmate_costume', this.createAmongCrewmateCostume()],
            ['sonic_costume', this.createSonicCostume()],
            ['frog_costume', this.createFrogCostume()]
        ];

        characterCostumes.forEach(([id, group]) => {
            group.name = id;
            group.visible = false;
            outfits.add(group);
        });

        return outfits;
    }

    private createBlockHumanCostume(options: {
        skin: THREE.Material;
        hair: THREE.Material;
        hairDark?: THREE.Material;
        shirt: THREE.Material;
        pants: THREE.Material;
        shoes: THREE.Material;
        eye: THREE.Material;
        alexBraid?: boolean;
        beard?: boolean;
    }): THREE.Group {
        const group = new THREE.Group();
        const hairDark = options.hairDark ?? options.hair;

        this.addVoxel(group, [0.62, 0.62, 0.58], [0, 1.36, -0.02], options.skin);
        this.addVoxel(group, [0.68, 0.18, 0.62], [0, 1.71, -0.02], options.hair);
        this.addVoxel(group, [0.7, 0.44, 0.12], [0, 1.48, 0.29], hairDark);
        this.addVoxel(group, [0.14, 0.44, 0.58], [-0.34, 1.46, -0.02], options.hair);
        this.addVoxel(group, [0.14, 0.44, 0.58], [0.34, 1.46, -0.02], options.hair);
        this.addVoxel(group, [0.18, 0.08, 0.05], [-0.14, 1.43, -0.34], options.eye);
        this.addVoxel(group, [0.18, 0.08, 0.05], [0.14, 1.43, -0.34], options.eye);
        this.addVoxel(group, [0.18, 0.08, 0.05], [0, 1.24, -0.35], options.beard ? hairDark : this.skinPeachMaterial);

        if (options.beard) {
            this.addVoxel(group, [0.5, 0.22, 0.07], [0, 1.18, -0.36], hairDark);
        }

        if (options.alexBraid) {
            this.addVoxel(group, [0.16, 0.64, 0.14], [0.33, 1.05, 0.32], options.hair);
            this.addVoxel(group, [0.12, 0.24, 0.12], [0.42, 0.7, 0.31], hairDark);
        }

        this.addVoxel(group, [0.72, 0.72, 0.52], [0, 0.66, 0], options.shirt);
        this.addVoxel(group, [0.74, 0.09, 0.56], [0, 0.3, -0.01], this.blackMaterial);
        this.addVoxel(group, [0.22, 0.4, 0.28], [-0.57, 0.74, 0], options.shirt, [0, 0, -0.12]);
        this.addVoxel(group, [0.22, 0.4, 0.28], [0.57, 0.74, 0], options.shirt, [0, 0, 0.12]);
        this.addVoxel(group, [0.22, 0.4, 0.26], [-0.6, 0.36, 0], options.skin, [0, 0, -0.08]);
        this.addVoxel(group, [0.22, 0.4, 0.26], [0.6, 0.36, 0], options.skin, [0, 0, 0.08]);
        this.addVoxel(group, [0.3, 0.48, 0.32], [-0.2, 0.12, 0], options.pants);
        this.addVoxel(group, [0.3, 0.48, 0.32], [0.2, 0.12, 0], options.pants);
        this.addVoxel(group, [0.32, 0.15, 0.38], [-0.2, -0.18, -0.04], options.shoes);
        this.addVoxel(group, [0.32, 0.15, 0.38], [0.2, -0.18, -0.04], options.shoes);

        return group;
    }

    private createMarioCostume(): THREE.Group {
        const group = new THREE.Group();
        this.addVoxel(group, [0.6, 0.5, 0.54], [0, 1.36, -0.02], this.skinPeachMaterial);
        this.addVoxel(group, [0.68, 0.16, 0.6], [0, 1.66, -0.02], this.marioRedMaterial);
        this.addVoxel(group, [0.52, 0.24, 0.5], [0, 1.82, 0], this.marioRedMaterial);
        this.addVoxel(group, [0.64, 0.1, 0.22], [0.08, 1.6, -0.35], this.marioRedMaterial);
        this.addVoxel(group, [0.18, 0.18, 0.09], [-0.2, 1.42, -0.33], this.steveHairMaterial);
        this.addVoxel(group, [0.42, 0.14, 0.07], [0.05, 1.22, -0.35], this.steveHairMaterial);
        this.addVoxel(group, [0.14, 0.08, 0.05], [-0.12, 1.44, -0.34], this.blackMaterial);
        this.addVoxel(group, [0.14, 0.08, 0.05], [0.18, 1.44, -0.34], this.blackMaterial);
        this.addVoxel(group, [0.74, 0.72, 0.54], [0, 0.67, 0], this.marioRedMaterial);
        this.addVoxel(group, [0.5, 0.6, 0.07], [0, 0.62, -0.31], this.marioBlueMaterial);
        this.addVoxel(group, [0.12, 0.6, 0.08], [-0.18, 0.78, -0.35], this.marioBlueMaterial);
        this.addVoxel(group, [0.12, 0.6, 0.08], [0.18, 0.78, -0.35], this.marioBlueMaterial);
        this.addVoxel(group, [0.1, 0.1, 0.08], [-0.15, 0.66, -0.4], this.goldMaterial);
        this.addVoxel(group, [0.1, 0.1, 0.08], [0.15, 0.66, -0.4], this.goldMaterial);
        this.addVoxel(group, [0.22, 0.42, 0.28], [-0.58, 0.66, 0], this.marioRedMaterial, [0, 0, -0.12]);
        this.addVoxel(group, [0.24, 0.22, 0.28], [-0.62, 0.3, 0], this.skinPeachMaterial);
        this.addVoxel(group, [0.22, 0.42, 0.28], [0.58, 0.66, 0], this.marioRedMaterial, [0, 0, 0.12]);
        this.addVoxel(group, [0.24, 0.22, 0.28], [0.62, 0.3, 0], this.skinPeachMaterial);
        this.addVoxel(group, [0.3, 0.5, 0.34], [-0.2, 0.14, 0], this.marioBlueMaterial);
        this.addVoxel(group, [0.3, 0.5, 0.34], [0.2, 0.14, 0], this.marioBlueMaterial);
        this.addVoxel(group, [0.38, 0.16, 0.42], [-0.23, -0.18, -0.05], this.marioBrownMaterial);
        this.addVoxel(group, [0.38, 0.16, 0.42], [0.23, -0.18, -0.05], this.marioBrownMaterial);
        return group;
    }

    private createAmongCrewmateCostume(): THREE.Group {
        const group = new THREE.Group();
        this.addVoxel(group, [0.76, 1.02, 0.62], [0, 0.68, 0], this.amongRedMaterial);
        this.addVoxel(group, [0.66, 0.28, 0.58], [0, 1.33, -0.01], this.amongRedMaterial);
        this.addVoxel(group, [0.54, 0.24, 0.08], [0.08, 1.2, -0.35], this.amongVisorMaterial);
        this.addVoxel(group, [0.2, 0.05, 0.09], [0.0, 1.28, -0.41], this.whiteMaterial);
        this.addVoxel(group, [0.24, 0.78, 0.36], [-0.48, 0.65, 0.22], this.amongDarkMaterial);
        this.addVoxel(group, [0.28, 0.44, 0.28], [-0.22, -0.04, -0.02], this.amongDarkMaterial);
        this.addVoxel(group, [0.28, 0.44, 0.28], [0.22, -0.04, -0.02], this.amongDarkMaterial);
        return group;
    }

    private createSonicCostume(): THREE.Group {
        const group = new THREE.Group();
        this.addVoxel(group, [0.62, 0.54, 0.52], [0, 1.42, -0.02], this.sonicBlueMaterial);
        this.addVoxel(group, [0.36, 0.22, 0.08], [0.12, 1.31, -0.32], this.sonicTanMaterial);
        this.addVoxel(group, [0.18, 0.2, 0.08], [-0.11, 1.47, -0.34], this.whiteMaterial);
        this.addVoxel(group, [0.18, 0.2, 0.08], [0.17, 1.47, -0.34], this.whiteMaterial);
        this.addVoxel(group, [0.06, 0.08, 0.09], [-0.04, 1.45, -0.4], this.blackMaterial);
        this.addVoxel(group, [0.06, 0.08, 0.09], [0.23, 1.45, -0.4], this.blackMaterial);
        this.addVoxel(group, [0.18, 0.32, 0.2], [-0.24, 1.77, -0.03], this.sonicBlueMaterial, [0, 0, -0.28]);
        this.addVoxel(group, [0.18, 0.32, 0.2], [0.24, 1.77, -0.03], this.sonicBlueMaterial, [0, 0, 0.28]);
        const quills: Array<[number, number, number]> = [[0, 1.58, 0.3], [0, 1.34, 0.36], [0, 1.1, 0.32], [-0.36, 1.36, 0.12], [0.36, 1.36, 0.12]];
        quills.forEach(([x, y, z], index) => {
            this.addVoxel(group, [index < 3 ? 0.28 : 0.22, 0.22, 0.42], [x, y, z], this.sonicBlueMaterial, [index < 3 ? -0.55 : 0, 0, x < 0 ? -0.42 : 0.42]);
        });
        this.addVoxel(group, [0.54, 0.62, 0.42], [0, 0.68, 0], this.sonicBlueMaterial);
        this.addVoxel(group, [0.34, 0.38, 0.08], [0, 0.68, -0.25], this.sonicTanMaterial);
        this.addVoxel(group, [0.2, 0.5, 0.22], [-0.5, 0.58, 0], this.sonicTanMaterial, [0, 0, -0.18]);
        this.addVoxel(group, [0.22, 0.18, 0.26], [-0.57, 0.24, 0], this.whiteMaterial);
        this.addVoxel(group, [0.2, 0.5, 0.22], [0.5, 0.58, 0], this.sonicTanMaterial, [0, 0, 0.18]);
        this.addVoxel(group, [0.22, 0.18, 0.26], [0.57, 0.24, 0], this.whiteMaterial);
        this.addVoxel(group, [0.22, 0.48, 0.24], [-0.17, 0.12, 0], this.sonicBlueMaterial);
        this.addVoxel(group, [0.22, 0.48, 0.24], [0.17, 0.12, 0], this.sonicBlueMaterial);
        this.addVoxel(group, [0.36, 0.16, 0.42], [-0.2, -0.18, -0.05], this.sonicShoeMaterial);
        this.addVoxel(group, [0.36, 0.16, 0.42], [0.2, -0.18, -0.05], this.sonicShoeMaterial);
        return group;
    }

    private createFrogCostume(): THREE.Group {
        const group = new THREE.Group();
        this.addVoxel(group, [0.82, 0.58, 0.58], [0, 1.42, -0.02], this.frogGreenMaterial);
        this.addVoxel(group, [0.24, 0.22, 0.22], [-0.24, 1.78, -0.08], this.frogGreenMaterial);
        this.addVoxel(group, [0.24, 0.22, 0.22], [0.24, 1.78, -0.08], this.frogGreenMaterial);
        this.addVoxel(group, [0.18, 0.16, 0.07], [-0.24, 1.78, -0.22], this.whiteMaterial);
        this.addVoxel(group, [0.18, 0.16, 0.07], [0.24, 1.78, -0.22], this.whiteMaterial);
        this.addVoxel(group, [0.06, 0.07, 0.08], [-0.24, 1.78, -0.27], this.blackMaterial);
        this.addVoxel(group, [0.06, 0.07, 0.08], [0.24, 1.78, -0.27], this.blackMaterial);
        this.addVoxel(group, [0.56, 0.06, 0.07], [0.04, 1.3, -0.35], this.marioBrownMaterial);
        this.addVoxel(group, [0.16, 0.08, 0.08], [-0.24, 1.28, -0.37], this.skinPeachMaterial);
        this.addVoxel(group, [0.62, 0.62, 0.46], [0, 0.68, 0], this.frogShirtMaterial);
        this.addVoxel(group, [0.22, 0.52, 0.24], [-0.52, 0.58, 0], this.frogGreenMaterial, [0, 0, -0.12]);
        this.addVoxel(group, [0.22, 0.52, 0.24], [0.52, 0.58, 0], this.frogGreenMaterial, [0, 0, 0.12]);
        this.addVoxel(group, [0.32, 0.42, 0.3], [-0.19, 0.12, 0], this.frogPantsMaterial);
        this.addVoxel(group, [0.32, 0.42, 0.3], [0.19, 0.12, 0], this.frogPantsMaterial);
        this.addVoxel(group, [0.34, 0.13, 0.38], [-0.21, -0.18, -0.04], this.frogDarkGreenMaterial);
        this.addVoxel(group, [0.34, 0.13, 0.38], [0.21, -0.18, -0.04], this.frogDarkGreenMaterial);
        return group;
    }

    private addVoxel(group: THREE.Group, size: [number, number, number], position: [number, number, number], material: THREE.Material, rotation?: [number, number, number]): THREE.Mesh {
        const [width, height, depth] = size;
        const mesh = new THREE.Mesh(new THREE.BoxGeometry(Math.max(width, 0.01), Math.max(height, 0.01), Math.max(depth, 0.01)), material);
        mesh.position.set(position[0], position[1], position[2]);
        if (rotation) mesh.rotation.set(rotation[0], rotation[1], rotation[2]);
        mesh.castShadow = true;
        group.add(mesh);
        return mesh;
    }

    private createRudolphMount(): THREE.Group {
        const mount = new THREE.Group();
        mount.name = 'adminRudolphMount';
        mount.visible = false;
        mount.position.set(0, -0.02, -0.02);

        const body = new THREE.Mesh(new THREE.BoxGeometry(1.34, 0.42, 0.68), this.rudolphBodyMaterial);
        body.position.set(0, 0.32, 0.08);
        body.castShadow = true;
        mount.add(body);

        const chest = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.38, 0.48), this.rudolphDarkMaterial);
        chest.position.set(0, 0.35, -0.34);
        chest.castShadow = true;
        mount.add(chest);

        const neck = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.46, 0.28), this.rudolphBodyMaterial);
        neck.position.set(0, 0.6, -0.48);
        neck.rotation.x = -0.18;
        neck.castShadow = true;
        mount.add(neck);

        const head = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.34, 0.42), this.rudolphBodyMaterial);
        head.position.set(0, 0.8, -0.78);
        head.castShadow = true;
        mount.add(head);

        const snout = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.18, 0.24), this.rudolphDarkMaterial);
        snout.position.set(0, 0.75, -1.08);
        snout.castShadow = true;
        mount.add(snout);

        const nose = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.18, 0.16), this.rudolphNoseMaterial);
        nose.position.set(0, 0.78, -1.25);
        mount.add(nose);

        [-0.24, 0.24].forEach((x) => {
            const ear = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.24, 0.12), this.rudolphDarkMaterial);
            ear.position.set(x, 1.0, -0.72);
            ear.rotation.z = x < 0 ? 0.32 : -0.32;
            ear.castShadow = true;
            mount.add(ear);

            const antlerBase = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.36, 0.08), this.rudolphAntlerMaterial);
            antlerBase.position.set(x * 0.72, 1.18, -0.76);
            antlerBase.rotation.z = x < 0 ? -0.24 : 0.24;
            antlerBase.castShadow = true;
            mount.add(antlerBase);

            [-0.08, 0.08].forEach((branchOffset) => {
                const branch = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.22, 0.07), this.rudolphAntlerMaterial);
                branch.position.set(x * 0.72 + Math.sign(x) * branchOffset, 1.28, -0.78);
                branch.rotation.z = Math.sign(x) * (branchOffset < 0 ? -0.62 : 0.52);
                branch.castShadow = true;
                mount.add(branch);
            });
        });

        const saddle = new THREE.Mesh(new THREE.BoxGeometry(0.86, 0.12, 0.58), this.adminVoidMaterial);
        saddle.position.set(0, 0.62, 0.1);
        saddle.castShadow = true;
        mount.add(saddle);

        const saddleTrim = new THREE.Mesh(new THREE.BoxGeometry(0.94, 0.08, 0.1), this.adminGoldMaterial);
        saddleTrim.position.set(0, 0.69, -0.18);
        saddleTrim.castShadow = true;
        mount.add(saddleTrim);

        const reins = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.05, 0.06), this.adminCyanMaterial);
        reins.position.set(0, 0.74, -0.46);
        reins.rotation.x = 0.38;
        mount.add(reins);

        const legGeometry = new THREE.BoxGeometry(0.16, 0.62, 0.16);
        const hoofGeometry = new THREE.BoxGeometry(0.22, 0.09, 0.2);
        [-0.44, 0.44].forEach((x) => {
            [-0.18, 0.36].forEach((z) => {
                const leg = new THREE.Mesh(legGeometry, this.rudolphDarkMaterial);
                leg.position.set(x, -0.05, z);
                leg.castShadow = true;
                mount.add(leg);

                const hoof = new THREE.Mesh(hoofGeometry, this.blackMaterial);
                hoof.position.set(x, -0.4, z - 0.02);
                hoof.castShadow = true;
                mount.add(hoof);
            });
        });

        const tail = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.14, 0.34), this.rudolphDarkMaterial);
        tail.position.set(0, 0.43, 0.58);
        tail.rotation.x = 0.38;
        tail.castShadow = true;
        mount.add(tail);

        return mount;
    }

    private createVehicleMount(vehicleId: string): THREE.Group | undefined {
        if (vehicleId === 'rudolph_vehicle') {
            const rudolph = this.createRudolphMount();
            rudolph.name = 'rudolphVehicleMount';
            rudolph.visible = true;
            return rudolph;
        }
        if (vehicleId === 'bmw_530_blue') return this.orientVehicleForward(this.createSedanVehicle('bmw530Vehicle', this.carBlueMaterial, true));
        if (vehicleId === 'bmw_320i_black_7907') return this.orientVehicleForward(this.createSedanVehicle('bmw320iBlack7907Vehicle', this.carBlackMaterial, true, '7907'));
        if (vehicleId === 'mercedes_e_class_white') return this.orientVehicleForward(this.createSedanVehicle('mercedesEClassVehicle', this.carWhiteMaterial, false));
        if (vehicleId === 'porsche_911_gt3_pink') return this.orientVehicleForward(this.createPorscheVehicle());
        if (vehicleId === 'cybertruck') return this.orientVehicleForward(this.createCybertruckVehicle());
        if (vehicleId === 'bugatti_chiron_red') return this.orientVehicleForward(this.createBugattiVehicle());
        return undefined;
    }

    private orientVehicleForward(vehicle: THREE.Group): THREE.Group {
        vehicle.rotation.y = Math.PI;
        return vehicle;
    }

    private createSedanVehicle(name: string, paint: THREE.Material, bmwDetails: boolean, plateText?: string): THREE.Group {
        const car = new THREE.Group();
        car.name = name;
        car.position.set(0, -0.02, 0.04);

        const body = new THREE.Mesh(new THREE.BoxGeometry(1.08, 0.34, 1.92), paint);
        body.position.y = 0.25;
        body.castShadow = true;
        car.add(body);

        const hood = new THREE.Mesh(new THREE.BoxGeometry(0.96, 0.12, 0.58), paint);
        hood.position.set(0, 0.48, -0.48);
        hood.castShadow = true;
        car.add(hood);

        const trunk = new THREE.Mesh(new THREE.BoxGeometry(0.96, 0.1, 0.44), paint);
        trunk.position.set(0, 0.47, 0.58);
        trunk.castShadow = true;
        car.add(trunk);

        const cabin = new THREE.Mesh(new THREE.BoxGeometry(0.76, 0.34, 0.72), this.carGlassMaterial);
        cabin.position.set(0, 0.64, 0.02);
        cabin.castShadow = true;
        car.add(cabin);

        const roof = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.1, 0.54), paint);
        roof.position.set(0, 0.86, 0.04);
        roof.castShadow = true;
        car.add(roof);

        const grille = new THREE.Mesh(new THREE.BoxGeometry(bmwDetails ? 0.34 : 0.56, 0.18, 0.06), this.blackMaterial);
        grille.position.set(0, 0.32, -0.99);
        grille.castShadow = true;
        car.add(grille);

        if (bmwDetails) {
            [-0.09, 0.09].forEach((x) => {
                const kidney = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.2, 0.075), this.carRimMaterial);
                kidney.position.set(x, 0.33, -1.03);
                kidney.castShadow = true;
                car.add(kidney);
            });
        } else {
            const badge = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 0.08), this.carRimMaterial);
            badge.position.set(0, 0.39, -1.04);
            badge.rotation.z = Math.PI / 4;
            badge.castShadow = true;
            car.add(badge);
        }

        [-0.34, 0.34].forEach((x) => {
            const headlight = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.09, 0.08), this.carLightMaterial);
            headlight.position.set(x, 0.39, -1.02);
            car.add(headlight);

            const tail = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.08, 0.08), this.carTailLightMaterial);
            tail.position.set(x, 0.36, 0.99);
            car.add(tail);
        });

        if (plateText) {
            this.addVehicleLicensePlate(car, plateText, -1.065, true);
            this.addVehicleLicensePlate(car, plateText, 1.065, false);
        }

        this.addVehicleWheels(car, 0.66, 0.72, 0.26);
        return car;
    }

    private addVehicleLicensePlate(car: THREE.Group, text: string, z: number, front: boolean): void {
        const plate = new THREE.Mesh(new THREE.PlaneGeometry(0.48, 0.17), this.createLicensePlateMaterial(text));
        plate.position.set(0, 0.28, z);
        if (front) plate.rotation.y = Math.PI;
        plate.renderOrder = 4;
        car.add(plate);
    }

    private createLicensePlateMaterial(text: string): THREE.MeshBasicMaterial {
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 48;
        const context = canvas.getContext('2d');
        if (context) {
            context.imageSmoothingEnabled = false;
            context.fillStyle = '#f7fbff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.strokeStyle = '#111418';
            context.lineWidth = 6;
            context.strokeRect(3, 3, canvas.width - 6, canvas.height - 6);
            context.fillStyle = '#111418';
            context.font = 'bold 30px monospace';
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillText(text, canvas.width / 2, canvas.height / 2 + 2);
        }
        const texture = new THREE.CanvasTexture(canvas);
        texture.magFilter = THREE.NearestFilter;
        texture.minFilter = THREE.NearestFilter;
        texture.needsUpdate = true;
        return new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide });
    }

    private createCybertruckVehicle(): THREE.Group {
        const truck = new THREE.Group();
        truck.name = 'cybertruckVehicle';
        truck.position.set(0, -0.02, 0.04);

        const lower = new THREE.Mesh(new THREE.BoxGeometry(1.22, 0.34, 2.08), this.carSilverMaterial);
        lower.position.y = 0.28;
        lower.castShadow = true;
        truck.add(lower);

        const wedge = new THREE.Mesh(new THREE.BoxGeometry(1.04, 0.42, 1.18), this.carSilverMaterial);
        wedge.position.set(0, 0.62, -0.02);
        wedge.rotation.x = -0.18;
        wedge.castShadow = true;
        truck.add(wedge);

        const windshield = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.22, 0.48), this.carGlassMaterial);
        windshield.position.set(0, 0.72, -0.34);
        windshield.rotation.x = -0.22;
        truck.add(windshield);

        const bedLine = new THREE.Mesh(new THREE.BoxGeometry(1.06, 0.06, 0.54), this.blackMaterial);
        bedLine.position.set(0, 0.54, 0.63);
        truck.add(bedLine);

        const lightBar = new THREE.Mesh(new THREE.BoxGeometry(0.88, 0.06, 0.06), this.carLightMaterial);
        lightBar.position.set(0, 0.45, -1.08);
        truck.add(lightBar);

        const tailBar = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.06, 0.06), this.carTailLightMaterial);
        tailBar.position.set(0, 0.42, 1.08);
        truck.add(tailBar);

        this.addVehicleWheels(truck, 0.75, 0.82, 0.29);
        return truck;
    }

    private createBugattiVehicle(): THREE.Group {
        const car = new THREE.Group();
        car.name = 'bugattiChironVehicle';
        car.position.set(0, -0.02, 0.04);

        const body = new THREE.Mesh(new THREE.BoxGeometry(1.16, 0.26, 2.02), this.carRedMaterial);
        body.position.y = 0.23;
        body.castShadow = true;
        car.add(body);

        const nose = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.14, 0.72), this.carRedMaterial);
        nose.position.set(0, 0.4, -0.54);
        nose.castShadow = true;
        car.add(nose);

        const cabin = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.28, 0.58), this.carGlassMaterial);
        cabin.position.set(0, 0.56, 0.16);
        cabin.rotation.x = 0.08;
        cabin.castShadow = true;
        car.add(cabin);

        const centerStripe = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.05, 1.58), this.carDarkBlueMaterial);
        centerStripe.position.set(0, 0.51, -0.08);
        car.add(centerStripe);

        const grille = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.22, 0.07), this.blackMaterial);
        grille.position.set(0, 0.28, -1.04);
        grille.castShadow = true;
        car.add(grille);

        [-0.34, 0.34].forEach((x) => {
            const headlight = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.08, 0.06), this.carLightMaterial);
            headlight.position.set(x, 0.38, -1.04);
            car.add(headlight);

            const tail = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.06, 0.07), this.carTailLightMaterial);
            tail.position.set(x, 0.34, 1.04);
            car.add(tail);
        });

        const wing = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.08, 0.16), this.carDarkBlueMaterial);
        wing.position.set(0, 0.55, 0.93);
        wing.castShadow = true;
        car.add(wing);

        this.addVehicleWheels(car, 0.72, 0.78, 0.28);
        return car;
    }

    private createPorscheVehicle(): THREE.Group {
        const car = new THREE.Group();
        car.name = 'porsche911Gt3PinkVehicle';
        car.position.set(0, -0.03, 0.04);

        const body = new THREE.Mesh(new THREE.BoxGeometry(1.08, 0.26, 1.92), this.carPinkMaterial);
        body.position.y = 0.22;
        body.castShadow = true;
        car.add(body);

        const nose = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.12, 0.68), this.carPinkMaterial);
        nose.position.set(0, 0.37, -0.55);
        nose.castShadow = true;
        car.add(nose);

        const rearDeck = new THREE.Mesh(new THREE.BoxGeometry(0.98, 0.1, 0.58), this.carPinkMaterial);
        rearDeck.position.set(0, 0.38, 0.56);
        rearDeck.castShadow = true;
        car.add(rearDeck);

        const cabin = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.3, 0.58), this.carGlassMaterial);
        cabin.position.set(0, 0.56, -0.02);
        cabin.rotation.x = 0.06;
        cabin.castShadow = true;
        car.add(cabin);

        const roof = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.08, 0.42), this.carPinkMaterial);
        roof.position.set(0, 0.74, 0.02);
        roof.castShadow = true;
        car.add(roof);

        const frontIntake = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.13, 0.06), this.blackMaterial);
        frontIntake.position.set(0, 0.27, -1.02);
        frontIntake.castShadow = true;
        car.add(frontIntake);

        const hoodStripe = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.05, 1.32), this.carDarkBlueMaterial);
        hoodStripe.position.set(0, 0.47, -0.18);
        car.add(hoodStripe);

        [-0.36, 0.36].forEach((x) => {
            const headlight = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.08, 0.06), this.carLightMaterial);
            headlight.position.set(x, 0.39, -1.03);
            car.add(headlight);

            const tail = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.07, 0.06), this.carTailLightMaterial);
            tail.position.set(x, 0.34, 1.02);
            car.add(tail);

            const wingPost = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.28, 0.08), this.blackMaterial);
            wingPost.position.set(x * 0.74, 0.56, 0.88);
            wingPost.castShadow = true;
            car.add(wingPost);
        });

        const wing = new THREE.Mesh(new THREE.BoxGeometry(1.04, 0.07, 0.18), this.carDarkBlueMaterial);
        wing.position.set(0, 0.73, 0.95);
        wing.castShadow = true;
        car.add(wing);

        this.addVehicleWheels(car, 0.69, 0.76, 0.27);
        return car;
    }

    private addVehicleWheels(group: THREE.Group, x: number, z: number, radius: number): void {
        const tireGeometry = new THREE.CylinderGeometry(radius, radius, 0.18, 8);
        const rimGeometry = new THREE.CylinderGeometry(radius * 0.48, radius * 0.48, 0.19, 8);
        [-x, x].forEach((wheelX) => {
            [-z, z].forEach((wheelZ) => {
                const tire = new THREE.Mesh(tireGeometry, this.carTireMaterial);
                tire.position.set(wheelX, 0.08, wheelZ);
                tire.rotation.z = Math.PI / 2;
                tire.castShadow = true;
                group.add(tire);

                const rim = new THREE.Mesh(rimGeometry, this.carRimMaterial);
                rim.position.set(wheelX + (wheelX < 0 ? -0.01 : 0.01), 0.08, wheelZ);
                rim.rotation.z = Math.PI / 2;
                group.add(rim);
            });
        });
    }

    private createAdminOutfit(): THREE.Group {
        const outfit = new THREE.Group();
        outfit.name = 'adminOutfit';
        outfit.visible = false;
        this.adminHalo = undefined;
        this.adminAura = undefined;

        const cape = new THREE.Mesh(new THREE.BoxGeometry(1.02, 1.08, 0.12), this.adminCapeMaterial);
        cape.position.set(0, 0.56, 0.48);
        cape.castShadow = true;
        outfit.add(cape);

        const capeLining = new THREE.Mesh(new THREE.BoxGeometry(0.64, 0.88, 0.14), this.adminVoidMaterial);
        capeLining.position.set(0, 0.56, 0.42);
        capeLining.castShadow = true;
        outfit.add(capeLining);

        const capeHem = new THREE.Mesh(new THREE.BoxGeometry(0.94, 0.12, 0.16), this.whiteMaterial);
        capeHem.position.set(0, 0.02, 0.43);
        capeHem.castShadow = true;
        outfit.add(capeHem);

        const coatFront = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.72, 0.07), this.adminVoidMaterial);
        coatFront.position.set(0, 0.58, -0.44);
        coatFront.castShadow = true;
        outfit.add(coatFront);

        const furCenter = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.76, 0.085), this.whiteMaterial);
        furCenter.position.set(0, 0.58, -0.5);
        furCenter.castShadow = true;
        outfit.add(furCenter);

        const furHem = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.12, 0.085), this.whiteMaterial);
        furHem.position.set(0, 0.2, -0.5);
        furHem.castShadow = true;
        outfit.add(furHem);

        const collar = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.18, 0.28), this.whiteMaterial);
        collar.position.set(0, 1.04, -0.04);
        collar.castShadow = true;
        outfit.add(collar);

        const shoulderGeometry = new THREE.BoxGeometry(0.28, 0.12, 0.34);
        [-0.64, 0.64].forEach((x) => {
            const shoulder = new THREE.Mesh(shoulderGeometry, this.adminGoldMaterial);
            shoulder.position.set(x * 0.86, 0.87, -0.04);
            shoulder.rotation.z = x < 0 ? -0.08 : 0.08;
            shoulder.castShadow = true;
            outfit.add(shoulder);
        });

        const belt = new THREE.Mesh(new THREE.BoxGeometry(0.76, 0.1, 0.09), this.blackMaterial);
        belt.position.set(0, 0.45, -0.52);
        belt.castShadow = true;
        outfit.add(belt);

        const buckle = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.16, 0.1), this.adminGoldMaterial);
        buckle.position.set(0, 0.45, -0.59);
        buckle.castShadow = true;
        outfit.add(buckle);

        const badge = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.18, 0.075), this.adminCyanMaterial);
        badge.position.set(0.2, 0.72, -0.55);
        badge.rotation.z = Math.PI / 4;
        badge.castShadow = true;
        outfit.add(badge);

        const bootTrimGeometry = new THREE.BoxGeometry(0.28, 0.08, 0.16);
        [-0.23, 0.23].forEach((x) => {
            const bootTrim = new THREE.Mesh(bootTrimGeometry, this.adminGoldMaterial);
            bootTrim.position.set(x, 0.1, -0.34);
            bootTrim.castShadow = true;
            outfit.add(bootTrim);
        });

        const crown = new THREE.Group();
        crown.position.set(0.04, 2.2, 0);
        const crownBase = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.09, 0.42), this.adminGoldMaterial);
        crownBase.castShadow = true;
        crown.add(crownBase);
        [-0.16, 0, 0.16].forEach((x, index) => {
            const spire = new THREE.Mesh(new THREE.BoxGeometry(0.1, index === 1 ? 0.22 : 0.17, 0.1), this.adminGoldMaterial);
            spire.position.set(x, index === 1 ? 0.13 : 0.1, 0);
            spire.castShadow = true;
            crown.add(spire);

            const gem = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.06, 0.08), index === 1 ? this.adminCyanMaterial : this.adminGoldMaterial);
            gem.position.set(x, index === 1 ? 0.26 : 0.2, 0);
            gem.castShadow = true;
            crown.add(gem);
        });
        outfit.add(crown);

        return outfit;
    }

    private createGift(): THREE.Group {
        const group = new THREE.Group();
        const box = new THREE.Mesh(new THREE.BoxGeometry(0.76, 0.76, 0.76), this.goldMaterial);
        box.castShadow = true;
        group.add(box);

        const vertical = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.86, 0.84), this.ribbonMaterial);
        vertical.castShadow = true;
        group.add(vertical);

        const horizontal = new THREE.Mesh(new THREE.BoxGeometry(0.84, 0.15, 0.84), this.ribbonMaterial);
        horizontal.castShadow = true;
        group.add(horizontal);

        const bowLeft = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.12, 0.2), this.ribbonMaterial);
        bowLeft.position.set(-0.16, 0.48, 0);
        bowLeft.castShadow = true;
        group.add(bowLeft);
        const bowRight = bowLeft.clone();
        bowRight.position.x = 0.16;
        group.add(bowRight);
        const bowKnot = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.16, 0.16), this.darkRedMaterial);
        bowKnot.position.y = 0.5;
        bowKnot.castShadow = true;
        group.add(bowKnot);
        return group;
    }

    private createCoin(): THREE.Group {
        const group = new THREE.Group();
        const coin = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.28, 0.12, 8), this.coinMaterial);
        coin.rotation.x = Math.PI / 2;
        coin.castShadow = true;
        group.add(coin);

        const center = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.03, 0.32), this.coinDarkMaterial);
        center.position.y = 0.01;
        center.castShadow = true;
        group.add(center);

        const shine = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.035, 0.08), this.whiteMaterial);
        shine.position.set(-0.08, 0.08, -0.08);
        group.add(shine);
        return group;
    }

    private createHeart(): THREE.Group {
        const group = new THREE.Group();
        const blocks = [
            { x: -0.18, y: 0.16, z: 0, w: 0.26, h: 0.24, d: 0.16, dark: false },
            { x: 0.18, y: 0.16, z: 0, w: 0.26, h: 0.24, d: 0.16, dark: false },
            { x: 0, y: 0.02, z: 0, w: 0.54, h: 0.28, d: 0.18, dark: false },
            { x: 0, y: -0.2, z: 0, w: 0.34, h: 0.24, d: 0.18, dark: true },
            { x: 0, y: -0.4, z: 0, w: 0.18, h: 0.2, d: 0.16, dark: true }
        ];

        blocks.forEach((piece) => {
            const block = new THREE.Mesh(
                new THREE.BoxGeometry(piece.w, piece.h, piece.d),
                piece.dark ? this.heartDarkMaterial : this.heartMaterial
            );
            block.position.set(piece.x, piece.y, piece.z);
            block.castShadow = true;
            group.add(block);
        });

        const shine = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.08, 0.04), this.whiteMaterial);
        shine.position.set(-0.18, 0.2, 0.09);
        group.add(shine);

        const glow = new THREE.PointLight(0xff4f7e, 0.78, 4.5);
        glow.position.y = 0.02;
        group.add(glow);
        group.userData = { glow };
        return group;
    }

    private createCheckpoint(index: number): THREE.Group {
        const group = new THREE.Group();
        group.name = `savePoint${index + 1}`;

        const base = new THREE.Mesh(new THREE.CylinderGeometry(0.82, 0.96, 0.2, 8), this.checkpointBaseMaterial);
        base.position.y = 0.1;
        base.castShadow = true;
        base.receiveShadow = true;
        group.add(base);

        const snowCap = new THREE.Mesh(new THREE.CylinderGeometry(0.88, 0.88, 0.08, 8), this.snowMaterial);
        snowCap.position.y = 0.25;
        snowCap.castShadow = true;
        group.add(snowCap);

        const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.18, 1.28, 0.18), this.checkpointBaseMaterial);
        pillar.position.y = 0.88;
        pillar.castShadow = true;
        group.add(pillar);

        const ring = new THREE.Mesh(new THREE.TorusGeometry(0.64, 0.06, 4, 4), this.checkpointRingMaterial.clone());
        ring.position.y = 1.48;
        ring.rotation.z = Math.PI / 4;
        ring.castShadow = true;
        group.add(ring);

        const core = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 0.9), this.checkpointCoreMaterial.clone());
        core.position.y = 1.48;
        core.rotation.z = Math.PI / 4;
        group.add(core);

        const flag = new THREE.Group();
        const flagBack = new THREE.Mesh(new THREE.BoxGeometry(0.54, 0.34, 0.07), this.checkpointRingMaterial.clone());
        flagBack.position.set(0.35, 1.14, 0);
        flagBack.castShadow = true;
        flag.add(flagBack);
        const flagCut = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.12, 0.08), this.snowMaterial);
        flagCut.position.set(0.5, 1.14, 0.01);
        flagCut.rotation.z = Math.PI / 4;
        flag.add(flagCut);
        group.add(flag);

        const light = new THREE.PointLight(0x76eeff, 1.15, 7);
        light.position.y = 1.5;
        group.add(light);

        group.userData = { ring, core, flagBack, light, reached: false, baseY: 1.48 };
        return group;
    }

    private createSnowman(): THREE.Group {
        const group = new THREE.Group();
        const body = new THREE.Mesh(new THREE.BoxGeometry(0.86, 0.72, 0.78), this.whiteMaterial);
        body.position.y = 0.1;
        body.castShadow = true;
        group.add(body);

        const middle = new THREE.Mesh(new THREE.BoxGeometry(0.68, 0.58, 0.64), this.whiteMaterial);
        middle.position.y = 0.64;
        middle.castShadow = true;
        group.add(middle);

        const head = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.44, 0.46), this.whiteMaterial);
        head.position.y = 1.11;
        head.castShadow = true;
        group.add(head);

        const hatBrim = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.1, 0.56), this.blackMaterial);
        hatBrim.position.y = 1.38;
        hatBrim.castShadow = true;
        group.add(hatBrim);

        const hat = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.36, 0.38), this.blackMaterial);
        hat.position.y = 1.6;
        hat.castShadow = true;
        group.add(hat);
        return group;
    }

    private addSnow(): void {
        const columns = 26;
        const rows = 22;
        const count = columns * rows;
        this.snowColumns = columns;
        this.snowFallSpeeds = new Float32Array(count);
        this.snowDriftPhases = new Float32Array(count);
        const positions = new Float32Array(count * 3);
        const cellX = 68 / columns;
        const cellZ = 94 / rows;

        for (let index = 0; index < count; index++) {
            const column = index % columns;
            const row = Math.floor(index / columns);
            positions[index * 3] = -34 + (column + 0.5) * cellX + THREE.MathUtils.randFloat(-cellX * 0.18, cellX * 0.18);
            positions[index * 3 + 1] = THREE.MathUtils.randFloat(1.5, 29);
            positions[index * 3 + 2] = 15 - (row + 0.5) * cellZ + THREE.MathUtils.randFloat(-cellZ * 0.18, cellZ * 0.18);
            this.snowFallSpeeds[index] = THREE.MathUtils.randFloat(1.25, 2.35);
            this.snowDriftPhases[index] = THREE.MathUtils.randFloat(0, Math.PI * 2);
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.snow = new THREE.Points(
            geometry,
            new THREE.PointsMaterial({ color: 0xffffff, size: 2.7, sizeAttenuation: false, transparent: true, opacity: 0.52, depthWrite: false, fog: false })
        );
        this.snow.renderOrder = 1;
        this.scene.add(this.snow);
    }

    private addStars(): void {
        const count = 620;
        const positions = new Float32Array(count * 3);
        const colors = new Float32Array(count * 3);
        const cool = new THREE.Color(0xb9d7ff);
        const warm = new THREE.Color(0xfff0c9);
        const bright = new THREE.Color(0xffffff);
        this.starBaseColors = new Float32Array(count * 3);
        this.starPhases = new Float32Array(count);

        for (let index = 0; index < count; index++) {
            positions[index * 3] = THREE.MathUtils.randFloatSpread(112);
            positions[index * 3 + 1] = THREE.MathUtils.randFloat(7, 39);
            positions[index * 3 + 2] = THREE.MathUtils.randFloat(-96, 10);

            const color = Math.random() > 0.86 ? warm : (Math.random() > 0.7 ? bright : cool);
            const brightness = THREE.MathUtils.randFloat(0.74, 1.25);
            this.starBaseColors[index * 3] = color.r * brightness;
            this.starBaseColors[index * 3 + 1] = color.g * brightness;
            this.starBaseColors[index * 3 + 2] = color.b * brightness;
            colors[index * 3] = this.starBaseColors[index * 3];
            colors[index * 3 + 1] = this.starBaseColors[index * 3 + 1];
            colors[index * 3 + 2] = this.starBaseColors[index * 3 + 2];
            this.starPhases[index] = THREE.MathUtils.randFloat(0, Math.PI * 2);
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        this.stars = new THREE.Points(
            geometry,
            new THREE.PointsMaterial({
                size: 3.1,
                sizeAttenuation: false,
                transparent: true,
                opacity: 1,
                vertexColors: true,
                depthWrite: false,
                fog: false
            })
        );
        this.stars.renderOrder = -1;
        this.scene.add(this.stars);
    }

    private tick = (frame: number): void => {
        const delta = Math.min((frame - this.lastFrame) / 1000, 0.033);
        this.lastFrame = frame;
        this.update(delta);
        this.renderer.render(this.scene, this.camera);
        this.animationId = requestAnimationFrame(this.tick);
    };

    private update(delta: number): void {
        this.elapsed += delta;
        this.updateMouseLook(delta);
        this.updateDamageUi(delta);
        if (!this.started) {
            this.updateVisuals(delta * 0.35);
            this.updateHud();
            this.hudAccumulator += delta;
            if (this.hudAccumulator > 0.12) {
                this.hudAccumulator = 0;
                this.changeDetector.detectChanges();
            }
            return;
        }

        if (this.paused || this.gameMenuOpen) {
            this.updateHud();
            this.hudAccumulator += delta;
            if (this.hudAccumulator > 0.12) {
                this.hudAccumulator = 0;
                this.changeDetector.detectChanges();
            }
            return;
        }

        if (!this.finished) {
            this.timeLeft = Math.max(0, this.timeLeft - delta);
            if (this.timeLeft <= 0) this.loseLife(true);
        }

        this.updateMovingPlatforms();
        this.updateShakyPlatforms();
        this.updateInput(delta);
        this.updatePhysics(delta);
        this.updateFireBridges(delta);
        this.updateDimensionPortals(delta);
        this.updateFragilePlatforms(delta);
        this.updateMeteorHazards(delta);
        this.updateVisuals(delta);
        this.collectGifts();
        this.collectCoins();
        this.collectHearts();
        this.checkSavePoints();
        this.checkEnemyHits();
        this.checkGoal();
        this.updateHud();

        this.hudAccumulator += delta;
        if (this.hudAccumulator > 0.12) {
            this.hudAccumulator = 0;
            this.changeDetector.detectChanges();
        }
    }

    private updateMovingPlatforms(): void {
        const intensity = this.movingPlatformIntensity();
        this.platforms.forEach((platform) => {
            const hasVectorMove = platform.moveX !== undefined || platform.moveZ !== undefined;
            if (!platform.speed || (!hasVectorMove && (!platform.axis || !platform.distance))) return;
            const previousX = platform.x;
            const previousY = platform.y;
            const previousZ = platform.z;
            const carryingPlayer = this.isPlayerOnPlatform(platform, previousX, previousZ);
            const phase = platform.phase || 0;
            const speed = platform.speed * intensity.speed;
            const baseWave = Math.sin(this.elapsed * speed + phase);
            const snapWave = Math.sin(this.elapsed * speed * 2.35 + phase * 1.7) * intensity.snap;
            const value = THREE.MathUtils.clamp(baseWave * intensity.range + snapWave, -intensity.range, intensity.range);
            platform.y = platform.startY + Math.sin(this.elapsed * speed * 1.6 + phase * 0.8) * intensity.bob;
            if (hasVectorMove) {
                platform.x = platform.startX + value * (platform.moveX ?? 0);
                platform.z = platform.startZ + value * (platform.moveZ ?? 0);
            } else {
                const axisValue = value * (platform.distance ?? 0);
                if (platform.axis === 'x') platform.x = platform.startX + axisValue;
                if (platform.axis === 'z') platform.z = platform.startZ + axisValue;
            }
            platform.mesh.position.set(platform.x, platform.y, platform.z);
            if (carryingPlayer) {
                this.playerPosition.x += platform.x - previousX;
                this.playerPosition.y += platform.y - previousY;
                this.playerPosition.z += platform.z - previousZ;
            }
        });
    }

    private updateShakyPlatforms(): void {
        const intensity = this.shakyPlatformIntensity();
        this.platforms.forEach((platform) => {
            if (!platform.shaky || platform.broken) return;
            const previousX = platform.x;
            const previousY = platform.y;
            const previousZ = platform.z;
            const carryingPlayer = this.isPlayerOnPlatform(platform, previousX, previousZ);
            const speed = (platform.speed ?? 5.6) * intensity.speed;
            const phase = platform.phase ?? 0;
            const power = (platform.shakePower ?? 0.16) * intensity.power;
            const wobble = Math.sin(this.elapsed * speed + phase);
            const cross = Math.cos(this.elapsed * (speed * 0.77) + phase * 1.7);

            platform.x = platform.startX + wobble * power;
            platform.y = platform.startY + cross * power * intensity.vertical;
            platform.z = platform.startZ + cross * power * intensity.lateral;
            platform.mesh.position.set(platform.x, platform.y, platform.z);

            if (platform.shakeGroup) {
                platform.shakeGroup.rotation.y = wobble * intensity.tilt;
                platform.shakeGroup.children.forEach((child, index) => {
                    const material = (child as THREE.Mesh).material;
                    if (material instanceof THREE.MeshBasicMaterial) {
                        material.opacity = 0.24 + Math.abs(Math.sin(this.elapsed * 7.2 + index + phase)) * 0.5;
                    }
                });
            }

            if (carryingPlayer) {
                this.playerPosition.x += platform.x - previousX;
                this.playerPosition.y += platform.y - previousY;
                this.playerPosition.z += platform.z - previousZ;
            }
        });
    }

    private movingPlatformIntensity(): { speed: number; range: number; snap: number; bob: number } {
        if (this.currentLevelIndex >= 15) return { speed: 1.55, range: 1.42, snap: 0.11, bob: 0.15 };
        if (this.currentLevelIndex >= 12) return { speed: 1.38, range: 1.32, snap: 0.08, bob: 0.11 };
        if (this.currentLevelIndex >= 5) return { speed: 1.25, range: 1.22, snap: 0.06, bob: 0.08 };
        return { speed: 1.14, range: 1.12, snap: 0.04, bob: 0.05 };
    }

    private shakyPlatformIntensity(): { speed: number; power: number; vertical: number; lateral: number; tilt: number } {
        if (this.currentLevelIndex >= 15) return { speed: 1.5, power: 1.62, vertical: 0.65, lateral: 1.4, tilt: 0.09 };
        if (this.currentLevelIndex >= 12) return { speed: 1.35, power: 1.45, vertical: 0.52, lateral: 1.2, tilt: 0.065 };
        return { speed: 1.18, power: 1.3, vertical: 0.44, lateral: 1.02, tilt: 0.05 };
    }

    private updateBouncePads(delta: number): void {
        this.platforms.forEach((platform, index) => {
            if (!platform.bounceGroup || platform.broken) return;

            const pulse = 0.5 + Math.sin(this.elapsed * 5.4 + index) * 0.5;
            platform.bounceGroup.scale.y = 0.94 + pulse * 0.08;

            const arrow = platform.bounceGroup.userData['arrow'];
            if (arrow instanceof THREE.Mesh) {
                arrow.position.y = platform.h * 1.35 + Math.sin(this.elapsed * 6.4 + index) * 0.12;
                arrow.rotation.y += delta * 1.6;
            }

            const light = platform.bounceGroup.userData['light'];
            if (light instanceof THREE.PointLight) {
                light.intensity = 0.75 + pulse * 0.55;
            }
        });
    }

    private updateFireBridges(delta: number): void {
        this.platforms.forEach((platform) => {
            if (!platform.fireBridge || !platform.fireGroup) return;
            const active = this.isFireBridgeActive(platform);
            platform.fireGroup.visible = active;
            platform.fireGroup.children.forEach((child, index) => {
                const phase = Number(child.userData['phase'] ?? index * 0.43);
                child.scale.setScalar(0.88 + Math.sin(this.elapsed * 7.2 + phase) * 0.08);
                const baseY = Number(child.userData['baseY'] ?? child.position.y);
                child.position.y = baseY + Math.sin(this.elapsed * 8.5 + phase) * 0.035;
                if (child instanceof THREE.PointLight) {
                    child.intensity = active ? 1.1 + Math.sin(this.elapsed * 9) * 0.22 : 0;
                }
            });

            if (!active || this.isAdmin || this.finished || this.gameOver) return;
            if (this.isPlayerOnPlatform(platform, platform.x, platform.z)) {
                this.loseLives(1, 'FIRE BRIDGE');
            }
        });
    }

    private isFireBridgeActive(platform: Platform): boolean {
        const cycle = platform.fireCycle ?? 6.4;
        const offDuration = platform.fireOffDuration ?? 3;
        const cycleTime = (this.elapsed + (platform.firePhase ?? 0)) % cycle;
        return cycleTime >= offDuration;
    }

    private updateDimensionPortals(delta: number): void {
        if (this.dimensionPortals.length === 0) return;
        this.dimensionPortalCooldown = Math.max(0, this.dimensionPortalCooldown - delta);

        this.dimensionPortals.forEach((portal) => {
            portal.cooldown = Math.max(0, portal.cooldown - delta);
            portal.group.rotation.y += delta * 0.75;
            const core = portal.group.userData['core'];
            if (core instanceof THREE.Mesh) {
                const material = core.material;
                if (material instanceof THREE.MeshBasicMaterial) {
                    material.opacity = 0.16 + Math.sin(this.elapsed * 5.2 + portal.x) * 0.06;
                }
            }
        });

        if (!this.started || this.finished || this.gameOver || this.dimensionPortalCooldown > 0) return;
        const portal = this.dimensionPortals.find((candidate) => (
            candidate.cooldown <= 0 &&
            Math.hypot(candidate.x - this.playerPosition.x, candidate.z - this.playerPosition.z) < 1.28 &&
            Math.abs(candidate.y - this.playerPosition.y) < 1.6
        ));
        if (!portal) return;

        this.playerPosition.copy(portal.target);
        this.playerVelocity.set(0, 0, 0);
        this.slideDrift.set(0, 0);
        this.airJumpsUsed = 0;
        this.clearControls();
        this.coyoteTimer = 0.12;
        this.dimensionPortalCooldown = 1.25;
        this.dimensionPortals.forEach((candidate) => {
            candidate.cooldown = 1.25;
        });
        this.banner = 'DIMENSION SHIFT';
        this.bannerDetail = 'Warped across the map.';
        this.bannerTone = 'normal';
        this.damageBannerTimer = 0;
    }

    private updateFragilePlatforms(delta: number): void {
        this.platforms.forEach((platform) => {
            if (!platform.fragile) return;

            if (platform.broken) {
                platform.respawnTimer = Math.max(0, (platform.respawnTimer ?? 0) - delta);
                if (platform.respawnTimer === 0) {
                    platform.broken = false;
                    platform.fragileTimer = 0;
                    platform.mesh.visible = true;
                }
                return;
            }

            const standing = this.started && !this.finished && this.isPlayerOnPlatform(platform, platform.x, platform.z);
            platform.fragileTimer = standing
                ? (platform.fragileTimer ?? 0) + delta
                : Math.max(0, (platform.fragileTimer ?? 0) - delta * 1.45);

            const progress = THREE.MathUtils.clamp((platform.fragileTimer ?? 0) / (platform.breakLimit ?? 1.5), 0, 1);
            if (platform.warningGroup) {
                platform.warningGroup.visible = progress > 0.02;
                platform.warningGroup.scale.setScalar(1 + progress * 0.06);
                platform.warningGroup.children.forEach((child) => {
                    const material = (child as THREE.Mesh).material;
                    if (material instanceof THREE.MeshBasicMaterial) {
                        material.opacity = 0.12 + progress * 0.5;
                    }
                });
            }

            if (progress < 1) return;
            platform.broken = true;
            platform.respawnTimer = this.currentLevelIndex >= 7 ? 5.1 : 4.2;
            platform.fragileTimer = 0;
            platform.mesh.visible = false;
            this.banner = 'ICE BREAK';
            this.bannerDetail = 'KEEP MOVING';
            this.bannerTone = 'danger';
            this.damageBannerTimer = 1.1;
            if (standing) {
                this.playerVelocity.y = Math.min(this.playerVelocity.y, -2.4);
            }
        });
    }

    private updateMeteorHazards(delta: number): void {
        if (this.currentLevelIndex < 6 || !this.started || this.finished || this.gameOver) {
            this.meteorTimer = 0.9;
            return;
        }

        this.meteorTimer -= delta;
        if (this.meteorTimer <= 0) {
            this.spawnMeteorHazard();
            this.meteorTimer = this.currentLevelIndex >= 12
                ? THREE.MathUtils.randFloat(0.52, 0.92)
                : this.currentLevelIndex >= 7
                    ? THREE.MathUtils.randFloat(0.95, 1.55)
                    : THREE.MathUtils.randFloat(1.65, 2.45);
        }

        this.meteors = this.meteors.filter((meteor) => {
            meteor.timer -= delta;
            const pulse = 1 + Math.sin(this.elapsed * 10) * 0.06;
            meteor.warning.scale.setScalar(pulse);

            if (!meteor.falling && meteor.timer <= 0) {
                meteor.falling = true;
                meteor.body.visible = true;
            }

            if (!meteor.falling) return true;
            meteor.body.position.y -= meteor.fallSpeed * this.meteorFallSpeedScale * delta;
            meteor.body.rotation.x += delta * 3.2;
            meteor.body.rotation.z += delta * 2.4;

            if (meteor.body.position.y > meteor.top + 0.78) return true;
            this.resolveMeteorImpact(meteor);
            this.world.remove(meteor.group);
            return false;
        });
    }

    private spawnMeteorHazard(): void {
        const target = this.pickMeteorTarget();
        if (!target) return;

        const radius = this.currentLevelIndex >= 12 ? 2.42 : this.currentLevelIndex >= 7 ? 2.05 : 1.7;
        const group = new THREE.Group();
        const warningMaterial = new THREE.MeshBasicMaterial({ color: 0xff2338, transparent: true, opacity: 0.34, depthWrite: false });
        const warning = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, 0.05, 24), warningMaterial);
        warning.position.set(target.x, target.top + 0.09, target.z);
        warning.rotation.y = Math.PI / 24;
        group.add(warning);

        const body = this.createMeteorMesh();
        body.visible = false;
        body.position.set(target.x, target.top + 15.5, target.z);
        group.add(body);

        this.world.add(group);
        this.meteors.push({
            group,
            warning,
            body,
            x: target.x,
            z: target.z,
            top: target.top,
            radius,
            timer: this.currentLevelIndex >= 12 ? 0.62 : this.currentLevelIndex >= 7 ? 0.86 : 1.08,
            fallSpeed: this.currentLevelIndex >= 12 ? 28 : this.currentLevelIndex >= 7 ? 22 : 18,
            falling: false,
            hit: false
        });
    }

    private pickMeteorTarget(): { x: number; z: number; top: number } | undefined {
        const offsets = [
            [0, 0],
            [2.2, 0.6],
            [-2.3, -0.4],
            [0.8, -2.1],
            [-0.6, 2.2]
        ];

        for (const [offsetX, offsetZ] of offsets.sort(() => Math.random() - 0.5)) {
            const x = this.playerPosition.x + offsetX + THREE.MathUtils.randFloat(-0.55, 0.55);
            const z = this.playerPosition.z + offsetZ + THREE.MathUtils.randFloat(-0.55, 0.55);
            const top = this.platformTopAt(x, z);
            if (top !== undefined) return { x, z, top };
        }

        const candidates = this.platforms.filter((platform) => !platform.broken && platform.kind !== 'woodDisk' && platform.mesh.visible);
        const platform = candidates[Math.floor(Math.random() * candidates.length)];
        if (!platform) return undefined;
        const safeX = Math.max(0.3, platform.w / 2 - 1.7);
        const safeZ = Math.max(0.3, platform.d / 2 - 1.7);
        return {
            x: platform.x + THREE.MathUtils.randFloat(-safeX, safeX),
            z: platform.z + THREE.MathUtils.randFloat(-safeZ, safeZ),
            top: platform.y + platform.h * 0.7
        };
    }

    private resolveMeteorImpact(meteor: MeteorHazard): void {
        if (meteor.hit) return;
        meteor.hit = true;
        const distance = Math.hypot(this.playerPosition.x - meteor.x, this.playerPosition.z - meteor.z);
        const nearFloor = Math.abs(this.playerPosition.y - meteor.top) < 2.2;
        if (distance <= meteor.radius && nearFloor) {
            this.loseLives(2, 'METEOR HIT');
        } else {
            this.banner = 'METEOR';
            this.bannerDetail = 'WATCH THE RED MARK';
            this.bannerTone = 'normal';
        }
    }

    private createMeteorMesh(): THREE.Group {
        const meteor = new THREE.Group();
        const core = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.92, 0.92), this.meteorMaterial);
        core.castShadow = true;
        meteor.add(core);

        [
            { x: 0.42, y: 0.18, z: -0.18, s: 0.42 },
            { x: -0.36, y: -0.18, z: 0.24, s: 0.36 },
            { x: 0.04, y: 0.48, z: 0.3, s: 0.28 },
            { x: -0.06, y: -0.42, z: -0.3, s: 0.3 }
        ].forEach((piece) => {
            const block = new THREE.Mesh(new THREE.BoxGeometry(piece.s, piece.s, piece.s), this.meteorCoreMaterial);
            block.position.set(piece.x, piece.y, piece.z);
            block.castShadow = true;
            meteor.add(block);
        });

        const glow = new THREE.PointLight(0xff4d25, 1.8, 7);
        glow.position.y = 0.25;
        meteor.add(glow);
        return meteor;
    }

    private isPlayerOnPlatform(platform: Platform, platformX: number, platformZ: number): boolean {
        if (platform.broken) return false;
        const top = platform.y + platform.h * 0.7;
        return (
            Math.abs(this.playerPosition.x - platformX) <= platform.w / 2 + 0.08 &&
            Math.abs(this.playerPosition.z - platformZ) <= platform.d / 2 + 0.08 &&
            Math.abs(this.playerPosition.y - top) < 0.16 &&
            this.playerVelocity.y <= 0.15
        );
    }

    private updateInput(delta: number): void {
        if (this.finished) return;

        this.jumpBufferTimer = Math.max(0, this.jumpBufferTimer - delta);
        const grounded = this.isGrounded();
        if (grounded) this.airJumpsUsed = 0;
        this.coyoteTimer = grounded ? 0.12 : Math.max(0, this.coyoteTimer - delta);

        const left = this.keys.has('ArrowLeft') || this.keys.has('KeyA') || this.touch.left;
        const right = this.keys.has('ArrowRight') || this.keys.has('KeyD') || this.touch.right;
        const forward = this.keys.has('ArrowUp') || this.keys.has('KeyW') || this.touch.forward;
        const back = this.keys.has('ArrowDown') || this.keys.has('KeyS') || this.touch.back;
        const sprint = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
        const keyboardRight = (right ? 1 : 0) - (left ? 1 : 0);
        const keyboardForward = (forward ? 1 : 0) - (back ? 1 : 0);
        const keyboardActive = keyboardRight !== 0 || keyboardForward !== 0;
        const joystickStrength = THREE.MathUtils.clamp(this.joystickVector.length(), 0, 1);
        const joystickAmount = this.joystickAnalogAmount(joystickStrength, this.joystickMoveDeadZone);
        const joystickMoving = this.joystickActive && joystickAmount > 0;
        let inputVelocityX = 0;
        let inputVelocityZ = 0;
        if (joystickMoving && !keyboardActive) {
            const yaw = this.joystickMovementYaw();
            const input = new THREE.Vector3(Math.sin(yaw), 0, -Math.cos(yaw));
            const speed = (sprint ? this.playerDashSpeed : this.playerMoveSpeed) * THREE.MathUtils.clamp(joystickAmount, 0.22, 1);
            this.playerPosition.x += input.x * speed * delta;
            this.playerPosition.z += input.z * speed * delta;
            inputVelocityX = input.x * speed;
            inputVelocityZ = input.z * speed;
            const targetYaw = Math.atan2(input.x, input.z) + (this.isAdmin ? Math.PI : 0);
            this.player.rotation.y = this.lerpAngle(this.player.rotation.y, targetYaw, sprint ? this.playerDashTurnBlend : this.playerTurnBlend);
        } else {
            const rightInput = THREE.MathUtils.clamp(keyboardRight + this.joystickVector.x, -1, 1);
            const forwardInput = THREE.MathUtils.clamp(keyboardForward - this.joystickVector.y, -1, 1);
            const inputMagnitude = Math.hypot(rightInput, forwardInput);

            if (inputMagnitude > 0.05) {
                const yaw = this.getLookYaw();
                const forwardX = Math.sin(yaw);
                const forwardZ = -Math.cos(yaw);
                const rightX = Math.cos(yaw);
                const rightZ = Math.sin(yaw);
                const input = new THREE.Vector3(
                    rightInput * rightX + forwardInput * forwardX,
                    0,
                    rightInput * rightZ + forwardInput * forwardZ
                ).normalize();
                const analogStrength = keyboardActive ? 1 : THREE.MathUtils.clamp(joystickAmount, 0.22, 1);
                const speed = (sprint ? this.playerDashSpeed : this.playerMoveSpeed) * analogStrength;
                this.playerPosition.x += input.x * speed * delta;
                this.playerPosition.z += input.z * speed * delta;
                inputVelocityX = input.x * speed;
                inputVelocityZ = input.z * speed;
                const targetYaw = Math.atan2(input.x, input.z) + (this.isAdmin ? Math.PI : 0);
                this.player.rotation.y = this.lerpAngle(this.player.rotation.y, targetYaw, sprint ? this.playerDashTurnBlend : this.playerTurnBlend);
            }
        }

        this.updateSlipperyDrift(delta, grounded, inputVelocityX, inputVelocityZ);

        if (this.jumpQueued && this.jumpBufferTimer > 0) {
            const canGroundJump = this.coyoteTimer > 0;
            if (canGroundJump) {
                this.playerVelocity.y = this.playerJumpVelocity;
                this.jumpBufferTimer = 0;
                this.coyoteTimer = 0;
                this.airJumpsUsed = 0;
            }
        }
        this.jumpQueued = this.jumpBufferTimer > 0;
    }

    private updateSlipperyDrift(delta: number, grounded: boolean, inputVelocityX: number, inputVelocityZ: number): void {
        const standingPlatform = grounded ? this.currentStandingPlatform() : undefined;
        if (standingPlatform?.slippery || standingPlatform?.kind === 'slipperyIce') {
            const inputSpeed = Math.hypot(inputVelocityX, inputVelocityZ);
            const nightmareIce = this.currentLevelIndex >= 12;
            if (inputSpeed > 0.1) {
                const blend = 1 - Math.exp(-delta * (nightmareIce ? 1.18 : 1.75));
                const targetDrift = new THREE.Vector2(
                    inputVelocityX * (nightmareIce ? 1.86 : 1.48),
                    inputVelocityZ * (nightmareIce ? 1.86 : 1.48)
                );
                const maxDrift = nightmareIce ? 13.4 : 10.6;
                if (targetDrift.length() > maxDrift) targetDrift.setLength(maxDrift);
                this.slideDrift.lerp(targetDrift, blend);
            } else {
                const carryingDrift = this.slideDrift.lengthSq() > 0.08;
                const idleAngle = carryingDrift
                    ? Math.atan2(this.slideDrift.x, this.slideDrift.y)
                    : this.player.rotation.y + Math.sin(this.elapsed * 0.82 + standingPlatform.startX * 0.13 + standingPlatform.startZ * 0.07) * 0.42;
                const idleTarget = new THREE.Vector2(Math.sin(idleAngle), Math.cos(idleAngle))
                    .multiplyScalar(nightmareIce ? 3.15 : 2.05);
                const idleBlend = 1 - Math.exp(-delta * (nightmareIce ? 1.05 : 0.72));
                this.slideDrift.lerp(idleTarget, idleBlend);
            }

            this.playerPosition.x += this.slideDrift.x * delta;
            this.playerPosition.z += this.slideDrift.y * delta;
            this.slideDrift.multiplyScalar(Math.exp(-delta * (nightmareIce ? 0.025 : 0.07)));
            if (this.slideDrift.lengthSq() < 0.0001) this.slideDrift.set(0, 0);
            return;
        }

        this.slideDrift.multiplyScalar(Math.exp(-delta * 8.5));
        if (this.slideDrift.lengthSq() < 0.0004) this.slideDrift.set(0, 0);
    }

    private currentStandingPlatform(): Platform | undefined {
        let best: Platform | undefined;
        this.platforms.forEach((platform) => {
            if (!this.isPlayerOnPlatform(platform, platform.x, platform.z)) return;
            if (!best || platform.y > best.y) best = platform;
        });
        return best;
    }

    private updateEnemies(delta: number): void {
        this.enemies.forEach((enemy) => {
            if (enemy.chasing && this.started && !this.finished) {
                const distanceToPlayer = Math.hypot(this.playerPosition.x - enemy.x, this.playerPosition.z - enemy.z);
                if (distanceToPlayer < (enemy.chaseRange ?? 12) && distanceToPlayer > 0.2) {
                    this.moveEnemyToward(enemy, this.playerPosition.x, this.playerPosition.z, (enemy.chaseSpeed ?? 2.4) * this.enemyChaseSpeedScale, delta);
                } else {
                    this.moveEnemyToward(enemy, enemy.baseX, enemy.baseZ, (enemy.chaseSpeed ?? 2.4) * 0.55 * this.enemyChaseSpeedScale, delta);
                }
                enemy.mesh.position.set(enemy.x, enemy.y, enemy.z);
                return;
            }

            const value = Math.sin(this.elapsed * enemy.speed * this.enemyPatrolSpeedScale + enemy.phase) * enemy.distance;
            enemy.x = enemy.baseX;
            enemy.z = enemy.baseZ;
            enemy[enemy.axis] += value;
            enemy.mesh.position.set(enemy.x, enemy.y, enemy.z);
            enemy.mesh.rotation.y += delta * 2.6;
        });
    }

    private moveEnemyToward(enemy: Enemy, targetX: number, targetZ: number, speed: number, delta: number): void {
        const dx = targetX - enemy.x;
        const dz = targetZ - enemy.z;
        const distance = Math.hypot(dx, dz);
        if (distance < 0.05) return;

        const step = Math.min(distance, speed * delta);
        const nextX = enemy.x + (dx / distance) * step;
        const nextZ = enemy.z + (dz / distance) * step;
        const top = this.platformTopAt(nextX, nextZ);
        if (top === undefined || Math.abs(top + 0.52 - enemy.y) > 1.35) {
            enemy.mesh.rotation.y = this.lerpAngle(enemy.mesh.rotation.y, Math.atan2(dx, dz), 0.12);
            return;
        }

        enemy.x = nextX;
        enemy.z = nextZ;
        enemy.y = top + 0.52;
        enemy.mesh.rotation.y = this.lerpAngle(enemy.mesh.rotation.y, Math.atan2(dx, dz), 0.18);
    }

    private updatePhysics(delta: number): void {
        this.playerVelocity.y -= 23 * delta;
        this.playerPosition.y += this.playerVelocity.y * delta;

        const landingPlatform = this.platformAt(this.playerPosition.x, this.playerPosition.z, this.playerPosition.y + 0.38);
        const top = landingPlatform ? landingPlatform.y + landingPlatform.h * 0.7 : undefined;
        if (top !== undefined && this.playerPosition.y <= top && this.playerVelocity.y <= 0) {
            this.playerPosition.y = top;
            if (landingPlatform?.bouncePad || landingPlatform?.kind === 'bouncePad') {
                this.playerVelocity.y = landingPlatform.bouncePower ?? 15.5;
                this.jumpBufferTimer = 0;
                this.airJumpsUsed = 0;
                this.banner = 'BOUNCE';
                this.bannerDetail = 'Spring jump!';
                this.bannerTone = 'normal';
                this.damageBannerTimer = 0;
            } else {
                this.playerVelocity.y = 0;
            }
        }

        if (this.playerPosition.y < -5) {
            if (this.isAdmin) {
                this.setPlayerSpawn();
                return;
            }
            this.loseLife(false);
        }
    }

    private updateVisuals(delta: number): void {
        if (this.isCustomPage) {
            const tightPreview = this.viewportAspect < 0.85;
            const hasVehicle = this.customPreviewHasVehicle();
            const previewYRatio = hasVehicle ? (tightPreview ? 0.56 : 0.6) : (tightPreview ? 0.58 : 0.62);
            const previewTarget = this.previewWorldPoint('.custom-preview-frame', 0.5, previewYRatio);
            const previewX = previewTarget?.x ?? (tightPreview ? 0 : -1.12);
            const previewY = previewTarget?.y ?? (hasVehicle ? (tightPreview ? 2.26 : 0.74) : (tightPreview ? 2.18 : 0.62));
            const turn = Math.sin(this.elapsed * 0.9) * 0.18;
            this.player.scale.setScalar(this.customPreviewScale());
            this.player.position.set(previewX, previewY, 0);
            this.player.rotation.set(0, Math.PI + turn, 0);
        } else if (this.isShopPage) {
            const tightPreview = this.viewportAspect < 0.85;
            const hasVehicle = this.shopPreviewHasVehicle();
            const previewYRatio = hasVehicle ? (tightPreview ? 0.52 : 0.56) : (tightPreview ? 0.58 : 0.62);
            const previewTarget = this.previewWorldPoint('.shop-preview-frame', 0.5, previewYRatio);
            const previewX = previewTarget?.x ?? (tightPreview ? 0 : -2.35);
            const previewY = previewTarget?.y ?? (hasVehicle ? (tightPreview ? 2.38 : 0.86) : (tightPreview ? 2.18 : 0.62));
            const turn = Math.sin(this.elapsed * 0.85) * 0.16;
            this.player.scale.setScalar(this.shopPreviewScale());
            this.player.position.set(previewX, previewY, 0);
            this.player.rotation.set(0, Math.PI + turn, 0);
        } else {
            this.player.position.set(0, this.playerPosition.y + this.rideGroundLift(), 0);
        }

        this.updateAdminOutfit(delta);

        this.gifts.forEach((gift, index) => {
            if (gift.collected) return;
            gift.mesh.rotation.y += delta * 2.4;
            gift.mesh.position.y = gift.y + Math.sin(this.elapsed * 3 + index) * 0.08;
        });

        this.coins.forEach((coin, index) => {
            if (coin.collected) return;
            coin.mesh.rotation.y += delta * 4.2;
            coin.mesh.position.y = coin.y + Math.sin(this.elapsed * 4.4 + index * 0.6) * 0.08;
        });

        this.hearts.forEach((heart, index) => {
            if (heart.collected) return;
            heart.mesh.rotation.y += delta * 2.8;
            heart.mesh.position.y = heart.y + Math.sin(this.elapsed * 3.8 + index * 0.7) * 0.1;
            const glow = heart.mesh.userData['glow'];
            if (glow instanceof THREE.PointLight) glow.intensity = 0.62 + Math.sin(this.elapsed * 4 + index) * 0.18;
        });

        this.updateCheckpoints(delta);
        this.updateBouncePads(delta);
        this.updateEnemies(delta);

        const positions = this.snow.geometry.getAttribute('position') as THREE.BufferAttribute;
        for (let index = 0; index < positions.count; index++) {
            let x = positions.getX(index);
            const y = positions.getY(index) - delta * this.snowFallSpeeds[index];
            x += Math.sin(this.elapsed * 0.62 + this.snowDriftPhases[index]) * delta * 0.18;

            if (x > 35) x = -35;
            if (x < -35) x = 35;
            positions.setX(index, x);

            if (y < -1.5) {
                const column = index % this.snowColumns;
                const cellX = 68 / this.snowColumns;
                positions.setX(index, -34 + (column + 0.5) * cellX + THREE.MathUtils.randFloat(-cellX * 0.15, cellX * 0.15));
                positions.setY(index, THREE.MathUtils.randFloat(24, 31));
            } else {
                positions.setY(index, y);
            }
        }
        positions.needsUpdate = true;
        this.updateStars();
        this.updateGiftBursts(delta);

        this.updateCamera();
        this.updatePortal(delta);
    }

    private updateGiftBursts(delta: number): void {
        this.giftBursts = this.giftBursts.filter((burst) => {
            burst.age += delta;
            const progress = THREE.MathUtils.clamp(burst.age / burst.duration, 0, 1);
            burst.particles.forEach((particle) => {
                particle.velocity.y -= 8.2 * delta;
                particle.mesh.position.x += particle.velocity.x * delta;
                particle.mesh.position.y += particle.velocity.y * delta;
                particle.mesh.position.z += particle.velocity.z * delta;
                particle.mesh.rotation.x += particle.spin.x * delta;
                particle.mesh.rotation.y += particle.spin.y * delta;
                particle.mesh.rotation.z += particle.spin.z * delta;
                const material = particle.mesh.material as THREE.MeshBasicMaterial;
                material.opacity = 1 - progress;
            });
            burst.group.scale.setScalar(1 + progress * 0.7);
            if (burst.age < burst.duration) return true;
            this.world.remove(burst.group);
            return false;
        });
    }

    private updatePortal(delta: number): void {
        const open = this.giftsCollected >= this.giftTotal;
        const portal = this.goal.userData as {
            outerRing?: THREE.Mesh;
            innerRing?: THREE.Mesh;
            closedSeal?: THREE.Mesh;
            core?: THREE.Mesh;
            sparks?: THREE.Mesh[];
            arrow?: THREE.Mesh;
        };

        this.goalLight.intensity = open ? 4.2 + Math.sin(this.elapsed * 5) * 0.75 : 0.45;
        this.goalLight.color.set(open ? 0x71f0ff : 0x47617c);

        if (portal.outerRing) {
            portal.outerRing.rotation.z += open ? delta * 1.45 : delta * 0.22;
            const material = portal.outerRing.material as THREE.MeshStandardMaterial;
            material.emissiveIntensity = open ? 1.35 + Math.sin(this.elapsed * 4.5) * 0.3 : 0.28;
        }
        if (portal.innerRing) {
            portal.innerRing.rotation.z -= open ? delta * 2.2 : delta * 0.18;
            const material = portal.innerRing.material as THREE.MeshStandardMaterial;
            material.emissiveIntensity = open ? 1.15 + Math.sin(this.elapsed * 5.2) * 0.26 : 0.2;
        }
        if (portal.closedSeal) {
            portal.closedSeal.visible = !open;
            portal.closedSeal.rotation.z += delta * 0.45;
        }
        if (portal.core) {
            portal.core.visible = open;
            const material = portal.core.material as THREE.MeshBasicMaterial;
            material.opacity = open ? 0.26 + Math.sin(this.elapsed * 6) * 0.08 : 0;
            portal.core.rotation.z -= delta * 0.7;
        }
        if (portal.arrow) {
            portal.arrow.visible = open;
            portal.arrow.position.y = 3.0 + Math.sin(this.elapsed * 3.2) * 0.18;
            portal.arrow.rotation.y += open ? delta * 1.3 : delta * 0.2;
        }
        portal.sparks?.forEach((spark, index, all) => {
            spark.visible = open;
            const angle = this.elapsed * 1.7 + index * (Math.PI * 2 / all.length);
            spark.position.set(
                Math.cos(angle) * 1.16,
                1.55 + Math.sin(angle) * 1.05,
                Math.sin(this.elapsed * 3.1 + index) * 0.12
            );
            spark.rotation.y += delta * 2.4;
            spark.rotation.z += delta * 1.9;
        });
    }

    private updateStars(): void {
        const colors = this.stars.geometry.getAttribute('color') as THREE.BufferAttribute;
        for (let index = 0; index < colors.count; index++) {
            const pulse = 0.55 + Math.sin(this.elapsed * 2.8 + this.starPhases[index]) * 0.45;
            const flare = Math.sin(this.elapsed * 7.5 + this.starPhases[index] * 1.7) > 0.92 ? 1.55 : 1;
            const brightness = THREE.MathUtils.clamp(0.42 + pulse * flare, 0.32, 1.8);
            colors.setXYZ(
                index,
                this.starBaseColors[index * 3] * brightness,
                this.starBaseColors[index * 3 + 1] * brightness,
                this.starBaseColors[index * 3 + 2] * brightness
            );
        }
        colors.needsUpdate = true;
        this.stars.rotation.y = Math.sin(this.elapsed * 0.08) * 0.025;
    }

    private updateAdminOutfit(delta: number): void {
        if (!this.adminOutfit?.visible) return;

        const pulse = 0.5 + Math.sin(this.elapsed * 3.2) * 0.5;
        this.adminGlowMaterial.opacity = 0.46 + pulse * 0.2;
        this.adminAuraMaterial.opacity = 0.58 + pulse * 0.2;

        if (this.adminHalo) {
            this.adminHalo.rotation.y += delta * 1.35;
            this.adminHalo.position.y = 2.48 + Math.sin(this.elapsed * 2.4) * 0.035;
        }

        if (this.adminAura) {
            this.adminAura.rotation.y -= delta * 1.1;
            this.adminAura.children.forEach((child, index) => {
                const baseY = Number(child.userData['baseY'] ?? child.position.y);
                child.position.y = baseY + Math.sin(this.elapsed * 3.5 + index * 0.9) * 0.06;
            });
        }
    }

    private updateCamera(): void {
        if (this.isCustomPage) {
            this.updateCustomCamera();
            return;
        }
        if (this.isShopPage) {
            this.updateShopCamera();
            return;
        }

        const tightScreen = this.viewportAspect < 0.75;
        const cameraBack = tightScreen ? 8.8 : 7.7;
        const cameraHeight = tightScreen ? 5.2 : 4.65;
        const lookAhead = tightScreen ? 5.0 : 6.25;
        const lookY = this.applyLookDeadZone(this.mouseLook.y);
        const verticalLook = tightScreen ? 2.9 : 3.8;
        const cameraLift = tightScreen ? 0.5 : 0.7;
        const yaw = this.getLookYaw();
        const forwardX = Math.sin(yaw);
        const forwardZ = -Math.cos(yaw);
        const orbitStrength = Math.abs(yaw) / (Math.PI * 0.94);
        const cameraDistance = cameraBack + orbitStrength * (tightScreen ? 1.0 : 1.35);
        const targetDistance = lookAhead + orbitStrength * (tightScreen ? 0.9 : 1.35);

        this.world.position.set(-this.playerPosition.x, 0, -this.playerPosition.z);
        this.cameraPosition.set(
            -forwardX * cameraDistance,
            this.playerPosition.y + cameraHeight - lookY * cameraLift,
            -forwardZ * cameraDistance
        );
        this.cameraTarget.set(
            forwardX * targetDistance,
            this.playerPosition.y + 1.35 - lookY * verticalLook,
            forwardZ * targetDistance
        );
        this.camera.position.copy(this.cameraPosition);
        this.camera.lookAt(this.cameraTarget);
        this.camera.updateMatrixWorld(true);
    }

    private updateCustomCamera(): void {
        const tightScreen = this.viewportAspect < 0.85;
        this.world.position.set(0, 0, 0);
        this.camera.position.set(
            0,
            tightScreen ? 2.35 : 2.25,
            tightScreen ? 7.6 : 7.15
        );
        this.cameraTarget.set(0, tightScreen ? 1.12 : 1.08, 0);
        this.camera.lookAt(this.cameraTarget);
        this.camera.updateMatrixWorld(true);
    }

    private updateShopCamera(): void {
        this.updateCustomCamera();
    }

    private previewWorldPoint(selector: string, xRatio: number, yRatio: number): THREE.Vector3 | undefined {
        const element = this.host.nativeElement.querySelector(selector);
        if (!(element instanceof HTMLElement) || !this.viewportElement) return undefined;
        const rect = element.getBoundingClientRect();
        const viewportRect = this.viewportElement.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0 || viewportRect.width <= 0 || viewportRect.height <= 0) return undefined;

        const screenX = rect.left + rect.width * xRatio;
        const screenY = rect.top + rect.height * yRatio;
        this.previewPointer.set(
            ((screenX - viewportRect.left) / viewportRect.width) * 2 - 1,
            -(((screenY - viewportRect.top) / viewportRect.height) * 2 - 1)
        );
        this.previewRaycaster.setFromCamera(this.previewPointer, this.camera);
        const directionZ = this.previewRaycaster.ray.direction.z;
        if (Math.abs(directionZ) < 0.0001) return undefined;

        const distance = -this.previewRaycaster.ray.origin.z / directionZ;
        if (distance < 0) return undefined;
        return this.previewTarget
            .copy(this.previewRaycaster.ray.direction)
            .multiplyScalar(distance)
            .add(this.previewRaycaster.ray.origin);
    }

    private customPreviewScale(): number {
        if (this.customPreviewHasVehicle()) return this.viewportAspect < 0.85 ? 0.38 : 0.72;
        if (this.isAdmin) return this.viewportAspect < 0.85 ? 0.58 : 0.86;
        return this.viewportAspect < 0.85 ? 0.74 : 1.08;
    }

    private shopPreviewScale(): number {
        if (this.shopPreviewHasVehicle()) return this.viewportAspect < 0.85 ? 0.38 : 0.72;
        if (this.isAdmin) return this.viewportAspect < 0.85 ? 0.36 : 0.7;
        return this.viewportAspect < 0.85 ? 0.46 : 0.88;
    }

    private getLookYaw(): number {
        if (this.mobileLookMode) {
            return this.mobileLookYaw;
        }
        const lookX = this.applyLookDeadZone(this.mouseLook.x);
        const orbit = Math.sign(lookX) * Math.pow(Math.abs(lookX), 1.18);
        return orbit * Math.PI * 0.94;
    }

    private updateMouseLook(delta: number): void {
        if (!this.started || this.paused || this.gameMenuOpen || this.gameOver) {
            this.mouseLookTarget.set(0, 0);
        }
        const amount = 1 - Math.exp(-delta * 8);
        this.mouseLook.lerp(this.mouseLookTarget, amount);

        const joystickStrength = THREE.MathUtils.clamp(this.joystickVector.length(), 0, 1);
        if (
            this.mobileLookMode &&
            this.joystickActive &&
            joystickStrength >= this.mobileLookDeadZone &&
            this.started &&
            !this.paused &&
            !this.gameMenuOpen &&
            !this.gameOver &&
            !this.isMenuPage
        ) {
            const turnAmount = this.joystickAnalogAmount(joystickStrength, this.mobileLookDeadZone);
            const turnSpeed = THREE.MathUtils.lerp(this.mobileLookTurnSpeedMin, this.mobileLookTurnSpeedMax, turnAmount);
            this.mobileLookYawTarget = this.moveAngleTowards(this.mobileLookYawTarget, this.mobileDesiredYaw, turnSpeed * delta);
        }

        const mobileAmount = 1 - Math.exp(-delta * this.mobileLookSmoothing);
        this.mobileLookYaw = this.lerpAngle(this.mobileLookYaw, this.mobileLookYawTarget, mobileAmount);
    }

    private applyLookDeadZone(value: number): number {
        const deadZone = 0.08;
        const magnitude = Math.abs(value);
        if (magnitude <= deadZone) return 0;
        return Math.sign(value) * ((magnitude - deadZone) / (1 - deadZone));
    }

    private checkSavePoints(): void {
        if (this.finished) return;
        this.checkpoints.forEach((checkpoint) => {
            if (checkpoint.reached) return;
            const distance = Math.hypot(checkpoint.x - this.playerPosition.x, checkpoint.z - this.playerPosition.z);
            if (distance >= 1.45 || Math.abs(checkpoint.y - this.playerPosition.y) >= 2.2) return;
            this.activateCheckpoint(checkpoint);
        });
    }

    private activateCheckpoint(checkpoint: Checkpoint): void {
        checkpoint.reached = true;
        this.activeCheckpointIndex = checkpoint.index;
        this.respawnPoint.set(checkpoint.x, checkpoint.y, checkpoint.z);
        this.setCheckpointVisual(checkpoint, true);
        if (this.minimapCheckpoints[checkpoint.index]) this.minimapCheckpoints[checkpoint.index].collected = true;
        this.banner = 'SAVE POINT';
        this.bannerDetail = 'Respawn set here.';
        this.bannerTone = 'normal';
        this.damageBannerTimer = 0;
        this.playCoinSound();
    }

    private resetCheckpoint(checkpoint: Checkpoint): void {
        checkpoint.reached = false;
        this.setCheckpointVisual(checkpoint, false);
    }

    private setCheckpointVisual(checkpoint: Checkpoint, reached: boolean): void {
        const data = checkpoint.mesh.userData as {
            ring?: THREE.Mesh;
            core?: THREE.Mesh;
            flagBack?: THREE.Mesh;
            light?: THREE.PointLight;
            reached?: boolean;
        };
        data.reached = reached;
        if (data.ring) data.ring.material = reached ? this.checkpointReachedMaterial : this.checkpointRingMaterial;
        if (data.flagBack) data.flagBack.material = reached ? this.checkpointReachedMaterial : this.checkpointRingMaterial;
        if (data.light) {
            data.light.color.setHex(reached ? 0xfff173 : 0x76eeff);
            data.light.intensity = reached ? 1.85 : 1.15;
        }

        const coreMaterial = data.core?.material;
        if (coreMaterial instanceof THREE.MeshBasicMaterial) {
            coreMaterial.color.setHex(reached ? 0xfff173 : 0x9effff);
            coreMaterial.opacity = reached ? 0.5 : 0.35;
        }
    }

    private updateCheckpoints(delta: number): void {
        this.checkpoints.forEach((checkpoint, index) => {
            const data = checkpoint.mesh.userData as {
                ring?: THREE.Mesh;
                core?: THREE.Mesh;
                flagBack?: THREE.Mesh;
                light?: THREE.PointLight;
                baseY?: number;
                reached?: boolean;
            };
            const pulse = 0.5 + Math.sin(this.elapsed * 3.3 + index * 0.8) * 0.5;
            if (data.ring) {
                data.ring.rotation.y += delta * (data.reached ? 1.9 : 1.2);
                data.ring.position.y = (data.baseY ?? 1.48) + pulse * 0.06;
            }
            if (data.core) {
                data.core.rotation.z += delta * (data.reached ? 0.85 : 0.45);
                data.core.position.y = (data.baseY ?? 1.48) + pulse * 0.06;
            }
            if (data.flagBack) data.flagBack.position.y = 1.14 + pulse * 0.035;
            if (data.light) data.light.intensity = (data.reached ? 1.55 : 0.95) + pulse * 0.45;
        });
    }

    private collectGifts(): void {
        if (this.finished) return;
        const radius = 1.05;
        this.gifts.forEach((gift) => {
            if (gift.collected) return;
            const distance = Math.hypot(gift.x - this.playerPosition.x, gift.z - this.playerPosition.z);
            if (distance < radius && Math.abs(gift.y - this.playerPosition.y) < 1.8) {
                gift.collected = true;
                gift.mesh.visible = false;
                this.giftsCollected += 1;
                this.playGiftSound(this.giftsCollected);
                this.spawnGiftBurst(gift.x, gift.y, gift.z);
                this.banner = this.giftsCollected === this.giftTotal ? 'Portal open' : '';
                this.bannerDetail = this.giftsCollected === this.giftTotal ? 'Enter the portal to finish this stage.' : '';
                this.bannerTone = 'normal';
                this.damageBannerTimer = 0;
            }
        });
    }

    private collectCoins(): void {
        if (this.finished) return;
        const radius = 0.95;
        this.coins.forEach((coin) => {
            if (coin.collected) return;
            const distance = Math.hypot(coin.x - this.playerPosition.x, coin.z - this.playerPosition.z);
            if (distance < radius && Math.abs(coin.y - this.playerPosition.y) < 1.7) {
                coin.collected = true;
                coin.mesh.visible = false;
                this.coinsCollected += 1;
                this.queueCoinDeposit(1);
                this.playCoinSound();
                this.spawnGiftBurst(coin.x, coin.y, coin.z);
                this.banner = `COIN +1`;
                this.bannerDetail = this.account ? 'Saving to account.' : 'Login to keep it.';
                this.bannerTone = 'normal';
            }
        });
    }

    private collectHearts(): void {
        if (this.finished || this.isAdmin || this.lives >= this.maxLives) return;
        const radius = 1.0;
        this.hearts.forEach((heart, index) => {
            if (heart.collected || this.lives >= this.maxLives) return;
            const distance = Math.hypot(heart.x - this.playerPosition.x, heart.z - this.playerPosition.z);
            if (distance < radius && Math.abs(heart.y - this.playerPosition.y) < 1.8) {
                heart.collected = true;
                heart.mesh.visible = false;
                this.lives = Math.min(this.maxLives, this.lives + 1);
                if (this.minimapHearts[index]) this.minimapHearts[index].collected = true;
                this.playGiftSound(this.lives);
                this.spawnGiftBurst(heart.x, heart.y, heart.z);
                this.banner = 'HEART +1';
                this.bannerDetail = `${this.lives}/${this.maxLives} lives`;
                this.bannerTone = 'normal';
                this.damageBannerTimer = 0;
            }
        });
    }

    private spawnGiftBurst(x: number, y: number, z: number): void {
        const group = new THREE.Group();
        group.position.set(x, y, z);
        const particles: GiftBurstParticle[] = [];

        for (let index = 0; index < 18; index++) {
            const material = this.giftSparkMaterials[index % this.giftSparkMaterials.length].clone();
            const mesh = new THREE.Mesh(this.giftSparkGeometry, material);
            const angle = (index / 18) * Math.PI * 2;
            const radius = 1.15 + (index % 4) * 0.16;
            const up = 2.2 + (index % 5) * 0.28;
            mesh.position.set(
                Math.cos(angle) * 0.08,
                0.2 + (index % 3) * 0.05,
                Math.sin(angle) * 0.08
            );
            mesh.scale.setScalar(index % 3 === 0 ? 1.25 : 0.9);
            group.add(mesh);
            particles.push({
                mesh,
                velocity: new THREE.Vector3(Math.cos(angle) * radius, up, Math.sin(angle) * radius),
                spin: new THREE.Vector3(
                    4.5 + (index % 3) * 1.2,
                    5.2 + (index % 4) * 0.8,
                    3.8 + (index % 5) * 0.6
                )
            });
        }

        const flash = new THREE.Mesh(
            new THREE.BoxGeometry(1.35, 0.08, 1.35),
            new THREE.MeshBasicMaterial({ color: 0xfff173, transparent: true, opacity: 0.65, depthWrite: false })
        );
        flash.position.y = -0.32;
        group.add(flash);
        particles.push({
            mesh: flash,
            velocity: new THREE.Vector3(0, 0.4, 0),
            spin: new THREE.Vector3(0, 5.6, 0)
        });

        this.world.add(group);
        this.giftBursts.push({ group, particles, age: 0, duration: 0.58 });
    }

    private playGiftSound(combo: number): void {
        const context = this.ensureAudioContext();
        if (!context) return;

        const start = context.currentTime;
        const master = context.createGain();
        master.gain.setValueAtTime(0.0001, start);
        master.gain.linearRampToValueAtTime(0.55, start + 0.01);
        master.gain.exponentialRampToValueAtTime(0.0001, start + 0.42);

        const sparkle = context.createBiquadFilter();
        sparkle.type = 'highpass';
        sparkle.frequency.setValueAtTime(520, start);
        sparkle.Q.setValueAtTime(0.65, start);
        sparkle.connect(master);
        master.connect(context.destination);

        const lift = combo % 4;
        [
            { frequency: 880 + lift * 18, delay: 0, duration: 0.17, volume: 0.11 },
            { frequency: 1174 + lift * 24, delay: 0.055, duration: 0.2, volume: 0.095 },
            { frequency: 1568 + lift * 32, delay: 0.11, duration: 0.24, volume: 0.07 }
        ].forEach(({ frequency, delay, duration, volume }, index) => {
            const hit = start + delay;
            const gain = context.createGain();
            gain.gain.setValueAtTime(0.0001, hit);
            gain.gain.linearRampToValueAtTime(volume, hit + 0.008);
            gain.gain.exponentialRampToValueAtTime(0.0001, hit + duration);
            gain.connect(sparkle);

            const oscillator = context.createOscillator();
            oscillator.type = index === 0 ? 'triangle' : 'sine';
            oscillator.frequency.setValueAtTime(frequency, hit);
            oscillator.frequency.exponentialRampToValueAtTime(frequency * 1.08, hit + duration * 0.72);
            oscillator.connect(gain);
            oscillator.start(hit);
            oscillator.stop(hit + duration + 0.02);
        });
    }

    private playCoinSound(): void {
        const context = this.ensureAudioContext();
        if (!context) return;

        const start = context.currentTime;
        const master = context.createGain();
        master.gain.setValueAtTime(0.0001, start);
        master.gain.linearRampToValueAtTime(0.75, start + 0.008);
        master.gain.exponentialRampToValueAtTime(0.0001, start + 0.38);

        const shimmer = context.createBiquadFilter();
        shimmer.type = 'highpass';
        shimmer.frequency.setValueAtTime(900, start);
        shimmer.Q.setValueAtTime(0.8, start);
        shimmer.connect(master);
        master.connect(context.destination);

        [
            { frequency: 1568, delay: 0, duration: 0.18, volume: 0.12 },
            { frequency: 2093, delay: 0.035, duration: 0.2, volume: 0.09 },
            { frequency: 2637, delay: 0.075, duration: 0.22, volume: 0.07 },
            { frequency: 3136, delay: 0.12, duration: 0.18, volume: 0.045 }
        ].forEach(({ frequency, delay, duration, volume }, index) => {
            const hit = start + delay;
            const gain = context.createGain();
            gain.gain.setValueAtTime(0.0001, hit);
            gain.gain.linearRampToValueAtTime(volume, hit + 0.006);
            gain.gain.exponentialRampToValueAtTime(0.0001, hit + duration);
            gain.connect(shimmer);

            const oscillator = context.createOscillator();
            oscillator.type = index % 2 === 0 ? 'triangle' : 'sine';
            oscillator.frequency.setValueAtTime(frequency, hit);
            oscillator.frequency.exponentialRampToValueAtTime(frequency * 1.06, hit + duration * 0.7);
            oscillator.connect(gain);
            oscillator.start(hit);
            oscillator.stop(hit + duration + 0.02);
        });
    }

    private startBackgroundMusic(): void {
        const context = this.ensureAudioContext();
        if (!context) return;

        this.ensureBackgroundMusicGraph(context);
        if (!this.bgmMaster) return;

        const now = context.currentTime;
        this.bgmMaster.gain.cancelScheduledValues(now);
        this.bgmMaster.gain.setValueAtTime(Math.max(this.bgmMaster.gain.value, 0.0001), now);
        this.bgmMaster.gain.linearRampToValueAtTime(0.15, now + 0.55);

        if (!this.bgmSchedulerId) {
            this.bgmNextLoopTime = now + 0.08;
            this.bgmSchedulerId = window.setInterval(() => this.scheduleBackgroundMusic(), 120);
        }
        this.scheduleBackgroundMusic();
    }

    private stopBackgroundMusic(fadeDuration = 0.45): void {
        if (this.bgmSchedulerId) {
            window.clearInterval(this.bgmSchedulerId);
            this.bgmSchedulerId = 0;
        }
        this.bgmNextLoopTime = 0;

        const context = this.audioContext;
        if (!context || !this.bgmMaster || context.state === 'closed') return;

        const now = context.currentTime;
        this.bgmMaster.gain.cancelScheduledValues(now);
        this.bgmMaster.gain.setValueAtTime(Math.max(this.bgmMaster.gain.value, 0.0001), now);
        this.bgmMaster.gain.linearRampToValueAtTime(0.0001, now + fadeDuration);
    }

    private ensureBackgroundMusicGraph(context: AudioContext): void {
        if (this.bgmMaster && this.bgmFilter) return;

        this.bgmFilter = context.createBiquadFilter();
        this.bgmFilter.type = 'lowpass';
        this.bgmFilter.frequency.setValueAtTime(4200, context.currentTime);
        this.bgmFilter.Q.setValueAtTime(0.35, context.currentTime);

        this.bgmMaster = context.createGain();
        this.bgmMaster.gain.setValueAtTime(0.0001, context.currentTime);

        this.bgmFilter.connect(this.bgmMaster);
        this.bgmMaster.connect(context.destination);
    }

    private scheduleBackgroundMusic(): void {
        const context = this.audioContext;
        if (!context || !this.bgmMaster || !this.bgmFilter || context.state === 'closed') return;

        const horizon = context.currentTime + this.bgmScheduleAheadSeconds;
        if (this.bgmNextLoopTime <= 0) this.bgmNextLoopTime = context.currentTime + 0.06;
        while (this.bgmNextLoopTime < horizon) {
            this.scheduleBackgroundMusicLoop(context, this.bgmNextLoopTime);
            this.bgmNextLoopTime += this.bgmLoopBeats * this.bgmBeatSeconds;
        }
    }

    private scheduleBackgroundMusicLoop(context: AudioContext, loopStart: number): void {
        let beat = 0;
        this.bgmMelody.forEach((note) => {
            if (note.midi !== undefined) {
                const start = loopStart + beat * this.bgmBeatSeconds;
                const duration = note.beats * this.bgmBeatSeconds;
                const velocity = note.velocity ?? 0.82;
                this.scheduleBgmTone(context, note.midi, start, duration, 'triangle', 0.052 * velocity, -4);
                this.scheduleBgmTone(context, note.midi + 12, start + 0.012, duration * 0.72, 'sine', 0.014 * velocity, 6);
            }
            beat += note.beats;
        });

        const barCount = Math.ceil(this.bgmLoopBeats / 3);
        for (let bar = 0; bar < barCount; bar += 1) {
            const chord = this.bgmHarmony[bar % this.bgmHarmony.length];
            const barStart = loopStart + bar * 3 * this.bgmBeatSeconds;
            this.scheduleBgmTone(context, chord[0], barStart, 2.75 * this.bgmBeatSeconds, 'sine', 0.028, 0);
            chord.slice(1).forEach((midi, index) => {
                const hit = barStart + index * this.bgmBeatSeconds;
                this.scheduleBgmTone(context, midi + 12, hit, 0.9 * this.bgmBeatSeconds, 'triangle', 0.018, index % 2 === 0 ? -6 : 5);
                this.scheduleBgmTone(context, midi, barStart, 2.9 * this.bgmBeatSeconds, 'sine', 0.0065, 0);
            });
        }
    }

    private scheduleBgmTone(
        context: AudioContext,
        midi: number,
        start: number,
        duration: number,
        type: OscillatorType,
        volume: number,
        detune: number
    ): void {
        if (!this.bgmFilter) return;

        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const end = start + Math.max(duration, 0.05);

        oscillator.type = type;
        oscillator.frequency.setValueAtTime(this.midiToFrequency(midi), start);
        oscillator.detune.setValueAtTime(detune, start);

        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.linearRampToValueAtTime(volume, start + 0.018);
        gain.gain.setTargetAtTime(0.0001, Math.max(start + 0.04, end - 0.08), 0.045);

        oscillator.connect(gain);
        gain.connect(this.bgmFilter);
        oscillator.start(start);
        oscillator.stop(end + 0.18);
    }

    private midiToFrequency(midi: number): number {
        return 440 * Math.pow(2, (midi - 69) / 12);
    }

    private ensureAudioContext(): AudioContext | undefined {
        const audioWindow = window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext };
        const AudioContextClass = audioWindow.AudioContext ?? audioWindow.webkitAudioContext;
        if (!AudioContextClass) return undefined;
        this.audioContext ??= new AudioContextClass();
        if (this.audioContext.state === 'suspended') {
            void this.audioContext.resume();
        }
        return this.audioContext;
    }

    private checkEnemyHits(): void {
        if (this.finished || this.isAdmin) return;
        this.enemies.forEach((enemy) => {
            if (Math.hypot(enemy.x - this.playerPosition.x, enemy.z - this.playerPosition.z) < 1.05 && Math.abs(enemy.y - this.playerPosition.y) < 1.4) {
                this.loseLife(false);
            }
        });
    }

    private checkGoal(): void {
        if (this.finished) return;
        const portalDistance = Math.hypot(this.playerPosition.x - this.goalPosition.x, this.playerPosition.z - this.goalPosition.z);
        if (portalDistance < 1.8 && this.giftsCollected < this.giftTotal) {
            this.banner = 'Portal sealed';
            this.bannerDetail = 'Collect every gift to open it.';
            this.bannerTone = 'normal';
            this.damageBannerTimer = 0;
            return;
        }
        if (this.giftsCollected < this.giftTotal) return;
        if (portalDistance < 1.8) {
            const clearedStage = this.stage;
            const clearedGifts = this.giftsCollected;
            const clearedGiftTotal = this.giftTotal;
            const clearedCoins = this.coinsCollected;
            const clearedTimeLeft = this.timeLeft;
            this.finished = true;
            this.paused = false;
            this.gameMenuOpen = false;
            this.clearControls();
            void this.completeStage(clearedStage, clearedGifts, clearedGiftTotal, clearedCoins, clearedTimeLeft);
        }
    }

    private async completeStage(
        clearedStage: number,
        clearedGifts: number,
        clearedGiftTotal: number,
        clearedCoins: number,
        clearedTimeLeft: number
    ): Promise<void> {
        await this.saveProgress(true, clearedStage, clearedGifts, clearedGiftTotal, clearedCoins, clearedTimeLeft);
        if (this.currentLevelIndex < this.visibleStageCount() - 1) {
            this.currentLevelIndex += 1;
            this.createLevel();
            this.resetGame();
            this.introMode = 'next';
            this.lastClearedStage = clearedStage;
            this.bannerDetail = this.activeLevel?.title ?? this.levels[this.currentLevelIndex].title;
            this.changeDetector.detectChanges();
            return;
        }

        this.finished = true;
        this.paused = false;
        this.gameMenuOpen = false;
        this.banner = 'All stages clear';
        this.bannerDetail = 'The final portal is complete.';
        this.bannerTone = 'normal';
        this.stopBackgroundMusic(1.2);
        this.changeDetector.detectChanges();
    }

    private async saveProgress(
        cleared: boolean,
        stage = this.stage,
        gifts = this.giftsCollected,
        giftTotal = this.giftTotal,
        coins = this.coinsCollected,
        timeLeft = this.timeLeft
    ): Promise<void> {
        if (this.adminTestMode) {
            this.saveMessage = 'Admin test mode';
            this.changeDetector.detectChanges();
            return;
        }
        if (!this.account && coins <= 0 && !cleared) return;
        const bankedCoinsBeforeFlush = Math.min(coins, this.bankedCoinsThisRun);
        const depositedCoins = await this.flushCoinDeposits();
        const bankedCoins = Math.min(coins, bankedCoinsBeforeFlush + depositedCoins);
        const result = await this.snowApi('save', {
            stage,
            gifts,
            gift_total: giftTotal,
            coins,
            banked_coins: bankedCoins,
            time_left: Math.floor(timeLeft),
            cleared
        });
        if (result.ok) {
            this.applyAccountPayload(result.data);
            if (coins > bankedCoins) this.coinDepositPending = Math.max(0, this.coinDepositPending - (coins - bankedCoins));
            if (coins > 0) {
                this.coinsCollected = 0;
                this.bankedCoinsThisRun = 0;
            }
            this.saveMessage = result.offline ? 'Saved locally' : 'Saved to account';
        } else if (this.account) {
            this.saveMessage = result.message ?? 'Save failed';
        }
        this.changeDetector.detectChanges();
    }

    private loseLife(timeout: boolean): void {
        if (this.finished || this.isAdmin) return;
        if (timeout) {
            this.lives = 0;
            this.clearControls();
            this.triggerDamageUi();
            this.finished = true;
            this.paused = false;
            this.gameMenuOpen = false;
            this.gameOver = true;
            this.gameOverReason = 'TIME OVER';
            this.banner = '';
            this.bannerDetail = '';
            this.bannerTone = 'danger';
            this.damageBannerTimer = 0;
            this.playerVelocity.set(0, 0, 0);
            this.stopBackgroundMusic(0.9);
            void this.saveProgress(false);
            return;
        }
        this.loseLives(1, 'CAREFUL');
    }

    private loseLives(amount: number, detail: string): void {
        if (this.finished || this.isAdmin) return;
        this.lives -= amount;
        this.clearControls();
        this.triggerDamageUi();
        if (this.lives <= 0) {
            if (this.activeCheckpointIndex >= 0) {
                this.reviveAtCheckpoint(detail);
                return;
            }
            this.lives = 0;
            this.finished = true;
            this.paused = false;
            this.gameMenuOpen = false;
            this.gameOver = true;
            this.gameOverReason = 'ALL LIVES LOST';
            this.banner = '';
            this.bannerDetail = '';
            this.bannerTone = 'danger';
            this.damageBannerTimer = 0;
            this.playerVelocity.set(0, 0, 0);
            this.stopBackgroundMusic(0.9);
            void this.saveProgress(false);
            return;
        }
        this.banner = `LIFE -${amount}`;
        this.bannerDetail = detail;
        this.bannerTone = 'danger';
        this.damageBannerTimer = 1.55;
        this.setPlayerSpawn();
    }

    private reviveAtCheckpoint(detail: string): void {
        this.lives = 1;
        this.finished = false;
        this.paused = false;
        this.gameMenuOpen = false;
        this.gameOver = false;
        this.gameOverReason = 'ALL LIVES LOST';
        this.banner = 'SAVE POINT';
        this.bannerDetail = `${detail} - revived here.`;
        this.bannerTone = 'normal';
        this.damageBannerTimer = 0;
        this.playerVelocity.set(0, 0, 0);
        this.setPlayerSpawn();
        this.updateHud();
        this.startBackgroundMusic();
    }

    private triggerDamageUi(): void {
        this.damageFlashTimer = 0.48;
        this.damageFlashActive = true;
    }

    private updateDamageUi(delta: number): void {
        if (this.damageFlashTimer > 0) {
            this.damageFlashTimer = Math.max(0, this.damageFlashTimer - delta);
            this.damageFlashActive = this.damageFlashTimer > 0;
        }

        if (this.damageBannerTimer > 0) {
            this.damageBannerTimer = Math.max(0, this.damageBannerTimer - delta);
            if (this.damageBannerTimer === 0 && this.bannerTone === 'danger' && !this.finished) {
                this.banner = '';
                this.bannerDetail = '';
                this.bannerTone = 'normal';
            }
        }
    }

    private setPlayerSpawn(): void {
        const target = this.activeCheckpointIndex >= 0 ? this.respawnPoint : this.spawn;
        const top = this.platformTopAt(target.x, target.z) ?? target.y ?? 0.42;
        this.playerPosition.set(target.x, top, target.z);
        this.playerVelocity.set(0, 0, 0);
        this.slideDrift.set(0, 0);
        this.player.rotation.set(0, this.isAdmin ? Math.PI : 0, 0);
        this.jumpBufferTimer = 0;
        this.coyoteTimer = 0.12;
        this.airJumpsUsed = 0;
        this.updateCamera();
    }

    private isGrounded(): boolean {
        const top = this.platformTopAt(this.playerPosition.x, this.playerPosition.z, this.playerPosition.y + 0.16);
        return top !== undefined && Math.abs(this.playerPosition.y - top) < 0.08 && Math.abs(this.playerVelocity.y) < 0.1;
    }

    private platformTopAt(x: number, z: number, maxTop = Number.POSITIVE_INFINITY): number | undefined {
        const platform = this.platformAt(x, z, maxTop);
        return platform ? platform.y + platform.h * 0.7 : undefined;
    }

    private platformAt(x: number, z: number, maxTop = Number.POSITIVE_INFINITY): Platform | undefined {
        let best: Platform | undefined;
        this.platforms.forEach((item) => {
            if (item.broken || !item.mesh.visible) return;
            const insidePlatform =
                Math.abs(x - item.x) <= item.w / 2 + 0.02 &&
                Math.abs(z - item.z) <= item.d / 2 + 0.02;
            if (!insidePlatform) return;

            const top = item.y + item.h * 0.7;
            if (top > maxTop) return;
            if (!best || top > best.y + best.h * 0.7) best = item;
        });
        return best;
    }

    private updateHud(): void {
        const minutes = Math.floor(this.timeLeft / 60).toString().padStart(2, '0');
        const seconds = Math.floor(this.timeLeft % 60).toString().padStart(2, '0');
        this.timeText = `${minutes}:${seconds}`;
        this.updateMinimap();
    }

    private lerpAngle(from: number, to: number, amount: number): number {
        const delta = Math.atan2(Math.sin(to - from), Math.cos(to - from));
        return from + delta * amount;
    }

    private moveAngleTowards(from: number, to: number, maxStep: number): number {
        const delta = Math.atan2(Math.sin(to - from), Math.cos(to - from));
        if (Math.abs(delta) <= maxStep) return to;
        return from + Math.sign(delta) * maxStep;
    }

    private joystickAnalogAmount(strength: number, deadZone: number): number {
        if (strength <= deadZone) return 0;
        return THREE.MathUtils.clamp((strength - deadZone) / (1 - deadZone), 0, 1);
    }

    private onResize = (): void => {
        const width = Math.max(this.viewportElement.clientWidth, 1);
        const height = Math.max(this.viewportElement.clientHeight, 1);
        const aspect = width / height;
        this.viewportAspect = aspect;
        this.camera.aspect = aspect;
        this.camera.fov = aspect < 0.75 ? 64 : aspect > 1.65 ? 52 : 56;
        this.camera.updateProjectionMatrix();
        const pixelScale = aspect < 0.75 ? 0.62 : 0.48;
        const renderWidth = Math.max(320, Math.floor(width * pixelScale));
        const renderHeight = Math.max(220, Math.floor(height * pixelScale));
        this.renderer.setSize(renderWidth, renderHeight, false);
        this.renderer.domElement.style.width = `${width}px`;
        this.renderer.domElement.style.height = `${height}px`;
        if (this.player) this.updateCamera();
    };

    private onPopState = (): void => {
        this.syncPageFromLocation();
    };

    private onKeyDown = (event: KeyboardEvent): void => {
        if (this.isCustomPage && event.code === 'Escape') {
            event.preventDefault();
            this.closeCustomPage();
            return;
        }
        if (this.isShopPage && event.code === 'Escape') {
            event.preventDefault();
            this.closeShopPage();
            return;
        }

        if (this.isUiKeyboardTarget(event.target)) return;

        if (this.isMenuPage) {
            return;
        }

        if (this.gameMenuOpen && event.code === 'Escape') {
            event.preventDefault();
            this.continueGame();
            return;
        }

        if (this.started && !this.gameOver && !this.finished && (event.code === 'Escape' || event.code === 'KeyP')) {
            event.preventDefault();
            if (event.code === 'Escape') this.toggleGameMenu();
            else this.stopOrResumeFromGameMenu();
            return;
        }

        if (this.gameplayKeyCodes.has(event.code)) {
            event.preventDefault();
        }
        if (this.paused || this.gameMenuOpen) {
            return;
        }
        if (!this.started && ['Space', 'Enter'].includes(event.code)) {
            this.startGame();
            return;
        }
        if (event.code === 'Space') {
            if (!event.repeat) this.queueJump();
        }
        if (event.code === 'KeyR') {
            this.resetGame();
        }
        this.keys.add(event.code);
    };

    private onKeyUp = (event: KeyboardEvent): void => {
        this.keys.delete(event.code);
    };

    private isUiKeyboardTarget(target: EventTarget | null): boolean {
        if (!(target instanceof HTMLElement)) return false;
        if (
            target instanceof HTMLInputElement ||
            target instanceof HTMLTextAreaElement ||
            target instanceof HTMLSelectElement ||
            target instanceof HTMLButtonElement ||
            target instanceof HTMLAnchorElement
        ) return true;
        if (target.isContentEditable) return true;
        return !!target.closest('input, textarea, select, button, a[href], [contenteditable="true"], [contenteditable="plaintext-only"], [role="textbox"], [role="button"]');
    }

    private onPointerMove = (event: PointerEvent): void => {
        if (event.pointerType === 'touch' || !this.viewportElement) return;
        this.mobileLookMode = false;
        if (!this.started || this.paused || this.gameMenuOpen || this.gameOver) {
            this.mouseLookTarget.set(0, 0);
            return;
        }

        const rect = this.viewportElement.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
            this.mouseLookTarget.set(0, 0);
            return;
        }

        const x = ((event.clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1;
        const y = ((event.clientY - rect.top) / Math.max(rect.height, 1)) * 2 - 1;
        this.mouseLookTarget.set(
            THREE.MathUtils.clamp(x, -1, 1),
            THREE.MathUtils.clamp(y, -1, 1)
        );
    };

    private onPointerLeave = (): void => {
        if (this.joystickActive) return;
        this.mouseLookTarget.set(0, 0);
    };

    private updateJoystickFromPointer(event: PointerEvent): void {
        const rawX = event.clientX - this.joystickCenter.x;
        const rawY = event.clientY - this.joystickCenter.y;
        const distance = Math.hypot(rawX, rawY);
        const scale = distance > this.joystickMaxDistance ? this.joystickMaxDistance / Math.max(distance, 1) : 1;
        const knobX = rawX * scale;
        const knobY = rawY * scale;
        this.joystickKnobX = knobX;
        this.joystickKnobY = knobY;
        this.joystickVector.set(
            THREE.MathUtils.clamp(knobX / this.joystickMaxDistance, -1, 1),
            THREE.MathUtils.clamp(knobY / this.joystickMaxDistance, -1, 1)
        );
        this.syncJoystickLook();
    }

    private syncJoystickLook(): void {
        const strength = this.joystickVector.length();
        if (!this.joystickActive || strength < this.mobileLookDeadZone || !this.started || this.paused || this.gameMenuOpen || this.gameOver || this.isMenuPage) {
            if (this.joystickActive && strength < this.mobileLookDeadZone) {
                this.mobileDesiredYaw = this.mobileLookYaw;
                this.mobileLookYawTarget = this.mobileLookYaw;
            }
            if (this.joystickActive) this.mouseLookTarget.set(0, 0);
            return;
        }
        this.mobileDesiredYaw = this.joystickMovementYaw();
        this.mouseLookTarget.set(
            0,
            THREE.MathUtils.clamp(this.joystickVector.y * 0.08, -0.08, 0.08)
        );
    }

    private joystickMovementYaw(): number {
        return this.joystickBaseYaw + Math.atan2(this.joystickVector.x, -this.joystickVector.y);
    }

    private resetJoystick(): void {
        this.joystickPointerId = -1;
        this.joystickActive = false;
        this.joystickVector.set(0, 0);
        this.joystickKnobX = 0;
        this.joystickKnobY = 0;
        this.mouseLookTarget.set(0, 0);
        this.joystickBaseYaw = this.mobileLookYaw;
        this.mobileDesiredYaw = this.mobileLookYaw;
        this.mobileLookYawTarget = this.mobileLookYaw;
    }

    private setPaused(active: boolean): void {
        if (active) {
            if (this.paused || !this.pauseButtonVisible) return;
            this.gameMenuOpen = false;
            this.paused = true;
            this.clearControls();
            this.stopBackgroundMusic(0.18);
            this.changeDetector.detectChanges();
            return;
        }

        if (!this.paused) return;
        this.paused = false;
        this.gameMenuOpen = false;
        this.clearControls();
        if (this.pauseButtonVisible) this.startBackgroundMusic();
        this.changeDetector.detectChanges();
    }

    private clearTouchControls = (event?: PointerEvent): void => {
        if (event) {
            if (event.pointerId === this.joystickPointerId) this.endJoystick(event);
            if (event.pointerId === this.jumpPointerId) this.releaseJump(event);
            return;
        }
        this.touch.forward = false;
        this.touch.back = false;
        this.touch.left = false;
        this.touch.right = false;
        this.resetJoystick();
        this.releaseJump();
    };

    private clearControls = (): void => {
        this.keys.clear();
        this.clearTouchControls();
        this.jumpQueued = false;
        this.jumpBufferTimer = 0;
    };

    private onVisibilityChange = (): void => {
        if (document.hidden) {
            if (this.pauseButtonVisible && !this.paused) {
                this.setPaused(true);
                return;
            }
            this.clearControls();
            this.stopBackgroundMusic(0.25);
            return;
        }
        if (this.started && !this.paused && !this.gameOver && !this.finished) this.startBackgroundMusic();
    };
}
